import { liteEn, liteFr, type LiteTranslations } from "./lite";

export type Language = "fr" | "en";

export interface MarketingPageCopy {
  h1: string;
  subtitle: string;
  bullets: string[];
}
export interface MarketingIntegrationCard {
  title: string;
  badge?: string;
  body: string;
}
export interface MarketingIntegrationsCopy {
  h1: string;
  subtitle: string;
  autoSyncTitle: string;
  autoSyncCards: MarketingIntegrationCard[];
  csvTitle: string;
  csvIntro: string;
  csvItems: string[];
}
export interface MarketingComparisonCopy {
  h1: string;
  subtitle: string;
  why: string[];
}
export interface MarketingJournalPillar {
  title: string;
  body: string;
}
export interface MarketingJournalFaqItem {
  q: string;
  a: string;
}
export interface MarketingJournalCopy {
  heroTitle: string;
  heroSubtitle: string;
  heroCta: string;
  statsWinRate: string;
  statsProfitFactor: string;
  statsDrawdown: string;
  pillarsTitle: string;
  pillars: MarketingJournalPillar[];
  importTitle: string;
  importSteps: string[];
  whyTitle: string;
  whyBody: string;
  toolsTitle: string;
  toolsExcel: string;
  toolsPaper: string;
  toolsApp: string;
  pricingTitle: string;
  pricingFree: string;
  pricingPro: string;
  pricingMax: string;
  faqTitle: string;
  faq: MarketingJournalFaqItem[];
  ctaTitle: string;
  ctaButton: string;
}

export interface Translations {
  /** Mode Lite : traductions dans src/client/i18n/lite/ (un fichier par page). */
  lite: LiteTranslations;
  // Navigation
  nav: {
    login: string;
    dashboard: string;
    journal: string;
    analytics: string;
    routines: string;
    goals: string;
    notebook: string;
    disciplineManager: string;
    accounts: string;
    account: string;
    sessions: string;
    logout: string;
    tools: string;
    features: string;
    pricing: string;
    faq: string;
    propFirmManager: string;
    reports: string;
    blog: string;
    mentor: string;
    affiliate: string;
    addPage: string;
    addPagePlaceholder: string;
    upgradeMyPlan: string;
  };
  themePicker: {
    label: string;
    classicDark: string;
    classicLight: string;
    deepJungle: string;
    sunrise: string;
    tokyoNight: string;
    dark: string;
    light: string;
    switchTo: string;
    comingSoon: string;
  };
  disciplineManager: {
    title: string;
    sections: {
      routines: string;
      goals: string;
      notebook: string;
    };
  };
  mentor: {
    title: string;
    dashboard: string;
    myStudents: string;
    myInvitations: string;
    noStudents: string;
    noInvitations: string;
    upgradeToBecomeMentor: string;
    revokeRelation: string;
    revokeConfirm: string;
    resendInvitation: string;
    cancelInvitation: string;
    viewStudent: string;
    status: {
      pending: string;
      accepted: string;
      declined: string;
      revoked: string;
      expired: string;
    };
    invite: {
      title: string;
      emailLabel: string;
      emailPlaceholder: string;
      sendInvitation: string;
      inviteButton: string;
      inviteEmailPlaceholder: string;
      inviteSubmit: string;
      importList: string;
      importTitle: string;
      importDescription: string;
      preview: string;
      confirmSend: string;
      willBeInvited: string;
      alreadyInvited: string;
      maxImport: string;
    };
    notifications: {
      invitationReceived: string;
      invitationAccepted: string;
      invitationDeclined: string;
      relationRevoked: string;
      noteAdded: string;
      markAllRead: string;
      noNotifications: string;
    };
    errors: {
      invalidEmail: string;
    };
    studentView: string;
    readOnlyMode: string;
    backToStudents: string;
    permissionsTitle: string;
    canViewJournal: string;
    canViewDashboard: string;
    canViewAnalytics: string;
    canViewSessions: string;
    canViewPropFirmManager: string;
    mentorNotes: string;
    addNote: string;
    editNote: string;
    deleteNote: string;
    noNotes: string;
    notePlaceholder: string;
    saveNote: string;
    acceptInvitation: string;
    declineInvitation: string;
    invitationExpired: string;
    invitationNotFound: string;
    invitationDeclinedConfirm: string;
    loginToAccept: string;
    invitationFrom: string;
    grantedPermissions: string;
    mentorsSection: string;
    noMentors: string;
    revokeMentor: string;
    revokeMentorConfirm: string;
    activeSince: string;
    pendingInvitations: string;
    globalNotes: string;
    sessionNotes: string;
    tradeNotes: string;
    noPermissionSection: string;
  };
  comparator: {
    metaTitle: string;
    metaDescription: string;
    h1: string;
    introP1: string;
    introP2: string;
    resultsCount: string;
    filtersTitle: string;
    searchLabel: string;
    searchPlaceholder: string;
    firmLabel: string;
    showAll: string;
    showLess: string;
    sizeLabel: string;
    accountTypeLabel: string;
    drawdownTypeLabel: string;
    platformLabel: string;
    dailyLossLabel: string;
    ratingLabel: string;
    maxPriceLabel: string;
    resetFilters: string;
    filtersButton: string;
    typeEval: string;
    typeDirect: string;
    ddEod: string;
    ddEot: string;
    ddTrailing: string;
    ddStatic: string;
    dailyAll: string;
    dailyNone: string;
    dailyDefined: string;
    ratingAll: string;
    ratingHigh: string;
    ratingMid: string;
    ratingLow: string;
    colFirm: string;
    colSize: string;
    colPrice: string;
    colBilling: string;
    colActivation: string;
    colPlatforms: string;
    colDrawdown: string;
    colTarget: string;
    colRoundTrip: string;
    colRating: string;
    colAction: string;
    sortBy: string;
    billingOneTime: string;
    billingMonthly: string;
    free: string;
    total: string;
    promoExpires: string;
    copyCode: string;
    codeCopied: string;
    startChallenge: string;
    officialSite: string;
    compareCheckbox: string;
    compareBar: string;
    compareReset: string;
    compareTitle: string;
    compareMax: string;
    bestValue: string;
    suggestFirmTitle: string;
    suggestFirmCta: string;
    edgelyCtaTitle: string;
    edgelyCtaButton: string;
    noResults: string;
  };
  blog: {
    title: string;
    readMore: string;
    backToBlog: string;
    publishedOn: string;
    by: string;
    noPostsFound: string;
    loadMore: string;
    filterByCategory: string;
    allCategories: string;
    draft: string;
    published: string;
    postNotFound: string;
    subtitle: string;
    tableOfContents: string;
  };
  help: {
    navLabel: string;
    metaTitle: string;
    heroTitle: string;
    heroSubtitle: string;
    searchPlaceholder: string;
    categoriesTitle: string;
    questionsCount: string;
    searchResultsTitle: string;
    noResultsTitle: string;
    noResultsBody: string;
    backToCategories: string;
    inCategory: string;
    notFoundTitle: string;
    notFoundBody: string;
    contactCta: string;
  };
  adminLayouts: {
    title: string;
    searchPlaceholder: string;
    scopeAll: string;
    noLayouts: string;
    owner: string;
    widgets: string;
    edgelyBadge: string;
    sharedBadge: string;
    markEdgely: string;
    unmarkEdgely: string;
    editMeta: string;
    displayName: string;
    description: string;
    requiredPlan: string;
    allPlans: string;
    saveMetaSuccess: string;
    flagUpdated: string;
    confirmUnmark: string;
  };
  adminBlog: {
    title: string;
    newPost: string;
    manageCategories: string;
    editPost: string;
    createPost: string;
    titleField: string;
    slugField: string;
    categoryField: string;
    noCategory: string;
    tagsField: string;
    authorField: string;
    coverImage: string;
    seoSection: string;
    metaTitle: string;
    metaDescription: string;
    seoBodyTitle: string;
    seoBodyHelp: string;
    seoBodyH1: string;
    seoBodySections: string;
    seoBodySectionsHelp: string;
    seoBodyLinks: string;
    seoBodyLinksHelp: string;
    excerptField: string;
    saveDraft: string;
    publish: string;
    unpublish: string;
    savePublished: string;
    deletePost: string;
    confirmDelete: string;
    statusAll: string;
    statusDraft: string;
    statusPublished: string;
    categoryName: string;
    categorySlug: string;
    addCategory: string;
    deleteCategory: string;
    confirmDeleteCategory: string;
    categoryHasPosts: string;
    postSaved: string;
    postPublished: string;
    postUnpublished: string;
    postDeleted: string;
    categorySaved: string;
    categoryDeleted: string;
  };
  adminHelp: {
    categoriesTitle: string;
    questionsTitle: string;
    newCategory: string;
    newQuestion: string;
    nameFr: string;
    nameEn: string;
    slug: string;
    icon: string;
    sortOrder: string;
    questionColumn: string;
    categoryColumn: string;
    statusColumn: string;
    published: string;
    draft: string;
    edit: string;
    delete: string;
    save: string;
    cancel: string;
    questionFr: string;
    answerFr: string;
    questionEn: string;
    answerEn: string;
    keywords: string;
    category: string;
    preview: string;
    publish: string;
    unpublish: string;
    deleteCategoryBlocked: string;
    confirmDelete: string;
    itemCount: string;
    filterAllStatuses: string;
    filterAllCategories: string;
    saved: string;
    deleted: string;
    manageQuestions: string;
    manageCategories: string;
  };
  propFirmManager: {
    title: string;
    subtitle: string;
    totalPayouts: string;
    totalExpenses: string;
    netProfit: string;
    roiGlobal: string;
    activeAccounts: string;
    bestRoi: string;
    noBestRoi: string;
    totalAccounts: string;
    accountsWithPayout: string;
    successRate: string;
    calendarPayout: string;
    calendarExpense: string;
    calendarRecurring: string;
    calendarTooltipPayouts: string;
    calendarTooltipExpenses: string;
    calendarTooltipRecurring: string;
    calendarEventSingular: string;
    calendarEventPlural: string;
    calendarFunded: string;
    calendarTooltipFunded: string;
    calendarReset: string;
    calendarTooltipReset: string;
    noEventsThisMonth: string;
    eventsThisMonth: string;
    proRequired: string;
    monthRecapTitle: string;
    monthRecapChallenges: string;
    monthRecapActivations: string;
    monthRecapSubscriptions: string;
    monthRecapPayouts: string;
    monthRecapTotalExpenses: string;
    monthRecapRoi: string;
    monthRecapNoData: string;
    importHistory: string;
    importModalTitle: string;
    importModalDesc: string;
    downloadTemplate: string;
    importBtn: string;
    importSuccess: string;
    noValidRows: string;
    importDropZone: string;
    importParseError: string;
    importParseErrorDesc: string;
    importWrongFormatTitle: string;
    importWrongFormatDesc: string;
    importFailError: string;
    importFailErrorDesc: string;
    importAccountsToCreate: string;
    importConflicts: string;
    importConflictSkip: string;
    importConflictUpdate: string;
    importConflictEventsWarning: string;
    importPayoutsSection: string;
    importSummaryLine: string;
    importSuccessAccounts: string;
    importBlockedTitle: string;
    importBlockedDesc: string;
    importFixFile: string;
    importSkippedRows: string;
    importErrorLine: string;
    importReferenceTitle: string;
    importRefPropFirm: string;
    importRefAccountType: string;
    importReferenceSizes: string;
    importRefStrategies: string;
    importRefNoStrategy: string;
    addPayoutBtn: string;
    addPayoutModalTitle: string;
    addPayoutLine: string;
    addPayoutSave: string;
    addPayoutSuccess: string;
    addPayoutSuccessWithCertFailures: string;
    addPayoutAccount: string;
    addPayoutDate: string;
    addPayoutAmount: string;
    addPayoutGrossAmount: string;
    addPayoutSplitPercent: string;
    addPayoutNetAmount: string;
    addPayoutCertificate: string;
    addPayoutCertificatePick: string;
    addPayoutNoActiveAccounts: string;
    recentPayoutsTitle: string;
    recentPayoutsEmpty: string;
    recentPayoutsHeaderDate: string;
    recentPayoutsHeaderAccount: string;
    recentPayoutsHeaderAmount: string;
    recentPayoutsHeaderActions: string;
    wallet: {
      title: string;
      availableBalance: string;
      initialBalance: string;
      transfersReceived: string;
      withdrawn: string;
      inFlight: string;
      setInitialBalance: string;
      editInitialBalance: string;
      initialBalanceBannerTitle: string;
      initialBalanceBannerDesc: string;
      ignore: string;
      add: string;
      transferTab: string;
      withdrawalTab: string;
      withdrawalTabDesc: string;
      transfersTableTitle: string;
      withdrawalsTableTitle: string;
      colDate: string;
      colAccount: string;
      colGross: string;
      colSplit: string;
      colNet: string;
      colAmount: string;
      colMethod: string;
      colStatus: string;
      colActions: string;
      statusPending: string;
      statusApproved: string;
      statusRejected: string;
      statusCompleted: string;
      detectedTag: string;
      detectedTitle: string;
      splitEditLabel: string;
      accountLabel: string;
      dateLabel: string;
      grossAmountLabel: string;
      splitPercentLabel: string;
      netAmountLabel: string;
      amountLabel: string;
      methodLabel: string;
      methodPlaceholder: string;
      notesLabel: string;
      notesPlaceholder: string;
      initialBalanceLabel: string;
      initialBalanceDateLabel: string;
      saveInitialBalance: string;
      transferAdded: string;
      withdrawalAdded: string;
      transferDeleted: string;
      withdrawalDeleted: string;
      statusUpdated: string;
      noTransfers: string;
      noWithdrawals: string;
      deleteTransferConfirm: string;
      deleteWithdrawalConfirm: string;
      emptyTitle: string;
      emptyDesc: string;
    };
  };
  // Dashboard
  dashboard: {
    lockedWidget: { title: string; description: string; cta: string };
    title: string;
    subtitle: string;
    totalPnl: string;
    winRate: string;
    profitFactor: string;
    totalTrades: string;
    recentTrades: string;
    activeGoals: string;
    activeRoutines: string;
    viewAll: string;
    open: string;
    noRecentTrades: string;
    noActiveGoals: string;
    tradesTotal: string;
    tradesOpen: string;
    addTrade: string;
    selectPlan: string;
    selectPlanDescription: string;
    upgradeTitle: string;
    upgradeDescription: string;
    upgradeButton: string;
    continueWithFree: string;
    planChange: {
      upgradedTitle: string;
      upgradedDescription: string;
      alreadyOnPlan: string;
    };
    wins: string;
    losses: string;
    journalLabel: string;
    goalsLabel: string;
    routinesToCompleteToday: string;
    routineToCompleteToday: string;
    manageAccounts: string;
    goalsWithDeadlineThisWeek: string;
    goalWithDeadlineThisWeek: string;
    topGoalProgress: string;
    topGoalDeadlineSoon: string;
    routineReminderToday: string;
    routineReminderWeek: string;
    routineReminderMonth: string;
    calendar: {
      title: string;
      previousMonth: string;
      nextMonth: string;
      trade: string;
      routine: string;
      goal: string;
      completed: string;
      notCompleted: string;
      more: string;
      win: string;
      loss: string;
      trades: string;
      noTrades: string;
      profitDay: string;
      lossDay: string;
      routinesCompleted: string;
      goalDeadline: string;
      weeklyTotal: string;
      week: string;
      usMarketClosed: string;
      euMarketClosed: string;
      marketClosed: string;
      frPublicHoliday: string;
      accountCreated: string;
      accountCrashed: string;
      accountCreatedMany: string;
      accountCrashedMany: string;
      accountFunded: string;
      accountFundedMany: string;
      calendarPayout: string;
      calendarPayoutMany: string;
      hideWeekend: string;
      showWeekend: string;
      hideErrors: string;
      showErrors: string;
      errorsOnDay: string;
      calendarSettings: string;
      settingsWeekend: string;
      settingsErrors: string;
      settingsTradingDayStart: string;
      settingsTradingDayStartDesc: string;
      violations: {
        dailyLossViolation: string;
        dailyLossWarning: string;
        consistencyViolation: string;
        consistencyWarning: string;
        consistencyViolationDetail: string;
        consistencyWarningDetail: string;
        tooltipDailyLoss: string;
        tooltipConsistency: string;
      };
    };
    dayDetail: {
      title: string;
      noTrades: string;
      tradesCount: string;
      tradesSummaryOnly: string;
      pnlByAccount: string;
      pnlByGroup: string;
      total: string;
      session: string;
      noSession: string;
      mood: string;
      duration: string;
      performance: string;
      sessionNotes: string;
      routines: string;
      noRoutines: string;
      completed: string;
      notCompleted: string;
      goalDeadlines: string;
      noGoalDeadlines: string;
      marketHolidays: string;
      marketClosed: string;
      symbol: string;
      direction: string;
      pnl: string;
      account: string;
      showAll: string;
      hideAll: string;
      page: string;
      of: string;
      accountEvents: string;
      noAccountEvents: string;
      fees: string;
      deleteAllTrades: string;
      deleteAllTradesTitle: string;
      deleteAllTradesConfirm: string;
      deleteAllTradesSuccess: string;
      winRate: string;
      profitFactor: string;
      avgWin: string;
      avgLoss: string;
      bestTrade: string;
      worstTrade: string;
      accountsTraded: string;
      winsLosses: (wins: number, losses: number) => string;
      intradayCurve: string;
    };
    marketHours: {
      title: string;
      open: string;
      closed: string;
      opensAt: string;
      closesAt: string;
    };
    cumulativePnl: string;
    chartModePnl: string;
    chartModeNetWorth: string;
    accountsLabel: string;
    noAccounts: string;
    addAccount: string;
    currentBalance: string;
    netPnlLabel: string;
    profitFactorLabel: string;
    tradeWinPct: string;
    avgWinLoss: string;
    todayRoutines: string;
    allRoutinesDone: string;
    noActiveRoutines: string;
    noActiveRoutinesDesc: string;
    createFirstRoutine: string;
    daily: string;
    weekly: string;
    monthly: string;
    daysLeft: string;
    today: string;
    topGoals: string;
    noGoalInProgress: string;
    noGoalInProgressDesc: string;
    createFirstGoal: string;
    allGoalsCompleted: string;
    allGoalsCompletedDesc: string;
    goalProgress: string;
    noTradesYet: string;
    noTradesYetDesc: string;
    accountsTableName: string;
    accountsTableFirm: string;
    accountsTableStatus: string;
    accountsTableStrategy: string;
    accountsTableBalance: string;
    accountsTablePnl: string;
    accountsTableObjective: string;
    accountsTableBuffer: string;
    avgWin: string;
    avgLoss: string;
    bufferLeft: string;
    bufferUsed: string;
    statusFunded: string;
    markRoutineComplete: string;
    period7d: string;
    period30d: string;
    period90d: string;
    // Widget customization
    customize: string;
    done: string;
    addWidget: string;
    removeWidget: string;
    resetLayout: string;
    resetLayoutConfirm: string;
    resetLayoutConfirmTitle: string;
    allWidgetsVisible: string;
    noWidgetsVisible: string;
    widgetSaved: string;
    widgetSaveError: string;
    widgetSizeLabel: string;
    resizeWidgetRight: string;
    resizeWidgetBottom: string;
    resizeWidgetCorner: string;
    moveUp: string;
    moveDown: string;
    widgets: {
      netPnl: string;
      profitFactor: string;
      winRate: string;
      avgWinLoss: string;
      calendar: string;
      marketHours: string;
      routines: string;
      goals: string;
      session: string;
      equityChart: string;
      accountsTable: string;
      analyticsKpis: string;
      equityCurve: string;
      pnlDistribution: string;
      monthlyPnl: string;
      symbolPerformance: string;
      rollingWinRate: string;
      heatmap: string;
      behavioralBiases: string;
      sessionTimeSlots: string;
      sessionMood: string;
      sessionDuration: string;
      sessionPnlChart: string;
      outsideSession: string;
      sessionInsight: string;
      sessionsList: string;
      lastSession: string;
      pipStats: string;
      pipDistribution: string;
      pipBySymbol: string;
      longShort: string;
      dayOfWeek: string;
      pnlPerTrade: string;
      netDailyPnl: string;
      riskAdjusted: string;
      drawdownCurve: string;
      holdDuration: string;
      consistencyScore: string;
      streakAnalysis: string;
      tagStats: string;
      sizingAnalysis: string;
      propfirmKpis: string;
      propfirmCalendar: string;
      propfirmMonthRecap: string;
      propfirmReporting: string;
      propfirmAccounts: string;
      propfirmAccountsTable: string;
      propfirmGlobalCharges: string;
      propfirmUpcomingCharges: string;
      propfirmPayoutsList: string;
      propfirmFilters: string;
      propfirmWalletBalance: string;
      propfirmWalletTransfers: string;
      propfirmWalletWithdrawals: string;
      propfirmChallengeOverview: string;
      note: string;
      disciplineRoutines: string;
      disciplineGoals: string;
      notebookBrowser: string;
      mentorNotes: string;
      mentorNotesEmpty: string;
      topbarDateFilter: string;
      topbarAccountFilter: string;
      topbarStrategyFilter: string;
      topbarAddTrade: string;
      topbarAddPayout: string;
      topbarPfmFilter: string;
      topbarPfmFilterAll: string;
      topbarPhaseFilter: string;
      topbarPhaseFilterAll: string;
      topbarPhaseFilterChallenge: string;
      topbarPhaseFilterPa: string;
      topbarPhaseFilterLive: string;
      newsFeed: string;
      interestRates: string;
      economicCalendar: string;
      earningsCalendar: string;
      chart: string;
      tradesTable: string;
      topbarSymbolFilter: string;
      topbarSideFilter: string;
      topbarStatusFilter: string;
      topbarPnlFilter: string;
      topbarTagsFilter: string;
    };
    widgetLock: {
      proFeatureLabel: string;
    };
    widgetsDesc: {
      netPnl: string;
      profitFactor: string;
      winRate: string;
      avgWinLoss: string;
      calendar: string;
      marketHours: string;
      routines: string;
      goals: string;
      session: string;
      equityChart: string;
      accountsTable: string;
      analyticsKpis: string;
      equityCurve: string;
      pnlDistribution: string;
      monthlyPnl: string;
      symbolPerformance: string;
      rollingWinRate: string;
      heatmap: string;
      behavioralBiases: string;
      sessionTimeSlots: string;
      sessionMood: string;
      sessionDuration: string;
      sessionPnlChart: string;
      outsideSession: string;
      sessionInsight: string;
      sessionsList: string;
      lastSession: string;
      pipStats: string;
      pipDistribution: string;
      pipBySymbol: string;
      longShort: string;
      dayOfWeek: string;
      pnlPerTrade: string;
      netDailyPnl: string;
      riskAdjusted: string;
      drawdownCurve: string;
      holdDuration: string;
      consistencyScore: string;
      streakAnalysis: string;
      tagStats: string;
      sizingAnalysis: string;
      propfirmKpis: string;
      propfirmCalendar: string;
      propfirmMonthRecap: string;
      propfirmReporting: string;
      propfirmAccounts: string;
      propfirmAccountsTable: string;
      propfirmGlobalCharges: string;
      propfirmUpcomingCharges: string;
      propfirmPayoutsList: string;
      propfirmFilters: string;
      propfirmWalletBalance: string;
      propfirmWalletTransfers: string;
      propfirmWalletWithdrawals: string;
      propfirmChallengeOverview: string;
      note: string;
      mentorNotes: string;
      mentorNotesEmpty: string;
      topbarDateFilter: string;
      topbarAccountFilter: string;
      topbarStrategyFilter: string;
      topbarAddTrade: string;
      topbarAddPayout: string;
      topbarPfmFilter: string;
      topbarPfmFilterAll: string;
      topbarPhaseFilter: string;
      topbarPhaseFilterAll: string;
      topbarPhaseFilterChallenge: string;
      topbarPhaseFilterPa: string;
      topbarPhaseFilterLive: string;
      newsFeed: string;
      interestRates: string;
      economicCalendar: string;
      earningsCalendar: string;
      chart: string;
    };
    widgetCategories: {
      dashboard: string;
      analytics: string;
      analyticsPerformance: string;
      analyticsSessions: string;
      analyticsPips: string;
      propFirm: string;
      notes: string;
      mentorProgram: string;
      topbar: string;
      marketData: string;
      journal: string;
      sessions: string;
      discipline: string;
    };
    // Rows + slots system
    rowLabel: string; // template with "{n}"
    addRow: string;
    removeRow: string;
    removeRowConfirm: string;
    removeRowConfirmTitle: string;
    rowColumnsLabel: string;
    rowColumnsCount: string; // template with "{n}"
    changeRowColumns: string;
    changeRowColumnsWarning: string;
    addRowPosition: string;
    addRowPositionBottom: string;
    addRowPositionBefore: string; // template with "{n}"
    addRowPositionAfter: string;  // template with "{n}"
    widgetMinCols: string;        // template with "{n}"
    widgetAlreadyPlaced: string;
    noCompatibleWidgets: string;

    // Layouts library
    layouts: {
      menuTitle: string;
      activeBadge: string;
      defaultBadge: string;
      newLayout: string;
      duplicate: string;
      rename: string;
      share: string;
      importLink: string;
      delete: string;
      upgradeForMultiple: string;
      cannotDeleteLast: string;

      // Rename / Delete dialogs
      renameDialogTitle: string;
      renameInputLabel: string;
      deleteDialogTitle: string;
      deleteDialogConfirm: string; // template "{name}"
      deleteDialogWarning: string;

      // Share dialog
      shareDialogTitle: string; // template "{name}"
      shareToggle: string;
      shareWarning: string;
      copyLink: string;
      linkCopied: string;

      // Import flow
      importPromptTitle: string;
      importPromptInputLabel: string;
      importedSuccess: string;

      // Preview page
      sharePreviewBy: string; // template "{username}"
      sharePreviewMeta: string; // template "{rows} {widgets}"
      importToLibrary: string;
      previewLoginRequired: string;
      previewLoginCta: string;
      previewLinkInactive: string;
      previewLinkInactiveDesc: string;

      // Layout Library Modal
      libraryTitle: string;
      sourceMyLayouts: string;
      sourceEdgely: string;
      sourceCommunity: string;
      wrongScopeTooltip: string;
      newLayoutPlaceholder: string;
      previewEmpty: string;
      communityEmpty: string;
      edgelyEmpty: string;
      authorLabel: string;
      actionsRename: string;
      actionsDuplicate: string;
      actionsShare: string;
      actionsDelete: string;
      importOnThisPage: string;
      importNewPage: string;
      importSaveOnly: string;
      importActivate: string;
      importConfirmTitle: string;
      importConfirmOnThisPage: string;
      importConfirmNewPage: string;
      importConfirmSaveOnly: string;
      shareLibraryToggle: string;
      shareDisplayNameLabel: string;
      shareDescriptionLabel: string;
      shareDescriptionPlaceholder: string;
      shareSettingsSaved: string;
      newPageWithLayout: string;
      newPageBlank: string;
      scopeDashboard: string;
      scopePfm: string;
      scopeCustomPage: string;
      noName: string;
      requiresPro: string;
    };
  };
  // Common
  common: {
    save: string;
    cancel: string;
    edit: string;
    delete: string;
    create: string;
    update: string;
    loading: string;
    error: string;
    success: string;
    close: string;
    back: string;
    next: string;
    previous: string;
    search: string;
    filter: string;
    actions: string;
    date: string;
    time: string;
    duration: string;
    minutes: string;
    validationError: string;
    tradeLimitReached: string;
    tradeLimitWarning: string;
    proceed: string;
    soon: string;
    beta: string;
  };
  // Auth
  auth: {
    login: string;
    signup: string;
    email: string;
    password: string;
    confirmPassword: string;
    forgotPassword: string;
    resetPassword: string;
    requestPasswordReset: string;
    passwordResetRequested: string;
    passwordResetSuccess: string;
    passwordResetError: string;
    noAccount: string;
    goToSignup: string;
    forgotPasswordQuestion: string;
    resetIt: string;
    ifEverythingOk: string;
    goToLogin: string;
    emailPlaceholder: string;
    passwordPlaceholder: string;
    confirmPasswordPlaceholder: string;
    passwordsDoNotMatch: string;
    passwordsMatch: string;
    passwordTooShort: string;
    passwordMinLength: string;
    signupError: string;
    signingUp: string;
    firstName: string;
    lastName: string;
    firstNamePlaceholder: string;
    lastNamePlaceholder: string;
    firstNameRequired: string;
    lastNameRequired: string;
    emailVerificationPending: string;
    emailVerificationCheckSpam: string;
    emailVerificationVerifying: string;
    emailVerificationSuccess: string;
    emailVerificationError: string;
    emailVerificationInvalidToken: string;
    resendEmail: string;
    resendEmailLoading: string;
    resendEmailCooldown: string;
    resendEmailSuccess: string;
    resendEmailError: string;
  };
  // Validation
  validation: {
    fieldRequired: string;
    fieldInvalid: string;
    stringTooShort: string;
    stringTooLong: string;
    numberTooSmall: string;
    numberTooBig: string;
    arrayTooSmall: string;
    invalidType: string;
    invalidEnum: string;
    invalidDate: string;
    invalidEmail: string;
    invalidString: string;
    validationError: string;
  };
  // Account
  account: {
    title: string;
    email: string;
    username: string;
    about: string;
    plan: string;
    freePlan: string;
    upgradeToPro: string;
    managePayment: string;
    notDefined: string;
    noDescription: string;
    profileUpdated: string;
    updateError: string;
    statusActive: string;
    statusCancelAtPeriodEnd: string;
    statusPastDue: string;
    renewalOn: string;
    expiresOn: string;
    inDays: string;
    tomorrow: string;
    today: string;
    pastDueWarning: string;
    statusDeleted: string;
    preferences: string;
    hideWeekend: string;
    showCalendarErrors: string;
    hideInactiveAccounts: string;
  };
  // Pricing
  pricing: {
    title: string;
    subtitle: string;
    perMonth: string;
    perYear: string;
    annual: string;
    monthly: string;
    savePercent: string;
    billedAnnually: string;
    manageSubscription: string;
    cta: {
      free: string;
      buy: string;
      login: string;
    };
    checkout: {
      successTitle: string;
      canceledTitle: string;
      activating: string;
      redirecting: string;
      pendingTitle: string;
      pendingDescription: string;
      backToAccount: string;
    };
    beta: {
      banner: string;
      comingSoon: string;
      comingSoonTooltip: string;
      modalTitle: string;
      modalDescription: string;
    };
    free: {
      price: string;
      description: string;
      features: {
        trades: string;
        accounts: string;
        sessions: string;
        routines: string;
        goals: string;
        analytics: string;
        support: string;
      };
    };
    pro: {
      description: string;
      badge: string;
      priceAnnual: string;
      totalAnnual: string;
      savingsPercent: string;
      features: {
        trades: string;
        accounts: string;
        sessions: string;
        routines: string;
        goals: string;
        analytics: string;
        support: string;
        propFirm: string;
        customPages: string;
        mentorMode: string;
        futureFeatures: string;
      };
    };
    max: {
      name: string;
      description: string;
      priceAnnual: string;
      totalAnnual: string;
      savingsPercent: string;
      features: {
        allPro: string;
        metaApi: string;
        tradovate: string;
        support: string;
        futureFeatures: string;
      };
    };
  };
  // MetaAPI
  metaApi: {
    sectionTitle: string;
    comingSoonNote: string;
    connect: string;
    disconnect: string;
    syncNow: string;
    deactivate: string;
    reactivate: string;
    retry: string;
    retrying: string;
    errorFriendly: string;
    errorTransient: string;
    status: {
      pending: string;
      active: string;
      syncing: string;
      idle: string;
      error: string;
      paused: string;
      deprovisioned: string;
      notConnected: string;
    };
    connectModal: {
      title: string;
      description: string;
      loginLabel: string;
      loginPlaceholder: string;
      passwordLabel: string;
      serverLabel: string;
      serverPlaceholder: string;
      platformLabel: string;
      submitButton: string;
      connecting: string;
      cancel: string;
    };
    syncBadge: {
      lastSync: string;
      never: string;
      newTrades: string;
      initialSyncInProgress: string;
      nextSync: string;
      suspendedNight: string;
      suspendedWeekend: string;
      pollQuota: string;
      pollQuotaReset: string;
      pollQuotaExhausted: string;
      jobQueued: string;
    };
    errors: {
      limitReached: string;
      connectionFailed: string;
      syncFailed: string;
      notPro: string;
      syncInProgress: string;
      quotaExhausted: string;
    };
    upsell: {
      title: string;
      description: string;
      cta: string;
    };
  };
  // Tradovate
  tradovate: {
    syncChoiceDescription: string;
    maxPlanRequired: string;
    wizardTitle: string;
    addAccount: string;
    addAccountTitle: string;
    sectionTitle: string;
    sectionFirmCount: string;
    firmAccountCount: string;
    addAccountForFirm: string;
    assignPropFirm: string;
    assignPropFirmHint: string;
    chooseTradovateTarget: string;
    addAccountHere: string;
    connectNewPropFirm: string;
    pickPropFirm: string;
    pickPropFirmHint: string;
    otherPropFirm: string;
    environment: string;
    envLive: string;
    envDemo: string;
    continueToTradovate: string;
    reviewTitle: string;
    reviewSubtitle: string; // template with "{count}"
    tierLabel: string;
    tierPlaceholder: string;
    statusLabel: string;
    costLabel: string;
    costPlaceholder: string;
    costHint: string;
    lowConfidenceHint: string;
    knownAccountHint: string;
    groupLabel: string;
    groupNone: string;
    groupNew: string;
    newGroupNameLabel: string;
    groupNamePlaceholder: string;
    strategyLabel: string;
    strategyNone: string;
    importButton: string;
    importing: string;
    statusActive: string;
    statusSyncing: string;
    statusNeedsReauth: string;
    statusError: string;
    lastSync: string;
    syncNow: string;
    fullResync: string;
    reconnect: string;
    moreActions: string;
    disconnect: string;
    disconnectConfirm: string;
    errorConnectionFailed: string;
    errorLimitReached: string;
    errorGeneric: string;
    syncErrorGeneric: string;
    statusActiveNoAccounts: string;
    statusSyncedLive: string;
    statusSyncedDelayed: string;
    statusSyncedDelayedHelp: string;
    statusSyncedStarting: string;
    statusSyncedStartingHelp: string;
    statusActiveNoAccountsHint: string;
    lastSyncLive: string;
    statusDelayed: string;
    statusIdle: string;
    statusIdleHint: string;
    statusForeignToken: string;
    statusForeignTokenHint: string;
    discoveredBannerTitle: string;
    discoveredImport: string;
    discoveredDismiss: string;
    foreignBannerTitle: string;
    foreignBannerBody: string;
    foreignBannerHint: string;
    foreignBadge: string;
    oauthErrorTitle: string;
    oauthErrorDescription: string;
    oauthPlanRequiredDescription: string;
    connectionBannerTitle: string;
    loadingAccountsTitle: string;
    loadingAccountsHint: string;
    loadAccountsErrorTitle: string;
    loadAccountsErrorHint: string;
    loadAccountsRateLimitedTitle: string;
    loadAccountsRateLimitedHint: string;
    loadAccountsBalanceErrorTitle: string;
    loadAccountsBalanceErrorHint: string;
    retry: string;
    noAccountsTitle: string;
    noAccountsHint: string;
    syncRunningTitle: string;
    syncRunningHint: string;
    syncThrottledTitle: string;
    syncThrottledHint: string;
    // Vue cards : une card par prop firm avec l'animation de faisceaux.
    beamFlowLabel: string;
    beamNoAccountsTitle: string;
    beamNoAccountsHint: string;
    beamAccountsMore: string; // template avec "{count}"
    // Environnement de la connexion (hote API choisi a l'OAuth).
    environmentDemo: string;
    environmentLive: string;
    demoBannerTitle: string;
    demoBannerHint: string;
  };
  // Session
  session: {
    title: string;
    start: string;
    pause: string;
    resume: string;
    stop: string;
    remaining: string;
    paused: string;
    started: string;
    evaluation: string;
    mood: string;
    performance: string;
    mentalState: string;
    opportunities: string;
    openTrades: string;
    closedTrades: string;
    totalPnl: string;
    notes: string;
    maxRunup: string;
    maxDrawdown: string;
    maxRunupPlaceholder: string;
    maxDrawdownPlaceholder: string;
    selfEvaluation: string;
    endSession: string;
    skipEvaluation: string;
    sessionEnded: string;
    tip: string;
    durationLabel: string;
    tipText: string;
    moodPlaceholder: string;
    performancePlaceholder: string;
    mentalStatePlaceholder: string;
    opportunitiesPlaceholder: string;
    notesPlaceholder: string;
    lastSessionEmpty: string;
    lastSessionOpen: string;
    lastSessionVideo: string;
    lastSessionPnl: string;
    lastSessionRunup: string;
    lastSessionDrawdown: string;
    selfEvaluationPlaceholder: string;
    moodExcellent: string;
    moodGood: string;
    moodNeutral: string;
    moodPoor: string;
    moodBad: string;
    perfExcellent: string;
    perfGood: string;
    perfAverage: string;
    perfPoor: string;
    tradovateImport: string;
    tradovatePastePlaceholder: string;
    tradovateOrUploadCSV: string;
    tradovateUploadCSV: string;
    tradovateParseSuccess: string;
    tradovateParseErrorEmpty: string;
    tradovateParseErrorFormat: string;
    tradovateParseErrorNoTrades: string;
    topstepxImport: string;
    topstepxPastePlaceholder: string;
    topstepxParseError: string;
    topstepxUnmatchedFillsWarning: string;
    tradovateImportConfirm: string;
    tradovateImportDone: string;
    tradovateImportFailed: string;
    noTimerLabel: string;
    elapsed: string;
    openSessionNotes: string;
    startedNoTimerDescription: string;
    sessionEndedDescription: string;
    evaluationDescription: string;
    preMoodTitle: string;
    preMoodDescription: string;
    errors: string;
    errorsPlaceholder: string;
    errorCreateNew: string;
    errorNamePlaceholder: string;
    errorDelete: string;
    errorDeleteConfirm: string;
  };
  sessions: {
    title: string;
    subtitle: string;
    filterMood: string;
    filterPerformance: string;
    filterDurationMin: string;
    filterDurationMax: string;
    filterReset: string;
    filterDateStart: string;
    filterDateEnd: string;
    colDate: string;
    colMood: string;
    colPerformance: string;
    colRunup: string;
    colDrawdown: string;
    colPnl: string;
    colDuration: string;
    colVideo: string;
    noSessions: string;
    noSessionsFiltered: string;
    viewDetail: string;
    viewAll: string;
    allMoods: string;
    allPerformances: string;
    addSession: string;
    colName: string;
    editNameTitle: string;
    editNameSave: string;
    editNamePlaceholder: string;
    activeSession: string;
    inProgress: string;
    autoDetected: string;
    autoDetectedMultiple: string;
    autoDetectedLimitReached: string;
    autoDetectedViewSession: string;
    pendingMergeBanner: string;
    pendingMergeAccept: string;
    pendingMergeDismiss: string;
    pendingMergeTitle: string;
    autoBadge: string;
    accountsCount: string;
    pendingMergeSubtitle: string;
    onboardingBanner: string;
    colErrors: string;
    chartTitle: string;
    tradesAddedCount: string;
  };
  sessionDetail: {
    back: string;
    title: string;
    timing: string;
    evaluation: string;
    metrics: string;
    opportunities: string;
    selfEval: string;
    notes: string;
    startedAt: string;
    endedAt: string;
    duration: string;
    planned: string;
    mentalState: string;
    saved: string;
    saveError: string;
    delete: string;
    deleteConfirm: string;
    deleted: string;
    pnl: string;
    maxRunup: string;
    maxDrawdown: string;
    noValue: string;
    videoTitle: string;
    videoInvite: string;
    videoPlaceholder: string;
    videoSave: string;
    videoCancel: string;
    videoEdit: string;
    videoDelete: string;
    videoInvalidUrl: string;
    videoSaved: string;
    videoDeleted: string;
    notFound: string;
    nameLabel: string;
    namePlaceholder: string;
    trades: string;
    addTrade: string;
    noTrades: string;
    shareButton: string;
    shareDialogTitle: string;
    shareDialogDescription: string;
    shareEnable: string;
    shareDisable: string;
    shareLinkLabel: string;
    shareCopy: string;
    shareCopied: string;
    shareEnabled: string;
    shareDisabled: string;
    shareError: string;
    errors: string;
    pnlByAccount: string;
    noAccount: string;
    sessionAccountsTitle: string;
    sessionContribution: string;
  };
  // Journal
  journal: {
    title: string;
    subtitle: string;
    createTrade: string;
    importTrades: string;
    exportTrades: string;
    addTradeTitle: string;
    addTradeSubtitle: string;
    methodManual: string;
    methodManualDescription: string;
    methodTradovate: string;
    methodTradovateDescription: string;
    methodTopstepX: string;
    methodTopstepXDescription: string;
    methodTopstepXNoAccount: string;
    methodCsvImport: string;
    methodCsvImportDescription: string;
    topstepxImportTitle: string;
    topstepxImportSubtitle: string;
    selectTopstepAccountOrGroup: string;
    noTopstepAccountAvailable: string;
    selectAccount: string;
    selectAccountPlaceholder: string;
    noAccount: string;
    tradovateImportTitle: string;
    tradovateImportSubtitle: string;
    commissionPerTrade: string;
    commissionPerTradePlaceholder: string;
    commissionFromPropFirm: string;
    filters: string;
    advancedFilters: string;
    symbol: string;
    type: string;
    status: string;
    startDate: string;
    endDate: string;
    allTypes: string;
    long: string;
    short: string;
    allStatus: string;
    open: string;
    closed: string;
    date: string;
    exitDate: string;
    entryPrice: string;
    exitPrice: string;
    quantity: string;
    pnl: string;
    commission: string;
    notes: string;
    tags: string;
    tagsPlaceholder: string;
    createTag: string;
    noTags: string;
    tagCreateError: string;
    trades: string;
    noTrades: string;
    noTradesDesc: string;
    totalPnl: string;
    newTrade: string;
    editTrade: string;
    deleteTrade: string;
    confirmDelete: string;
    confirmBulkDelete: string;
    bulkDeleteSuccess: string;
    bulkDeleteError: string;
    selectAll: string;
    deselectAll: string;
    selectedCount: string;
    tradeCreated: string;
    tradeUpdated: string;
    tradeDeleted: string;
    importSuccess: string;
    importSuccessDescription: string;
    importError: string;
    selectFile: string;
    required: string;
    advancedFiltersTitle: string;
    advancedFiltersDescription: string;
    account: string;
    allAccounts: string;
    strategy: string;
    allStrategies: string;
    clearStrategies: string;
    strategiesSelected: string;
    all: string;
    noTagsAvailable: string;
    minPnl: string;
    maxPnl: string;
    customFields: string;
    applyFilters: string;
    reset: string;
    equals: string;
    greater: string;
    less: string;
    between: string;
    contains: string;
    startsWith: string;
    after: string;
    before: string;
    yes: string;
    no: string;
    value: string;
    tagsSeparatedByCommas: string;
    setupRating: string;
    setupRatingHint: string;
    cannotCreate: string;
    cannotUpdate: string;
    cannotDelete: string;
    tradeLimitReachedTitle: string;
    tradeLimitReachedDescription: string;
    tradeLimitReachedContinue: string;
    tradeLimitReachedUpgrade: string;
    swaps: string;
    noTradesExport: string;
    templateDownloaded: string;
    templateDownloadedDescription: string;
    downloadTemplate: string;
    cannotDownloadTemplate: string;
    importErrors: string;
    noValidTrades: string;
    noValidTradesDescription: string;
    cannotImport: string;
    accountUpdatedTrade: string;
    accountUpdatedTradeDescription: string;
    cannotUpdateAccount: string;
    noAccountSelect: string;
    tradeUpdatedDescription: string;
    tradeDeletedDescription: string;
    tradeCreatedDescription: string;
    columnsLabel: string;
    hiddenBySpace: string;
    customizeColumns: string;
    customizeColumnsDescription: string;
    columnsSaved: string;
    columnsSavedDescription: string;
    cannotSaveColumns: string;
    addCustomField: string;
    editField: string;
    newCustomField: string;
    fieldTechnicalName: string;
    fieldNamePlaceholder: string;
    fieldNameHint: string;
    fieldDisplayLabel: string;
    fieldLabelPlaceholder: string;
    fieldTypeLabel: string;
    fieldTypeText: string;
    fieldTypeNumber: string;
    fieldTypeDate: string;
    fieldTypeBoolean: string;
    fieldTypeTags: string;
    fieldRequired: string;
    fieldCreated: string;
    fieldCreatedDescription: string;
    fieldUpdated: string;
    fieldUpdatedDescription: string;
    fieldDeleted: string;
    fieldDeletedDescription: string;
    deleteFieldConfirm: string;
    cannotCreateField: string;
    cannotUpdateField: string;
    cannotDeleteField: string;
    save: string;
    create: string;
    importErrorsCount: string;
    selectAccountOrGroup: string;
    importNoAccountHint: string;
    selectAccountOrGroupPlaceholder: string;
    groupPrefix: string;
    importConfirm: string;
    importInProgress: string;
    importSelectedFile: string;
    importCommissionLabel: string;
    importCommissionHint: string;
    importGroupMultiplier: string;
    longsLabel: string;
    shortsLabel: string;
    importingToGroup: string;
    importAllRowsInvalid: string;
    importSomeRowsSkipped: string;
    importDuplicatesSkipped: string;
    importNothingNew: string;
    manualTradeTitle: string;
    manualTradeSubtitle: string;
    tradeAdded: string;
    tradeAddedDescription: string;
    cannotAddTrade: string;
    tradesListWidget: string;
    export: string;
    symbolPlaceholder: string;
    sideAll: string;
    sideLong: string;
    sideShort: string;
    statusAll: string;
    statusOpen: string;
    statusClosed: string;
    pnlFilterLabel: string;
    minPnlLabel: string;
    maxPnlLabel: string;
    tagsFilterLabel: string;
  };
  // Trading Accounts
  tradingAccounts: {
    noRulesTitle: string;
    noRulesBody: string;
    noRulesAction: string;
    ruleUpdateTitle: string;
    ruleUpdateBody: string;
    ruleUpdateFrozenOn: string;
    ruleUpdateAccept: string;
    ruleUpdateDecline: string;
    ruleUpdateStricter: string;
    ruleUpdateLooser: string;
    ruleUpdateAccepted: string;
    ruleUpdateDeclined: string;
    rulesFrozenBadge: string;
    title: string;
    subtitle: string;
    newAccount: string;
    createFirstAccount: string;
    loading: string;
    noAccounts: string;
    allAccountsInactive: string;
    currentBalance: string;
    balanceStalePrefix: string;
    balanceStaleUnknown: string;
    balanceStaleHelp: string;
    initialBalance: string;
    variation: string;
    closedTrades: string;
    synced: string;
    manual: string;
    createAccountTitle: string;
    createAccountDescription: string;
    editAccountTitle: string;
    editAccountDescription: string;
    accountName: string;
    accountNamePlaceholder: string;
    accountType: string;
    syncedComingSoon: string;
    initialBalanceLabel: string;
    cancel: string;
    create: string;
    save: string;
    accountCreated: string;
    accountCreatedDescription: string;
    accountUpdated: string;
    accountUpdatedDescription: string;
    accountDeleted: string;
    accountDeletedDescription: string;
    error: string;
    createError: string;
    updateError: string;
    deleteError: string;
    confirmDelete: string;
    deleteAccountConfirmTitle: string;
    deleteAccountConfirmDescription: string;
    deleteAccountConfirmWarning: string;
    deleteAccountConfirmHint: string;
    deactivateInstead: string;
    deactivateAccountConfirmTitle: string;
    deactivateAccountConfirmDescription: string;
    deactivate: string;
    delete: string;
    accountNameRequired: string;
    accountSizeRequired: string;
    eur: string;
    usd: string;
    gbp: string;
    chf: string;
    jpy: string;
    cad: string;
    aud: string;
    nzd: string;
    cny: string;
    btc: string;
    eth: string;
    // Groups
    groups: string;
    noGroups: string;
    ungrouped: string;
    createGroup: string;
    editGroup: string;
    deleteGroup: string;
    groupName: string;
    groupNamePlaceholder: string;
    groupCreated: string;
    groupUpdated: string;
    groupDeleted: string;
    groupDeleteConfirm: string;
    groupPropFirm: string;
    groupStrategy: string;
    groupStrategyHint: string;
    accountStrategy: string;
    accountStrategyHint: string;
    groupConsolidatedPnl: string;
    groupActiveAccounts: string;
    groupDeleteWarning: string;
    // Prop firms
    propFirm: string;
    propFirmApex: string;
    propFirmFtmo: string;
    propFirmTopstep: string;
    propFirmTakeProfitTrader: string;
    propFirmLucid: string;
    propFirmCustom: string;
    noPropFirm: string;
    inheritedFromGroup: string;
    // Account status
    statusChallenge: string;
    statusPa: string;
    statusInactive: string;
    statusDemo: string;
    statusLive: string;
    statusChallenge2: string;
    // Phases
    activePhase: string;
    addPhase: string;
    editPhase: string;
    deletePhase: string;
    confirmDeletePhase: string;
    phaseTypeLabel: string;
    phaseNameLabel: string;
    phaseTypeEvaluation: string;
    phaseTypeEvaluation2: string;
    phaseFundedVariant: string;
    phaseDrawdownType: string;
    phaseDrawdownTrailing: string;
    phaseDrawdownTotal: string;
    phaseDrawdownDaily: string;
    phaseDrawdownNone: string;
    accountStatus: string;
    personalAccount: string;
    tradovateAccount: string;
    tradovateAccountDescription: string;
    tradovateModeManual: string;
    tradovateModeSync: string;
    topstepxAccount: string;
    topstepxAccountDescription: string;
    metatraderAccount: string;
    metatraderAccountDescription: string;
    mtCredentialsStep: string;
    mtAccountDetailsStep: string;
    mtLogin: string;
    mtLoginPlaceholder: string;
    mtPassword: string;
    mtServer: string;
    mtServerPlaceholder: string;
    mtPlatform: string;
    mtAccountCreating: string;
    mtAccountCreated: string;
    mtAccountError: string;
    mtConnectionRetryHint: string;
    selectPropFirm: string;
    // Drawdown warnings
    drawdownWarning: string;
    drawdownViolation: string;
    drawdownRemainingRoom: string;
    // Bulk creation
    addAccounts: string;
    addAccountsDescription: string;
    selectPropFirmStep: string;
    selectAccountTypeStep: string;
    enterAccountsStep: string;
    assignGroupStep: string;
    selectAccountType: string;
    selectAccountTypeDescription: string;
    selectTierBalance: string;
    customBalance: string;
    challengePriceMode: string;
    challengePriceModeTotal: string;
    challengePriceModeUnit: string;
    challengePriceTotalLabel: string;
    challengePriceUnitLabel: string;
    accountsCreated: string;
    accountsCreatedDescription: string;
    assignToGroup: string;
    selectGroupPlaceholder: string;
    propFirmRulesApplied: string;
    createNewGroup: string;
    groupNameRequired: string;
    existingGroup: string;
    noGroupOption: string;
    numberOfAccounts: string;
    addAnotherAccount: string;
    removeAccount: string;
    bulkCreateError: string;
    duplicateAccountName: string;
    // Active toggle
    activateAccount: string;
    deactivateAccount: string;
    accountActivated: string;
    accountDeactivated: string;
    activeAccountsOnly: string;
    inactiveAccountNote: string;
    // Plan limit
    accountLimitReached: string;
    accountLimitReachedDescription: string;
    hiddenBanner: {
      title: string;
      cta: string;
    };
    // Filter
    filterAllAccounts: string;
    filterAllAccountsWithInactive: string;
    filterNoAccount: string;
    filterByAccount: string;
    filterByGroup: string;
    includeInactive: string;
    hideInactiveAccounts: string;
    showInactiveAccounts: string;
    // Misc
    proFeature: string;
    next: string;
    previous: string;
    step: string;
    of: string;
    // PropFirm DB-driven
    systemPropFirms: string;
    myCustomPropFirms: string;
    createCustomPropFirm: string;
    autoDetected: string;
    tierOverride: string;
    selectTier: string;
    // Progress / metrics
    challengeProgress: string;
    profitTarget: string;
    tradingDays: string;
    bufferDrawdown: string;
    bufferRemaining: string;
    axisFloor: string;
    axisFloorHint: string;
    axisStart: string;
    axisTarget: string;
    axisPeak: string;
    axisDrawdown: string;
    axisBalance: string;
    axisRemaining: string;
    maxLossExceeded: string;
    daysTradedCount: string;
    tradesPnl: string;
    syncNeedsAttention: string;
    rulesMeasured: string;
    rulesDeclared: string;
    bufferConsumed: string;
    payoutProgress: string;
    payoutTarget: string;
    payoutTargetLabel: string;
    payoutReached: string;
    setPayoutTarget: string;
    // Detail sheet
    accountDetails: string;
    rules: string;
    maxDailyLoss: string;
    maxTotalLoss: string;
    trailingDrawdown: string;
    consistencyRule: string;
    maxContracts: string;
    minTradingDays: string;
    payoutCooldown: string;
    payoutThreshold: string;
    daysCountShort: string;
    consistencyRuleValue: string;
    /** Variante COURTE pour la card de compte, trop étroite pour « Max X% / jour ». */
    consistencyShort: string;
    noRulesDefined: string;
    payoutsHistory: string;
    noPayouts: string;
    addPayout: string;
    totalPayoutsReceived: string;
    fees: string;
    totalCommissionTooltip: string;
    payoutAmount: string;
    payoutGrossAmount: string;
    payoutSplitPercent: string;
    payoutNetAmount: string;
    payoutGrossBelowNet: string;
    payoutGrossHint: string;
    payoutNetHint: string;
    payoutEdit: string;
    payoutUpdated: string;
    payoutGrossShort: string;
    payoutAmountsRequired: string;
    payoutDate: string;
    payoutNotes: string;
    payoutNotesPlaceholder: string;
    payoutAdded: string;
    payoutAddedNoCert: string;
    payoutCertificate: string;
    payoutDeleteConfirm: string;
    payoutDeleted: string;
    peakBalance: string;
    activeTier: string;
    changeTier: string;
    changeStatus: string;
    // Challenge complete
    challengeComplete: string;
    // Detail page
    backToAccounts: string;
    editAccount: string;
    accountDetailTitle: string;
    groupDetailTitle: string;
    statsOverview: string;
    winRate: string;
    wins: string;
    losses: string;
    avgWin: string;
    avgLoss: string;
    profitFactor: string;
    maxDrawdown: string;
    totalPnl: string;
    tradesCount: string;
    equityCurve: string;
    equityCurveMaxLossLimit: string;
    symbolStats: string;
    symbol: string;
    pnl: string;
    trades: string;
    noSymbolStats: string;
    noEquityData: string;
    groupAccounts: string;
    consolidatedStats: string;
    activeAccounts: string;
    totalAccounts: string;
    totalInitialBalance: string;
    totalCurrentBalance: string;
    totalPayoutsGroup: string;
    accountsInGroup: string;
    noActiveAccounts: string;
    viewAccountDetail: string;
    // Challenge cost & coupon
    challengeCostLabel: string;
    challengeCostPlaceholder: string;
    challengeCostHint: string;
    subscriptionPriceLabel: string;
    subscriptionPriceHint: string;
    directFundedHint: string;
    purchasedAtLabel: string;
    purchasedAtHint: string;
    closedAtLabel: string;
    closedAtNotice: string;
    historicalAccountToggle: string;
    historicalAccountHint: string;
    activationFeeLabel: string;
    activationFeePlaceholder: string;
    activationFeeHint: string;
    fundedAtLabel: string;
    fundedAtHint: string;
    markAsFunded: string;
    // Challenge -> Funded transition
    fundedCelebrationTitle: string;
    fundedCelebrationSubtitle: string;
    fundedCelebrationSyncNote: string;
    fundedCelebrationCloseButton: string;
    promoteToFunded: string;
    promoteToFundedTitle: string;
    promoteToFundedDescription: string;
    fundedDateLabel: string;
    fundedStartingBalanceLabel: string;
    fundedStartingBalanceHint: string;
    activationFeeOptionalLabel: string;
    promotePreview: string;
    promoteConfirm: string;
    promoteCancel: string;
    promoteSuccess: string;
    promoteBalanceInvalid: string;
    challengeRecapTitle: string;
    challengeRecapFinalBalance: string;
    challengeRecapNetProfit: string;
    challengeRecapTradeCount: string;
    challengeRecapWinRate: string;
    challengeRecapDuration: string;
    challengeRecapDurationDays: string;
    phaseChallenge: string;
    phaseFunded: string;
    phaseLive: string;
    phaseDemo: string;
    phaseFilterAll: string;
    phaseFilterLabel: string;
    couponCode: string;
    couponCodePlaceholder: string;
    couponDiscount: string;
    couponDiscountPlaceholder: string;
    couponHint: string;
    // Account charges
    charges: string;
    addCharge: string;
    addGlobalCharge: string;
    chargeName: string;
    chargeAmount: string;
    chargeFrequency: string;
    chargeMonthly: string;
    chargeAnnual: string;
    chargeOneTime: string;
    chargeAdded: string;
    chargeDeleted: string;
    chargeAddError: string;
    chargeDeleteError: string;
    chargeDelete: string;
    chargeNamePlaceholder: string;
    chargeAmountPlaceholder: string;
    noCharges: string;
    chargeStartDate: string;
    chargeStartDatePlaceholder: string;
    chargeBillingDay: string;
    chargeBillingDayPlaceholder: string;
    chargeEndDate: string;
    chargeEndDateHint: string;
    chargeSourceSubscription: string;
    chargeDeleteRecurringConfirm: string;
    chargeRemindersTitle: string;
    chargeRemindersLabel: string;
    chargeRemindersHint: string;
    chargeEdit: string;
    chargeEditError: string;
    chargeUpdated: string;
    chargeSave: string;
    chargeCancel: string;
    chargeAddTitle: string;
    chargeEditTitle: string;
    globalCharges: string;
    linkedCharges: string;
    disabledAtLabel: string;
    disabledAtHint: string;
    consistencyRuleOverrideLabel: string;
    consistencyRuleOverrideHint: string;
    maxDailyLossOverrideLabel: string;
    maxDailyLossOverrideHint: string;
    violationDailyLoss: string;
    warningDailyLoss: string;
    violationConsistency: string;
    warningConsistency: string;
    // Détail chiffré d'une violation : {share} = part du jour, {threshold} = seuil
    violationConsistencyDetail: string;
    warningConsistencyDetail: string;
    violationConsistencyBase: string;
    // Écart entre le solde broker et le journal des trades
    balanceGapJournalAhead: string;
    balanceGapJournalAheadHint: string;
    balanceGapJournalIncomplete: string;
    balanceGapJournalIncompleteHint: string;
    balanceGapJournalLabel: string;
    balanceGapStale: string;
    // Qualifying days
    qualifyingDaysLabel: string;
    payoutEligible: string;
    // Strategies
    strategies: string;
    allStrategies: string;
    strategiesPlaceholder: string;
    strategyNone: string;
    addStrategy: string;
    strategyName: string;
    strategyNamePlaceholder: string;
    strategyCreated: string;
    strategyDeleted: string;
    strategyCreateError: string;
    strategyDeleteError: string;
    strategyExists: string;
    noStrategies: string;
    strategyHint: string;
    resyncTradesStrategies: string;
    resyncTradesStrategiesHint: string;
    resyncTradesStrategiesSuccess: string;
    resyncTradesStrategiesError: string;
    // View toggle
    tableView: string;
    cardsView: string;
    // Table columns
    colName: string;
    colStatus: string;
    colPropFirm: string;
    colGroup: string;
    colType: string;
    colSource: string;
    sourceTradovate: string;
    sourceMetaTrader: string;
    sourceManual: string;
    deactivatedBrokerGone: string;
    deactivatedBrokerGoneHint: string;
    deactivatedChallengeCompleted: string;
    deactivatedChallengeCompletedHint: string;
    deactivatedDrawdown: string;
    deactivatedDrawdownHint: string;
    payoutSuggestionLabel: string;
    cashQuestionTitle: string;
    cashQuestionBody: string;
    cashQuestionAmountLabel: string;
    cashQuestionAnswerWithdrawal: string;
    cashQuestionAnswerReset: string;
    cashQuestionAnswerFees: string;
    cashQuestionAnswerUnknown: string;
    cashQuestionConfirm: string;
    cashQuestionDismiss: string;
    cashQuestionCelebrate: string;
    cashQuestionRecorded: string;
    cashQuestionReasonUnavailable: string;
    payoutSuggestionCreate: string;
    payoutSuggestionDismiss: string;
    accountResetSuggestionLabel: string;
    accountResetDetectedTitle: string;
    accountResetDetectedDescription: string;
    accountResetObservedBalance: string;
    accountResetExpectedBalance: string;
    accountResetDiscrepancy: string;
    accountResetDateLabel: string;
    accountResetConfidenceHigh: string;
    accountResetConfidenceLow: string;
    accountResetConfirm: string;
    accountResetConfirming: string;
    accountResetDismiss: string;
    accountResetInvalidDate: string;
    accountResetError: string;
    colStrategy: string;
    colBalance: string;
    colPnl: string;
    colProgress: string;
    colTrades: string;
    colActions: string;
    // Blew-up modal
    blewUpModal: {
      title: string;
      subtitleDailyLoss: string;
      subtitleTotalLoss: string;
      subtitleTrailingDrawdown: string;
      question: string;
      confirm: string;
      dismiss: string;
    };
  };
  // Notebook
  notebook: {
    title: string;
    subtitle: string;
    newNote: string;
    untitled: string;
    empty: string;
    selectNote: string;
    createFirstNote: string;
    loading: string;
    noNotes: string;
    search: string;
    searchPlaceholder: string;
    category: string;
    allCategories: string;
    noCategory: string;
    categories: string;
    manageCategories: string;
    createCategory: string;
    editCategory: string;
    deleteCategory: string;
    categoryName: string;
    categoryColor: string;
    categoryCreated: string;
    categoryUpdated: string;
    categoryDeleted: string;
    categoryError: string;
    confirmDeleteCategory: string;
    confirmDeleteNote: string;
    noteCreated: string;
    noteUpdated: string;
    noteDeleted: string;
    noteError: string;
    saving: string;
    saved: string;
    saveError: string;
    editorPlaceholder: string;
    toolbar: {
      bold: string;
      italic: string;
      underline: string;
      strike: string;
      heading1: string;
      heading2: string;
      heading3: string;
      bulletList: string;
      orderedList: string;
      link: string;
      image: string;
      divider: string;
    };
    uploadImage: string;
    pasteImage: string;
    imageUploaded: string;
    imageUploadError: string;
    gridColumns1?: string;
    gridColumns2?: string;
    gridColumns3?: string;
    gridColumns4?: string;
    updatedOn: string;
    readMore: string;
    collapse: string;
    symbols: string;
    symbolsPlaceholder: string;
    tags: string;
    tagsPlaceholder: string;
    backToNotebook: string;
    editNote: string;
    saveNote: string;
    cancel: string;
  };
  // Trade Detail
  tradeDetail: {
    title: string;
    backToJournal: string;
    edit: string;
    save: string;
    cancel: string;
    tradeInfo: string;
    symbol: string;
    type: string;
    status: string;
    date: string;
    entryPrice: string;
    exitPrice: string;
    quantity: string;
    pnl: string;
    commission: string;
    notes: string;
    notesSaved: string;
    notesError: string;
    templates: string;
    noNotes: string;
    clickToAdd: string;
    previousContent: string;
    chartTitle: string;
    chartError: string;
    noChartData: string;
    customFields: string;
    noValue: string;
    editCustomFields: string;
    saveCustomFields: string;
  };
  // Analytics
  analytics: {
    title: string;
    subtitle: string;
    loading: string;
    timeFilter: string;
    last7Days: string;
    last30Days: string;
    last90Days: string;
    allTime: string;
    totalPnl: string;
    winRate: string;
    profitFactor: string;
    totalTrades: string;
    averagePnl: string;
    cumulativePnl: string;
    pnlDistribution: string;
    monthlyPerformance: string;
    pnlPerTrade: string;
    sessionRecaps: string;
    noSessionRecaps: string;
    startFromDashboard: string;
    date: string;
    duration: string;
    mood: string;
    performance: string;
    opportunities: string;
    openTrades: string;
    closedTrades: string;
    totalPnlLabel: string;
    selfEvaluation: string;
    mentalState: string;
    notes: string;
    excellent: string;
    good: string;
    neutral: string;
    poor: string;
    bad: string;
    average: string;
    symbol: string;
    noDataAvailable: string;
    largestWinLoss: string;
    numberOfTrades: string;
    period: string;
    year: string;
    quarter: string;
    month: string;
    selectYear: string;
    selectPeriod: string;
    months: string[];
    quarters: string[];
    perTradePerformance: string;
    netDailyPnlLabel: string;
    netDailyPnlTitle: string;
    holdDurationTradeCount: string;
    longVsShort: string;
    winLossDistribution: string;
    dayOfWeekPerformance: string;
    longPerforming: string;
    shortPerforming: string;
    equalPerformance: string;
    longShortTotalLabel: string;
    longShortTotalTooltip: string;
    longShortAvgLabel: string;
    longShortAvgTooltip: string;
    wins: string;
    losses: string;
    breakeven: string;
    best: string;
    worst: string;
    equityCurve: string;
    maxDrawdown: string;
    peakToTrough: string;
    sharpeRatio: string;
    riskAdjustedReturn: string;
    expectancy: string;
    averagePerTrade: string;
    topSymbols: string;
    symbolsLabel: string;
    insightsLabel: string;
    sessionsLabel: string;
    sessionDurationLabel: string;
    distributionLabel: string;
    dayLabel: string;
    pnlLabel: string;
    dateLabel: string;
    valueLabel: string;
    tradeLabel: string;
    durationLabel: string;
    holdDurationLabel: string;
    avgPnlLabel: string;
    moodLabel: string;
    bucketLabel: string;
    sizeLabel: string;
    sessionsCountLabel: string;
    calendarLabel: string;
    marketsLabel: string;
    sessionLabel: string;
    winsCount: string;
    lossesCount: string;
    consistencyScore: string;
    consistencyScoreMin: string;
    csWinRateStability: string;
    csRiskConsistency: string;
    csNoRevenge: string;
    csDrawdownMgmt: string;
    consistencyScoreInfo: string;
    streakAnalysis: string;
    currentStreak: string;
    streakWins: string;
    streakLosses: string;
    longestWinStreak: string;
    longestLossStreak: string;
    trades: string;
    riskManagement: string;
    drawdownMgmt: string;
    drawdownMgmtHint: string;
    drawupMgmt: string;
    drawupMgmtHint: string;
    streakInsufficient: string;
    streakInsufficientLoss: string;
    streakInsufficientWin: string;
    accountValue: string;
    sessionDelete: string;
    sessionDeleteConfirm: string;
    sessionDeleted: string;
    sessionDeleteError: string;
    sessionEdit: string;
    sessionSaved: string;
    sessionSaveError: string;
    sessionExpandDetails: string;
    sessionMaxRunup: string;
    sessionMaxDrawdown: string;
    sessionDate: string;
    sessionDurationMin: string;
    sessionStartedAt: string;
    sessionEndedAt: string;
    sessionPnl: string;
    sessionAddManual: string;
    sessionAddManualTitle: string;
    sessionAddManualDescription: string;
    sessionAddManualSuccess: string;
    sessionAddManualError: string;
    sessionStartTime: string;
    sessionEndTime: string;
    // Advanced analytics categories
    cat1Title: string;
    cat1Desc: string;
    cat2Title: string;
    cat2Desc: string;
    cat3Title: string;
    cat3Desc: string;
    cat4Title: string;
    cat4Desc: string;
    cat5Title: string;
    cat5Desc: string;
    cat6Title: string;
    cat6Desc: string;
    // Cat6 – Session Analytics
    sessionStatsTimeTitle: string;
    sessionStatsTimeDesc: string;
    sessionStatsWinRateTitle: string;
    sessionStatsWinRateDesc: string;
    sessionStatsMoodTitle: string;
    sessionStatsMoodDesc: string;
    sessionStatsDurationTitle: string;
    sessionStatsDurationDesc: string;
    sessionInsightLabel: string;
    sessionInsightTimeBucket: string;
    sessionInsightMood: string;
    sessionInsightDuration: string;
    sessionNoData: string;
    sessionMoodExcellent: string;
    sessionMoodGood: string;
    sessionMoodNeutral: string;
    sessionMoodPoor: string;
    sessionMoodBad: string;
    outsideSessionTitle: string;
    outsideSessionDesc: string;
    outsideSessionTrades: string;
    outsideSessionWinRate: string;
    outsideSessionPnl: string;
    outsideSessionAvgPnl: string;
    outsideSessionProfitFactor: string;
    outsideSessionPctTrades: string;
    outsideSessionPctPnl: string;
    outsideSessionNoSessions: string;
    sessionsListNoSessions: string;
    sessionsListName: string;
    sessionsListDate: string;
    sessionsListDuration: string;
    sessionsListTrades: string;
    sessionsListPnl: string;
    sessionPnlChartLabel: string;
    // Cat1 – Performance & Risk
    calmarRatio: string;
    calmarRatioDesc: string;
    recoveryFactor: string;
    recoveryFactorDesc: string;
    extendedStreak: string;
    drawdownCurveTitle: string;
    drawdownCurveDesc: string;
    // Cat2 – Timing
    heatmapTitle: string;
    heatmapDesc: string;
    heatmapPnl: string;
    heatmapWinRate: string;
    heatmapMode: string;
    heatmapLegendBelow50: string;
    heatmapLegendAbove50: string;
    timeSlotTitle: string;
    timeSlotDesc: string;
    timeSlotPreMarket: string;
    timeSlotOpen: string;
    timeSlotMidDay: string;
    timeSlotAfternoon: string;
    timeSlotPowerHour: string;
    holdDurationTitle: string;
    holdDurationDesc: string;
    holdDurationX: string;
    holdDurationY: string;
    holdDurationWinRate: string;
    holdDurationAvgPnl: string;
    // Cat3 – Risk management
    sizingTitle: string;
    sizingDesc: string;
    // Cat4 – Setup & Tags
    tagStatsTitle: string;
    tagStatsDesc: string;
    noTags: string;
    monthlyStatsTitle: string;
    monthlyStatsDesc: string;
    rollingWinRateTitle: string;
    rollingWinRateDesc: string;
    rollingWindow: string;
    // Cat5 – Behavioral
    biasesTitle: string;
    biasesDesc: string;
    fomoIndicator: string;
    fomoIndicatorDesc: string;
    lossAversionRatio: string;
    lossAversionRatioDesc: string;
    overconfidenceScore: string;
    overconfidenceScoreDesc: string;
    revengeTradingCount: string;
    revengeTradingCountDesc: string;
    noExitDateData: string;
    // Misc reused
    proOnly: string;
    propFirmAnalytics: {
      categoryTitle: string;
      invested: string;
      payoutsReceived: string;
      netResult: string;
      profitLabel: string;
      profitFactor: string;
      roiGlobal: string;
      successRate: string;
      successRateDesc: string;
      successRateByFirm: string;
      bestAccountSize: string;
      timelineMetrics: string;
      avgChallengeDuration: string;
      avgPaDuration: string;
      avgTotalDuration: string;
      fastestSuccess: string;
      ratios: string;
      avgMultiplier: string;
      avgPayoutPerSuccess: string;
      avgCostPerSuccess: string;
      bestFirm: string;
      colFirm: string;
      colAccounts: string;
      colInvested: string;
      colPayouts: string;
      colNet: string;
      colRoi: string;
      colWinRate: string;
      colSuccess: string;
      totalRow: string;
      noData: string;
      noDataLink: string;
      days: string;
      wins: string;
      inProgress: string;
      paLabel: string;
      unknownFirm: string;
      multiplierPrefix: string;
    };
    // Payouts section (CAT 7)
    payoutsTitle: string;
    payoutsDesc: string;
    payoutsTotalReceived: string;
    payoutsTotalCost: string;
    payoutsRoi: string;
    payoutsBest: string;
    payoutsNet: string;
    payoutsNetSubtitle: string;
    payoutsRoiSubtitle: string;
    payoutsOverTime: string;
    payoutsChartPayouts: string;
    payoutsChartCosts: string;
    payoutsChartNet: string;
    payoutsPerAccount: string;
    payoutsAccountName: string;
    payoutsChallengeCost: string;
    payoutsActivationFee: string;
    payoutsLinkedCharges: string;
    payoutsReceived: string;
    payoutsNetPnl: string;
    payoutsNetResult: string;
    payoutsEditAction: string;
    payoutsEditTitle: string;
    payoutsEditDescription: string;
    payoutsEditPayouts: string;
    payoutsEditNoPayouts: string;
    payoutsEditDetected: string;
    payoutsEditDetectedHint: string;
    payoutsEditMarkAdjustment: string;
    payoutsEditAdjustments: string;
    payoutsEditAdjustmentsHint: string;
    payoutsEditMarkPayout: string;
    payoutsEditDeletePayout: string;
    payoutsEditDeleteConfirm: string;
    payoutsEditCharges: string;
    payoutsEditSave: string;
    payoutsEditClose: string;
    payoutsEditSaved: string;
    payoutsEditError: string;
    payoutsEditInvalidAmount: string;
    payoutsDeleteExpenseAction: string;
    payoutsDeleteExpenseTitle: string;
    payoutsDeleteExpenseBody: string;
    payoutsDeleteExpenseTotal: string;
    payoutsDeleteExpenseKeepsAccount: string;
    payoutsDeleteExpenseCancel: string;
    payoutsDeleteExpenseConfirm: string;
    payoutsDeleteExpenseDone: string;
    payoutsGlobalCharges: string;
    payoutsGlobalChargesAnnual: string;
    payoutsHistory: string;
    payoutsNoData: string;
    payoutsPaginationPage: string;
    payoutsPaginationOf: string;
    payoutsPaginationPrev: string;
    payoutsPaginationNext: string;
    payoutsNoEligibleAccounts: string;
    payoutsAnnualized: string;
    payoutsAccountsCount: string;
    pips: {
      sectionTitle: string;
      avgPips: string;
      totalPipsWonLost: string;
      pipRatio: string;
      bestWorstTrade: string;
      winRateByBucket: string;
      distribution: string;
      bySymbol: string;
      avgPipsCol: string;
      totalPipsCol: string;
      noData: string;
      estimatedPipSize: string;
      winRateSeriesLabel: string;
      tradesSeriesLabel: string;
      rangeHeader: string;
      avgPipsWin: string;
      avgPipsLoss: string;
    };
    payoutsPerYear: string;
    payoutsTotalCharges: string;
    payoutsBreakdown: string;
    equityViewGlobal: string;
    equityViewByGroup: string;
    equityViewByAccount: string;
    // Profitability report table
    reportTitle: string;
    reportGranMonth: string;
    reportGranQuarter: string;
    reportGranYear: string;
    reportColPeriod: string;
    reportColPayouts: string;
    reportColChallengeCost: string;
    reportColActivationFee: string;
    reportColLinkedCharges: string;
    reportColGlobalCharges: string;
    reportColTotalCost: string;
    reportColNet: string;
    reportTotalRow: string;
    reportNoData: string;
    noData: string;
    notEnoughSessionData: string;
    biasesfomoDesc: string;
    biasesRevengeDesc: string;
    biasesLossAversionDesc: string;
    biasesOverconfidenceDesc: string;
    chartView: string;
    tableView: string;
    sideLabel: string;
    winRateLabel: string;
    monthLabel: string;
    tagLabel: string;
  };
  // Routines
  routines: {
    title: string;
    subtitle: string;
    createRoutine: string;
    editRoutine: string;
    deleteRoutine: string;
    newRoutine: string;
    noRoutines: string;
    createFirst: string;
    name: string;
    description: string;
    frequency: string;
    daily: string;
    weekly: string;
    monthly: string;
    custom: string;
    checklistItems: string;
    addItem: string;
    isActive: string;
    cancel: string;
    save: string;
    routineCreated: string;
    routineUpdated: string;
    routineDeleted: string;
    error: string;
    atLeastOneItem: string;
    cannotCreate: string;
    cannotUpdate: string;
    cannotDelete: string;
    confirmDelete: string;
    completedToday: string;
    markCompleted: string;
    items: string;
    lastCompletion: string;
    completionProgress: string;
    loading: string;
    routineCompleted: string;
    heatmapTitle: string;
    heatmapSubtitle: string;
    month: string;
    quarter: string;
    monthNames: string[];
    monthNamesShort: string[];
    dayLetters: string[];
    excellent: string;
    good: string;
    partial: string;
    notDue: string;
    missed: string;
    limitReached: string;
    limitReachedDescription: string;
  };
  // Analysis
  analysis: {
    title: string;
    subtitle: string;
    newAnalysis: string;
    editAnalysis: string;
    createFirst: string;
    noAnalyses: string;
    loading: string;
    search: string;
    clear: string;
    searchPlaceholder: string;
    symbolPlaceholder: string;
    titleLabel: string;
    contentLabel: string;
    contentPlaceholder: string;
    symbolsLabel: string;
    symbolsPlaceholder: string;
    tagsLabel: string;
    tagsPlaceholder: string;
    analysisCreated: string;
    analysisUpdated: string;
    analysisDeleted: string;
    cannotCreate: string;
    cannotUpdate: string;
    cannotDelete: string;
    confirmDelete: string;
    editDescription: string;
    createDescription: string;
    updatedOn: string;
    readMore: string;
    collapse: string;
  };
  // Templates
  templates: {
    noTemplate: string;
    manage: string;
    createTemplate: string;
    edit: string;
    delete: string;
    selectTemplate: string;
    default: string;
    templateCreated: string;
    templateUpdated: string;
    templateDeleted: string;
    cannotCreate: string;
    cannotUpdate: string;
    cannotDelete: string;
    confirmDelete: string;
    createDescription: string;
    editTemplate: string;
    editDescription: string;
    nameLabel: string;
    namePlaceholder: string;
    contentLabel: string;
    contentPlaceholder: string;
    setAsDefault: string;
    systemDefaultNote: string;
    create: string;
    loading: string;
  };
  // Markdown Editor
  markdownEditor: {
    placeholder: string;
    helpText: string;
    titlePlaceholder: string;
    boldPlaceholder: string;
    italicPlaceholder: string;
    underlinePlaceholder: string;
    listItemPlaceholder: string;
    heading1: string;
    heading2: string;
    heading3: string;
    bold: string;
    italic: string;
    underline: string;
    list: string;
    link: string;
    image: string;
    edit: string;
    preview: string;
    noContent: string;
    imageUrlPrompt: string;
    imageAltPrompt: string;
    linkUrlPrompt: string;
    linkTextPrompt: string;
    pastedImage: string;
  };
  // Goals
  goals: {
    title: string;
    subtitle: string;
    createGoal: string;
    editGoal: string;
    deleteGoal: string;
    newGoal: string;
    noGoals: string;
    createFirst: string;
    goalTitle: string;
    description: string;
    type: string;
    targetValue: string;
    deadline: string;
    pnl: string;
    winRate: string;
    tradesCount: string;
    custom: string;
    cancel: string;
    save: string;
    goalCreated: string;
    goalUpdated: string;
    goalDeleted: string;
    error: string;
    cannotCreate: string;
    cannotUpdate: string;
    cannotDelete: string;
    confirmDelete: string;
    progress: string;
    completed: string;
    notCompleted: string;
    daysRemaining: string;
    daysOverdue: string;
    loading: string;
    recurrence: string;
    recurrenceNone: string;
    recurrenceDaily: string;
    recurrenceWeekly: string;
    recurrenceMonthly: string;
    badgeDaily: string;
    badgeWeekly: string;
    badgeMonthly: string;
    resetAt: string;
    goalCreatedDescription: string;
    goalUpdatedDescription: string;
    goalDeletedDescription: string;
    limitReached: string;
    limitReachedDescription: string;
    heatmapTitle: string;
    heatmapAchieved: string;
    heatmapOf: string;
    heatmapDays: string;
    heatmapWeeks: string;
    heatmapMonths: string;
    heatmapNoData: string;
  };
  onboarding: {
    stepOf: string;
    step1Title: string;
    step1Description: string;
    step1Cta: string;
    step1Celebration: string;
    step2Title: string;
    step2Description: string;
    step2Cta: string;
    step2Celebration: string;
    step3Title: string;
    step3Description: string;
    step3Cta: string;
    step3Celebration: string;
    step4Title: string;
    step4Description: string;
    step4Cta: string;
    step4Celebration: string;
    finalTitle: string;
    finalCta: string;
    pillExpand: string;
  };
  // Landing Page
  landing: {
    announcement: {
      text: string;
      cta: string;
    };
    hero: {
      title: string;
      subtitle: string;
      ctaPricing: string;
      ctaSignup: string;
      ctaGetStarted: string;
      ctaFeatures: string;
      badge: string;
    };
    features: {
      title: string;
      description: string;
    };
    examples: {
      overline: string;
      title: string;
      subtitle: string;
      videoCta: string;
      videoAria: string;
      integrationsTitle: string;
      integrationsSubtitle: string;
    };
    pricing: {
      title: string;
      subtitle: string;
      popular: string;
      seeAllPlans: string;
      needMoreInfo: string;
      freeBadge: string;
      trustCopy: string;
      earlyAdopterBadge: string;
      ctaFree: string;
      ctaPro: string;
      ctaMax: string;
      disclaimer: string;
    };
    testimonials: {
      title: string;
    };
    faq: {
      title: string;
      description: string;
    };
    footer: {
      product: string;
      company: string;
      legal: string;
      resources: string;
      documentation: string;
      blog: string;
      about: string;
      privacy: string;
      terms: string;
      disclosures: string;
      affiliation: string;
      help: string;
      comparateur: string;
      copyright: string;
      allRightsReserved: string;
      community: string;
      followUs: string;
      followInstagram: string;
      contactSupport: string;
      joinDiscord: string;
    };
    featuresList: {
      personalizedJournal: { name: string; description: string };
      advancedAnalytics: { name: string; description: string };
      disciplineSupport: { name: string; description: string };
      propFirmManagement: { name: string; description: string };
      tradingSessions: { name: string; description: string };
      autoSync: { name: string; description: string };
    };
    testimonialsList: {
      alexandre: { quote: string };
      sophie: { quote: string };
      marc: { quote: string };
    };
    faqsList: {
      import: { question: string; answer: string };
      metrics: { question: string; answer: string };
      security: { question: string; answer: string };
      export: { question: string; answer: string };
      sessions: { question: string; answer: string };
      templates: { question: string; answer: string };
      beginner: { question: string; answer: string };
      propFirm: { question: string; answer: string };
      freePlan: { question: string; answer: string };
      multiAccount: { question: string; answer: string };
      dataLoss: { question: string; answer: string };
    };
    brokers: {
      title: string;
      description: string;
      colBroker: string;
      colMarkets: string;
      colManualImport: string;
      colCsvUpload: string;
      colAutoSync: string;
    };
    freePlan: {
      overline: string;
      title: string;
      subtitle: string;
      badge: string;
      trades: { value: string; label: string };
      sessions: { value: string; label: string };
      routines: { value: string; label: string };
      analytics: { value: string; label: string };
      accounts: { value: string; label: string };
      cta: string;
      brokersImportTitle: string;
      brokersSyncTitle: string;
      comingSoon: string;
    };
    howItWorks: {
      overline: string;
      title: string;
      subtitle: string;
      step1: {
        badge: string;
        title: string;
        description: string;
        statusConnected: string;
        statusSyncing: string;
      };
      step2: {
        badge: string;
        title: string;
        description: string;
        winRate: string;
        profitFactor: string;
        biasRevenge: string;
        biasBestHour: string;
        biasAvg: string;
        biasOvertrading: string;
      };
      step3: {
        badge: string;
        title: string;
        description: string;
        payoutLabel: string;
      };
    };
    ctaFinal: {
      title: string;
      cta: string;
      badge: string;
    };
  };
  attachments: {
    sectionTitle: string;
    empty: string;
    uploadButton: string;
    uploadDialogTitle: string;
    uploadDialogDescription: string;
    fieldName: string;
    fieldType: string;
    fieldFile: string;
    fieldLinkedPayout: string;
    fieldLinkedPayoutNone: string;
    typeChallenge: string;
    typePayout: string;
    typeInvoice: string;
    typeOther: string;
    view: string;
    download: string;
    delete: string;
    deleteConfirm: string;
    limitReached: string;
    errorUpload: string;
    errorMimeRejected: string;
    errorTooLarge: string;
    errorPayoutTaken: string;
    uploading: string;
    certificateBadge: string;
    addCertificate: string;
  };
  reports: {
    shareButton: string;
    dialogTitle: string;
    dialogDescription: string;
    period: string;
    periodDaily: string;
    periodWeekly: string;
    periodMonthly: string;
    format: string;
    formatStory: string;
    formatCarousel: string;
    generate: string;
    generating: string;
    copyLink: string;
    copied: string;
    download: string;
    downloadAll: string;
    saveToPhotos: string;
    saving: string;
    share: string;
    openInstagram: string;
    openInstagramDesktopHint: string;
    openTiktok: string;
    openX: string;
    socialDesktopHint: string;
    publicCta: string;
    publicCtaButton: string;
    publicLoading: string;
    publicNotFound: string;
    error: string;
    pageTitle: string;
    pageSubtitle: string;
    generateCta: string;
    emptyState: string;
    emptyStateCta: string;
    thumbnailAlt: string;
    viewReport: string;
    reportDetailTitle: string;
    filterLabel: string;
    formatPdfComingSoon: string;
    formatComingSoonBadge: string;
    scopeLabel: string;
    scopeAll: string;
    scopeAllInactive: string;
    scopeAccount: string;
    scopeGroup: string;
    scopePropfirm: string;
    scopeStrategy: string;
    scopePickAccount: string;
    scopePickGroup: string;
    scopePickPropfirm: string;
    scopePickStrategy: string;
    sectionsLabel: string;
    sectionTrading: string;
    sectionPropfirm: string;
    sectionAnalytics: string;
    sectionsRequired: string;
    periodQuarterly: string;
    periodAnnual: string;
    deleteAriaLabel: string;
    periodNavPrev: string;
    periodNavNext: string;
    deleteConfirm: string;

    // Page de détail
    detailTitle: string;
    detailGeneratedOn: string;
    detailShare: string;
    detailSharing: string;
    detailBackToList: string;
    detailEmptySection: string;
    detailLegacyNotice: string;

    // Bilan
    summaryTitle: string;
    summaryNetPnl: string;
    summaryTrades: string;
    summaryDaysTraded: string;
    summaryAvgPnlPerDay: string;
    summaryOutOfDays: string;

    // Cycle de vie
    lifecycleTitle: string;
    lifecycleAdded: string;
    lifecycleBlown: string;
    lifecycleFunded: string;
    lifecycleLowConfidence: string;
    lifecycleNoEvents: string;
    lifecycleUnlinked: string;
    lifecycleOutcomesTitle: string;
    lifecycleOutcomeBlown: string;
    lifecycleOutcomeLikelyBlown: string;
    lifecycleOutcomePassed: string;
    lifecycleOutcomeActive: string;

    // Payouts
    payoutsTitle: string;
    payoutsTotal: string;
    payoutsCount: string;
    payoutsBest: string;
    payoutsNone: string;
    payoutsNoPropFirm: string;

    // Bilan financier prop firm
    financeTitle: string;
    financeSpent: string;
    financePayouts: string;
    financeNet: string;
    financeRoi: string;
    financeRoiNone: string;

    // Avancement des challenges
    advancementTitle: string;
    advancementTarget: string;
    advancementDays: string;
    advancementComplete: string;
    advancementNone: string;
  };
  dateRangePicker: {
    allTime: string;
    today: string;
    thisWeek: string;
    thisMonth: string;
    last30Days: string;
    lastMonth: string;
    thisQuarter: string;
    ytd: string;
    custom: string;
  };
  pages: {
    loadErrorTitle: string;
    loadErrorDescription: string;
    loadErrorRetry: string;
    manage: string;
    newPage: string;
    pageName: string;
    pageIcon: string;
    cancel: string;
    create: string;
    noPages: string;
    deleteConfirm: string;
    edit: string;
    emptyPage: string;
    addFirstWidget: string;
    topbarSection: string;
    topbarAdd: string;
    topbarFilters: string;
    topbarFiltersTitle: string;
    pinAsDefault: string;
    pinAsDefaultConfirm: string;
    unpinDefault: string;
    topbarAddPayout: string;
  };
  marketDataWidgets: {
    categoryLabel: string;
    newsFeed: {
      title: string;
      noNews: string;
      error: string;
      retry: string;
      /** `{count}` est remplacé par le nombre d'actualités restantes. */
      showMore: string;
      justNow: string;
      sentimentPositive: string;
      sentimentNegative: string;
      sentimentNeutral: string;
    };
    interestRates: {
      title: string;
      lastUpdate: string;
      nextMeeting: string;
      consensusHold: string;
      consensusCut: string;
      consensusHike: string;
    };
    economicCalendar: {
      title: string;
      actual: string;
      forecast: string;
      previous: string;
      noEvents: string;
      filterHigh: string;
      filterMedium: string;
      filterLow: string;
    };
    earningsCalendar: {
      title: string;
      noEarnings: string;
      timingBmo: string;
      timingAmc: string;
      timingDmh: string;
      guidanceRaised: string;
      guidanceLowered: string;
      guidanceMaintained: string;
      colSymbol: string;
      colCompany: string;
      colTiming: string;
      colEpsEstimate: string;
      colEpsActual: string;
      colEpsSurprise: string;
      colRevenueEstimate: string;
      colRevenueActual: string;
      colRevenueSurprise: string;
      colPriceReaction: string;
      colGuidance: string;
      configureColumns: string;
    };
  };
  chart: {
    symbol: string;
    invalidSymbol: string;
    unavailable: string;
    reload: string;
    noData: string;
    noTradesForSymbol: string;
    showTrades: string;
    widgetTitle: string;
  };
  marketing: {
    nav: {
      features: string;
      forWho: string;
      pricing: string;
      comparateur: string;
      blog: string;
      login: string;
      ctaStart: string;
      megaFeatures: { key: string; label: string; desc: string }[];
      megaSolutions: { key: string; label: string; desc: string }[];
    };
    common: {
      ctaFinalTitle: string;
      ctaFinalButton: string;
      whyTitle: string;
      relatedTitle: string;
      criteria: string[];
      yes: string;
      no: string;
    };
    features: {
      journal: MarketingPageCopy;
      propFirmManager: MarketingPageCopy;
      analytics: MarketingPageCopy;
      sessions: MarketingPageCopy;
      mentor: MarketingPageCopy;
      dashboard: MarketingPageCopy;
      integrations: MarketingIntegrationsCopy;
    };
    solutions: {
      debutant: MarketingPageCopy;
      multiComptes: MarketingPageCopy;
      coach: MarketingPageCopy;
    };
    comparisons: {
      tradezella: MarketingComparisonCopy;
      tradersync: MarketingComparisonCopy;
      notion: MarketingComparisonCopy;
      excel: MarketingComparisonCopy;
      best: MarketingComparisonCopy;
      alternatives: MarketingComparisonCopy;
    };
    journalPage: MarketingJournalCopy;
    featureTabs: {
      eyebrow: string;
      title: string;
      subtitle: string;
      discoverPrefix: string; // "Découvrir " — concatenated with the tab name + " →" handled in component
      tabs: {
        key: string;
        tabLabel: string;     // short label in the pill
        sectionLabel: string; // small-caps label above H3
        h1: string;
        description: string;
        bullets: string[];    // 4 entries
        linkLabel: string;    // full link text e.g. "Découvrir le Dashboard"
      }[];
    };
    hero: {
      titleBeforeHighlight: string; // "Arrête de perdre tes comptes "
      titleHighlight: string;       // "prop firm"
      titleAfterHighlight: string;  // " avec les mêmes erreurs."
      subtitle: string;
      cta: string;
      microcopy: string;
      pillsLabel: string;
      imageAlt: string;
      pills: { label: string; to: string }[]; // 6 pills
    };
    floatingDashboard: {
      ariaLabel: string;         // accessible label for the decorative dashboard composition
      netPnlLabel: string;       // "NET P&L"
      winRateLabel: string;      // "TRADE WIN %"
      profitFactorLabel: string; // "PROFIT FACTOR"
      avgWinLossLabel: string;   // "AVG WIN/LOSS TRADE"
      avgWin: string;            // "Avg Win"
      avgLoss: string;           // "Avg Loss"
      marketHoursLabel: string;  // "HORAIRES DES MARCHÉS"
      closed: string;            // "Fermé"
      calendarLabel: string;     // "CALENDRIER"
      weekLabel: string;         // "Sem." — weekly recap column header
      ecoCalendarLabel: string;  // "CALENDRIER ÉCONOMIQUE"
      routinesLabel: string;     // "ROUTINES DU JOUR"
      routinePreOpen: string;    // "Pré-open routine"
      routineOpenUs: string;     // "Open US"
      daily: string;             // "Quotidien"
      goalsLabel: string;        // "OBJECTIFS EN COURS"
      goalName: string;          // "PnL Maker"
      newsLabel: string;         // "NEWS"
      tradesLabel: string;       // "trades" suffix, e.g. "108 trades"
      dashboardTitle: string;    // "Dashboard" — mockup window title
      liveBadge: string;         // "LIVE"
    };
    socialProof: {
      stats: { value: string; label: string }[]; // 4 stats
      compatibleLabel: string;
      // Eyebrow above the NinjaTrader + Tradovate logos (Vendor Program)
      partnerLabel: string;
      // prop firm display names are brand proper nouns, kept in a string[] (not translated)
      propFirms: string[]; // display order of the marquee
    };
  };
  // Page publique /affiliation
  affiliation: {
    kicker: string;
    heroTitle: string;
    heroSubtitle: string;
    benefits: {
      recurring: { title: string; description: string };
      attribution: { title: string; description: string };
      brandingKit: { title: string; description: string };
      payments: { title: string; description: string };
    };
    howTitle: string;
    steps: {
      apply: { title: string; description: string };
      access: { title: string; description: string };
      earn: { title: string; description: string };
    };
    cta: string;
    ctaNote: string;
  };
  // Dashboard ambassadeur /affiliate
  affiliate: {
    pageTitle: string;
    pageSubtitle: string;
    kpis: {
      clicks: string;
      signups: string;
      conversions: string;
      conversionRate: string;
    };
    balances: {
      pending: string;
      confirmed: string;
      totalPaid: string;
    };
    commissions: {
      title: string;
      empty: string;
      colDate: string;
      colClient: string;
      colPayment: string;
      colRate: string;
      colCommission: string;
      colStatus: string;
    };
    status: {
      pending: string;
      confirmed: string;
      cancelled: string;
      paid: string;
      requested: string;
      rejected: string;
    };
    payout: {
      requestButton: string;
      requestHint: string;
      detailsMissing: string;
      requestSuccess: string;
      requestError: string;
      historyTitle: string;
      historyEmpty: string;
    };
    link: {
      title: string;
      copy: string;
      copied: string;
      terms: string;
    };
    settings: {
      title: string;
      method: string;
      methodIban: string;
      methodPaypal: string;
      detailsIban: string;
      detailsPaypal: string;
      save: string;
      saved: string;
      saveError: string;
      changePassword: string;
    };
    branding: {
      title: string;
      empty: string;
      download: string;
      categoryLogo: string;
      categoryBanner: string;
      categoryScreenshot: string;
      categoryGuidelines: string;
      rulesTitle: string;
      rulesLogo: string;
      ruleNoEarningsPromise: string;
      ruleNoLogoEdit: string;
      ruleColors: string;
    };
  };
  // Admin /admin/affiliates
  adminAffiliates: {
    title: string;
    tabs: { affiliates: string; payouts: string; assets: string };
    newAffiliate: string;
    create: string;
    createHint: string;
    createdToast: string;
    savedToast: string;
    cancel: string;
    save: string;
    cols: {
      email: string;
      refCode: string;
      rate: string;
      duration: string;
      clicks: string;
      referred: string;
      commissions: string;
      balanceDue: string;
      actions: string;
      active: string;
      date: string;
      amount: string;
      paymentDetails: string;
      status: string;
    };
    form: {
      email: string;
      fullName: string;
      companyName: string;
      ratePercent: string;
      durationMonths: string;
      refCode: string;
      refCodeOptional: string;
      refCodePlaceholder: string;
      notes: string;
      adminNotes: string;
      assetName: string;
      assetCategory: string;
      sortOrder: string;
      file: string;
    };
    emptyAffiliates: string;
    emptyPayouts: string;
    emptyAssets: string;
    payoutStatus: { requested: string; paid: string; rejected: string };
    markPaid: string;
    reject: string;
    rejectTitle: string;
    payoutPaidToast: string;
    payoutRejectedToast: string;
    assetCategories: {
      logo: string;
      banner: string;
      screenshot: string;
      guidelines: string;
    };
    upload: string;
    uploadError: string;
    assetCreatedToast: string;
    assetDeletedToast: string;
  };
  // Admin /admin/b2b (partenaires B2B / VIP)
  adminB2b: {
    pageTitle: string;
    partnersTitle: string;
    newPartner: string;
    partnerName: string;
    contactEmail: string;
    createdAt: string;
    seats: string;
    seatsBillable: string;
    statusActive: string;
    statusPending: string;
    statusShadowed: string;
    statusRevoked: string;
    apiActive: string;
    apiDisabled: string;
    rotateKey: string;
    rotateKeyConfirm: string;
    disablePartner: string;
    enablePartner: string;
    disablePartnerConfirm: string;
    keyDialogTitle: string;
    keyDialogWarning: string;
    copy: string;
    copied: string;
    close: string;
    grantsTitle: string;
    newGrant: string;
    filterStatusAll: string;
    filterEmailPlaceholder: string;
    colEmail: string;
    colPartner: string;
    colExternalRef: string;
    colStatus: string;
    colUser: string;
    colActivatedAt: string;
    colEndsAt: string;
    colCreatedAt: string;
    noGrants: string;
    revoke: string;
    revokeDialogTitle: string;
    revokeNow: string;
    revokeAtDate: string;
    revokePastDate: string;
    revokeScheduledToast: string;
    revokedToast: string;
    newGrantDialogTitle: string;
    email: string;
    externalRef: string;
    create: string;
    cancel: string;
    createdToast: string;
    page: string;
    of: string;
    colActions: string;
    previous: string;
    next: string;
    partnerUpdatedToast: string;
    filterPartnerAll: string;
  };
  // Membre B2B (accès offert par un partenaire)
  b2bMember: {
    offeredBy: string;
    offeredBadge: string;
    pricingBannerTitle: string;
    pricingBannerBody: string;
    checkoutBlocked: string;
    currentPlan: string;
  };
  // Admin /admin (dashboard)
  adminDashboard: {
    usersByPlanTitle: string;
    usersByPlanSubtitle: string;
    activeUsersLabel: string;
    activeUsersHint: string;
    activeUsersTotal: string;
    searchPlaceholder: string;
    sortBy: string;
    sortNewest: string;
    sortOldest: string;
    sortLastActive: string;
    sortEmail: string;
    emptyColumn: string;
    noSearchResult: string;
    annualBadge: string;
    adminBadge: string;
    brokerBadge: string;
    graceBadge: string;
    expiredBadge: string;
    inactiveBadge: string;
    neverActive: string;
    signedUpOn: string;
    lastActiveOn: string;
    mrrTitle: string;
    mrrTotal: string;
    mrrMax: string;
    mrrPro: string;
    paymentIssues: string;
    totalUsers: string;
    mrrHint: string;
    cohortTitle: string;
    cohortHint: string;
    cohortMonth: string;
    cohortSignups: string;
    cohortRetained: string;
    cohortRate: string;
    cohortEmpty: string;
    acquisitionTitle: string;
    acquisitionHint: string;
    signups7d: string;
    signups30d: string;
    sourceLabel: string;
    sourceDirect: string;
    sourceEmpty: string;
    brokerHealthTitle: string;
    brokerHealthHint: string;
    brokerHealthEmpty: string;
    quotaTitle: string;
    quotaHint: string;
    quotaEmpty: string;
    quotaPerUserLabel: string;
    quotaWorstUser: string;
    quotaIpTotal: string;
    quotaNearLimit: string;
    quotaOverLimit: string;
    quotaHistoryTitle: string;
    quotaHistoryHint: string;
    quotaHistoryEmpty: string;
    quotaHistoryCallsLabel: string;
    quotaHistoryRateLimitLabel: string;
    quotaHistoryPeakLabel: string;
    quotaHistoryRange24h: string;
    quotaHistoryRange48h: string;
    quotaHistoryRange7d: string;
    quotaHistoryRange30d: string;
    quotaHistoryQuotaLine: string;
    endpointBreakdownTitle: string;
    endpointBreakdownHint: string;
    endpointBreakdownEmpty: string;
    tradovateErrorsTitle: string;
    tradovateErrorsHint: string;
    tradovateErrorsEmpty: string;
    tradovateErrorsRateLimits: string;
    tradovateErrorsCalls: string;
    tradovateErrorsCaptcha: string;
    tradovateErrorsCaptchaTag: string;
    tradovateErrorsRetryFailures: string;
    tradovateErrorsRetryHint: string;
    tradovateErrorsHitsCol: string;
    tradovateErrorsCallsCol: string;
    tradovateErrorsRecentTitle: string;
    tradovateErrorsTimeCol: string;
    tradovateErrorsStatusCol: string;
    tradovateErrorsPenaltyCol: string;
    tradovateErrorsPhaseCol: string;
    tradovateErrorsHostCol: string;
    tradovateErrorsUserCol: string;
    tradovateErrorsPhaseInitial: string;
    tradovateErrorsPhaseRetry: string;
    accountLifecycleTitle: string;
    accountLifecycleHint: string;
    accountLifecycleEmpty: string;
    accountLifecycleRange14d: string;
    accountLifecycleVerdictGreen: string;
    accountLifecycleVerdictRed: string;
    accountLifecycleVerdictUnjudged: string;
    accountLifecycleReasonUnjudged: string;
    accountLifecycleReasonNotSeen: string;
    accountLifecycleReasonTooFew: string;
    accountLifecycleReasonUnstable: string;
    accountLifecycleReasonWsDeleted: string;
    accountLifecycleDecisionNew: string;
    accountLifecycleDecisionGone: string;
    accountLifecycleDecisionBreach: string;
    accountLifecycleDecisionFunded: string;
    accountLifecycleCoverageTitle: string;
    accountLifecycleColDecision: string;
    accountLifecycleColWithSocket: string;
    accountLifecycleColSeenBefore: string;
    accountLifecycleColCoverage: string;
    accountLifecycleColMedianLead: string;
    accountLifecycleColEvidence: string;
    accountLifecycleColNoSocket: string;
    accountLifecycleNoSocketHint: string;
    accountLifecycleTransitionsTitle: string;
    accountLifecycleTransitionsHint: string;
    accountLifecycleTransitionsEmpty: string;
    accountLifecycleColField: string;
    accountLifecycleColFrom: string;
    accountLifecycleColTo: string;
    accountLifecycleColCount: string;
    accountLifecycleColLastSeen: string;
    accountLifecycleColExample: string;
    accountLifecycleFirstSight: string;
    accountLifecycleParityTitle: string;
    accountLifecycleParityRestOnly: string;
    accountLifecycleParityWsOnly: string;
    accountLifecycleParityWsDeleted: string;
    accountLifecycleParityWsDeletedLag: string;
    accountLifecycleParityName: string;
    accountLifecycleParityEmpty: string;
    accountLifecycleProbeTitle: string;
    accountLifecycleProbeSeen: string;
    accountLifecycleProbeNotSeen: string;
    accountLifecycleReasonUnparsed: string;
    accountLifecycleColCommonFields: string;
    accountLifecycleTruncated: string;
    accountLifecycleLoading: string;
    accountLifecycleError: string;
    accountLifecycleParityRestLabel: string;
    accountLifecycleParityWsLabel: string;
    endpointBreakdownEndpointCol: string;
    endpointBreakdownCallsCol: string;
    endpointBreakdownShareCol: string;
    endpointBreakdownPerHourCol: string;
    endpointBreakdownUnattributed: string;
    endpointBreakdownUnattributedHint: string;
    endpointBreakdownUsersTitle: string;
    endpointBreakdownUserCol: string;
    endpointBreakdownAccountsCol: string;
    endpointBreakdownRatioCol: string;
    endpointBreakdownRatioHint: string;
    endpointBreakdownTransportCol: string;
    endpointBreakdownTransportRest: string;
    endpointBreakdownTransportWs: string;
    endpointBreakdownBetaBadge: string;
    endpointBreakdownWitnessBadge: string;
    endpointBreakdownProjectionTitle: string;
    endpointBreakdownProjectionHint: string;
    endpointBreakdownProjectionAccounts: string;
    endpointBreakdownProjectionConnections: string;
    endpointBreakdownCostModel: string;
    endpointBreakdownCostPerConnection: string;
    endpointBreakdownCostPerAccount: string;
    endpointBreakdownProjectionCalls: string;
    endpointBreakdownProjectionWithin: string;
    endpointBreakdownProjectionOver: string;
    endpointBreakdownProjectionUnknown: string;
    endpointBreakdownSocketTitle: string;
    endpointBreakdownSocketEvents: string;
    endpointBreakdownSocketReconnects: string;
    endpointBreakdownSocketSilent: string;
    endpointBreakdownSocketNotStarted: string;
    endpointBreakdownMedianLabel: string;
    shadowDetailRest: string;
    shadowDetailWs: string;
    compareTitle: string;
    compareHint: string;
    wsWrittenLabel: string;
    wsWriteFailuresLabel: string;
    wsRepairedLabel: string;
    wsWrittenActive: string;
    wsWrittenNone: string;
    compareTrade: string;
    compareMatch: string;
    compareMismatch: string;
    compareNotSeen: string;
    compareFeesUnverified: string;
    compareFeesUnverifiedHint: string;
    compareEmpty: string;
    betaDiagTitle: string;
    betaDiagHint: string;
    betaDiagEmpty: string;
    betaDiagConnection: string;
    betaDiagUser: string;
    betaDiagHealth: string;
    betaDiagEvents: string;
    betaDiagReconnects: string;
    betaDiagDiffs: string;
    betaDiagLeaseAge: string;
    betaDiagHealthy: string;
    betaDiagSilent: string;
    betaDiagStale: string;
    betaDiagFlapping: string;
    betaDiagCut: string;
    betaDiagModeFallback: string;
    betaDiagNeverConnected: string;
    betaDiagFlagWithoutPlan: string;
    betaDiagNotEnrolled: string;
    betaDiagSubscribed: string;
    betaDiagSockets: string;
    betaDiagSocketsHint: string;
    betaDiagProblems: string;
    betaDiagPropFirm: string;
    betaDiagWriteMode: string;
    betaDiagCadence: string;
    betaDiagControl: string;
    betaDiagLatency: string;
    betaDiagModeWsOnly: string;
    betaDiagModeRestOnly: string;
    betaDiagCadenceSpaced: string;
    betaDiagCadenceNominal: string;
    betaDiagControlNever: string;
    betaDiagControlHint: string;
    betaDiagLatencyHint: string;
    betaDiagWindow: string;
    betaDiagSocketShare: string;
    betaDiagFallback: string;
    betaDiagShareHint: string;
    reauthTitle: string;
    reauthHint: string;
    reauthDisabled: string;
    reauthEmpty: string;
    reauthUsers: string;
    reauthCountsHint: string;
    reauthColUser: string;
    reauthColPropFirm: string;
    reauthColVerdict: string;
    reauthColReauth: string;
    reauthColProof: string;
    reauthColLast: string;
    reauthVerdictProven: string;
    reauthVerdictAwaiting: string;
    reauthVerdictFailing: string;
    reauthVerdictNoActivity: string;
    backfillRepairTitle: string;
    backfillRepairHint: string;
    backfillRepairEmpty: string;
    backfillRepairColAccount: string;
    backfillRepairColUser: string;
    backfillRepairColTrades: string;
    backfillRepairColInitial: string;
    backfillRepairColBroker: string;
    backfillRepairColKind: string;
    backfillRepairKindNew: string;
    backfillRepairKindGap: string;
    backfillRepairInactive: string;
    backfillRepairReplay: string;
    backfillRepairReplayAll: string;
    backfillRepairRequested: string;
    backfillRepairKindBoth: string;
    backfillRepairKindNone: string;
    backfillRepairLastRun: string;
    backfillRepairError: string;
    endpointBreakdownReplaceable: string;
    endpointBreakdownReplaceableYes: string;
    endpointBreakdownReplaceableNo: string;
    endpointBreakdownSavings: string;
    endpointBreakdownAfterWs: string;
    betaTesterColumn: string;
    betaTesterEnable: string;
    betaTesterDisable: string;
    betaTesterNoEffect: string;
    brokerProvider: string;
    brokerUser: string;
    brokerStatus: string;
    brokerLastSync: string;
    brokerAccounts: string;
    brokerNeverSynced: string;
    statusError: string;
    statusNeedsReauth: string;
    statusDelayed: string;
    statusDeprovisioned: string;
    statusForeignToken: string;
    detailTitle: string;
    detailLoading: string;
    detailPlan: string;
    detailStatus: string;
    detailJoined: string;
    detailLastActive: string;
    detailOnboarding: string;
    detailOnboardingDone: string;
    detailOnboardingPending: string;
    detailAcquisition: string;
    detailActivity: string;
    detailTrades: string;
    detailSessions: string;
    detailNotes: string;
    detailConnections: string;
    detailAccounts: string;
    detailNoAccounts: string;
    detailNextBilling: string;
    detailClose: string;
    detailNone: string;
  };
  playbook: {
    title: string;
    subtitle: string;
    stepOf: string;
    next: string;
    back: string;
    save: string;
    saving: string;
    edit: string;
    versionLabel: string;
    activeVersion: string;
    createdOn: string;
    emptyTitle: string;
    emptyDescription: string;
    emptyCta: string;
    savedToast: string;
    newVersionToast: string;
    saveError: string;
    newPlaybook: string;
    deleteAction: string;
    deleteConfirm: string;
    deletedToast: string;
    deleteError: string;
    nameLabel: string;
    namePlaceholder: string;
    nameRequired: string;
    targetsLabel: string;
    targetsDescription: string;
    targetsAccounts: string;
    targetsGroups: string;
    targetsEmpty: string;
    referenceAccountLabel: string;
    referenceAccountHelp: string;
    steps: {
      strategy: string;
      setups: string;
      rules: string;
    };
    strategyStep: {
      title: string;
      description: string;
      strategyLabel: string;
      strategyPlaceholder: string;
      strategyNameLabel: string;
      strategyNamePlaceholder: string;
      strategyNameHint: string;
      marketsLabel: string;
      marketsHint: string;
      marketsEmpty: string;
      sessionsLabel: string;
      sessionsHint: string;
      addSession: string;
      sessionName: string;
      sessionStart: string;
      sessionEnd: string;
      sessionHours: string;
      sessionMinutes: string;
      sessionTimezone: string;
      removeSession: string;
      presetAsia: string;
      presetLondon: string;
      presetNewYork: string;
    };
    setupsStep: {
      title: string;
      description: string;
      setupsLabel: string;
      addSetup: string;
      setupName: string;
      setupNamePlaceholder: string;
      entryRules: string;
      entryRulesPlaceholder: string;
      exitRules: string;
      exitRulesPlaceholder: string;
      removeSetup: string;
      noSetups: string;
      riskLabel: string;
      riskTypePercent: string;
      riskTypeAmount: string;
      riskValueLabel: string;
      maxTradesPerDayLabel: string;
      maxTradesPerDayPlaceholder: string;
      minRiskRewardLabel: string;
      minRiskRewardPlaceholder: string;
    };
    rulesStep: {
      title: string;
      description: string;
      sourceAccountLabel: string;
      sourceAccountHint: string;
      sourceAccountNone: string;
      derivedTitle: string;
      derivedHint: string;
      noRulesDetected: string;
      maxDailyDrawdown: string;
      maxTotalDrawdown: string;
      profitTarget: string;
      overrideToggle: string;
      overrideHint: string;
      resetOverride: string;
      unitPercent: string;
      unitAmount: string;
      personalRulesLabel: string;
      personalRulesPlaceholder: string;
      personalRulesHint: string;
    };
    validation: {
      strategyNameRequired: string;
      marketsRequired: string;
      setupNameRequired: string;
      setupRequired: string;
      riskValueRequired: string;
    };
  };
  edgy: {
    buttonLabel: string;
    drawerTitle: string;
    drawerSubtitle: string;
    close: string;
    checking: string;
    missingPrereqsTitle: string;
    missingPrereqsIntro: string;
    addStrategy: string;
    addTrades: string;
    noTradesOnStrategy: string;
    selectStrategyTitle: string;
    selectStrategySubtitle: string;
    tradesCount: string;
    versionLabel: string;
    scanning: {
      reading: string;
      sessions: string;
      playbook: string;
      leaks: string;
      writing: string;
    };
    reveal: {
      strengths: string;
      leaks: string;
      playbookGaps: string;
      weeklyActions: string;
      edgeScore: string;
      declared: string;
      actual: string;
      impact: string;
      estimatedCost: string;
      checkMetric: string;
      copy: string;
      copied: string;
      copyAll: string;
      lowSampleNotice: string;
    };
    questions: {
      title: string;
      limitReached: string;
      asking: string;
      error: string;
    };
    credits: {
      remaining: string;
      discovery: string;
      none: string;
    };
    noCredits: {
      title: string;
      description: string;
      refillOn: string;
      upgrade: string;
    };
    betaLock: {
      badge: string;
      title: string;
      description: string;
      upgrade: string;
      lockedTooltip: string;
    };
    errors: {
      title: string;
      retry: string;
      killSwitch: string;
      cooldown: string;
      generic: string;
      notConfigured: string;
    };
    progress: {
      title: string;
      followed: string;
      partiallyFollowed: string;
      notFollowed: string;
      unverifiable: string;
    };
    history: {
      navLabel: string;
      pageTitle: string;
      pageSubtitle: string;
      empty: string;
      emptyCta: string;
      scoreTrend: string;
      questionsCount: string;
      tradesAnalysed: string;
      allStrategies: string;
      filterStrategy: string;
      back: string;
      loadMore: string;
      conversation: string;
      analysedOn: string;
    };
  };
  notificationsCenter: {
    title: string;
    bellAriaLabel: string;
    unreadAriaLabel: string;
    markAllRead: string;
    dismiss: string;
    dismissAll: string;
    empty: string;
    reportReady: string;
    planUpgraded: string;
    planDowngraded: string;
    paymentFailed: string;
    paymentFailedWithDays: string;
    planDowngradedPaymentFailed: string;
    accountChallengeCompleted: string;
    accountClosedBroker: string;
    accountDrawdownBreach: string;
    subscriptionCancelQuestion: string;
    chargeUpcoming: string;
    subscriptionResetReactivate: string;
    payoutDetected: string;
    propFirmAdjustmentDetected: string;
    walletTransferDetected: string;
    walletTransferDetectedToConfirm: string;
    tradovateAccountsDiscoveredOne: string;
    tradovateAccountsDiscovered: string;
    tradovateRealtimeInterrupted: string;
    tradovateNeedsReauth: string;
    mentorFallback: string;
    periodMonthly: string;
    periodQuarterly: string;
    periodAnnual: string;
  };
  fundedLineage: {
    importLabel: string;
    importNone: string;
    importHint: string;
    importSuggested: string;
    targetReached: string;
    inactiveTag: string;
    cardTitle: string;
    cardDescription: string;
    cardLinkedTo: string;
    cardLinkedHint: string;
    cardSelectPlaceholder: string;
    cardNoCandidates: string;
    cardLinkButton: string;
    cardUnlinkButton: string;
    cardLinked: string;
    cardUnlinked: string;
    cardConfirmTitle: string;
    cardConfirmBody: string;
    cancel: string;
    modalNone: string;
    modalLinkHint: string;
    modalChangeHint: string;
  };
  paymentGrace: {
    modalTitle: string;
    modalBody: string;
    modalBodyLastDay: string;
    modalConsequence: string;
    updatePayment: string;
    later: string;
    bannerText: string;
    bannerTextLastDay: string;
    bannerAction: string;
    expiredModalTitle: string;
    expiredModalBody: string;
    expiredAction: string;
    daysLeft: string;
    oneDayLeft: string;
  };
  newNav: {
    more: string;
    myPages: string;
    addPage: string;
    collapse: string;
    expand: string;
    settings: string;
    lockedTooltip: string;
    menu: string;
    dashboard: {
      overview: string;
    };
  };
}

export const translations: Record<Language, Translations> = {
  fr: {
    lite: liteFr,
    nav: {
      login: "Connexion",
      dashboard: "Dashboard",
      journal: "Journal",
      analytics: "Analytics",
      routines: "Routines",
      goals: "Objectifs",
      notebook: "Notebook",
      disciplineManager: "Discipline",
      accounts: "Comptes",
      account: "Compte",
      sessions: "Sessions",
      logout: "Déconnexion",
      tools: "Outils",
      features: "Fonctionnalités",
      pricing: "Tarifs",
      faq: "FAQ",
      propFirmManager: "Prop Firm Manager",
      reports: "Rapports",
      blog: "Blog",
      mentor: "Mentor",
      affiliate: "Affiliation",
      addPage: "Ajouter une page",
      addPagePlaceholder: "Nom de la page...",
      upgradeMyPlan: "Passer au plan supérieur",
    },
    themePicker: {
      label: "Thème",
      classicDark: "Classic Dark",
      classicLight: "Classic Light",
      deepJungle: "Deep Jungle",
      sunrise: "Sunrise",
      tokyoNight: "Tokyo Night",
      dark: "Sombre",
      light: "Clair",
      switchTo: "Changer de thème",
      comingSoon: "Bientôt",
    },
    disciplineManager: {
      title: "Discipline Manager",
      sections: {
        routines: "Routines",
        goals: "Objectifs",
        notebook: "Notebook",
      },
    },
    mentor: {
      title: "Programme de tutorat",
      dashboard: "Gérez vos élèves et vos invitations.",
      myStudents: "Mes élèves",
      myInvitations: "Invitations",
      noStudents: "Aucun élève pour l'instant. Invitez un trader à rejoindre votre espace.",
      noInvitations: "Aucune invitation envoyée.",
      upgradeToBecomeMentor: "Passez en plan Pro ou Max pour accéder au programme de tutorat.",
      revokeRelation: "Retirer l'élève",
      revokeConfirm: "Confirmer le retrait de cet élève ? Vous n'aurez plus accès à son espace.",
      resendInvitation: "Renvoyer",
      cancelInvitation: "Annuler",
      viewStudent: "Voir",
      status: {
        pending: "En attente",
        accepted: "Acceptée",
        declined: "Refusée",
        revoked: "Révoquée",
        expired: "Expirée",
      },
      invite: {
        title: "Inviter un élève",
        emailLabel: "Adresse email",
        emailPlaceholder: "trader@exemple.com",
        sendInvitation: "Invitation envoyée",
        inviteButton: "Inviter un élève",
        inviteEmailPlaceholder: "trader@exemple.com",
        inviteSubmit: "Envoyer l'invitation",
        importList: "Importer une liste",
        importTitle: "Import CSV / Excel",
        importDescription: "Importez un fichier .csv ou .xlsx contenant des emails (colonne « email » ou première colonne avec @). Limité à 50 emails.",
        preview: "Aperçu",
        confirmSend: "Envoyer {count} invitation(s)",
        willBeInvited: "Sera invité",
        alreadyInvited: "Déjà invité (ignoré)",
        maxImport: "50 emails max",
      },
      notifications: {
        invitationReceived: "vous a invité comme élève",
        invitationAccepted: "a accepté votre invitation",
        invitationDeclined: "a refusé votre invitation",
        relationRevoked: "a révoqué la relation de tutorat",
        noteAdded: "a laissé une note sur votre session",
        markAllRead: "Tout marquer comme lu",
        noNotifications: "Aucune notification",
      },
      errors: {
        invalidEmail: "Format de fichier non supporté",
      },
      studentView: "Vue élève",
      readOnlyMode: "Mode lecture",
      backToStudents: "← Mes élèves",
      permissionsTitle: "Accès accordé au tuteur",
      canViewJournal: "Journal",
      canViewDashboard: "Dashboard",
      canViewAnalytics: "Analytics",
      canViewSessions: "Sessions",
      canViewPropFirmManager: "Prop Firm Manager",
      mentorNotes: "Notes du tuteur",
      addNote: "Ajouter une note",
      editNote: "Modifier",
      deleteNote: "Supprimer",
      noNotes: "Aucune note pour l'instant.",
      notePlaceholder: "Laissez une note de correction...",
      saveNote: "Enregistrer",
      acceptInvitation: "Accepter l'invitation",
      declineInvitation: "Refuser",
      invitationExpired: "Cette invitation a expiré ou n'est plus valide.",
      invitationNotFound: "Invitation introuvable.",
      invitationDeclinedConfirm: "Invitation refusée.",
      loginToAccept: "Connectez-vous pour accepter cette invitation",
      invitationFrom: "Invitation de",
      grantedPermissions: "Accès accordés :",
      mentorsSection: "Mes tuteurs",
      noMentors: "Vous n'avez pas de tuteur actif.",
      revokeMentor: "Révoquer",
      revokeMentorConfirm: "Révoquer ce tuteur ? Ils perdront l'accès à vos données.",
      activeSince: "Actif depuis le",
      pendingInvitations: "Invitations en attente",
      globalNotes: "Notes générales",
      sessionNotes: "Notes de session",
      tradeNotes: "Notes de trade",
      noPermissionSection: "L'élève n'a pas accordé l'accès à cette section.",
    },
    comparator: {
      metaTitle: "Comparateur de prop firms 2026 | Edgely",
      metaDescription:
        "Compare les meilleures prop firms futures : prix, drawdown, plateformes, commissions. Trouve le compte de trading adapté à ton style.",
      h1: "Comparateur de prop firms futures",
      introP1:
        "Compare en un coup d'œil les offres des meilleures prop firms de trading futures : frais d'évaluation, type de drawdown, plateformes supportées et commissions round-trip.",
      introP2:
        "Filtre par taille de compte, plateforme ou type de challenge, trie par prix ou par note, et compare jusqu'à 4 offres côte à côte pour choisir la firme faite pour ton style.",
      resultsCount: "{shown} / {total} résultats",
      filtersTitle: "Filtres",
      searchLabel: "Recherche",
      searchPlaceholder: "Nom de firme ou de plan…",
      firmLabel: "Prop firm",
      showAll: "Tout afficher",
      showLess: "Réduire",
      sizeLabel: "Taille de compte",
      accountTypeLabel: "Type de compte",
      drawdownTypeLabel: "Type de drawdown",
      platformLabel: "Plateforme",
      dailyLossLabel: "Perte journalière",
      ratingLabel: "Note",
      maxPriceLabel: "Prix maximum",
      resetFilters: "Réinitialiser tous les filtres",
      filtersButton: "Filtres",
      typeEval: "Évaluation",
      typeDirect: "Direct",
      ddEod: "EOD",
      ddEot: "EOT",
      ddTrailing: "Trailing",
      ddStatic: "Statique",
      dailyAll: "Toutes",
      dailyNone: "Aucune",
      dailyDefined: "Définie",
      ratingAll: "Toutes",
      ratingHigh: "Haut (8–10)",
      ratingMid: "Moyen (4–7)",
      ratingLow: "Bas (1–3)",
      colFirm: "Prop firm",
      colSize: "Taille",
      colPrice: "Prix",
      colBilling: "Facturation",
      colActivation: "Activation",
      colPlatforms: "Plateformes",
      colDrawdown: "Drawdown",
      colTarget: "Objectif",
      colRoundTrip: "Round trip",
      colRating: "Note",
      colAction: "Action",
      sortBy: "Trier par",
      billingOneTime: "Paiement unique",
      billingMonthly: "Mensuel",
      free: "GRATUIT",
      total: "Total",
      promoExpires: "Expire le {date}",
      copyCode: "Copier le code",
      codeCopied: "Code copié",
      startChallenge: "Démarrer le challenge",
      officialSite: "Site officiel",
      compareCheckbox: "Comparer",
      compareBar: "Comparer ({n})",
      compareReset: "Réinitialiser",
      compareTitle: "Comparaison des offres",
      compareMax: "Maximum 4 offres",
      bestValue: "Meilleure valeur",
      suggestFirmTitle: "Une firme fiable à ajouter ?",
      suggestFirmCta: "Proposer une prop firm",
      edgelyCtaTitle:
        "Tu as choisi ta prop firm ? Journalise tes trades dessus avec Edgely.",
      edgelyCtaButton: "Créer mon compte gratuit",
      noResults: "Aucune offre ne correspond à ces filtres.",
    },
    blog: {
      title: "Blog",
      readMore: "Lire l'article",
      backToBlog: "← Retour au blog",
      publishedOn: "Publié le",
      by: "Par",
      noPostsFound: "Aucun article trouvé.",
      loadMore: "Charger plus",
      filterByCategory: "Filtrer par catégorie",
      allCategories: "Toutes les catégories",
      draft: "Brouillon",
      published: "Publié",
      postNotFound: "Article introuvable.",
      subtitle: "Conseils, stratégies et ressources pour les traders actifs.",
      tableOfContents: "Sommaire",
    },
    help: {
      navLabel: "Centre d'aide",
      metaTitle: "Centre d'aide | Edgely",
      heroTitle: "Comment pouvons-nous vous aider ?",
      heroSubtitle: "Trouvez rapidement des réponses à vos questions sur Edgely.",
      searchPlaceholder: "Rechercher une question, un mot-clé…",
      categoriesTitle: "Parcourir par catégorie",
      questionsCount: "{count} question(s)",
      searchResultsTitle: "{count} résultat(s)",
      noResultsTitle: "Aucun résultat pour « {query} »",
      noResultsBody: "Essayez d'autres mots-clés ou contactez notre équipe.",
      backToCategories: "Effacer la recherche",
      inCategory: "dans {category}",
      notFoundTitle: "Vous n'avez pas trouvé votre réponse ?",
      notFoundBody: "Notre équipe est là pour vous aider. Écrivez-nous et nous vous répondrons rapidement.",
      contactCta: "Contacter le support",
    },
    adminLayouts: {
      title: "Layouts Edgely",
      searchPlaceholder: "Rechercher un layout...",
      scopeAll: "Tous les scopes",
      noLayouts: "Aucun layout trouvé.",
      owner: "Propriétaire",
      widgets: "widgets",
      edgelyBadge: "Edgely",
      sharedBadge: "Partagé",
      markEdgely: "Marquer Edgely",
      unmarkEdgely: "Retirer Edgely",
      editMeta: "Modifier les métadonnées",
      displayName: "Nom affiché",
      description: "Description",
      requiredPlan: "Plan requis",
      allPlans: "Tous les plans",
      saveMetaSuccess: "Métadonnées sauvegardées.",
      flagUpdated: "Flag Edgely mis à jour.",
      confirmUnmark: "Retirer le flag Edgely de ce layout ?",
    },
    adminBlog: {
      title: "Blog",
      newPost: "Nouvel article",
      manageCategories: "Gérer les catégories",
      editPost: "Modifier l'article",
      createPost: "Créer un article",
      titleField: "Titre",
      slugField: "Slug",
      categoryField: "Catégorie",
      noCategory: "Aucune catégorie",
      tagsField: "Tags",
      authorField: "Auteur",
      coverImage: "Image de couverture",
      seoSection: "SEO",
      metaTitle: "Meta titre",
      metaDescription: "Meta description",
      seoBodyTitle: "Corps SSR (avancé)",
      seoBodyHelp:
        "Contenu servi uniquement aux robots des moteurs de recherche (caché aux visiteurs). Laisser vide pour utiliser le corps générique.",
      seoBodyH1: "H1 SSR",
      seoBodySections: "Sections H2 (une par ligne)",
      seoBodySectionsHelp: "Format : Titre H2 | Paragraphe, une section par ligne.",
      seoBodyLinks: "Liens internes (un par ligne)",
      seoBodyLinksHelp: "Format : /chemin | Libellé, un lien par ligne.",
      excerptField: "Résumé",
      saveDraft: "Sauvegarder brouillon",
      publish: "Publier",
      unpublish: "Dépublier",
      savePublished: "Sauvegarder",
      deletePost: "Supprimer",
      confirmDelete: "Supprimer cet article ?",
      statusAll: "Tous",
      statusDraft: "Brouillons",
      statusPublished: "Publiés",
      categoryName: "Nom",
      categorySlug: "Slug",
      addCategory: "Ajouter une catégorie",
      deleteCategory: "Supprimer",
      confirmDeleteCategory: "Supprimer cette catégorie ?",
      categoryHasPosts: "Cette catégorie contient des articles. Réassignez-les avant de supprimer.",
      postSaved: "Article sauvegardé.",
      postPublished: "Article publié.",
      postUnpublished: "Article dépublié.",
      postDeleted: "Article supprimé.",
      categorySaved: "Catégorie sauvegardée.",
      categoryDeleted: "Catégorie supprimée.",
    },
    adminHelp: {
      categoriesTitle: "Catégories d'aide",
      questionsTitle: "Questions",
      newCategory: "Nouvelle catégorie",
      newQuestion: "Nouvelle question",
      nameFr: "Nom (FR)",
      nameEn: "Nom (EN)",
      slug: "Slug",
      icon: "Icône (Lucide)",
      sortOrder: "Ordre",
      questionColumn: "Question",
      categoryColumn: "Catégorie",
      statusColumn: "Statut",
      published: "Publié",
      draft: "Brouillon",
      edit: "Modifier",
      delete: "Supprimer",
      save: "Enregistrer",
      cancel: "Annuler",
      questionFr: "Question (FR)",
      answerFr: "Réponse (FR, markdown)",
      questionEn: "Question (EN)",
      answerEn: "Réponse (EN, markdown)",
      keywords: "Mots-clés (synonymes, séparés par des virgules)",
      category: "Catégorie",
      preview: "Aperçu",
      publish: "Publier",
      unpublish: "Dépublier",
      deleteCategoryBlocked: "Impossible de supprimer une catégorie contenant des questions.",
      confirmDelete: "Confirmer la suppression ?",
      itemCount: "{count} questions",
      filterAllStatuses: "Tous les statuts",
      filterAllCategories: "Toutes les catégories",
      saved: "Enregistré.",
      deleted: "Supprimé.",
      manageQuestions: "Gérer les questions",
      manageCategories: "Gérer les catégories",
    },
    dashboard: {
      lockedWidget: { title: "Fonctionnalité Pro", description: "Passez à Pro pour débloquer ce widget.", cta: "Passer à Pro" },
      title: "Dashboard",
      subtitle: "Vue d'ensemble de vos performances et activités",
      totalPnl: "P&L Total",
      winRate: "Win Rate",
      profitFactor: "Profit Factor",
      totalTrades: "Total Trades",
      recentTrades: "Derniers Trades",
      activeGoals: "Objectifs Actifs",
      activeRoutines: "Routines Actives",
      viewAll: "Voir tout",
      open: "Ouvert",
      noRecentTrades: "Aucun trade récent",
      noActiveGoals: "Aucun objectif actif",
      tradesTotal: "trades au total",
      tradesOpen: "ouverts",
      addTrade: "Ajouter un trade",
      selectPlan: "Choisissez votre plan",
      selectPlanDescription: "Sélectionnez le plan qui correspond le mieux à vos besoins de trading.",
      upgradeTitle: "Passez au niveau supérieur",
      upgradeDescription: "Débloquez toutes les fonctionnalités avancées avec le plan Pro",
      upgradeButton: "Voir les plans",
      continueWithFree: "Continuer avec le plan gratuit",
      planChange: {
        upgradedTitle: "Plan mis à niveau",
        upgradedDescription: "Ton nouveau plan est actif. Nous n'avons facturé que la différence au prorata.",
        alreadyOnPlan: "Tu es déjà sur ce plan.",
      },
      wins: "Gains",
      losses: "Pertes",
      journalLabel: "Journal",
      goalsLabel: "Objectifs",
      routinesToCompleteToday: "routines à compléter aujourd'hui",
      routineToCompleteToday: "routine à compléter aujourd'hui",
      manageAccounts: "Gérer vos comptes",
      goalsWithDeadlineThisWeek: "objectifs avec échéance cette semaine",
      goalWithDeadlineThisWeek: "objectif avec échéance cette semaine",
      topGoalProgress: "{progress}% vers « {title} »",
      topGoalDeadlineSoon: "⚡ {progress}% · {days}j restants · « {title} »",
      routineReminderToday: "⏰ « {name} » à faire aujourd'hui",
      routineReminderWeek: "⏰ « {name} » à faire cette semaine",
      routineReminderMonth: "⏰ « {name} » à faire ce mois",
      calendar: {
        title: "Calendrier",
        previousMonth: "Mois précédent",
        nextMonth: "Mois suivant",
        trade: "Trade",
        routine: "Routine",
        goal: "Objectif",
        completed: "Complétée",
        notCompleted: "Non complétée",
        more: "de plus",
        win: "Gagnant",
        loss: "Perdant",
        trades: "trades",
        noTrades: "Aucun trade",
        profitDay: "Jour gagnant",
        lossDay: "Jour perdant",
        routinesCompleted: "routines complétées",
        goalDeadline: "Échéance objectif",
        weeklyTotal: "Total semaine",
        week: "Sem.",
        usMarketClosed: "Marché US fermé",
        euMarketClosed: "Marché EU fermé",
        marketClosed: "fermé",
        frPublicHoliday: "Jour férié",
        accountCreated: "Compte créé",
        accountCrashed: "Compte désactivé",
        accountCreatedMany: "5+ comptes créés",
        accountCrashedMany: "5+ comptes désactivés",
        accountFunded: "Compte passé en funded",
        accountFundedMany: "Comptes passés en funded",
        calendarPayout: "Payout",
        calendarPayoutMany: "Payouts",
        hideWeekend: "Masquer le weekend",
        showWeekend: "Afficher le weekend",
        hideErrors: "Masquer les erreurs",
        showErrors: "Afficher les erreurs",
        errorsOnDay: "Erreurs commises ce jour",
        calendarSettings: "Paramètres du calendrier",
        settingsWeekend: "Afficher les weekends",
        settingsErrors: "Afficher les erreurs de session",
        settingsTradingDayStart: "Début de journée de trading",
        settingsTradingDayStartDesc: "Les trades pris après cette heure sont comptés dans le jour suivant",
        violations: {
          dailyLossViolation: "Stop loss journalier dépassé",
          dailyLossWarning: "Stop loss journalier approché",
          consistencyViolation: "Règle de consistance violée",
          consistencyWarning: "Règle de consistance approchée",
          consistencyViolationDetail: "Règle de consistance violée : {share}% le {date} (seuil {threshold}%)",
          consistencyWarningDetail: "Règle de consistance approchée : {share}% le {date} (seuil {threshold}%)",
          tooltipDailyLoss: "Perte journalière : {loss} / {max} autorisé",
          tooltipConsistency: "Ce jour représente {pct}% de l'objectif (max {max}%)",
        },
      },
      dayDetail: {
        title: "Résumé du jour",
        noTrades: "Aucun trade ce jour",
        tradesCount: "trades",
        tradesSummaryOnly: "trades ce jour, résumé par compte",
        pnlByAccount: "P&L par compte",
        pnlByGroup: "P&L par groupe",
        total: "Total",
        session: "Session",
        noSession: "Aucune session ce jour",
        mood: "Humeur",
        duration: "Durée",
        performance: "Performance",
        sessionNotes: "Notes",
        routines: "Routines",
        noRoutines: "Aucune routine active",
        completed: "Complétée",
        notCompleted: "Non complétée",
        goalDeadlines: "Objectifs, deadline aujourd'hui",
        noGoalDeadlines: "Aucun objectif deadline ce jour",
        marketHolidays: "Marchés fermés",
        marketClosed: "fermé",
        symbol: "Symbole",
        direction: "Direction",
        pnl: "P&L",
        account: "Compte",
        showAll: "Voir tous les trades",
        hideAll: "Réduire",
        page: "Page",
        of: "sur",
        accountEvents: "Comptes",
        noAccountEvents: "Aucun événement de compte",
        fees: "frais",
        deleteAllTrades: "Supprimer les trades",
        deleteAllTradesTitle: "Supprimer les trades du jour",
        deleteAllTradesConfirm: "Cette action supprimera définitivement les {count} trades du {date}. Cette action est irréversible.",
        deleteAllTradesSuccess: "Trades supprimés",
        winRate: "Taux de réussite",
        profitFactor: "Profit factor",
        avgWin: "Gain moyen",
        avgLoss: "Perte moyenne",
        bestTrade: "Meilleur trade",
        worstTrade: "Pire trade",
        accountsTraded: "Comptes",
        winsLosses: (wins: number, losses: number) => `${wins} gagnants · ${losses} perdants`,
        intradayCurve: "Courbe de la journée",
      },
      marketHours: {
        title: "Horaires des marchés",
        open: "Ouvert",
        closed: "Fermé",
        opensAt: "Ouvre à",
        closesAt: "Ferme à",
      },
      cumulativePnl: "P&L Cumulé",
      chartModePnl: "P&L cumulé",
      chartModeNetWorth: "Net Worth",
      accountsLabel:"Comptes de trading",
      noAccounts: "Aucun compte connecté",
      addAccount: "Ajouter un compte",
      currentBalance: "Balance actuelle",
      netPnlLabel: "Net P&L",
      profitFactorLabel: "Profit Factor",
      tradeWinPct: "Trade win %",
      avgWinLoss: "Avg win/loss trade",
      todayRoutines: "Routines du jour",
      allRoutinesDone: "Toutes les routines sont à jour !",
      noActiveRoutines: "Aucune routine active",
      noActiveRoutinesDesc: "Créez votre première routine pour structurer votre trading.",
      createFirstRoutine: "Créer une routine",
      daily: "Quotidien",
      weekly: "Hebdomadaire",
      monthly: "Mensuel",
      daysLeft: "j restants",
      today: "Aujourd'hui",
      topGoals: "Objectifs en cours",
      noGoalInProgress: "Aucun objectif en cours",
      noGoalInProgressDesc: "Définissez un objectif pour suivre votre progression.",
      createFirstGoal: "Créer un objectif",
      allGoalsCompleted: "Tous les objectifs atteints ! 🎯",
      allGoalsCompletedDesc: "Bravo ! Créez de nouveaux objectifs pour continuer à progresser.",
      goalProgress: "progression",
      noTradesYet: "Aucun trade enregistré",
      noTradesYetDesc: "Ajoutez votre premier trade pour voir votre courbe de performance.",
      accountsTableName: "Compte",
      accountsTableFirm: "Prop Firm",
      accountsTableStatus: "Statut",
      accountsTableStrategy: "Stratégie",
      accountsTableBalance: "Balance",
      accountsTablePnl: "PnL",
      accountsTableObjective: "Objectif",
      accountsTableBuffer: "Buffer",
      avgWin: "Avg Win",
      avgLoss: "Avg Loss",
      bufferLeft: "% restant",
      bufferUsed: "% utilisé",
      statusFunded: "Funded",
      markRoutineComplete: "Marquer comme complétée",
      period7d: "7J",
      period30d: "30J",
      period90d: "90J",
      customize: "Personnaliser",
      done: "Terminé",
      addWidget: "Ajouter un widget",
      removeWidget: "Retirer",
      resetLayout: "Réinitialiser",
      resetLayoutConfirm: "Remettre le dashboard à sa configuration d'origine ?",
      resetLayoutConfirmTitle: "Réinitialiser le dashboard",
      allWidgetsVisible: "Tous les widgets sont déjà affichés.",
      noWidgetsVisible: "Aucun widget affiché. Cliquez sur Personnaliser pour en ajouter.",
      widgetSaved: "Configuration sauvegardée",
      widgetSaveError: "Erreur de sauvegarde",
      widgetSizeLabel: "Taille",
      resizeWidgetRight: "Redimensionner horizontalement",
      resizeWidgetBottom: "Redimensionner verticalement",
      resizeWidgetCorner: "Redimensionner",
      moveUp: "Monter",
      moveDown: "Descendre",
      widgets: {
        netPnl: "P&L net",
        profitFactor: "Profit Factor",
        winRate: "Taux de réussite",
        avgWinLoss: "Gain / Perte moyen",
        calendar: "Calendrier de trading",
        marketHours: "Heures de marché",
        routines: "Routines du jour",
        goals: "Objectifs",
        session: "Session Launcher",
        equityChart: "Courbe de P&L",
        accountsTable: "Comptes",
        analyticsKpis: "KPIs Performance",
        equityCurve: "Equity Curve",
        pnlDistribution: "Distribution P&L",
        monthlyPnl: "P&L Mensuel",
        symbolPerformance: "Performance par symbole",
        rollingWinRate: "Rolling Win Rate",
        heatmap: "Heatmap Jour/Heure",
        behavioralBiases: "Biais comportementaux",
        sessionTimeSlots: "P&L par créneau",
        sessionMood: "Analyse humeur",
        sessionDuration: "P&L par durée de session",
        sessionPnlChart: "P&L par session",
        outsideSession: "Trades hors session",
        sessionInsight: "Enseignement clé",
        sessionsList: "Liste des sessions",
        lastSession: "Dernière session",
        pipStats: "Pip Stats",
        pipDistribution: "Distribution des pips",
        pipBySymbol: "Pips par symbole",
        longShort: "Long vs Short",
        dayOfWeek: "Performance par jour",
        pnlPerTrade: "P&L par trade",
        netDailyPnl: "P&L net quotidien",
        riskAdjusted: "Rendement ajusté au risque",
        drawdownCurve: "Courbe de drawdown",
        holdDuration: "Durée de détention",
        consistencyScore: "Score de consistance",
        streakAnalysis: "Analyse des séries",
        tagStats: "Statistiques par tag",
        sizingAnalysis: "Analyse du sizing",
        propfirmKpis: "KPIs Prop Firm",
        propfirmCalendar: "Calendrier Prop Firm",
        propfirmMonthRecap: "Récap du mois",
        propfirmReporting: "Reporting par période",
        propfirmAccounts: "Suivi par compte",
        propfirmAccountsTable: "Suivi par compte",
        propfirmGlobalCharges: "Charges globales",
        propfirmUpcomingCharges: "Prélèvements à venir",
        propfirmPayoutsList: "Liste des payouts",
        propfirmFilters: "Filtres PFM",
        propfirmWalletBalance: "TPT - Solde Wallet",
        propfirmWalletTransfers: "TPT - Transferts",
        propfirmWalletWithdrawals: "TPT - Retraits",
        propfirmChallengeOverview: "Taux de succès comptes",
        note: "Note",
        disciplineRoutines: "Routines",
        disciplineGoals: "Objectifs",
        notebookBrowser: "Carnet de notes",
        mentorNotes: "Notes du mentor",
        mentorNotesEmpty: "Aucune note de votre mentor pour l'instant",
        topbarDateFilter: "Filtre de temps",
        topbarAccountFilter: "Filtre de comptes",
        topbarStrategyFilter: "Filtre de stratégie",
        topbarAddTrade: "Ajouter un trade",
        topbarAddPayout: "Ajouter un payout",
        topbarPfmFilter: "Filtre Prop Firm",
        topbarPfmFilterAll: "Toutes les prop firms",
        topbarPhaseFilter: "Filtre par phase",
        topbarPhaseFilterAll: "Tous les statuts",
        topbarPhaseFilterChallenge: "Challenge",
        topbarPhaseFilterPa: "Funded (PA)",
        topbarPhaseFilterLive: "Live",
        newsFeed: "Flux d'actualités",
        interestRates: "Taux d'intérêt",
        economicCalendar: "Calendrier économique",
        earningsCalendar: "Calendrier des résultats",
        chart: "Graphique",
        tradesTable: "Liste des trades",
        topbarSymbolFilter: "Filtre symbole",
        topbarSideFilter: "Filtre sens (Long/Short)",
        topbarStatusFilter: "Filtre statut",
        topbarPnlFilter: "Filtre P&L",
        topbarTagsFilter: "Filtre tags",
      },
      widgetLock: {
        proFeatureLabel: "Fonctionnalité Pro",
      },
      widgetsDesc: {
        netPnl: "Profit ou perte net sur la période sélectionnée",
        profitFactor: "Ratio gains / pertes",
        winRate: "Pourcentage de trades gagnants",
        avgWinLoss: "Gain et perte moyens par trade",
        calendar: "Calendrier mensuel avec trades, routines et objectifs",
        marketHours: "Statut en temps réel des principales places boursières",
        routines: "Routines à compléter aujourd'hui",
        goals: "Tes objectifs les plus urgents",
        session: "Session de trading active avec timer",
        equityChart: "Courbe cumulée du P&L ou de la valeur nette",
        accountsTable: "Liste de tes comptes de trading avec P&L et progression",
        analyticsKpis: "Métriques avancées : Sharpe, Drawdown, Expectancy, Calmar, Recovery",
        equityCurve: "Courbe d'équité cumulée avec sélecteur de granularité",
        pnlDistribution: "Distribution des trades par plage de P&L",
        monthlyPnl: "P&L agrégé par mois",
        symbolPerformance: "Performance et statistiques par symbole",
        rollingWinRate: "Taux de réussite glissant sur 20 trades",
        heatmap: "Visualisation des performances par jour et heure",
        behavioralBiases: "Détection des biais : FOMO, vengeance, peur des pertes",
        sessionTimeSlots: "P&L et statistiques par créneau horaire de session",
        sessionMood: "Analyse de l'humeur et son impact sur la performance",
        sessionDuration: "P&L moyen regroupé par tranche de durée de session",
        sessionPnlChart: "P&L de chaque session sous forme de graphe en barres",
        outsideSession: "Performance des trades exécutés en dehors d'une session de trading",
        sessionInsight: "Enseignement automatique issu de l'analyse de vos sessions",
        sessionsList: "Liste de vos sessions récentes avec date, durée, trades et PnL",
        lastSession: "Vue condensée de votre dernière session terminée",
        pipStats: "Statistiques globales en pips",
        pipDistribution: "Distribution des trades par plage de pips",
        pipBySymbol: "Performance en pips par symbole",
        longShort: "Comparaison des performances long vs short",
        dayOfWeek: "Performance moyenne par jour de la semaine",
        pnlPerTrade: "P&L de chaque trade dans l'ordre chronologique",
        netDailyPnl: "P&L net agrégé par jour calendaire",
        riskAdjusted: "Ratios Calmar et Recovery Factor",
        drawdownCurve: "Évolution du drawdown depuis le pic d'équité",
        holdDuration: "Nombre de trades, win rate et P&L par durée de détention",
        consistencyScore: "Score de consistance global (0-100) et sous-scores",
        streakAnalysis: "Séries de gains/pertes et gestion du risque",
        tagStats: "Statistiques de performance par tag",
        sizingAnalysis: "Évolution de la taille de position dans le temps",
        propfirmKpis: "KPIs globaux : payouts, dépenses, ROI",
        propfirmCalendar: "Calendrier des événements et résultats prop firm",
        propfirmMonthRecap: "Récapitulatif mensuel prop firm",
        propfirmReporting: "Reporting des performances par période",
        propfirmAccounts: "Suivi détaillé par compte prop firm",
        propfirmAccountsTable: "Tableau de suivi des payouts par compte",
        propfirmGlobalCharges: "Gestion des charges globales et par compte",
        propfirmUpcomingCharges: "Prélèvements des 30 prochains jours, échéances passées à confirmer et abonnements à résilier",
        propfirmPayoutsList: "Liste de tous les payouts reçus",
        propfirmFilters: "Filtres de période, compte et stratégie pour les widgets Prop Firm",
        propfirmWalletBalance: "Solde disponible, historique des entrées et sorties du wallet",
        propfirmWalletTransfers: "Tableau des transferts reçus dans le wallet",
        propfirmWalletWithdrawals: "Tableau des retraits effectués depuis le wallet",
        propfirmChallengeOverview: "Nombre total de comptes, comptes avec payout et taux de succès",
        note: "Affiche une note du notebook en lecture/écriture directement dans le dashboard",
        mentorNotes: "Notes générales laissées par votre mentor",
        mentorNotesEmpty: "",
        topbarDateFilter: "Filtre de période affiché dans la barre supérieure",
        topbarAccountFilter: "Filtre de comptes affiché dans la barre supérieure",
        topbarStrategyFilter: "Filtre de stratégie affiché dans la barre supérieure",
        topbarAddTrade: "Bouton d'ajout de trade dans la barre supérieure",
        topbarAddPayout: "Bouton d'ajout de payout dans la barre supérieure",
        topbarPfmFilter: "Filtre Prop Firm dans la barre supérieure",
        topbarPfmFilterAll: "Sélecteur toutes prop firms dans la barre supérieure",
        topbarPhaseFilter: "Filtre par phase dans la barre supérieure",
        topbarPhaseFilterAll: "Sélecteur tous statuts dans la barre supérieure",
        topbarPhaseFilterChallenge: "Challenge",
        topbarPhaseFilterPa: "Funded (PA)",
        topbarPhaseFilterLive: "Live",
        newsFeed: "Flux d'actualités financières en temps réel",
        interestRates: "Tableau des taux directeurs des principales banques centrales",
        economicCalendar: "Calendrier des événements macroéconomiques à venir",
        earningsCalendar: "Calendrier des publications de résultats d'entreprises",
        chart: "Graphique en chandelier avec marqueurs de trades",
      },
      widgetCategories: {
        dashboard: "Dashboard",
        analytics: "Analytics",
        analyticsPerformance: "Analytics, Performance",
        analyticsSessions: "Analytics, Sessions",
        sessions: "Sessions",
        analyticsPips: "Analytics, Pips",
        propFirm: "Prop Firm",
        notes: "Notes",
        mentorProgram: "Mentor Program",
        topbar: "Barre d'outils",
        marketData: "News & Market Data",
        journal: "Journal",
        discipline: "Discipline, Routines, objectifs & notes",
      },
      // Rows + slots
      rowLabel: "Rangée {n}",
      addRow: "Ajouter une rangée",
      removeRow: "Retirer la rangée",
      removeRowConfirm: "Supprimer cette rangée et tous ses widgets ?",
      removeRowConfirmTitle: "Supprimer la rangée",
      rowColumnsLabel: "Colonnes",
      rowColumnsCount: "{n} colonnes",
      changeRowColumns: "Changer le nombre de colonnes",
      changeRowColumnsWarning: "Réduire le nombre de colonnes va retirer les widgets qui débordent. Continuer ?",
      addRowPosition: "Position",
      addRowPositionBottom: "En bas",
      addRowPositionBefore: "Avant la rangée {n}",
      addRowPositionAfter: "Après la rangée {n}",
      widgetMinCols: "Min {n} col",
      widgetAlreadyPlaced: "Déjà placé",
      noCompatibleWidgets: "Aucun widget compatible avec cet emplacement.",

      layouts: {
        menuTitle: "Mes layouts",
        activeBadge: "Actif",
        defaultBadge: "Défaut",
        newLayout: "Nouveau layout",
        duplicate: "Dupliquer le layout actif",
        rename: "Renommer le layout actif",
        share: "Partager…",
        importLink: "Importer un lien partagé…",
        delete: "Supprimer le layout actif",
        upgradeForMultiple: "Disponible avec Pro",
        cannotDeleteLast: "Vous devez conserver au moins un layout",

        renameDialogTitle: "Renommer le layout",
        renameInputLabel: "Nom",
        deleteDialogTitle: "Supprimer le layout",
        deleteDialogConfirm: "Voulez-vous vraiment supprimer « {name} » ?",
        deleteDialogWarning: "Cette action est définitive et supprimera toutes les rangées de ce layout.",

        shareDialogTitle: "Partager « {name} »",
        shareToggle: "Activer le partage par lien",
        shareWarning: "Toute personne avec ce lien pourra importer ce layout dans sa propre bibliothèque.",
        copyLink: "Copier le lien",
        linkCopied: "Lien copié !",

        importPromptTitle: "Importer un layout",
        importPromptInputLabel: "Collez ici le lien ou le code partagé",
        importedSuccess: "Layout importé dans votre bibliothèque",

        sharePreviewBy: "Layout partagé par @{username}",
        sharePreviewMeta: "{rows} rangées · {widgets} widgets",
        importToLibrary: "Importer dans ma bibliothèque",
        previewLoginRequired: "Vous devez être connecté pour importer ce layout.",
        previewLoginCta: "Se connecter",
        previewLinkInactive: "Ce lien n'est plus actif",
        previewLinkInactiveDesc: "Le propriétaire de ce layout a désactivé le partage ou supprimé le layout.",

        libraryTitle: "Bibliothèque de layouts",
        sourceMyLayouts: "Mes layouts",
        sourceEdgely: "Edgely",
        sourceCommunity: "Communauté",
        wrongScopeTooltip: "Ce layout est conçu pour le {scope}",
        newLayoutPlaceholder: "Nom du layout...",
        previewEmpty: "Sélectionne un layout pour voir l'aperçu",
        communityEmpty: "Aucun layout partagé pour le moment. Partage le tien depuis Mes layouts !",
        edgelyEmpty: "Aucun layout Edgely disponible.",
        authorLabel: "par {username}",
        actionsRename: "Renommer",
        actionsDuplicate: "Dupliquer",
        actionsShare: "Partager",
        actionsDelete: "Supprimer",
        importOnThisPage: "Importer sur cette page",
        importNewPage: "Importer sur une nouvelle page",
        importSaveOnly: "Sauvegarder dans mes layouts",
        importActivate: "Activer ce layout",
        importConfirmTitle: "Importer ce layout",
        importConfirmOnThisPage: "Remplacer le layout actuel de cette page",
        importConfirmNewPage: "Créer une nouvelle page Custom avec ce layout",
        importConfirmSaveOnly: "Ajouter à mes layouts sans l'activer",
        shareLibraryToggle: "Visible dans la bibliothèque communautaire",
        shareDisplayNameLabel: "Nom public",
        shareDescriptionLabel: "Description",
        shareDescriptionPlaceholder: "Explique ce que ce layout affiche et comment il fonctionne...",
        shareSettingsSaved: "Paramètres de partage enregistrés",
        newPageWithLayout: "Créer une page avec ce layout",
        newPageBlank: "Partir d'un layout vide",
        scopeDashboard: "Dashboard",
        scopePfm: "Prop Firm Manager",
        scopeCustomPage: "Pages personnalisées",
        noName: "(sans nom)",
        requiresPro: "Requiert Pro",
      },
    },
    common: {
      save: "Enregistrer",
      cancel: "Annuler",
      edit: "Modifier",
      delete: "Supprimer",
      create: "Créer",
      update: "Mettre à jour",
      loading: "Chargement...",
      error: "Erreur",
      success: "Succès",
      close: "Fermer",
      back: "Retour",
      next: "Suivant",
      previous: "Précédent",
      search: "Rechercher",
      filter: "Filtrer",
      actions: "Actions",
      date: "Date",
      time: "Heure",
      duration: "Durée",
      minutes: "minutes",
      validationError: "Une erreur de validation s'est produite",
      tradeLimitReached: "Limite de Trades Atteinte",
      tradeLimitWarning: "Vous avez atteint votre limite de 500 trades actifs. Créer ce trade supprimera automatiquement votre trade le plus ancien. Voulez-vous continuer ?",
      proceed: "Continuer",
      soon: "Bientôt",
      beta: "Bêta",
    },
    validation: {
      fieldRequired: "Le champ \"{field}\" est requis",
      fieldInvalid: "Le champ \"{field}\" est invalide",
      stringTooShort: "Le champ \"{field}\" est requis",
      stringTooLong: "Le champ \"{field}\" est trop long",
      numberTooSmall: "Le champ \"{field}\" doit être supérieur à {minimum}",
      numberTooBig: "Le champ \"{field}\" doit être inférieur à {maximum}",
      arrayTooSmall: "Le champ \"{field}\" doit contenir au moins {minimum} élément(s)",
      invalidType: "Le champ \"{field}\" a un type invalide",
      invalidEnum: "Le champ \"{field}\" a une valeur invalide",
      invalidDate: "Le champ \"{field}\" doit être une date valide",
      invalidEmail: "Le champ \"{field}\" doit être une adresse email valide",
      invalidString: "Le champ \"{field}\" est invalide",
      validationError: "Une erreur de validation s'est produite",
    },
    auth: {
      login: "Connexion",
      signup: "Inscription",
      email: "Email",
      password: "Mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      forgotPassword: "Mot de passe oublié",
      resetPassword: "Réinitialiser le mot de passe",
      requestPasswordReset: "Demander la réinitialisation",
      passwordResetRequested: "Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.",
      passwordResetSuccess: "Votre mot de passe a été réinitialisé avec succès.",
      passwordResetError: "Une erreur s'est produite lors de la réinitialisation.",
      noAccount: "Vous n'avez pas encore de compte ?",
      goToSignup: "S'inscrire",
      forgotPasswordQuestion: "Mot de passe oublié ?",
      resetIt: "Réinitialiser",
      ifEverythingOk: "Déjà un compte ?",
      goToLogin: "Se connecter",
      emailPlaceholder: "votre@email.com",
      passwordPlaceholder: "Votre mot de passe",
      confirmPasswordPlaceholder: "Confirmez votre mot de passe",
      passwordsDoNotMatch: "Les mots de passe ne correspondent pas",
      passwordsMatch: "✓ Mots de passe identiques",
      passwordTooShort: "Le mot de passe doit contenir au moins 8 caractères",
      passwordMinLength: "8 caractères minimum",
      signupError: "Une erreur s'est produite lors de l'inscription",
      signingUp: "Inscription en cours...",
      firstName: "Prénom",
      lastName: "Nom",
      firstNamePlaceholder: "Jean",
      lastNamePlaceholder: "Dupont",
      firstNameRequired: "Le prénom est requis",
      lastNameRequired: "Le nom est requis",
      emailVerificationPending: "Vérifiez votre boîte mail et cliquez sur le lien de confirmation.",
      emailVerificationCheckSpam: "Vous ne voyez pas l'email ? Vérifiez vos spams.",
      emailVerificationVerifying: "Vérification en cours...",
      emailVerificationSuccess: "Votre email a été vérifié. Vous pouvez maintenant vous connecter.",
      emailVerificationError: "Le lien de vérification est invalide ou a expiré.",
      emailVerificationInvalidToken: "Lien invalide. Vérifiez l'email reçu ou vos spams.",
      resendEmail: "Renvoyer l'email",
      resendEmailLoading: "Envoi en cours...",
      resendEmailCooldown: "Renvoyer ({seconds}s)",
      resendEmailSuccess: "Email renvoyé ! Vérifiez votre boîte mail.",
      resendEmailError: "Impossible d'envoyer l'email. Réessayez.",
    },
    account: {
      title: "Informations du compte",
      email: "Adresse email",
      username: "Nom d'utilisateur",
      about: "À propos",
      plan: "Votre plan",
      freePlan: "Plan Gratuit",
      upgradeToPro: "Passer à Pro",
      managePayment: "Manage Payment Details",
      notDefined: "Non défini",
      noDescription: "Aucune description",
      profileUpdated: "Profil mis à jour",
      updateError: "Impossible de mettre à jour",
      statusActive: "Actif",
      statusCancelAtPeriodEnd: "Annulation en cours",
      statusPastDue: "Paiement en retard",
      renewalOn: "Renouvellement le",
      expiresOn: "Expire le",
      inDays: "dans {days} jour(s)",
      tomorrow: "demain",
      today: "aujourd'hui",
      pastDueWarning: "Mettez à jour vos informations de paiement pour éviter la suspension.",
      statusDeleted: "Abonnement expiré",
      preferences: "Préférences",
      hideWeekend: "Masquer les weekends dans le calendrier",
      showCalendarErrors: "Afficher les erreurs de session dans le calendrier",
      hideInactiveAccounts: "Masquer les comptes inactifs",
    },
    pricing: {
      title: "Choisissez votre tarif",
      subtitle: "Sélectionnez le plan qui correspond à vos besoins de trading.",
      perMonth: "/mois",
      perYear: "/an",
      annual: "Annuel",
      monthly: "Mensuel",
      savePercent: "Économisez 20%",
      billedAnnually: "Facturé €{amount}/an",
      manageSubscription: "Gérer l'abonnement",
      cta: {
        free: "Commencer",
        buy: "Acheter le plan",
        login: "Se connecter pour acheter",
      },
      checkout: {
        successTitle: "🥳 Paiement réussi !",
        canceledTitle: "😢 Paiement annulé.",
        activating: "Activation de votre plan...",
        redirecting: "Redirection vers votre compte...",
        pendingTitle: "Paiement non finalisé",
        pendingDescription:
          "Votre paiement n'a pas pu être confirmé - l'authentification bancaire a peut-être échoué. Votre plan n'a pas été modifié et vous n'avez pas été débité. Vous pouvez réessayer depuis votre compte.",
        backToAccount: "Retour à mon compte",
      },
      beta: {
        banner: "🎉 Bêta ouverte, Accès gratuit pendant le lancement. Places limitées.",
        comingSoon: "Bientôt disponible",
        comingSoonTooltip: "Les plans payants arrivent bientôt. Tu seras notifié !",
        modalTitle: "Plans payants bientôt disponibles",
        modalDescription: "Profite de toutes les fonctionnalités gratuitement pendant la bêta. Tu seras notifié dès que le plan Pro sera disponible.",
      },
      free: {
        price: "Gratuit",
        description: "Pour journaliser tes premiers trades et découvrir tes patterns",
        features: {
          trades: "Trades illimités",
          accounts: "1 compte de trading",
          sessions: "Sessions illimitées",
          routines: "3 routines",
          goals: "3 objectifs",
          analytics: "Analytics de base",
          support: "Support communautaire",
        },
      },
      pro: {
        description: "Pour le prop trader actif sur 1 à 3 comptes",
        badge: "Populaire",
        priceAnnual: "€12",
        totalAnnual: "€144/an",
        savingsPercent: "Économisez 20%",
        features: {
          trades: "Trades illimités",
          accounts: "Comptes illimités (prop firms)",
          sessions: "Sessions illimitées",
          routines: "Routines illimitées",
          goals: "Objectifs illimités",
          analytics: "Analytics avancées",
          support: "Support prioritaire",
          propFirm: "Prop Firm Manager",
          customPages: "Dashboard modulaire & pages personnalisées",
          mentorMode: "Mode mentor (encadrement d'étudiants)",
          futureFeatures: "Toutes les futures fonctionnalités incluses",
        },
      },
      max: {
        name: "Max",
        description: "Pour le prop trader multi-firmes qui extrait du cash régulièrement",
        priceAnnual: "€23",
        totalAnnual: "€279/an",
        savingsPercent: "Économisez 20%",
        features: {
          allPro: "Toutes les fonctionnalités Pro",
          metaApi: "Synchronisation MetaTrader 4/5 (1 compte)",
          tradovate: "Sync auto Tradovate (lecture seule)",
          support: "Support prioritaire",
          futureFeatures: "Toutes les futures fonctionnalités incluses",
        },
      },
    },
    metaApi: {
      sectionTitle: "Connexion MetaTrader",
      comingSoonNote: "La synchronisation automatique MetaTrader arrive bientôt. En attendant, ajoute tes trades par import CSV ou saisie manuelle.",
      connect: "Connecter MetaTrader",
      disconnect: "Déconnecter",
      syncNow: "Synchroniser maintenant",
      deactivate: "Désactiver",
      reactivate: "Réactiver",
      retry: "Réessayer",
      retrying: "Nouvelle tentative de connexion…",
      errorFriendly: "La connexion au broker a échoué. Réessayez ; si le problème persiste, vérifiez vos identifiants.",
      errorTransient: "Le service MetaTrader n'a pas répondu à temps. Nouvelle tentative automatique en cours…",
      status: {
        pending: "Connexion en cours…",
        active: "Connecté",
        syncing: "Synchronisation…",
        idle: "Connecté",
        error: "Erreur de sync",
        paused: "Inactif",
        deprovisioned: "Déconnecté",
        notConnected: "Non connecté",
      },
      connectModal: {
        title: "Connecter un compte MetaTrader",
        description: "Entrez vos identifiants de connexion MetaTrader. Ils sont chiffrés et stockés de façon sécurisée.",
        loginLabel: "Login (numéro de compte)",
        loginPlaceholder: "ex: 12345678",
        passwordLabel: "Mot de passe du compte",
        serverLabel: "Serveur du broker",
        serverPlaceholder: "ex: ICMarkets-Demo02",
        platformLabel: "Plateforme",
        submitButton: "Connecter",
        connecting: "Connexion en cours…",
        cancel: "Annuler",
      },
      syncBadge: {
        lastSync: "Dernière synchro",
        never: "Jamais",
        newTrades: "trades importés",
        initialSyncInProgress: "Import de l'historique en cours…",
        nextSync: "Prochaine sync auto",
        suspendedNight: "Sync suspendue · nuit",
        suspendedWeekend: "Sync suspendue · weekend",
        pollQuota: "Polls manuels",
        pollQuotaReset: "reset lundi",
        pollQuotaExhausted: "Quota atteint · reset lundi",
        jobQueued: "Synchronisation lancée en arrière-plan…",
      },
      errors: {
        limitReached: "Limite atteinte : 1 compte MetaTrader synchronisé maximum en plan Max.",
        connectionFailed: "Connexion échouée. Vérifiez vos identifiants MetaTrader.",
        syncFailed: "La synchronisation a échoué.",
        notPro: "La synchronisation MetaTrader est réservée aux utilisateurs Max.",
        syncInProgress: "Une synchronisation est déjà en cours.",
        quotaExhausted: "Quota hebdomadaire atteint (5/5). Disponible à nouveau lundi.",
      },
      upsell: {
        title: "Synchronisation automatique MetaTrader",
        description: "Passez en Max pour connecter 1 compte MT4/MT5 et importer vos trades automatiquement.",
        cta: "Passer en Max",
      },
    },
    tradovate: {
      syncChoiceDescription: "Connexion en lecture seule : tes trades Tradovate sont importés et synchronisés automatiquement.",
      maxPlanRequired: "Réservé au plan Max",
      wizardTitle: "Connecter Tradovate",
      addAccount: "Ajouter un compte",
      addAccountTitle: "Ajouter un compte Tradovate",
      sectionTitle: "Synchronisation Tradovate",
      sectionFirmCount: "{count} prop firm(s)",
      firmAccountCount: "{count} compte(s) synchronisé(s)",
      addAccountForFirm: "Ajouter un compte {firm}",
      assignPropFirm: "Associer une prop firm",
      assignPropFirmHint: "Identifie cette connexion et s'applique aux prochains comptes importés. Les comptes déjà importés gardent leur prop firm actuelle.",
      chooseTradovateTarget: "Ajouter à une prop firm existante, ou en connecter une nouvelle",
      addAccountHere: "Ajouter un compte ici",
      connectNewPropFirm: "Connecter une nouvelle prop firm",
      pickPropFirm: "Choisis la prop firm",
      pickPropFirmHint: "Définit les règles appliquées à tous les comptes importés.",
      otherPropFirm: "Autre / aucune",
      environment: "Environnement",
      envLive: "Réel (live)",
      envDemo: "Démo",
      continueToTradovate: "Continuer vers Tradovate",
      reviewTitle: "Comptes Tradovate trouvés",
      reviewSubtitle: "{count} compte(s) prêt(s) à être importé(s).",
      tierLabel: "Taille",
      tierPlaceholder: "Choisir une taille",
      statusLabel: "Statut",
      costLabel: "Prix payé (USD)",
      costPlaceholder: "ex: 120",
      costHint: "Nouveau compte : renseigne le prix pour le suivi des coûts.",
      lowConfidenceHint: "Vérifie la taille détectée",
      knownAccountHint: "Compte déjà suivi : taille et statut se modifient depuis la page Comptes.",
      groupLabel: "Groupe",
      groupNone: "Aucun groupe",
      groupNew: "Nouveau groupe",
      newGroupNameLabel: "Nom du nouveau groupe",
      groupNamePlaceholder: "Nom du groupe",
      strategyLabel: "Stratégie (optionnel)",
      strategyNone: "Aucune stratégie",
      importButton: "Importer et synchroniser",
      importing: "Importation…",
      statusActive: "Synchronisé",
      statusSyncing: "Synchronisation…",
      statusNeedsReauth: "Reconnexion requise",
      statusError: "Erreur",
      lastSync: "Dernière synchro",
      syncNow: "Synchroniser maintenant",
      fullResync: "Réimporter les trades récents",
      reconnect: "Reconnecter",
      moreActions: "Plus d'actions",
      disconnect: "Déconnecter",
      disconnectConfirm: "Déconnecter Tradovate ? La synchronisation automatique sera arrêtée. Tes trades déjà importés sont conservés.",
      oauthErrorTitle: "Connexion Tradovate échouée",
      oauthErrorDescription: "La connexion à Tradovate n'a pas abouti. Veuillez réessayer.",
      oauthPlanRequiredDescription: "La synchronisation Tradovate est réservée au plan Max. Réactivez votre abonnement pour la retrouver.",
      connectionBannerTitle: "Connexion Tradovate",
      loadingAccountsTitle: "Récupération de vos comptes Tradovate…",
      loadingAccountsHint: "Nous interrogeons Tradovate pour lister vos comptes et leurs soldes. Cela peut prendre quelques secondes.",
      loadAccountsErrorTitle: "Impossible de récupérer vos comptes",
      loadAccountsErrorHint: "Tradovate n'a pas répondu correctement. Vérifiez votre connexion et réessayez.",
      loadAccountsRateLimitedTitle: "Tradovate limite temporairement nos requêtes",
      loadAccountsRateLimitedHint: "Nous n'avons pas pu lire le solde de vos comptes, et sans lui la taille de compte serait mal détectée. Aucun compte n'a été importé. Réessayez dans quelques heures ; si le problème persiste, contactez le support.",
      loadAccountsBalanceErrorTitle: "Solde des comptes indisponible",
      loadAccountsBalanceErrorHint: "Le solde d'un compte à importer est illisible ou nul, et sans lui sa taille serait mal détectée. Aucun compte n'a été importé. Si ce compte est vide ou clôturé, réessayez une fois qu'il n'apparaît plus chez Tradovate ; sinon contactez le support.",
      retry: "Réessayer",
      noAccountsTitle: "Aucun compte trouvé",
      noAccountsHint: "Tradovate n'a renvoyé aucun compte pour cette connexion.",
      syncRunningTitle: "Synchronisation en cours",
      syncRunningHint: "Nous importons votre historique depuis Tradovate. Vos totaux se complètent au fur et à mesure et peuvent encore être incomplets.",
      syncThrottledTitle: "Synchro partielle",
      syncThrottledHint: "Tradovate a limité nos requêtes sur le dernier cycle : certains comptes ont pu être sautés. La synchro reprend automatiquement au cycle suivant.",
      beamFlowLabel: "Flux de synchronisation",
      beamNoAccountsTitle: "Aucun compte synchronisé",
      beamNoAccountsHint: "La connexion est établie, il reste à importer un compte.",
      beamAccountsMore: "+{count} comptes",
      environmentDemo: "Évaluation / PA",
      environmentLive: "Live",
      demoBannerTitle: "Données de démonstration",
      demoBannerHint: "Connexions simulées pour prévisualiser le rendu. Aucune donnée réelle.",
      errorConnectionFailed: "Échec de la connexion Tradovate",
      errorLimitReached: "Limite de connexions atteinte",
      errorGeneric: "Une erreur est survenue",
      syncErrorGeneric: "La synchronisation a échoué. Réessaie ou reconnecte ton compte.",
      statusActiveNoAccounts: "Sync active, aucun compte actif",
      statusSyncedLive: "Synchro en direct",
      statusSyncedStarting: "Synchronisé, direct en activation",
      statusSyncedStartingHelp:
        "Connexion réussie. Le temps réel s'active à la fin de la première synchronisation, en général en moins de 15 minutes. Vos trades remontent déjà, il n'y a rien à faire.",
      statusSyncedDelayed: "Synchronisé avec délai",
      statusSyncedDelayedHelp:
        "Vos trades continuent d'arriver, avec un délai pouvant aller jusqu'à 15 minutes. Reconnectez la prop firm pour retrouver le direct.",
      statusActiveNoAccountsHint:
        "La connexion fonctionne, mais aucun compte synchronisé n'est actif. Importez un compte ou réactivez-en un pour voir vos trades.",
      lastSyncLive: "Trades reçus à l'instant",
      statusDelayed: "Sync retardée, reprise automatique",
      statusIdle: "Sync en veille",
      statusIdleHint: "Aucune activité récente : la connexion s'est mise en pause. Reconnecte-toi pour reprendre la synchronisation.",
      statusForeignToken: "Mauvais identifiants Tradovate",
      statusForeignTokenHint:
        "Aucun des comptes de cette connexion n'a été trouvé chez le broker : la connexion a probablement été autorisée avec les identifiants d'une autre prop firm. Reconnecte-toi avec les identifiants de celle-ci.",
      discoveredBannerTitle: "{count} nouveau(x) compte(s) Tradovate détecté(s)",
      discoveredImport: "Importer",
      discoveredDismiss: "Ignorer",
      foreignBannerTitle: "Ces comptes semblent appartenir à une autre prop firm",
      foreignBannerBody:
        "Les comptes détectés ne ressemblent pas à ceux de {propFirm}. Cela arrive quand la connexion Tradovate a été autorisée avec les identifiants d'une autre prop firm.",
      foreignBannerHint:
        "Vous pouvez les ignorer, puis relancer la connexion avec les bons identifiants. Vos comptes et leur historique de trades sont conservés : reconnecter la bonne prop firm les rattache sans rien perdre.",
      foreignBadge: "Autre prop firm ?",
    },
    session: {
      title: "Session",
      start: "Démarrer",
      pause: "Pause",
      resume: "Reprendre",
      stop: "Arrêter",
      remaining: "Restant",
      paused: "En pause",
      started: "Session démarrée",
      evaluation: "Évaluation de la Session",
      mood: "Humeur",
      performance: "Performance",
      mentalState: "État mental",
      opportunities: "Opportunités repérées",
      openTrades: "Trades ouverts",
      closedTrades: "Trades fermés",
      totalPnl: "P&L Total",
      notes: "Notes générales",
      maxRunup: "Max Run-up ($)",
      maxDrawdown: "Max Drawdown ($)",
      maxRunupPlaceholder: "Ex: 250.00",
      maxDrawdownPlaceholder: "Ex: -150.00",
      selfEvaluation: "Auto-évaluation et points d'amélioration",
      endSession: "Terminer la session",
      skipEvaluation: "Terminer sans évaluation",
      sessionEnded: "Session terminée",
      tip: "Astuce",
      durationLabel: "Durée (minutes)",
      tipText: "Vous pourrez retrouver toutes vos évaluations de session dans la page Analytics pour suivre votre progression et vous améliorer.",
      moodPlaceholder: "Sélectionnez votre humeur",
      performancePlaceholder: "Sélectionnez votre performance",
      mentalStatePlaceholder: "Ex: Concentré, distrait, fatigué...",
      opportunitiesPlaceholder: "Décrivez les opportunités que vous avez repérées pendant cette session...",
      notesPlaceholder: "Notes générales sur la session...",
      lastSessionEmpty: "Aucune session terminée pour le moment.",
      lastSessionOpen: "Voir le détail",
      lastSessionVideo: "Vidéo",
      lastSessionPnl: "PnL total",
      lastSessionRunup: "Runup max",
      lastSessionDrawdown: "Drawdown max",
      selfEvaluationPlaceholder: "Qu'avez-vous bien fait ? Qu'est-ce qui peut être amélioré ? Qu'avez-vous appris ?",
      moodExcellent: "Excellent",
      moodGood: "Bon",
      moodNeutral: "Neutre",
      moodPoor: "Médiocre",
      moodBad: "Mauvais",
      perfExcellent: "Excellent",
      perfGood: "Bon",
      perfAverage: "Moyen",
      perfPoor: "Médiocre",
      tradovateImport: "Importer les trades Tradovate",
      tradovatePastePlaceholder: "Collez ici le tableau copié depuis Tradovate (Symbol, Qty, Buy Price, Buy Time...)",
      tradovateOrUploadCSV: "ou importez un fichier CSV",
      tradovateUploadCSV: "Choisir un fichier CSV",
      tradovateParseSuccess: "trades détectés, prêts à importer",
      tradovateParseErrorEmpty: "Le fichier est vide ou corrompu. Re-téléchargez l'export depuis Tradovate.",
      tradovateParseErrorFormat: "Format non reconnu. Utilisez l'export « Performance.csv » depuis Tradovate.",
      tradovateParseErrorNoTrades: "Aucun trade complet trouvé dans ce fichier.",
      topstepxImport: "Importer les trades TopstepX",
      topstepxPastePlaceholder: "Collez ici les trades copiés depuis TopstepX...",
      topstepxParseError: "Format non reconnu. Assurez-vous de copier les trades depuis TopstepX.",
      topstepxUnmatchedFillsWarning: "Import partiel : {count} ordre(s) n'ont pas pu être appariés à un trade complet. Vérifiez que votre copier-coller contient bien toute la session.",
      tradovateImportConfirm: "Importer dans le journal",
      tradovateImportDone: "trades importés avec succès",
      tradovateImportFailed: "Erreur lors de l'import des trades",
      noTimerLabel: "Sans timer",
      elapsed: "Écoulé",
      openSessionNotes: "Ouvrir la session (notes)",
      startedNoTimerDescription: "Session lancée sans timer. Prenez des notes dans la page de session.",
      sessionEndedDescription: "Votre session a été enregistrée. Vous pouvez la consulter dans la page Sessions.",
      evaluationDescription: "Partagez vos réflexions sur cette session pour vous améliorer",
      preMoodTitle: "Comment vous sentez-vous ?",
      preMoodDescription: "Enregistrez votre humeur avant de commencer à trader.",
      errors: "Erreurs",
      errorsPlaceholder: "Aucune erreur",
      errorCreateNew: "+ Nouvelle erreur",
      errorNamePlaceholder: "Nom de l'erreur",
      errorDelete: "Supprimer du catalogue",
      errorDeleteConfirm: "Supprimer cette erreur de toutes les sessions ?",
    },
    sessions: {
      title: "Sessions de Trading",
      subtitle: "Consultez et filtrez l'historique de vos sessions",
      filterMood: "Humeur",
      filterPerformance: "Performance",
      filterDurationMin: "Durée min (min)",
      filterDurationMax: "Durée max (min)",
      filterReset: "Réinitialiser les filtres",
      filterDateStart: "Du",
      filterDateEnd: "Au",
      colDate: "Date",
      colMood: "Humeur",
      colPerformance: "Performance",
      colRunup: "Max Run-up",
      colDrawdown: "Max Drawdown",
      colPnl: "P&L session",
      colDuration: "Durée",
      colVideo: "Vidéo",
      noSessions: "Aucune session enregistrée. Démarrez votre première session depuis le dashboard.",
      noSessionsFiltered: "Aucune session ne correspond aux filtres sélectionnés.",
      viewDetail: "Voir le détail",
      viewAll: "Voir toutes les sessions",
      allMoods: "Toutes les humeurs",
      allPerformances: "Toutes les performances",
      addSession: "Nouvelle session",
      colName: "Nom",
      editNameTitle: "Modifier le nom",
      editNameSave: "Enregistrer",
      editNamePlaceholder: "Nom de la session...",
      activeSession: "Session en cours",
      inProgress: "En cours...",
      autoDetected: "Session détectée automatiquement ({start} → {end})",
      autoDetectedMultiple: "{count} sessions détectées automatiquement",
      autoDetectedLimitReached: "Session détectée mais limite mensuelle atteinte, Passez à Pro",
      autoDetectedViewSession: "Voir la session",
      pendingMergeBanner: "Une session existante peut être étendue ({start} → {end}, {count} trades)",
      pendingMergeAccept: "Accepter",
      pendingMergeDismiss: "Ignorer",
      pendingMergeTitle: "Fusions de sessions en attente",
      autoBadge: "Auto",
      accountsCount: "{count} comptes",
      pendingMergeSubtitle: "Des trades récents chevauchent une session existante.",
      onboardingBanner: "Clique sur une session dans la liste ci-dessous pour voir son détail et valider cette étape.",
      colErrors: "Erreurs",
      chartTitle: "Graphique de session",
      tradesAddedCount: "{count} trade(s) ajouté(s)",
    },
    sessionDetail: {
      back: "Retour aux sessions",
      title: "Détail de la session",
      timing: "Chronologie",
      evaluation: "Évaluation",
      metrics: "Métriques",
      opportunities: "Opportunités repérées",
      selfEval: "Auto-évaluation",
      notes: "Notes générales",
      startedAt: "Début",
      endedAt: "Fin",
      duration: "Durée réelle",
      planned: "Durée prévue",
      mentalState: "État mental",
      saved: "Session mise à jour",
      saveError: "Erreur lors de la mise à jour",
      delete: "Supprimer la session",
      deleteConfirm: "Confirmer la suppression de cette session ? Cette action est irréversible.",
      deleted: "Session supprimée",
      pnl: "P&L session",
      maxRunup: "Max Run-up",
      maxDrawdown: "Max Drawdown",
      noValue: "",
      videoTitle: "Enregistrement vidéo",
      videoInvite: "Ajoutez l'URL YouTube de votre session enregistrée",
      videoPlaceholder: "Collez l'URL YouTube de votre session...",
      videoSave: "Enregistrer",
      videoCancel: "Annuler",
      videoEdit: "Modifier la vidéo",
      videoDelete: "Supprimer la vidéo",
      videoInvalidUrl: "URL YouTube invalide",
      videoSaved: "Vidéo enregistrée",
      videoDeleted: "Vidéo supprimée",
      notFound: "Session introuvable",
      nameLabel: "Nom de la session",
      namePlaceholder: "Nommer cette session...",
      trades: "Trades de la session",
      addTrade: "Ajouter un trade",
      noTrades: "Aucun trade enregistré pendant cette session.",
      shareButton: "Partager",
      shareDialogTitle: "Partager cette session",
      shareDialogDescription: "Générez un lien unique pour permettre à votre tuteur de consulter cette session en lecture seule.",
      shareEnable: "Activer le partage",
      shareDisable: "Désactiver le partage",
      shareLinkLabel: "Lien de partage",
      shareCopy: "Copier le lien",
      shareCopied: "Lien copié !",
      shareEnabled: "Partage activé",
      shareDisabled: "Partage désactivé",
      shareError: "Erreur lors du partage",
      errors: "Erreurs",
      pnlByAccount: "P&L par compte",
      noAccount: "Sans compte",
      sessionAccountsTitle: "Comptes actifs sur cette session",
      sessionContribution: "session",
    },
    journal: {
      title: "Journal de Trading",
      subtitle: "Gérez et suivez tous vos trades",
      createTrade: "Nouveau Trade",
      importTrades: "Importer des Trades",
      addTradeTitle: "Ajouter des trades",
      addTradeSubtitle: "Choisissez comment vous souhaitez ajouter vos trades",
      methodManual: "Trade manuel",
      methodManualDescription: "Saisir un trade manuellement avec tous les détails",
      methodTradovate: "Coller depuis Tradovate",
      methodTradovateDescription: "Coller le tableau de trades copié depuis votre rapport Tradovate",
      methodTopstepX: "Coller depuis Topstep X",
      methodTopstepXDescription: "Coller les trades copiés depuis votre rapport Topstep X",
      methodTopstepXNoAccount: "Aucun compte ni groupe Topstep, créez-en un d'abord",
      methodCsvImport: "Importer un CSV",
      methodCsvImportDescription:
        "Importer un CSV edgely, Tradovate, TopstepX ou Quantower",
      topstepxImportTitle: "Coller le tableau Topstep X",
      topstepxImportSubtitle: "Copiez les trades depuis votre rapport Topstep X et collez-les ci-dessous",
      selectTopstepAccountOrGroup: "Compte ou groupe Topstep",
      noTopstepAccountAvailable: "Aucun compte ou groupe Topstep disponible",
      selectAccount: "Compte de trading",
      selectAccountPlaceholder: "Sélectionner un compte (optionnel)",
      noAccount: "Aucun compte",
      tradovateImportTitle: "Coller le tableau Tradovate",
      tradovateImportSubtitle: "Copiez le tableau depuis votre rapport de compte Tradovate et collez-le ci-dessous",
      commissionPerTrade: "Commission par trade ($)",
      commissionPerTradePlaceholder: "Ex: 2.09",
      commissionFromPropFirm: "depuis {name}",
      exportTrades: "Exporter",
      filters: "Filtres",
      advancedFilters: "Filtres avancés",
      symbol: "Symbole",
      type: "Type",
      status: "Statut",
      startDate: "Date de début",
      endDate: "Date de fin",
      allTypes: "Tous",
      long: "Long",
      short: "Short",
      allStatus: "Tous",
      open: "Ouvert",
      closed: "Fermé",
      date: "Date",
      exitDate: "Date de sortie",
      entryPrice: "Prix d'entrée",
      exitPrice: "Prix de sortie",
      quantity: "Quantité",
      pnl: "P&L",
      commission: "Commission",
      notes: "Notes",
      tags: "Tags",
      tagsPlaceholder: "Ajouter un tag…",
      createTag: "Créer",
      noTags: "Aucun tag",
      tagCreateError: "Impossible de créer le tag",
      trades: "Trades",
      noTrades: "Aucun trade trouvé",
      noTradesDesc: "Ajoute ton premier trade pour commencer à analyser ta performance",
      totalPnl: "P&L Total",
      advancedFiltersTitle: "Filtres avancés",
      advancedFiltersDescription: "Utilisez ces filtres pour affiner votre recherche de trades",
      account: "Compte",
      allAccounts: "Tous les comptes",
      strategy: "Stratégie",
      allStrategies: "Toutes les stratégies",
      clearStrategies: "Tout effacer",
      strategiesSelected: "{n} stratégie(s)",
      all: "Tous",
      noTagsAvailable: "Aucun tag disponible",
      minPnl: "P&L Minimum ($)",
      maxPnl: "P&L Maximum ($)",
      customFields: "Champs personnalisés",
      applyFilters: "Appliquer les filtres",
      reset: "Réinitialiser",
      equals: "Égal à",
      greater: "Supérieur à",
      less: "Inférieur à",
      between: "Entre",
      contains: "Contient",
      startsWith: "Commence par",
      after: "Après",
      before: "Avant",
      yes: "Oui",
      no: "Non",
      value: "Valeur",
      tagsSeparatedByCommas: "Tags séparés par des virgules",
      setupRating: "Note du setup",
      setupRatingHint: "Notez la qualité de ce setup et votre confiance (1 = faible, 5 = excellent)",
      cannotCreate: "Impossible de créer le trade.",
      cannotUpdate: "Impossible de modifier le trade.",
      cannotDelete: "Impossible de supprimer le trade.",
      tradeLimitReachedTitle: "Limite de trades atteinte",
      tradeLimitReachedDescription: "Vous avez atteint la limite de 500 trades managés. Créer un nouveau trade supprimera votre plus ancien.",
      tradeLimitReachedContinue: "Créer et supprimer le plus ancien",
      tradeLimitReachedUpgrade: "Changer de plan",
      swaps: "Swaps",
      noTradesExport: "Aucun trade à exporter.",
      templateDownloaded: "Template téléchargé",
      templateDownloadedDescription: "Le template CSV a été téléchargé avec succès.",
      downloadTemplate: "Télécharger le template CSV",
      cannotDownloadTemplate: "Impossible de télécharger le template.",
      importErrors: "Erreurs d'import",
      noValidTrades: "Aucun trade valide",
      noValidTradesDescription: "Aucun trade valide trouvé dans le fichier CSV.",
      cannotImport: "Impossible d'importer les trades.",
      accountUpdatedTrade: "Compte mis à jour",
      accountUpdatedTradeDescription: "Le compte du trade a été mis à jour.",
      cannotUpdateAccount: "Impossible de mettre à jour le compte.",
      noAccountSelect: "Aucun compte",
      tradeUpdatedDescription: "Le trade a été modifié avec succès.",
      tradeDeletedDescription: "Le trade a été supprimé avec succès.",
      tradeCreatedDescription: "Le trade a été ajouté avec succès.",
      columnsLabel: "Colonnes",
      hiddenBySpace: "Masqué (place)",
      customizeColumns: "Personnaliser les colonnes",
      customizeColumnsDescription: "Choisissez les colonnes à afficher et leur ordre. Les colonnes obligatoires ne peuvent pas être masquées.",
      columnsSaved: "Configuration sauvegardée",
      columnsSavedDescription: "Les préférences de colonnes ont été mises à jour.",
      cannotSaveColumns: "Impossible de sauvegarder la configuration.",
      addCustomField: "Ajouter un champ personnalisé",
      editField: "Modifier le champ",
      newCustomField: "Nouveau champ personnalisé",
      fieldTechnicalName: "Nom technique (unique)",
      fieldNamePlaceholder: "ex: strategy_type",
      fieldNameHint: "Lettres, chiffres et underscores uniquement",
      fieldDisplayLabel: "Libellé",
      fieldLabelPlaceholder: "ex: Type de stratégie",
      fieldTypeLabel: "Type",
      fieldTypeText: "Texte",
      fieldTypeNumber: "Nombre",
      fieldTypeDate: "Date",
      fieldTypeBoolean: "Booléen",
      fieldTypeTags: "Tags / Pastilles",
      fieldRequired: "Champ obligatoire",
      fieldCreated: "Champ créé",
      fieldCreatedDescription: "Le champ personnalisé a été créé avec succès.",
      fieldUpdated: "Champ modifié",
      fieldUpdatedDescription: "Le champ personnalisé a été modifié avec succès.",
      fieldDeleted: "Champ supprimé",
      fieldDeletedDescription: "Le champ personnalisé a été supprimé avec succès.",
      deleteFieldConfirm: "Êtes-vous sûr de vouloir supprimer ce champ ? Toutes les valeurs associées seront également supprimées.",
      cannotCreateField: "Impossible de créer le champ.",
      cannotUpdateField: "Impossible de modifier le champ.",
      cannotDeleteField: "Impossible de supprimer le champ.",
      save: "Enregistrer",
      create: "Créer",
      importErrorsCount: "{count} erreur(s) détectée(s). Cliquez pour voir les détails.",
      selectAccountOrGroup: "Compte ou groupe",
      importNoAccountHint:
        "Les trades importés ne seront rattachés à aucun compte : ils n'apparaîtront ni dans la balance ni dans les analytics par compte.",
      selectAccountOrGroupPlaceholder: "Aucun compte",
      groupPrefix: "Groupe",
      importConfirm: "Importer les trades",
      importInProgress: "Importation…",
      importSelectedFile: "Fichier sélectionné : {name}",
      importCommissionLabel: "Commission par contrat ($)",
      importCommissionHint:
        "Appliquée aux trades dont le fichier ne détaille pas les frais (Tradovate). Laisser à 0 si le CSV les contient déjà.",
      importGroupMultiplier: "x {count} comptes = {total} trades au total",
      longsLabel: "longs",
      shortsLabel: "shorts",
      importingToGroup: "Trades importés sur chaque compte du groupe",
      importAllRowsInvalid: "Aucune ligne valide à importer (prix, quantité ou date manquants/incorrects).",
      importSomeRowsSkipped: "{count} ligne(s) ignorée(s) car invalide(s) (prix, quantité ou date incorrects).",
      importDuplicatesSkipped: "{count} trade(s) déjà présent(s) dans votre journal, ignoré(s).",
      importNothingNew: "Aucun nouveau trade : tout était déjà importé",
      manualTradeTitle: "Ajouter un trade manuellement",
      manualTradeSubtitle: "Renseignez les informations du trade",
      tradeAdded: "Trade ajouté",
      tradeAddedDescription: "Le trade a été ajouté avec succès.",
      cannotAddTrade: "Impossible d'ajouter le trade",
      newTrade: "Nouveau Trade",
      editTrade: "Modifier Trade",
      deleteTrade: "Supprimer",
      confirmDelete: "Êtes-vous sûr de vouloir supprimer ce trade ?",
      confirmBulkDelete: "Êtes-vous sûr de vouloir supprimer {count} trade(s) ?",
      bulkDeleteSuccess: "{count} trade(s) supprimé(s)",
      bulkDeleteError: "Erreur lors de la suppression des trades",
      selectAll: "Tout sélectionner",
      deselectAll: "Tout désélectionner",
      selectedCount: "{count} sélectionné(s)",
      tradeCreated: "Trade créé",
      tradeUpdated: "Trade modifié",
      tradeDeleted: "Trade supprimé",
      importSuccess: "Trades importés avec succès",
      importSuccessDescription: "{count} trade(s) importé(s) avec succès.",
      importError: "Erreur lors de l'import",
      selectFile: "Sélectionner un fichier CSV",
      required: "Ce champ est requis",
      tradesListWidget: "Liste des trades",
      export: "Exporter",
      symbolPlaceholder: "AAPL, TSLA…",
      sideAll: "Tous",
      sideLong: "Long",
      sideShort: "Short",
      statusAll: "Tous",
      statusOpen: "Ouvert",
      statusClosed: "Fermé",
      pnlFilterLabel: "P&L",
      minPnlLabel: "P&L Minimum ($)",
      maxPnlLabel: "P&L Maximum ($)",
      tagsFilterLabel: "Tags",
    },
    tradingAccounts: {
      noRulesTitle: "Aucune règle surveillée sur ce compte",
      noRulesBody: "Ce compte n'est rattaché à aucune prop firm : Edgely ne peut donc surveiller ni objectif, ni drawdown, ni perte journalière, et aucun dépassement ne vous sera signalé. Choisissez sa prop firm pour activer les alertes.",
      noRulesAction: "Choisir la prop firm",
      ruleUpdateTitle: "Les règles de ce compte ont changé",
      ruleUpdateBody: "Votre prop firm a modifié ses règles depuis la configuration de ce compte. Vos alertes utilisent toujours les anciennes règles tant que vous n'avez pas accepté.",
      ruleUpdateFrozenOn: "Règles figées le {date}",
      ruleUpdateAccept: "Appliquer les nouvelles règles",
      ruleUpdateDecline: "Conserver mes règles",
      ruleUpdateStricter: "plus stricte",
      ruleUpdateLooser: "plus souple",
      ruleUpdateAccepted: "Nouvelles règles appliquées",
      ruleUpdateDeclined: "Vos règles d'origine sont conservées",
      rulesFrozenBadge: "Règles figées",
      title: "Comptes de Trading",
      subtitle: "Gérez vos comptes de trading et suivez leurs balances",
      newAccount: "Nouveau compte",
      createFirstAccount: "Créer votre premier compte",
      loading: "Chargement...",
      noAccounts: "Aucun compte créé pour le moment.",
      allAccountsInactive:
        "Aucun compte actif. Vos comptes inactifs sont masqués par le filtre.",
      currentBalance: "Balance actuelle",
      balanceStalePrefix: "mis à jour",
      balanceStaleUnknown: "ancienneté inconnue",
      balanceStaleHelp:
        "Le solde du broker n'a pas pu être actualisé récemment. Le montant affiché est la dernière valeur connue.",
      initialBalance: "Balance initiale",
      variation: "Variation",
      closedTrades: "trade(s) fermé(s)",
      synced: "Synchronisé",
      manual: "Manuel",
      createAccountTitle: "Nouveau compte",
      createAccountDescription: "Créez un nouveau compte de trading. Les comptes synchronisés seront disponibles prochainement.",
      editAccountTitle: "Modifier le compte",
      editAccountDescription: "Modifiez les informations du compte. Le type de compte ne peut pas être modifié.",
      accountName: "Nom du compte",
      accountNamePlaceholder: "Ex: Compte principal",
      accountType: "Type de compte",
      syncedComingSoon: "Synchronisé (bientôt disponible)",
      initialBalanceLabel: "Balance initiale",
      cancel: "Annuler",
      create: "Créer",
      save: "Enregistrer",
      accountCreated: "Compte créé",
      accountCreatedDescription: "Le compte a été créé avec succès.",
      accountUpdated: "Compte modifié",
      accountUpdatedDescription: "Le compte a été modifié avec succès.",
      accountDeleted: "Compte supprimé",
      accountDeletedDescription: "Le compte a été supprimé avec succès.",
      error: "Erreur",
      createError: "Impossible de créer le compte.",
      updateError: "Impossible de modifier le compte.",
      deleteError: "Impossible de supprimer le compte.",
      confirmDelete: "Êtes-vous sûr de vouloir supprimer le compte",
      deleteAccountConfirmTitle: "Supprimer le compte \"{name}\" ?",
      deleteAccountConfirmDescription: "Les trades associés seront conservés mais dissociés de ce compte.",
      deleteAccountConfirmWarning: "La suppression efface DÉFINITIVEMENT ce compte, tous ses trades et tout son historique. Cette action est irréversible.",
      deleteAccountConfirmHint: "Si le compte est cramé ou clôturé, désactivez-le plutôt que de le supprimer : vous conservez ainsi toutes vos données et votre historique.",
      deactivateInstead: "Désactiver à la place",
      deactivateAccountConfirmTitle: "Désactiver le compte \"{name}\" ?",
      deactivateAccountConfirmDescription: "Le compte sera marqué comme inactif et considéré comme cramé. Vous pourrez le réactiver à tout moment et aucune donnée n'est perdue.",
      deactivate: "Désactiver",
      delete: "Supprimer",
      accountNameRequired: "Le nom du compte est requis",
      accountSizeRequired: "La taille de compte est requise",
      eur: "EUR - Euro",
      usd: "USD - Dollar américain",
      gbp: "GBP - Livre sterling",
      chf: "CHF - Franc suisse",
      jpy: "JPY - Yen japonais",
      cad: "CAD - Dollar canadien",
      aud: "AUD - Dollar australien",
      nzd: "NZD - Dollar néo-zélandais",
      cny: "CNY - Yuan chinois",
      btc: "BTC - Bitcoin",
      eth: "ETH - Ethereum",
      // Groups
      groups: "Groupes",
      noGroups: "Aucun groupe créé.",
      ungrouped: "Sans groupe",
      createGroup: "Créer un groupe",
      editGroup: "Modifier le groupe",
      deleteGroup: "Supprimer le groupe",
      groupName: "Nom du groupe",
      groupNamePlaceholder: "Ex: Prop firms actives",
      groupCreated: "Groupe créé",
      groupUpdated: "Groupe modifié",
      groupDeleted: "Groupe supprimé",
      groupDeleteConfirm: "Supprimer le groupe ? Les comptes ne seront pas supprimés, ils passeront en « Sans groupe ».",
      groupPropFirm: "Prop firm du groupe",
      groupStrategy: "Stratégie du groupe",
      groupStrategyHint: "Les comptes du groupe hériteront automatiquement de cette stratégie",
      accountStrategy: "Stratégie des comptes",
      accountStrategyHint: "Les comptes créés hériteront automatiquement de cette stratégie",
      groupConsolidatedPnl: "P&L consolidé",
      groupActiveAccounts: "comptes actifs",
      groupDeleteWarning: "Les comptes de ce groupe ne seront pas supprimés, ils passeront en « Sans groupe ».",
      // Prop firms
      propFirm: "Prop firm",
      propFirmApex: "Apex Trader Funding",
      propFirmFtmo: "FTMO",
      propFirmTopstep: "Topstep",
      propFirmTakeProfitTrader: "Take Profit Trader",
      propFirmLucid: "Lucid Trading",
      propFirmCustom: "Personnalisé",
      noPropFirm: "Aucune prop firm",
      inheritedFromGroup: "Hérité du groupe",
      // Account status
      statusChallenge: "Challenge",
      statusPa: "Compte Financé (PA)",
      statusInactive: "Inactif",
      statusDemo: "Démo",
      statusLive: "Live",
      statusChallenge2: "Challenge Phase 2",
      activePhase: "Phase active",
      addPhase: "Ajouter une phase",
      editPhase: "Modifier la phase",
      deletePhase: "Supprimer la phase",
      confirmDeletePhase: "Supprimer cette phase ?",
      phaseTypeLabel: "Type de phase",
      phaseNameLabel: "Nom de la phase",
      phaseTypeEvaluation: "Évaluation",
      phaseTypeEvaluation2: "Évaluation Phase 2",
      phaseFundedVariant: "Funded (variante)",
      phaseDrawdownType: "Type de drawdown",
      phaseDrawdownTrailing: "Trailing",
      phaseDrawdownTotal: "Perte totale",
      phaseDrawdownDaily: "Perte journalière",
      phaseDrawdownNone: "Aucun",
      accountStatus: "Statut du compte",
      personalAccount: "Compte Personnel",
      tradovateAccount: "Tradovate",
      tradovateAccountDescription: "Import depuis votre compte Tradovate",
      tradovateModeManual: "Import manuel",
      tradovateModeSync: "Synchronisation automatique",
      topstepxAccount: "Topstep X",
      topstepxAccountDescription: "Compte Topstep, import depuis Topstep X",
      metatraderAccount: "MetaTrader Synchronisé",
      metatraderAccountDescription: "Synchronisation automatique depuis MT4/MT5",
      mtCredentialsStep: "Connexion MT",
      mtAccountDetailsStep: "Détails",
      mtLogin: "Login MetaTrader",
      mtLoginPlaceholder: "123456",
      mtPassword: "Mot de passe",
      mtServer: "Serveur broker",
      mtServerPlaceholder: "ICMarkets-Live01",
      mtPlatform: "Plateforme",
      mtAccountCreating: "Connexion en cours...",
      mtAccountCreated: "Compte MetaTrader connecté",
      mtAccountError: "Échec de la connexion",
      mtConnectionRetryHint: "Vérifiez vos identifiants et réessayez. Revenez à l'étape précédente pour les modifier.",
      selectPropFirm: "Sélectionner la prop firm",
      // Drawdown warnings
      drawdownWarning: "⚠️ Attention : drawdown à {percent}% de la limite",
      drawdownViolation: "🚫 Limite de drawdown dépassée",
      drawdownRemainingRoom: "{amount} de marge restante",
      // Bulk creation
      addAccounts: "Ajouter des comptes",
      addAccountsDescription: "Ajoutez plusieurs comptes en une seule fois. Sélectionnez une prop firm, renseignez les détails et assignez un groupe.",
      selectPropFirmStep: "Prop firm",
      selectAccountTypeStep: "Type de compte",
      enterAccountsStep: "Comptes",
      assignGroupStep: "Groupe",
      selectAccountType: "Type de compte",
      selectAccountTypeDescription: "Sélectionnez le type de compte {name} que vous ajoutez. Ce choix s'applique à tous les comptes créés.",
      selectTierBalance: "Taille de compte",
      customBalance: "Autre montant",
      challengePriceMode: "Mode de saisie du prix",
      challengePriceModeTotal: "Prix total",
      challengePriceModeUnit: "Prix par compte",
      challengePriceTotalLabel: "Prix total payé ({count} comptes)",
      challengePriceUnitLabel: "Prix par compte",
      accountsCreated: "Comptes créés",
      accountsCreatedDescription: "{count} compte(s) créé(s) avec succès.",
      assignToGroup: "Assigner à un groupe",
      selectGroupPlaceholder: "Choisir un groupe...",
      propFirmRulesApplied: "Les règles de {name} seront appliquées automatiquement selon la balance de chaque compte.",
      createNewGroup: "Créer un nouveau groupe",
      groupNameRequired: "Le nom du groupe est requis",
      existingGroup: "Groupe existant",
      noGroupOption: "Sans groupe",
      numberOfAccounts: "Nombre de comptes",
      addAnotherAccount: "Ajouter un compte",
      removeAccount: "Retirer",
      bulkCreateError: "Certains comptes n'ont pas pu être créés.",
      duplicateAccountName: "Un compte avec ce nom existe déjà. Choisissez un nom différent.",
      // Active toggle
      activateAccount: "Réactiver le compte",
      deactivateAccount: "Désactiver le compte",
      accountActivated: "Compte réactivé",
      accountDeactivated: "Compte désactivé",
      activeAccountsOnly: "Seuls les comptes actifs sont pris en compte dans les analytics.",
      inactiveAccountNote: "Ce compte est désactivé. Ses trades ne sont pas comptabilisés dans les analytics globaux.",
      // Plan limit
      accountLimitReached: "Limite atteinte",
      accountLimitReachedDescription: "Le plan Free est limité à 1 compte. Passez en Pro pour des comptes illimités.",
      hiddenBanner: {
        title: "Vous avez {{count}} compte(s) caché(s). Vos données sont en sécurité, souscrivez pour les retrouver.",
        cta: "Voir les plans",
      },
      // Filter
      filterAllAccounts: "Tous les comptes",
      filterAllAccountsWithInactive: "Tous les comptes (+ inactifs)",
      filterNoAccount: "Sans compte",
      filterByAccount: "Filtrer par compte",
      filterByGroup: "Filtrer par groupe",
      includeInactive: "Inclure inactifs",
      hideInactiveAccounts: "Masquer les comptes inactifs",
      showInactiveAccounts: "Afficher les comptes inactifs",
      // Misc
      proFeature: "Fonctionnalité Pro",
      next: "Suivant",
      previous: "Précédent",
      step: "Étape",
      of: "sur",
      // PropFirm DB-driven
      systemPropFirms: "Prop firms officielles",
      myCustomPropFirms: "Mes prop firms custom",
      createCustomPropFirm: "+ Créer une prop firm custom",
      autoDetected: "Auto-détecté",
      tierOverride: "Tier manuel",
      selectTier: "Sélectionner un tier",
      // Progress / metrics
      challengeProgress: "Progression du challenge",
      profitTarget: "Objectif de profit",
      tradingDays: "Jours tradés",
      bufferDrawdown: "Buffer drawdown",
      bufferRemaining: "Buffer restant",
      axisFloor: "Plancher",
      axisFloorHint: "Seuil de perte maximale (MLL)",
      axisStart: "Départ",
      axisTarget: "Cible",
      axisPeak: "Pic",
      axisDrawdown: "Marge",
      axisBalance: "Balance",
      axisRemaining: "Restant",
      maxLossExceeded: "Perte max dépassée",
      daysTradedCount: "{count} jours tradés",
      tradesPnl: "PnL des trades",
      syncNeedsAttention: "Synchronisation à vérifier",
      rulesMeasured: "Progression",
      rulesDeclared: "Contraintes",
      bufferConsumed: "consommé",
      payoutProgress: "Progression payout",
      payoutTarget: "Objectif payout",
      payoutTargetLabel: "Objectif payout ($)",
      payoutReached: "Objectif payout atteint !",
      setPayoutTarget: "Définir un objectif",
      // Detail sheet
      accountDetails: "Détails du compte",
      rules: "Règles du tier",
      maxDailyLoss: "Perte journalière max",
      maxTotalLoss: "Perte totale max",
      trailingDrawdown: "Drawdown trailing",
      consistencyRule: "Règle de consistance",
      maxContracts: "Contrats max",
      minTradingDays: "Jours minimum",
      payoutCooldown: "Délai entre payouts",
      payoutThreshold: "Seuil de payout",
      daysCountShort: "{count} j",
      consistencyRuleValue: "Max {percent}% / jour",
      consistencyShort: "Consistance {percent}%",
      noRulesDefined: "Aucune règle définie pour ce tier.",
      payoutsHistory: "Historique des payouts",
      noPayouts: "Aucun payout enregistré.",
      addPayout: "Ajouter un payout",
      totalPayoutsReceived: "Total perçu",
      fees: "Frais",
      totalCommissionTooltip: "Total des commissions et swaps sur tous les trades du compte",
      payoutAmount: "Montant",
      payoutGrossAmount: "Montant brut",
      payoutSplitPercent: "Profit split (%)",
      payoutNetAmount: "Montant net reçu",
      payoutGrossBelowNet: "Le montant brut ne peut pas être inférieur au montant net reçu",
      payoutGrossHint: "Débité du compte, sert au drawdown",
      payoutNetHint: "Reçu par vous après le split",
      payoutEdit: "Modifier le payout",
      payoutUpdated: "Payout modifié",
      payoutGrossShort: "Brut {amount}",
      payoutAmountsRequired: "Saisissez au moins un montant",
      payoutDate: "Date",
      payoutNotes: "Notes",
      payoutNotesPlaceholder: "Notes optionnelles...",
      payoutAdded: "Payout ajouté",
      payoutAddedNoCert: "Payout ajouté, mais l'upload du certificat a échoué.",
      payoutCertificate: "Certificat (optionnel)",
      payoutDeleteConfirm: "Supprimer ce payout ? Le certificat associé sera également supprimé. Cette action est irréversible.",
      payoutDeleted: "Payout supprimé",
      peakBalance: "Balance peak",
      activeTier: "Tier actif",
      changeTier: "Changer de tier",
      changeStatus: "Changer le statut",
      // Challenge complete
      challengeComplete: "Challenge complété ! 🎉",
      // Detail page
      backToAccounts: "Retour aux comptes",
      editAccount: "Modifier",
      accountDetailTitle: "Détail du compte",
      groupDetailTitle: "Détail du groupe",
      statsOverview: "Statistiques",
      winRate: "Win Rate",
      wins: "Gagnants",
      losses: "Perdants",
      avgWin: "Gain moyen",
      avgLoss: "Perte moyenne",
      profitFactor: "Profit Factor",
      maxDrawdown: "Drawdown max",
      totalPnl: "P&L total",
      tradesCount: "Trades fermés",
      equityCurve: "Courbe de capital",
      equityCurveMaxLossLimit: "Limite de perte max",
      symbolStats: "Statistiques par symbole",
      symbol: "Symbole",
      pnl: "P&L",
      trades: "Trades",
      noSymbolStats: "Aucune statistique disponible.",
      noEquityData: "Aucune donnée de capital.",
      groupAccounts: "Comptes du groupe",
      consolidatedStats: "Stats consolidées",
      activeAccounts: "Comptes actifs",
      totalAccounts: "Total comptes",
      totalInitialBalance: "Balance initiale totale",
      totalCurrentBalance: "Balance actuelle totale",
      totalPayoutsGroup: "Payouts totaux",
      accountsInGroup: "comptes dans ce groupe",
      noActiveAccounts: "Aucun compte actif dans ce groupe.",
      viewAccountDetail: "Voir le détail",
      challengeCostLabel: "Prix du compte",
      challengeCostPlaceholder: "ex: 120",
      challengeCostHint: "Prix réellement payé pour ce compte (challenge fee, abonnement)",
      subscriptionPriceLabel: "Prix mensuel de l'abonnement",
      subscriptionPriceHint: "Compté chaque mois dans le Prop Firm Manager tant que le compte reste en challenge.",
      directFundedHint: "Compte funded dès l'achat : pas de phase de challenge.",
      purchasedAtLabel: "Date d'achat",
      purchasedAtHint:
        "Date réelle d'achat du compte. Laisse vide si le compte a été acheté aujourd'hui - utile pour reconstituer un historique.",
      closedAtLabel: "Date de clôture",
      closedAtNotice:
        "Le compte sera créé comme clôturé : exclu des analytics globales et de la limite de comptes de ton plan.",
      historicalAccountToggle: "Compte historique (déjà acheté ou clôturé)",
      historicalAccountHint:
        "Renseigne les dates réelles pour que les dépenses et payouts tombent sur la bonne période dans le Prop Firm Manager.",
      activationFeeLabel: "Frais d'activation",
      activationFeePlaceholder: "ex: 89",
      activationFeeHint: "Montant payé lors du passage en funded (laisser vide si aucun frais).",
      fundedAtLabel: "Date de passage en Funded",
      fundedAtHint: "Date réelle du passage en funded, utilisée pour le calendrier des dépenses.",
      markAsFunded: "Marquer comme Funded",
      // Challenge -> Funded transition
      fundedCelebrationTitle: "Objectif atteint, félicitations !",
      fundedCelebrationSubtitle:
        "Bravo, tu as atteint l'objectif de profit de ton challenge. Bien joué !",
      fundedCelebrationSyncNote:
        "Quand la prop firm activera ton compte Funded, la synchro le détectera automatiquement. Tu pourras renseigner tes frais d'activation à ce moment-là.",
      fundedCelebrationCloseButton: "Merci !",
      promoteToFunded: "Passer en Funded",
      promoteToFundedTitle: "Passer le compte en Funded",
      promoteToFundedDescription: "Le compte garde tout son historique de trades. La balance repart de la valeur de départ du compte funded à la date indiquée.",
      fundedDateLabel: "Date de passage en Funded",
      fundedStartingBalanceLabel: "Balance de départ du compte Funded",
      fundedStartingBalanceHint: "Par défaut la balance initiale du compte. À ajuster si la prop firm crédite un autre montant.",
      activationFeeOptionalLabel: "Frais d'activation (optionnel)",
      promotePreview: "{challenge} trade(s) resteront en phase Challenge, {funded} passeront en phase Funded.",
      promoteConfirm: "Confirmer le passage en Funded",
      promoteCancel: "Annuler",
      promoteSuccess: "Compte passé en Funded",
      promoteBalanceInvalid: "Saisissez une balance de départ valide (supérieure à 0).",
      challengeRecapTitle: "Challenge validé",
      challengeRecapFinalBalance: "Balance atteinte",
      challengeRecapNetProfit: "Profit net",
      challengeRecapTradeCount: "Trades",
      challengeRecapWinRate: "Taux de réussite",
      challengeRecapDuration: "Durée du challenge",
      challengeRecapDurationDays: "{days} jour(s)",
      phaseChallenge: "Challenge",
      phaseFunded: "Funded",
      phaseLive: "Live",
      phaseDemo: "Démo",
      phaseFilterAll: "Toutes les phases",
      phaseFilterLabel: "Phase",
      couponCode: "Code coupon",
      couponCodePlaceholder: "ex: SAVE20",
      couponDiscount: "Réduction (%)",
      couponDiscountPlaceholder: "ex: 20",
      couponHint: "Renseignez le coupon utilisé pour calculer le prix réel payé",
      charges: "Charges",
      addCharge: "Ajouter une charge",
      addGlobalCharge: "Ajouter une charge globale",
      chargeName: "Nom",
      chargeAmount: "Montant",
      chargeFrequency: "Fréquence",
      chargeMonthly: "Mensuel",
      chargeAnnual: "Annuel",
      chargeOneTime: "Ponctuel",
      chargeAdded: "Charge ajoutée",
      chargeDeleted: "Charge supprimée",
      chargeAddError: "Erreur lors de l'ajout",
      chargeDeleteError: "Erreur lors de la suppression",
      chargeDelete: "Supprimer la charge",
      chargeNamePlaceholder: "ex: TradingView",
      chargeAmountPlaceholder: "ex: 30",
      noCharges: "Aucune charge",
      chargeStartDate: "Date de début",
      chargeStartDatePlaceholder: "ex: 2024-03-15",
      chargeBillingDay: "Jour de facturation",
      chargeBillingDayPlaceholder: "ex: 15",
      chargeEndDate: "Date de fin",
      chargeEndDateHint: "Vide = toujours en cours",
      chargeSourceSubscription: "Abonnement prop firm",
      chargeDeleteRecurringConfirm: "Supprimer efface aussi tous les prélèvements passés. Si tu as résilié, utilise plutôt « J'ai résilié » dans la carte « À venir ». Cliquer à nouveau pour confirmer.",
      chargeRemindersTitle: "Rappels de prélèvement",
      chargeRemindersLabel: "Recevoir un email 3 jours avant un prélèvement",
      chargeRemindersHint: "La notification dans l'app reste active.",
      chargeEdit: "Modifier la charge",
      chargeEditError: "Erreur lors de la modification",
      chargeUpdated: "Charge modifiée",
      chargeSave: "Enregistrer",
      chargeCancel: "Annuler",
      chargeAddTitle: "Ajouter une charge",
      chargeEditTitle: "Modifier une charge",
      globalCharges: "Charges globales",
      linkedCharges: "Charges liées",
      disabledAtLabel: "Date de désactivation",
      disabledAtHint: "Date à laquelle le compte a été désactivé ou crashé",
      consistencyRuleOverrideLabel: "Règle de consistance (%)",
      consistencyRuleOverrideHint: "Ex : 40 → aucun jour ne peut dépasser 40% du profit total. Laissez vide si la règle est déjà définie dans le tier.",
      maxDailyLossOverrideLabel: "Stop loss journalier (%)",
      maxDailyLossOverrideHint: "Ex : 5 → perte max de 5% de la balance initiale par jour. Laissez vide si déjà défini dans le tier.",
      violationDailyLoss: "Stop loss journalier dépassé",
      warningDailyLoss: "Approche du stop loss journalier",
      violationConsistency: "Règle de consistance violée",
      warningConsistency: "Approche de la règle de consistance",
      violationConsistencyDetail: "Règle de consistance violée : {share}% sur la journée du {date}, seuil {threshold}%",
      warningConsistencyDetail: "Approche de la règle de consistance : {share}% sur la journée du {date}, seuil {threshold}%",
      violationConsistencyBase: "Calculé sur l'objectif de {base}",
      balanceGapJournalAhead: "Solde broker en retard de {gap}",
      balanceGapJournalAheadHint: "Vos trades sont bien enregistrés, mais le solde communiqué par le broker date d'avant. Il se met à jour tout seul au prochain relevé.",
      balanceGapJournalIncomplete: "Écart de {gap} avec le broker",
      balanceGapJournalIncompleteHint: "Le broker annonce plus que ce que vos trades expliquent : des trades antérieurs à la connexion manquent au journal.",
      balanceGapJournalLabel: "Selon vos trades",
      balanceGapStale: "Dernier relevé il y a plus de 2 h",
      qualifyingDaysLabel: "Jours qualifiants",
      payoutEligible: "Éligible au payout",
      strategies: "Stratégies",
      allStrategies: "Toutes les stratégies",
      strategiesPlaceholder: "Sélectionner ou créer une stratégie...",
      strategyNone: "Aucune stratégie",
      addStrategy: "Ajouter une stratégie",
      strategyName: "Nom de la stratégie",
      strategyNamePlaceholder: "ex : Scalping, Swing, Mean Reversion...",
      strategyCreated: "Stratégie créée",
      strategyDeleted: "Stratégie supprimée",
      strategyCreateError: "Erreur lors de la création de la stratégie",
      strategyDeleteError: "Erreur lors de la suppression de la stratégie",
      strategyExists: "Une stratégie avec ce nom existe déjà",
      noStrategies: "Aucune stratégie définie",
      strategyHint: "Les trades créés sur ce compte hériteront de ces stratégies",
      resyncTradesStrategies: "Resynchroniser les trades existants",
      resyncTradesStrategiesHint: "Applique les stratégies actuelles du compte à tous ses trades existants (remplace leurs stratégies)",
      resyncTradesStrategiesSuccess: "Trades resynchronisés",
      resyncTradesStrategiesError: "Erreur lors de la resynchronisation",
      tableView: "Vue tableau",
      cardsView: "Vue cartes",
      colName: "Nom",
      colStatus: "Statut",
      colPropFirm: "Prop Firm",
      colGroup: "Groupe",
      colType: "Type",
      colSource: "Source",
      sourceTradovate: "Tradovate",
      sourceMetaTrader: "MetaTrader",
      sourceManual: "Manuel",
      deactivatedBrokerGone: "Clôturé côté broker",
      deactivatedBrokerGoneHint: "Ce compte n'existe plus chez Tradovate. Ses trades sont conservés.",
      deactivatedChallengeCompleted: "Challenge validé",
      deactivatedChallengeCompletedHint: "Vous avez validé ce challenge : la prop firm a clôturé le compte d'évaluation et vous en a délivré un nouveau. Ses trades sont conservés. Activez l'affichage des comptes inactifs pour retrouver son PnL dans le calendrier.",
      deactivatedDrawdown: "Drawdown dépassé",
      deactivatedDrawdownHint: "Ce compte a franchi sa limite de drawdown et a été désactivé automatiquement. Ses trades sont conservés.",
      payoutSuggestionLabel: "Payout détecté",
      cashQuestionTitle: "Mouvement inexplique",
      cashQuestionBody:
        "Le solde de ce compte a baisse sans trade associe. Que s'est-il passe ?",
      cashQuestionAmountLabel: "Montant",
      cashQuestionAnswerWithdrawal: "Un retrait",
      cashQuestionAnswerReset: "Un reset de compte",
      cashQuestionAnswerFees: "Des frais",
      cashQuestionAnswerUnknown: "Je ne sais pas",
      cashQuestionConfirm: "Confirmer",
      cashQuestionDismiss: "Ignorer",
      cashQuestionCelebrate: "Retrait enregistre. Beau travail.",
      cashQuestionRecorded: "Reponse enregistree.",
      cashQuestionReasonUnavailable:
        "Le journal de tresorerie du broker n'a pas pu etre lu.",
      payoutSuggestionCreate: "Créer",
      payoutSuggestionDismiss: "Ignorer",
      accountResetSuggestionLabel: "Reset de compte détecté",
      accountResetDetectedTitle: "Reset de compte probablement détecté",
      accountResetDetectedDescription:
        "Le solde du broker ne correspond pas à celui calculé par Edgely, ce qui arrive quand la prop firm reset un compte. Vérifiez la date ci-dessous puis confirmez : les trades antérieurs seront archivés (exclus du solde) mais resteront dans votre journal et vos analyses.",
      accountResetObservedBalance: "Solde chez le broker",
      accountResetExpectedBalance: "Solde calculé par Edgely",
      accountResetDiscrepancy: "Écart",
      accountResetDateLabel: "Date du reset",
      accountResetConfidenceHigh:
        "Cette date provient du journal de trésorerie du broker. Corrigez-la si elle ne correspond pas à votre reset.",
      accountResetConfidenceLow:
        "Attention : cette date n'est qu'une estimation (le journal de trésorerie du broker ne remonte que quelques jours). Vérifiez-la et corrigez-la avant de confirmer.",
      accountResetConfirm: "Confirmer le reset",
      accountResetConfirming: "Confirmation...",
      accountResetDismiss: "Ignorer",
      accountResetInvalidDate: "Veuillez saisir une date de reset valide.",
      accountResetError: "La confirmation du reset a échoué. Veuillez réessayer.",
      colStrategy: "Stratégie",
      colBalance: "Solde",
      colPnl: "PnL",
      colProgress: "Progression",
      colTrades: "Trades",
      colActions: "Actions",
      blewUpModal: {
        title: "Compte potentiellement cramé",
        subtitleDailyLoss: "Le daily loss maximum a été atteint sur **{accountName}**.",
        subtitleTotalLoss: "Le drawdown maximum a été atteint sur **{accountName}**.",
        subtitleTrailingDrawdown: "Le trailing drawdown a été déclenché sur **{accountName}**.",
        question: "Veux-tu marquer ce compte comme inactif ?",
        confirm: "Oui, désactiver le compte",
        dismiss: "Non, continuer",
      },
    },
    notebook: {
      title: "Notebook",
      subtitle: "Regroupez vos notes, analyses et cours",
      newNote: "Nouvelle note",
      untitled: "Sans titre",
      empty: "Aucune note",
      selectNote: "Sélectionnez une note",
      createFirstNote: "Créer votre première note",
      loading: "Chargement...",
      noNotes: "Aucune note trouvée. Créez votre première note !",
      search: "Rechercher",
      searchPlaceholder: "Rechercher par titre ou contenu...",
      category: "Catégorie",
      allCategories: "Toutes les catégories",
      noCategory: "Sans catégorie",
      categories: "Catégories",
      manageCategories: "Gérer les catégories",
      createCategory: "Créer une catégorie",
      editCategory: "Modifier la catégorie",
      deleteCategory: "Supprimer la catégorie",
      categoryName: "Nom de la catégorie",
      categoryColor: "Couleur",
      categoryCreated: "Catégorie créée",
      categoryUpdated: "Catégorie modifiée",
      categoryDeleted: "Catégorie supprimée",
      categoryError: "Erreur lors de la gestion de la catégorie",
      confirmDeleteCategory: "Êtes-vous sûr de vouloir supprimer cette catégorie ? Les notes associées ne seront pas supprimées.",
      confirmDeleteNote: "Êtes-vous sûr de vouloir supprimer cette note ?",
      noteCreated: "Note créée",
      noteUpdated: "Note modifiée",
      noteDeleted: "Note supprimée",
      noteError: "Erreur lors de la gestion de la note",
      saving: "Enregistrement...",
      saved: "Enregistré",
      saveError: "Erreur lors de l'enregistrement",
      editorPlaceholder: "Commencez à écrire votre note...",
      toolbar: {
        bold: "Gras",
        italic: "Italique",
        underline: "Souligné",
        strike: "Barré",
        heading1: "Titre 1",
        heading2: "Titre 2",
        heading3: "Titre 3",
        bulletList: "Liste à puces",
        orderedList: "Liste numérotée",
        link: "Lien",
        image: "Image",
        divider: "Séparateur",
      },
      uploadImage: "Télécharger une image",
      pasteImage: "Coller une image",
      imageUploaded: "Image téléchargée avec succès",
      imageUploadError: "Erreur lors du téléchargement de l'image",
      gridColumns1: "1 colonne",
      gridColumns2: "2 colonnes",
      gridColumns3: "3 colonnes",
      gridColumns4: "4 colonnes",
      updatedOn: "Mis à jour le",
      readMore: "Lire la suite",
      collapse: "Réduire",
      symbols: "Symboles (séparés par des virgules)",
      symbolsPlaceholder: "AAPL, TSLA, MSFT",
      tags: "Tags (séparés par des virgules)",
      tagsPlaceholder: "cours, analyse, stratégie",
      backToNotebook: "Retour au notebook",
      editNote: "Modifier la note",
      saveNote: "Enregistrer",
      cancel: "Annuler",
    },
    tradeDetail: {
      title: "Détails du Trade",
      backToJournal: "Retour au journal",
      edit: "Modifier",
      save: "Sauvegarder",
      cancel: "Annuler",
      tradeInfo: "Informations du Trade",
      symbol: "Symbole",
      type: "Type",
      status: "Statut",
      date: "Date",
      entryPrice: "Prix d'entrée",
      exitPrice: "Prix de sortie",
      quantity: "Quantité",
      pnl: "P&L",
      commission: "Commission",
      notes: "Notes",
      notesSaved: "Notes sauvegardées",
      notesError: "Impossible de sauvegarder les notes",
      templates: "Templates",
      noNotes: "Aucune note pour ce trade. Cliquez sur \"Modifier\" pour en ajouter.",
      clickToAdd: "Modifier",
      previousContent: "Contenu précédent",
      chartTitle: "Graphique de Prix",
      chartError: "Impossible de charger les données du graphique",
      noChartData: "Aucune donnée disponible pour ce symbole",
      customFields: "Champs personnalisés",
      noValue: "",
      editCustomFields: "Modifier les champs",
      saveCustomFields: "Sauvegarder",
    },
    analytics: {
      title: "Analytics",
      subtitle: "Analysez vos performances de trading",
      loading: "Chargement...",
      timeFilter: "Période",
      last7Days: "7 derniers jours",
      last30Days: "30 derniers jours",
      last90Days: "90 derniers jours",
      allTime: "Tout",
      totalPnl: "P&L Total",
      winRate: "Win Rate",
      profitFactor: "Facteur de Profit",
      totalTrades: "Total Trades",
      averagePnl: "P&L Moyen",
      cumulativePnl: "P&L Cumulé",
      pnlDistribution: "Distribution du P&L",
      monthlyPerformance: "Performance Mensuelle",
      pnlPerTrade: "P&L par Trade",
      sessionRecaps: "Récapitulatifs des Sessions",
      noSessionRecaps: "Aucune session enregistrée pour cette période",
      startFromDashboard: "Commencer une session depuis le dashboard",
      date: "Date",
      duration: "Durée",
      mood: "Humeur",
      performance: "Performance",
      opportunities: "Opportunités",
      openTrades: "Trades Ouverts",
      closedTrades: "Trades Clôturés",
      totalPnlLabel: "P&L Total",
      selfEvaluation: "Auto-évaluation",
      mentalState: "État Mental",
      notes: "Notes",
      excellent: "Excellent",
      good: "Bon",
      neutral: "Neutre",
      poor: "Mauvais",
      bad: "Terrible",
      average: "Moyen",
      symbol: "Symbole",
      noDataAvailable: "Aucune donnée disponible",
      largestWinLoss: "Plus Gros Gain / Perte",
      numberOfTrades: "Nombre de trades",
      period: "Période",
      year: "Année",
      quarter: "Trimestre",
      month: "Mois",
      selectYear: "Sélectionner l'année",
      selectPeriod: "Sélectionner la période",
      months: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
      quarters: ["T1", "T2", "T3", "T4"],
      perTradePerformance: "Performance par Trade",
      netDailyPnlLabel: "P&L Net Quotidien",
      netDailyPnlTitle: "P&L net par jour calendaire",
      holdDurationTradeCount: "Nombre de trades par durée de détention",
      longVsShort: "Long vs Short",
      winLossDistribution: "Distribution Gains/Pertes",
      dayOfWeekPerformance: "Performance par Jour",
      longPerforming: "Longs plus performants",
      shortPerforming: "Shorts plus performants",
      equalPerformance: "Performance égale",
      longShortTotalLabel: "Total",
      longShortTotalTooltip: "P&L cumulé de tous les trades dans cette direction",
      longShortAvgLabel: "Moy.",
      longShortAvgTooltip: "P&L moyen par trade dans cette direction",
      wins: "Gains",
      losses: "Pertes",
      breakeven: "Neutre",
      best: "Meilleur",
      worst: "Pire",
      equityCurve: "Equity Curve",
      maxDrawdown: "Max Drawdown",
      peakToTrough: "Pic à creux",
      sharpeRatio: "Ratio de Sharpe",
      riskAdjustedReturn: "Rendement ajusté au risque",
      expectancy: "Espérance",
      averagePerTrade: "Moyenne par trade",
      topSymbols: "Top Symboles",
      symbolsLabel: "Symboles",
      insightsLabel: "Insights",
      sessionsLabel: "Sessions",
      sessionDurationLabel: "P&L moyen par durée de session",
      distributionLabel: "Distribution",
      dayLabel: "Jour",
      pnlLabel: "P&L",
      dateLabel: "Date",
      valueLabel: "Valeur",
      tradeLabel: "Trade",
      durationLabel: "Durée de session",
      holdDurationLabel: "Durée de détention",
      avgPnlLabel: "P&L moyen",
      moodLabel: "Humeur",
      bucketLabel: "Moment dans la session",
      sizeLabel: "Taille",
      sessionsCountLabel: "Sessions",
      calendarLabel: "Calendrier",
      marketsLabel: "Marchés",
      sessionLabel: "Session",
      winsCount: "gains",
      lossesCount: "pertes",
      consistencyScore: "Score de Consistance",
      consistencyScoreMin: "Minimum 5 trades fermés requis",
      csWinRateStability: "Stabilité win rate",
      csRiskConsistency: "Consistance du risque",
      csNoRevenge: "Discipline (no revenge)",
      csDrawdownMgmt: "Gestion drawdown",
      consistencyScoreInfo: "Le score est calculé sur 4 composantes (25 pts chacune) :\n• Stabilité win rate : variance mensuelle de votre taux de gain\n• Consistance du risque : régularité des montants engagés par trade\n• Discipline (no revenge) : absence de trades dans les 30 min après une perte\n• Gestion drawdown : 25/25 si aucune série de pertes (signe de régularité), sinon mesure si vous réduisez bien votre risque après 2+ pertes consécutives",
      streakAnalysis: "Analyse des Séries",
      currentStreak: "Série en cours",
      streakWins: "victoires",
      streakLosses: "pertes",
      longestWinStreak: "Meilleure série",
      longestLossStreak: "Pire série",
      trades: "trades",
      riskManagement: "Gestion du risque en série",
      drawdownMgmt: "Réduction risque après pertes",
      drawdownMgmtHint: "Réduire son risque après 2+ pertes consécutives",
      drawupMgmt: "Augmentation risque après gains",
      drawupMgmtHint: "Légèrement augmenter son risque après 3+ gains consécutifs",
      streakInsufficient: "Pas assez de séries de 2+ pertes consécutives",
      streakInsufficientLoss: "Pas encore assez de séries de 2+ pertes consécutives pour analyser",
      streakInsufficientWin: "Pas encore assez de séries de 3+ gains consécutifs pour analyser",
      accountValue: "Valeur du compte",
      sessionDelete: "Supprimer la session",
      sessionDeleteConfirm: "Supprimer cette session ?",
      sessionDeleted: "Session supprimée",
      sessionDeleteError: "Erreur lors de la suppression",
      sessionEdit: "Modifier la session",
      sessionSaved: "Session enregistrée",
      sessionSaveError: "Erreur lors de l'enregistrement",
      sessionExpandDetails: "Voir les détails",
      sessionMaxRunup: "Max Run-up",
      sessionMaxDrawdown: "Max Drawdown",
      sessionDate: "Date",
      sessionDurationMin: "min",
      sessionStartedAt: "Début",
      sessionEndedAt: "Fin",
      sessionPnl: "P&L session",
      sessionAddManual: "Ajouter une session",
      sessionAddManualTitle: "Ajouter une session manuellement",
      sessionAddManualDescription: "Saisissez les informations d'une session passée pour enrichir votre historique.",
      sessionAddManualSuccess: "Session ajoutée avec succès",
      sessionAddManualError: "Erreur lors de l'ajout de la session",
      sessionStartTime: "Heure de début",
      sessionEndTime: "Heure de fin",
      // Advanced analytics categories
      cat1Title: "Performance",
      cat1Desc: "Calmar Ratio, Recovery Factor, courbe de drawdown",
      cat2Title: "Risque & Statistiques",
      cat2Desc: "Max drawdown, Sharpe, Expectancy, distribution P&L, top symboles",
      cat3Title: "Timing & Horaires",
      cat3Desc: "Heatmap heure×jour, tranches horaires, durée de hold",
      cat4Title: "Psychologie & Consistance",
      cat4Desc: "Score de consistance, analyse des séries",
      cat5Title: "Setups & Tags",
      cat5Desc: "Performance par tag, P&L mensuel, rolling win rate",
      cat6Title: "Sessions de Trading",
      cat6Desc: "Récapitulatif des sessions, humeur, performance",
      sessionStatsTimeTitle: "PnL par moment de la session",
      sessionStatsTimeDesc: "Où dans la session vos profits et pertes se concentrent-ils ?",
      sessionStatsWinRateTitle: "Win Rate par moment",
      sessionStatsWinRateDesc: "À quel moment de la session vos trades sont-ils les plus souvent gagnants ?",
      sessionStatsMoodTitle: "PnL moyen par humeur",
      sessionStatsMoodDesc: "Votre rentabilité selon votre état d'esprit déclaré en début de session.",
      sessionStatsDurationTitle: "PnL moyen par durée de session",
      sessionStatsDurationDesc: "Quelle durée de session vous est la plus profitable ?",
      sessionInsightLabel: "Enseignement clé",
      sessionInsightTimeBucket: "{pct}% de vos profits sont réalisés dans les {bucket} de la session.",
      sessionInsightMood: "Vos meilleures sessions sont quand vous vous sentez \"{mood}\" (moy. {avg}).",
      sessionInsightDuration: "Les sessions de {dur} sont les plus rentables en moyenne ({avg}).",
      sessionNoData: "Pas assez de données, tradez au moins 3 sessions pour voir ces statistiques.",
      sessionMoodExcellent: "Excellent",
      sessionMoodGood: "Bon",
      sessionMoodNeutral: "Neutre",
      sessionMoodPoor: "Mauvais",
      sessionMoodBad: "Très mauvais",
      outsideSessionTitle: "Trades hors session",
      outsideSessionDesc: "Performance des trades exécutés en dehors d'une session de trading.",
      outsideSessionTrades: "Nombre de trades",
      outsideSessionWinRate: "Win Rate",
      outsideSessionPnl: "PnL total",
      outsideSessionAvgPnl: "PnL moyen",
      outsideSessionProfitFactor: "Profit Factor",
      outsideSessionPctTrades: "% du total des trades",
      outsideSessionPctPnl: "% du PnL total",
      outsideSessionNoSessions: "Aucune session enregistrée, impossible de distinguer les trades hors session.",
      sessionsListNoSessions: "Aucune session enregistrée.",
      sessionsListName: "Nom",
      sessionsListDate: "Date",
      sessionsListDuration: "Durée",
      sessionsListTrades: "Trades",
      sessionsListPnl: "PnL",
      sessionPnlChartLabel: "P&L par session",
      calmarRatio: "Calmar Ratio",
      calmarRatioDesc: "Rendement annualisé / Max Drawdown",
      recoveryFactor: "Recovery Factor",
      recoveryFactorDesc: "P&L total / Max Drawdown, capacité à récupérer",
      extendedStreak: "Analyse des séries étendue",
      drawdownCurveTitle: "Courbe de Drawdown",
      drawdownCurveDesc: "Évolution du drawdown cumulatif dans le temps",
      heatmapTitle: "Heatmap Heure × Jour",
      heatmapDesc: "Performance par tranche horaire et jour de la semaine",
      heatmapPnl: "P&L moyen",
      heatmapWinRate: "Win Rate",
      heatmapMode: "Mode d'affichage",
      heatmapLegendBelow50: "WR < 50%",
      heatmapLegendAbove50: "WR ≥ 50%",
      timeSlotTitle: "P&L par tranche horaire",
      timeSlotDesc: "Vos meilleures et pires fenêtres de trading",
      timeSlotPreMarket: "Pré-marché",
      timeSlotOpen: "Ouverture (9h-11h)",
      timeSlotMidDay: "Milieu de journée (11h-14h)",
      timeSlotAfternoon: "Après-midi (14h-16h)",
      timeSlotPowerHour: "Power Hour (16h+)",
      holdDurationTitle: "Durée de détention des trades",
      holdDurationDesc: "Correlation entre durée du trade et résultat",
      holdDurationX: "Durée (min)",
      holdDurationY: "P&L ($)",
      holdDurationWinRate: "Win Rate par durée de détention",
      holdDurationAvgPnl: "P&L moyen par durée de détention",
      sizingTitle: "Analyse du Sizing",
      sizingDesc: "Évolution de la taille de position dans le temps",
      tagStatsTitle: "Performance par Tag",
      tagStatsDesc: "Win rate, P&L total et moyen par setup/tag",
      noTags: "Aucun tag sur vos trades",
      monthlyStatsTitle: "Tableau mensuel",
      monthlyStatsDesc: "Comparaison mois par mois",
      rollingWinRateTitle: "Rolling Win Rate",
      rollingWinRateDesc: "Win rate glissant sur les N derniers trades",
      rollingWindow: "Fenêtre",
      biasesTitle: "Biais cognitifs",
      biasesDesc: "Détection automatique des biais comportementaux",
      fomoIndicator: "Indicateur FOMO",
      fomoIndicatorDesc: "% de trades pris < 10min après une perte",
      lossAversionRatio: "Aversion aux pertes",
      lossAversionRatioDesc: "Durée moy. losers / durée moy. winners (>1 = on tient trop longtemps les pertes)",
      overconfidenceScore: "Overconfidence",
      overconfidenceScoreDesc: "Après 3+ gains d'affilée, size-up au trade suivant ?",
      revengeTradingCount: "Revenge trading",
      revengeTradingCountDesc: "Trades pris < 30min après une perte",
      noExitDateData: "Les données de durée nécessitent une date de sortie renseignée sur les trades",
      proOnly: "Réservé Pro",
      propFirmAnalytics: {
        categoryTitle: "Analytics Prop Firm",
        invested: "Investi",
        payoutsReceived: "Payouts reçus",
        netResult: "Résultat net",
        profitLabel: "Profit",
        profitFactor: "Profit factor",
        roiGlobal: "ROI global",
        successRate: "Taux de réussite",
        successRateDesc: "PA / fermés",
        successRateByFirm: "Taux de réussite par prop firm",
        bestAccountSize: "Meilleure taille de compte",
        timelineMetrics: "Timeline",
        avgChallengeDuration: "Durée moy. challenge",
        avgPaDuration: "Durée moy. en PA",
        avgTotalDuration: "Durée moy. totale",
        fastestSuccess: "Succès le plus rapide",
        ratios: "Ratios",
        avgMultiplier: "Multiplicateur moyen",
        avgPayoutPerSuccess: "Payout moyen / succès",
        avgCostPerSuccess: "Coût moyen / succès",
        bestFirm: "Meilleures prop firms",
        colFirm: "Prop firm",
        colAccounts: "Comptes",
        colInvested: "Investi",
        colPayouts: "Payouts",
        colNet: "Net",
        colRoi: "ROI",
        colWinRate: "Réussite Challenges",
        colSuccess: "Réussite",
        totalRow: "Total",
        noData: "Aucun compte prop firm dans la sélection actuelle",
        noDataLink: "Aller au Prop Firm Manager",
        days: "j",
        wins: "succès",
        inProgress: "en cours",
        paLabel: "PA",
        unknownFirm: "Inconnu",
        multiplierPrefix: "×",
      },
      payoutsTitle: "Rentabilité",
      payoutsDesc: "ROI prop firm, coûts vs payouts, rentabilité par compte",
      payoutsTotalReceived: "Total payouts",
      payoutsTotalCost: "Total dépenses",
      payoutsRoi: "ROI net",
      payoutsBest: "Meilleur payout",
      payoutsNet: "Résultat net",
      payoutsNetSubtitle: "payouts − dépenses",
      payoutsRoiSubtitle: "retour sur investissement",
      payoutsOverTime: "Payouts & dépenses",
      payoutsChartPayouts: "Payouts cumulés",
      payoutsChartCosts: "Dépenses cumulées",
      payoutsChartNet: "Net cumulé",
      payoutsPerAccount: "Suivi par compte",
      payoutsAccountName: "Compte",
      payoutsChallengeCost: "Challenge",
      payoutsActivationFee: "Activation",
      payoutsLinkedCharges: "Charges liées",
      payoutsReceived: "Payouts reçus",
      payoutsNetPnl: "Net PnL trades",
      payoutsNetResult: "Résultat net",
      payoutsEditAction: "Modifier les infos financières",
      payoutsEditTitle: "Infos financières de {account}",
      payoutsEditDescription: "Coûts, payouts et charges de ce compte, sans repasser par la page Comptes.",
      payoutsEditPayouts: "Payouts",
      payoutsEditNoPayouts: "Aucun payout enregistré.",
      payoutsEditDetected: "Détecté",
      payoutsEditDetectedHint: "Ajouté automatiquement par la synchronisation après une baisse de solde sans trade.",
      payoutsEditMarkAdjustment: "Pas un payout : PnL repris par la prop firm",
      payoutsEditAdjustments: "PnL repris par la prop firm",
      payoutsEditAdjustmentsHint: "Profit retiré du compte par la prop firm (par exemple une infraction à la règle MAE). Compté dans le solde, pas dans vos payouts.",
      payoutsEditMarkPayout: "C'était un payout",
      payoutsEditDeletePayout: "Supprimer ce payout",
      payoutsEditDeleteConfirm: "Supprimer le payout de {amount} ?",
      payoutsEditCharges: "Charges liées",
      payoutsEditSave: "Enregistrer",
      payoutsEditClose: "Fermer",
      payoutsEditSaved: "Infos financières mises à jour",
      payoutsEditError: "Impossible d'enregistrer les modifications",
      payoutsEditInvalidAmount: "Montant invalide",
      payoutsDeleteExpenseAction: "Supprimer la dépense",
      payoutsDeleteExpenseTitle: "Supprimer la dépense ?",
      payoutsDeleteExpenseBody: "Les montants ci-dessous seront retirés de **{account}** et du Prop Firm Manager.",
      payoutsDeleteExpenseTotal: "Total retiré",
      payoutsDeleteExpenseKeepsAccount: "Le compte est conservé, avec ses trades, ses payouts et ses charges liées.",
      payoutsDeleteExpenseCancel: "Annuler",
      payoutsDeleteExpenseConfirm: "Supprimer la dépense",
      payoutsDeleteExpenseDone: "Dépense supprimée",
      payoutsGlobalCharges: "Charges globales",
      payoutsGlobalChargesAnnual: "Coût annualisé",
      payoutsHistory: "Historique des payouts",
      payoutsNoData: "Aucun payout enregistré",
      payoutsPaginationPage: "Page",
      payoutsPaginationOf: "sur",
      payoutsPaginationPrev: "Page précédente",
      payoutsPaginationNext: "Page suivante",
      payoutsNoEligibleAccounts: "Aucun compte funded (PA) avec des payouts",
      payoutsAnnualized: "annualisé",
      payoutsAccountsCount: "comptes",
      pips: {
        sectionTitle: "Analyse des Pips",
        avgPips: "Pips moyen / trade",
        totalPipsWonLost: "Pips gagnés / perdus",
        pipRatio: "Ratio pips W/L",
        bestWorstTrade: "Meilleur / Pire trade",
        winRateByBucket: "Win rate par tranche de pips",
        distribution: "Distribution des pips",
        bySymbol: "Pips par symbole",
        avgPipsCol: "Pips moy.",
        totalPipsCol: "Total pips",
        noData: "Aucun trade avec prix d'entrée et de sortie disponibles.",
        estimatedPipSize: "Taille de pip estimée",
        winRateSeriesLabel: "Win rate %",
        tradesSeriesLabel: "Trades",
        rangeHeader: "Range",
        avgPipsWin: "Pips moy. gagnants",
        avgPipsLoss: "Pips moy. perdants",
      },
      payoutsPerYear: "/an",
      payoutsTotalCharges: "Charges",
      payoutsBreakdown: "Détail des dépenses",
      equityViewGlobal: "Global",
      equityViewByGroup: "Par groupe",
      equityViewByAccount: "Par compte",
      reportTitle: "Rapport de rentabilité",
      reportGranMonth: "Par mois",
      reportGranQuarter: "Par trimestre",
      reportGranYear: "Par année",
      reportColPeriod: "Période",
      reportColPayouts: "Payouts",
      reportColChallengeCost: "Challenges",
      reportColActivationFee: "Activation",
      reportColLinkedCharges: "Charges liées",
      reportColGlobalCharges: "Charges globales",
      reportColTotalCost: "Total coûts",
      reportColNet: "Net",
      reportTotalRow: "Total",
      reportNoData: "Aucune donnée pour la période sélectionnée",
      noData: "Aucune donnée disponible",
      notEnoughSessionData: "Pas assez de données de session",
      biasesfomoDesc: "Trades pris en dehors de votre fenêtre habituelle ou après de longues pauses",
      biasesRevengeDesc: "Trades pris rapidement après une perte",
      biasesLossAversionDesc: "Trades fermés prématurément avant d'atteindre l'objectif",
      biasesOverconfidenceDesc: "Sur-trading après une série de gains",
      chartView: "Vue graphique",
      tableView: "Vue tableau",
      sideLabel: "Côté",
      winRateLabel: "Taux de réussite",
      monthLabel: "Mois",
      tagLabel: "Tag",
    },
    routines: {
      title: "Routines de Trading",
      subtitle: "Créez et suivez vos routines quotidiennes",
      createRoutine: "Créer une Routine",
      editRoutine: "Modifier Routine",
      deleteRoutine: "Supprimer",
      newRoutine: "Nouvelle Routine",
      noRoutines: "Aucune routine créée. Créez votre première routine !",
      createFirst: "Créer une Routine",
      name: "Nom",
      description: "Description",
      frequency: "Fréquence",
      daily: "Quotidienne",
      weekly: "Hebdomadaire",
      monthly: "Mensuelle",
      custom: "Personnalisée",
      checklistItems: "Items de Checklist",
      addItem: "Ajouter un item",
      isActive: "Routine active",
      cancel: "Annuler",
      save: "Enregistrer",
      routineCreated: "Routine créée",
      routineUpdated: "Routine modifiée",
      routineDeleted: "Routine supprimée",
      error: "Erreur",
      atLeastOneItem: "Au moins un item de checklist est requis.",
      cannotCreate: "Impossible de créer la routine.",
      cannotUpdate: "Impossible de modifier la routine.",
      cannotDelete: "Impossible de supprimer la routine.",
      confirmDelete: "Êtes-vous sûr de vouloir supprimer cette routine ?",
      completedToday: "(Complété aujourd'hui)",
      markCompleted: "Marquer {count} item(s) comme complété(s)",
      items: "item(s)",
      lastCompletion: "Dernière complétion",
      completionProgress: "Progression dernière complétion",
      loading: "Chargement...",
      routineCompleted: "Routine complétée",
      heatmapTitle: "Heatmap globale des routines",
      heatmapSubtitle: "Vue d'ensemble de toutes vos complétions de routines",
      month: "Mois",
      quarter: "Trimestre",
      monthNames: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
      monthNamesShort: ["Jan", "Fév", "Mar", "Avr", "Mai", "Jui", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"],
      dayLetters: ["L", "M", "M", "J", "V", "S", "D"],
      excellent: "Excellent",
      good: "Bon",
      partial: "Partiel",
      notDue: "Non dû",
      missed: "Manqué",
      limitReached: "Limite atteinte",
      limitReachedDescription: "Le plan Free est limité à 3 routines. Passez en Pro pour des routines illimitées.",
    },
    analysis: {
      title: "Analyses Fondamentales",
      subtitle: "Documentez vos analyses fondamentales",
      newAnalysis: "Nouvelle Analyse",
      editAnalysis: "Modifier Analyse",
      createFirst: "Créer une Analyse",
      noAnalyses: "Aucune analyse trouvée. Créez votre première analyse !",
      loading: "Chargement...",
      search: "Rechercher",
      clear: "Effacer",
      searchPlaceholder: "Rechercher par titre ou contenu...",
      symbolPlaceholder: "Symbole (ex: AAPL)",
      titleLabel: "Titre",
      contentLabel: "Contenu (Markdown)",
      contentPlaceholder: "# Titre\n\nVotre analyse en markdown...",
      symbolsLabel: "Symboles (séparés par des virgules)",
      symbolsPlaceholder: "AAPL, TSLA, MSFT",
      tagsLabel: "Tags (séparés par des virgules)",
      tagsPlaceholder: "earnings, tech, growth",
      analysisCreated: "Analyse créée",
      analysisUpdated: "Analyse modifiée",
      analysisDeleted: "Analyse supprimée",
      cannotCreate: "Impossible de créer l'analyse.",
      cannotUpdate: "Impossible de modifier l'analyse.",
      cannotDelete: "Impossible de supprimer l'analyse.",
      confirmDelete: "Êtes-vous sûr de vouloir supprimer cette analyse ?",
      editDescription: "Modifiez votre analyse fondamentale",
      createDescription: "Créez une nouvelle analyse fondamentale",
      updatedOn: "Mis à jour le",
      readMore: "Lire la suite",
      collapse: "Réduire",
    },
    goals: {
      title: "Objectifs de Trading",
      subtitle: "Définissez et suivez vos objectifs",
      createGoal: "Créer un Objectif",
      editGoal: "Modifier Objectif",
      deleteGoal: "Supprimer",
      newGoal: "Nouvel Objectif",
      noGoals: "Aucun objectif créé. Créez votre premier objectif !",
      createFirst: "Créer un Objectif",
      goalTitle: "Titre",
      description: "Description",
      type: "Type",
      targetValue: "Valeur Cible",
      deadline: "Date limite",
      pnl: "P&L",
      winRate: "Win Rate",
      tradesCount: "Nombre de Trades",
      custom: "Personnalisé",
      cancel: "Annuler",
      save: "Enregistrer",
      goalCreated: "Objectif créé",
      goalUpdated: "Objectif modifié",
      goalDeleted: "Objectif supprimé",
      error: "Erreur",
      cannotCreate: "Impossible de créer l'objectif.",
      cannotUpdate: "Impossible de modifier l'objectif.",
      cannotDelete: "Impossible de supprimer l'objectif.",
      confirmDelete: "Êtes-vous sûr de vouloir supprimer cet objectif ?",
      progress: "Progression",
      completed: "Terminé",
      notCompleted: "En cours",
      daysRemaining: "jours restants",
      daysOverdue: "jours de retard",
      loading: "Chargement...",
      recurrence: "Récurrence",
      recurrenceNone: "Aucune",
      recurrenceDaily: "Quotidienne",
      recurrenceWeekly: "Hebdomadaire",
      recurrenceMonthly: "Mensuelle",
      badgeDaily: "Quotidien",
      badgeWeekly: "Hebdomadaire",
      badgeMonthly: "Mensuel",
      resetAt: "Réinitialisé le",
      goalCreatedDescription: "L'objectif a été créé avec succès.",
      goalUpdatedDescription: "L'objectif a été modifié avec succès.",
      goalDeletedDescription: "L'objectif a été supprimé avec succès.",
      limitReached: "Limite atteinte",
      limitReachedDescription: "Le plan Free est limité à 3 objectifs. Passez en Pro pour des objectifs illimités.",
      heatmapTitle: "Historique",
      heatmapAchieved: "atteint(s)",
      heatmapOf: "/",
      heatmapDays: "jours",
      heatmapWeeks: "semaines",
      heatmapMonths: "mois",
      heatmapNoData: "Aucun historique disponible",
    },
    onboarding: {
      stepOf: "Étape {current} sur {total}",
      step1Title: "Crée ton premier compte",
      step1Description: "Sans compte, tu ne peux pas lier tes trades à une prop firm ou un compte perso. C'est la base de tout.",
      step1Cta: "Créer mon compte",
      step1Celebration: "Compte créé ! 🎉",
      step2Title: "Enregistre ton premier trade",
      step2Description: "Un journal sans trades, c'est une page blanche. Commence à construire ton historique de trading.",
      step2Cta: "Ajouter un trade",
      step2Celebration: "Premier trade enregistré ! 🚀",
      step3Title: "Ouvre une Session",
      step3Description: "Les sessions te permettent de timer tes journées et analyser tes performances. Sur la page Sessions, clique sur une ligne pour ouvrir son détail, c'est là que ça se passe.",
      step3Cta: "Aller aux Sessions →",
      step3Celebration: "Sessions explorées ! ⏱️",
      step4Title: "Découvre le Prop Firm Manager",
      step4Description: "Gère tes comptes prop firm, suis tes payouts et visualise ta progression. Visite la page pour valider cette étape.",
      step4Cta: "Ouvrir le Prop Firm Manager",
      step4Celebration: "Prop Firm Manager découvert ! 🏆",
      finalTitle: "Tu es prêt à trader avec un vrai edge. 🚀",
      finalCta: "C'est parti !",
      pillExpand: "Reprendre l'onboarding",
    },
    landing: {
      announcement: {
        text: "🎯 Early Bird : Pro Annuel à 144€/an, économisez €36 vs Pro mensuel !",
        cta: "Voir l'offre →",
      },
      hero: {
        title: "Arrête de perdre tes comptes prop firm avec les mêmes erreurs.",
        subtitle: "Edgely analyse chaque trade, révèle tes patterns destructeurs et t'indique exactement quoi corriger pour obtenir des résultats récurrents.",
        ctaPricing: "Voir les Tarifs",
        ctaSignup: "Commencer Gratuitement",
        ctaGetStarted: "Commencer",
        ctaFeatures: "Voir les fonctionnalités",
        badge: "Gratuit · Pas de CB · Upgrade quand tu veux",
      },
      features: {
        title: "Du chaos au système.",
        description: "Les outils qui font la différence entre trader à l'aveugle et trader avec un vrai edge.",
      },
      examples: {
        overline: "Edgely",
        title: "Tout en un seul endroit",
        subtitle: "Net PnL, Profit Factor, Win Rate, calendrier de sessions, une vue complète pour savoir exactement où tu en es et ne plus jamais rater un payout par manque de visibilité.",
        videoCta: "Voir la démo",
        videoAria: "Lancer la vidéo de présentation d'Edgely",
        integrationsTitle: "Intégrations prop firms",
        integrationsSubtitle: "Connectez vos comptes de toutes les prop firms.",
      },
      pricing: {
        title: "Tarifs",
        subtitle: "Choisissez le plan qui correspond à vos besoins.",
        popular: "Populaire",
        seeAllPlans: "Voir tous les plans",
        needMoreInfo: "Besoin de plus d'informations ?",
        freeBadge: "Commence ici",
        trustCopy: "Pas de CB pour le plan gratuit · Résiliation Pro en 1 clic · Paiement sécurisé Stripe",
        earlyAdopterBadge: "Early adopter · 25 places",
        ctaFree: "Démarrer gratuitement →",
        ctaPro: "Démarrer avec Pro →",
        ctaMax: "Passer à Max →",
        disclaimer: "Un seul trade évité grâce à Edgely rembourse 12 mois d'abonnement Pro.",
      },
      testimonials: {
        title: "Témoignages",
      },
      faq: {
        title: "FAQ",
        description: "Tout ce que vous devez savoir sur notre plateforme.",
      },
      footer: {
        product: "Produit",
        company: "Entreprise",
        legal: "Légal",
        resources: "Ressources",
        documentation: "Documentation",
        blog: "Blog",
        about: "À propos",
        privacy: "Confidentialité",
        terms: "Conditions d'utilisation",
        disclosures: "Disclosures",
        affiliation: "Affiliation",
        help: "Aide",
        comparateur: "Comparateur de prop firms",
        copyright: "© {year} Edgely. Tous droits réservés.",
        allRightsReserved: "Tous droits réservés",
        community: "Communauté",
        followUs: "Suivez-nous sur X",
        followInstagram: "Suivez-nous sur Instagram",
        contactSupport: "Support par email",
        joinDiscord: "Rejoindre le Discord",
      },
      featuresList: {
        personalizedJournal: {
          name: "Journal de Trading",
          description: "Chaque trade documenté, chaque erreur tracée. Importez depuis CSV, ajoutez vos notes, vos tags, vos screenshots et construisez un historique que vous pourrez réellement exploiter.",
        },
        advancedAnalytics: {
          name: "Analytics de Trading Avancées",
          description: "Win Rate, Profit Factor, Sharpe Ratio, meilleures plages horaires, patterns par setup... Arrêtez de deviner. Vos données parlent : écoutez-les.",
        },
        disciplineSupport: {
          name: "Discipline & Routines",
          description: "Sessions chronométrées, routines quotidiennes, objectifs mesurables. Parce que la régularité fait la différence entre un bon trader et un trader profitable.",
        },
        propFirmManagement: {
          name: "Prop Firm Manager",
          description: "Gérez vos challenges Apex, FTMO, Topstep et bien d'autres comme un vrai business : statuts, cycles de payout, groupes de comptes. Une vue d'ensemble pour ne jamais perdre le contrôle.",
        },
        tradingSessions: {
          name: "Sessions de Trading",
          description: "Lancez un timer, tradez, puis évaluez votre session : humeur, discipline, performance. Identifiez vos meilleures plages horaires et vos patterns comportementaux sur le long terme.",
        },
        autoSync: {
          name: "Synchronisation Automatique MetaTrader",
          description: "Connectez MetaTrader 4 ou 5 directement à Edgely. Vos trades sont importés automatiquement, sans manipulation manuelle. Disponible pour les membres Max.",
        },
      },
      testimonialsList: {
        alexandre: {
          quote: "Ce journal m'a permis d'identifier mes patterns de trading et d'améliorer mon win rate de 15% en 3 mois. Le suivi des sessions est un game-changer !",
        },
        sophie: {
          quote: "Les analytics sont incroyables. Je peux enfin voir clairement où je dois m'améliorer. Les graphiques TradingView intégrés sont parfaits pour analyser mes trades.",
        },
        marc: {
          quote: "L'interface est intuitive et les routines m'aident à rester discipliné. La heatmap me motive vraiment à compléter mes routines quotidiennes !",
        },
      },
      faqsList: {
        import: {
          question: "Puis-je importer mes trades et comptes existants ?",
          answer: "Oui. La voie la plus simple est la synchronisation automatique de tes trades Tradovate (plan Max, en bêta) : tes exécutions arrivent seules dans ton journal. Sinon, l'import Tradovate et TopstepX en 10 secondes, l'import CSV et la saisie manuelle restent disponibles. La synchro MetaTrader arrive bientôt.",
        },
        metrics: {
          question: "Quelles métriques sont calculées ?",
          answer: "Nous calculons le win rate, profit factor, Sharpe ratio, max drawdown, expectancy et bien plus encore.",
        },
        security: {
          question: "Mes données sont-elles sécurisées ?",
          answer: "Absolument. Toutes vos données sont chiffrées et stockées de manière sécurisée. Nous ne partageons jamais vos informations.",
        },
        export: {
          question: "Puis-je exporter mes données ?",
          answer: "Oui, vous pouvez exporter tous vos trades au format CSV à tout moment.",
        },
        sessions: {
          question: "Comment fonctionnent les sessions de trading ?",
          answer: "edgely détecte automatiquement vos sessions pendant l'ajout de trades. Évaluez votre humeur, votre discipline et vos conditions de marché, ajoutez des notes et même une vidéo YouTube (replay, debriefing). Chaque session est personnalisable et partageable.",
        },
        templates: {
          question: "Puis-je personnaliser mes templates de notes ?",
          answer: "Oui, vous pouvez créer, modifier et supprimer vos propres templates. Un template par défaut est fourni pour vous aider à démarrer.",
        },
        beginner: {
          question: "Je débute en trading, edgely est-il fait pour moi ?",
          answer: "Absolument. edgely est conçu pour tous les niveaux. Pour un débutant, tenir un journal de trading dès le départ est l'une des meilleures habitudes à prendre : vous comprendrez rapidement vos erreurs récurrentes, vos biais émotionnels et les setups qui fonctionnent pour vous. Le plan gratuit est suffisant pour commencer.",
        },
        propFirm: {
          question: "Je trade avec plusieurs prop firms, edgely peut-il gérer ça ?",
          answer: "Oui, c'est justement l'un des points forts d'edgely. Vous pouvez créer autant de comptes que nécessaire (Apex, FTMO, Topstep, etc.), les organiser par groupes, suivre leur statut (challenge, funded, inactif) et visualiser vos drawdowns et payouts en temps réel. Une vue centralisée pour piloter votre business prop firm comme un pro.",
        },
        freePlan: {
          question: "Que comprend le plan gratuit ?",
          answer: "Le plan gratuit inclut 1 compte de trading, des trades illimités, des sessions illimitées, 3 routines et 3 objectifs, ainsi que les analytics de base. C'est largement suffisant pour démarrer et évaluer si edgely correspond à votre façon de trader.",
        },
        multiAccount: {
          question: "Puis-je analyser mes performances sur plusieurs comptes en même temps ?",
          answer: "Oui. Vous pouvez créer des groupes de comptes et visualiser vos performances de manière agrégée. Idéal si vous tradez sur plusieurs comptes prop firm ou si vous séparez vos stratégies par compte.",
        },
        dataLoss: {
          question: "Que se passe-t-il si je supprime un trade ou un compte ?",
          answer: "La suppression est définitive. Nous vous recommandons d'exporter régulièrement vos données au format CSV depuis l'application. Vos données ne sont jamais supprimées automatiquement et restent disponibles tant que votre compte est actif.",
        },
      },
      brokers: {
        title: "Intégrations & Brokers Supportés",
        description: "Importez vos trades depuis vos plateformes préférées. La synchronisation automatique arrive bientôt.",
        colBroker: "Broker / Plateforme",
        colMarkets: "Marchés",
        colManualImport: "Import Manuel",
        colCsvUpload: "CSV & Copier-Coller",
        colAutoSync: "Auto Sync",
      },
      freePlan: {
        overline: "Intégrations",
        title: "Toutes tes prop firms, un seul journal.",
        subtitle: "Apex, TopStep, FundedNext, MyFundedFutures et bien d'autres : centralise tous tes comptes prop firm dans Edgely et analyse l'ensemble de tes performances au même endroit.",
        badge: "Compatible avec les principales prop firms",
        trades: { value: "∞", label: "trades journalisés" },
        sessions: { value: "∞", label: "sessions" },
        routines: { value: "3", label: "routines actives" },
        analytics: { value: "∞", label: "analytics de base" },
        accounts: { value: "1", label: "compte de trading" },
        cta: "Connecter mes comptes gratuitement →",
        brokersImportTitle: "Import en 10 secondes",
        brokersSyncTitle: "Synchronisation automatique",
        comingSoon: "Bientôt",
      },
      howItWorks: {
        overline: "Comment ça marche",
        title: "Passer au niveau supérieur dans son trading. En trois étapes.",
        subtitle: "Connecte tes comptes, analyse tes patterns, corrige ce qui te coûte des payouts.",
        step1: {
          badge: "Connecte",
          title: "Tous tes comptes. Un seul endroit.",
          description: "Importe tes trades en 10 secondes.",
          statusConnected: "Connecté",
          statusSyncing: "Sync en cours…",
        },
        step2: {
          badge: "Analyse",
          title: "Tes patterns. Tes biais. Tes forces.",
          description: "Edgely détecte automatiquement tes setups gagnants, tes horaires de perte, tes biais cognitifs. Tu vois enfin ce que tu ne voyais pas.",
          winRate: "Win Rate",
          profitFactor: "Profit Factor",
          biasRevenge: "Revenge trading détecté",
          biasBestHour: "Best hour : 15h30",
          biasAvg: "NQ scalp +$53 avg",
          biasOvertrading: "Overtrading vendredi",
        },
        step3: {
          badge: "Performe",
          title: "Corrige. Progresse. Atteins de nouveaux sommets.",
          description: "Applique les insights d'Edgely session après session. Tes métriques deviennent constantes. Tu passes à un niveau supérieur dans ton trading. Tu obtiens un nouveau niveau de résultats.",
          payoutLabel: "Payout validé, Apex 100K",
        },
      },
      ctaFinal: {
        title: "Ton journal de trading t'attend.",
        cta: "Commencer gratuitement",
        badge: "Sans CB · Sans engagement",
      },
    },
    templates: {
      noTemplate: "Aucun template",
      manage: "Gérer",
      createTemplate: "Créer un template",
      edit: "Modifier",
      delete: "Supprimer",
      selectTemplate: "Sélectionner un template",
      default: "par défaut",
      templateCreated: "Template créé",
      templateUpdated: "Template modifié",
      templateDeleted: "Template supprimé",
      cannotCreate: "Impossible de créer le template.",
      cannotUpdate: "Impossible de modifier le template.",
      cannotDelete: "Impossible de supprimer le template.",
      confirmDelete: "Êtes-vous sûr de vouloir supprimer ce template ?",
      createDescription: "Créez un template personnalisé pour vos notes de trade",
      editTemplate: "Modifier le Template",
      editDescription: "Modifiez votre template personnalisé",
      nameLabel: "Nom du template",
      namePlaceholder: "Ex: Template de base",
      contentLabel: "Contenu (Markdown)",
      contentPlaceholder: "# Titre\n\nVotre template en markdown...",
      setAsDefault: "Définir comme template par défaut",
      systemDefaultNote: "Ce template par défaut du système ne peut pas être supprimé, mais vous pouvez le modifier.",
      create: "Créer",
      loading: "Chargement...",
    },
    markdownEditor: {
      placeholder: "Écrivez vos notes en Markdown...\n\nExemples:\n# Titre\n**Gras** *Italique*\n- Liste\n![Image](url)\n\nAstuce: Vous pouvez coller des images directement depuis le presse-papiers !",
      helpText: "Astuce: Utilisez la barre d'outils ci-dessus pour formater votre texte. Markdown supporté: titres, gras, italique, listes, liens, images.",
      titlePlaceholder: "Titre",
      boldPlaceholder: "gras",
      italicPlaceholder: "italique",
      underlinePlaceholder: "souligné",
      listItemPlaceholder: "élément de liste",
      heading1: "Titre 1",
      heading2: "Titre 2",
      heading3: "Titre 3",
      bold: "Gras",
      italic: "Italique",
      underline: "Souligné",
      list: "Liste",
      link: "Lien",
      image: "Image",
      edit: "Éditer",
      preview: "Aperçu",
      noContent: "Aucun contenu",
      imageUrlPrompt: "URL de l'image (vous pouvez aussi coller une image depuis le presse-papiers):",
      imageAltPrompt: "Texte alternatif (optionnel):",
      linkUrlPrompt: "URL du lien:",
      linkTextPrompt: "Texte du lien:",
      pastedImage: "Image collée",
    },
    propFirmManager: {
      title: "Prop Firm Manager",
      subtitle: "Rentabilité de vos comptes prop firm",
      totalPayouts: "Total Payouts",
      totalExpenses: "Total Dépenses",
      netProfit: "Net Profit",
      roiGlobal: "ROI Global",
      activeAccounts: "Comptes actifs",
      bestRoi: "Meilleur ROI",
      noBestRoi: "",
      totalAccounts: "Total comptes",
      accountsWithPayout: "Avec payout(s)",
      successRate: "Taux de succès",
      calendarPayout: "Payout",
      calendarExpense: "Dépense",
      calendarRecurring: "Charge récurrente",
      calendarTooltipPayouts: "Payouts",
      calendarTooltipExpenses: "Dépenses",
      calendarTooltipRecurring: "Charges récurrentes",
      calendarEventSingular: "événement",
      calendarEventPlural: "événements",
      calendarFunded: "Funded",
      calendarTooltipFunded: "Passages en funded",
      calendarReset: "Reset",
      calendarTooltipReset: "Comptes resetés",
      noEventsThisMonth: "Aucun événement ce mois",
      eventsThisMonth: "Événements du mois",
      proRequired: "Fonctionnalité Pro",
      monthRecapTitle: "Récap du mois",
      monthRecapChallenges: "Challenges",
      monthRecapActivations: "Activations",
      monthRecapSubscriptions: "Abonnements",
      monthRecapPayouts: "Payouts",
      monthRecapTotalExpenses: "Total dépenses",
      monthRecapRoi: "ROI du mois",
      monthRecapNoData: "Aucun événement ce mois",
      importHistory: "Importer historique (CSV)",
      importModalTitle: "Importer l'historique des comptes",
      importModalDesc: "Importez vos comptes prop firm inactifs et leurs payouts en une seule opération. Une ligne = un compte.",
      downloadTemplate: "Télécharger le template CSV",
      importBtn: "Importer",
      importSuccess: "Import terminé",
      noValidRows: "Aucune ligne valide détectée dans le fichier.",
      importDropZone: "Glissez un fichier CSV ou cliquez pour sélectionner",
      importParseError: "Erreur",
      importParseErrorDesc: "Impossible de lire le fichier CSV.",
      importWrongFormatTitle: "Ce fichier n'est pas un export de comptes prop firm",
      importWrongFormatDesc:
        "Cet import attend la liste de tes comptes prop firm (propfirm, nom_compte, tiers, taille_compte…), pas des trades. Pour importer des trades, passe par Journal → Importer CSV. Tu peux aussi télécharger le modèle ci-dessous.",
      importFailError: "Erreur",
      importFailErrorDesc: "L'import a échoué.",
      importAccountsToCreate: "Comptes à créer",
      importConflicts: "Conflits détectés",
      importConflictSkip: "Ignorer",
      importConflictUpdate: "Mettre à jour",
      importConflictEventsWarning: "Les payouts liés seront ignorés si vous choisissez Ignorer",
      importPayoutsSection: "Payouts",
      importSummaryLine: "{accounts} compte(s) à créer, {conflicts} conflit(s), {payouts} payout(s), {errors} erreur(s)",
      importSuccessAccounts: "{created} créé(s), {updated} mis à jour, {skipped} ignoré(s), {payouts} payouts importés",
      importBlockedTitle: "Import impossible",
      importBlockedDesc: "Corrige les erreurs suivantes et réimporte le fichier.",
      importFixFile: "Corriger le fichier",
      importSkippedRows: "Lignes ignorées",
      importErrorLine: "Ligne",
      importReferenceTitle: "Valeurs acceptées",
      importRefPropFirm: "Prop firm",
      importRefAccountType: "Tiers",
      importReferenceSizes: "Tailles",
      importRefStrategies: "Stratégies existantes",
      importRefNoStrategy: "Aucune stratégie créée",
      addPayoutBtn: "Ajouter un payout",
      addPayoutModalTitle: "Ajouter des payouts",
      addPayoutLine: "Ajouter une ligne",
      addPayoutSave: "Enregistrer ({n} payout(s))",
      addPayoutSuccess: "{n} payout(s) ajouté(s)",
      addPayoutSuccessWithCertFailures: "{n} payout(s) ajoutés. {f} certificat(s) en échec.",
      addPayoutAccount: "Compte",
      addPayoutDate: "Date",
      addPayoutAmount: "Montant (USD)",
      addPayoutGrossAmount: "Montant brut",
      addPayoutSplitPercent: "Profit split (%)",
      addPayoutNetAmount: "Montant net reçu",
      addPayoutCertificate: "Certificat (optionnel)",
      addPayoutCertificatePick: "Choisir un fichier",
      addPayoutNoActiveAccounts: "Aucun compte actif disponible",
      recentPayoutsTitle: "Payouts récents",
      recentPayoutsEmpty: "Aucun payout pour le moment.",
      recentPayoutsHeaderDate: "Date",
      recentPayoutsHeaderAccount: "Compte",
      recentPayoutsHeaderAmount: "Montant",
      recentPayoutsHeaderActions: "Actions",
      wallet: {
        title: "Wallet TakeProfitTrader",
        availableBalance: "Solde disponible",
        initialBalance: "Solde initial",
        transfersReceived: "Transferts reçus",
        withdrawn: "Retraits effectués",
        inFlight: "En cours",
        setInitialBalance: "Définir un solde initial",
        editInitialBalance: "Modifier le solde initial",
        initialBalanceBannerTitle: "Tu as un historique TPT existant ?",
        initialBalanceBannerDesc: "Saisis un solde initial pour que le solde soit juste.",
        ignore: "Ignorer",
        add: "Ajouter",
        transferTab: "Transfert depuis un compte",
        withdrawalTab: "Retrait vers l'externe",
        withdrawalTabDesc: "Enregistre un retrait du wallet vers ton compte externe (banque, PayPal, Wise…).",
        transfersTableTitle: "TPT - Transferts (compte → wallet)",
        withdrawalsTableTitle: "TPT - Retraits (wallet → externe)",
        colDate: "Date",
        colAccount: "Compte",
        colGross: "Brut",
        colSplit: "Split",
        colNet: "Net",
        colAmount: "Montant",
        colMethod: "Méthode",
        colStatus: "Statut",
        colActions: "Actions",
        statusPending: "En attente",
        statusApproved: "Approuvé",
        statusRejected: "Rejeté",
        statusCompleted: "Complété",
        detectedTag: "détecté",
        detectedTitle: "Détecté par la synchronisation : vérifiez la part reçue puis passez le transfert en Approuvé.",
        splitEditLabel: "Part reçue (%)",
        accountLabel: "Compte source",
        dateLabel: "Date",
        grossAmountLabel: "Montant brut",
        splitPercentLabel: "Split (%)",
        netAmountLabel: "Montant net",
        amountLabel: "Montant",
        methodLabel: "Méthode de paiement",
        methodPlaceholder: "ex : Wise, PayPal, ACH",
        notesLabel: "Notes",
        notesPlaceholder: "Optionnel",
        initialBalanceLabel: "Solde initial",
        initialBalanceDateLabel: "Date de référence",
        saveInitialBalance: "Enregistrer",
        transferAdded: "Transfert ajouté",
        withdrawalAdded: "Retrait ajouté",
        transferDeleted: "Transfert supprimé",
        withdrawalDeleted: "Retrait supprimé",
        statusUpdated: "Statut mis à jour",
        noTransfers: "Aucun transfert",
        noWithdrawals: "Aucun retrait",
        deleteTransferConfirm: "Supprimer ce transfert ?",
        deleteWithdrawalConfirm: "Supprimer ce retrait ?",
        emptyTitle: "Wallet TakeProfitTrader",
        emptyDesc: "Ce widget affiche le portefeuille TPT (transferts et retraits). Ajoute un compte TakeProfitTrader pour l'activer.",
      },
    },
    attachments: {
      sectionTitle: "Documents",
      empty: "Aucun document pour ce compte.",
      uploadButton: "Ajouter un document",
      uploadDialogTitle: "Nouveau document",
      uploadDialogDescription:
        "Certificat de compte, certificat de payout, facture… PDF, PNG ou JPG. 5 Mo maximum.",
      fieldName: "Nom",
      fieldType: "Type",
      fieldFile: "Fichier",
      fieldLinkedPayout: "Payout associé (optionnel)",
      fieldLinkedPayoutNone: "Aucun",
      typeChallenge: "Certificat de compte",
      typePayout: "Certificat de payout",
      typeInvoice: "Facture",
      typeOther: "Autre",
      view: "Voir",
      download: "Télécharger",
      delete: "Supprimer",
      deleteConfirm: "Supprimer ce document ?",
      limitReached: "Limite atteinte (3/3). Passez en Pro pour des documents illimités.",
      errorUpload: "Erreur lors de l'envoi.",
      errorMimeRejected: "Format non autorisé. PDF, PNG ou JPG uniquement.",
      errorTooLarge: "Fichier trop volumineux (max 5 Mo).",
      errorPayoutTaken: "Ce payout a déjà un certificat.",
      uploading: "Envoi en cours…",
      certificateBadge: "Certificat validé",
      addCertificate: "Ajouter certificat",
    },
    reports: {
      shareButton: "Partager",
      dialogTitle: "Générer un rapport partageable",
      dialogDescription: "Choisis une période et un format. Ton rapport sera prêt à publier sur Instagram, X ou WhatsApp.",
      period: "Période",
      periodDaily: "Quotidien",
      periodWeekly: "Hebdomadaire",
      periodMonthly: "Mensuel",
      format: "Format",
      formatStory: "Story (vertical)",
      formatCarousel: "Carrousel (jusqu'à 7 slides)",
      generate: "Générer",
      generating: "Génération en cours…",
      copyLink: "Copier le lien",
      copied: "Lien copié !",
      download: "Télécharger",
      downloadAll: "Tout télécharger (ZIP)",
      saveToPhotos: "Enregistrer dans la pellicule",
      saving: "Enregistrement…",
      share: "Partager",
      openInstagram: "Publier sur Instagram",
      openInstagramDesktopHint: "Télécharge l'image et publie depuis ton téléphone.",
      openTiktok: "Publier sur TikTok",
      openX: "Publier sur X",
      socialDesktopHint: "Image téléchargée. Ouvre l'app sur ton téléphone pour publier.",
      publicCta: "Crée ton journal de trading sur edgely.",
      publicCtaButton: "Commencer gratuitement",
      publicLoading: "Chargement…",
      publicNotFound: "Rapport introuvable.",
      error: "Une erreur est survenue. Réessaie.",
      pageTitle: "Rapports",
      pageSubtitle: "Génère et partage des rapports illustrés de tes performances.",
      generateCta: "Générer un rapport",
      emptyState: "Aucun rapport généré pour le moment.",
      emptyStateCta: "Génère ton premier rapport",
      thumbnailAlt: "Aperçu du rapport",
      viewReport: "Voir le rapport",
      reportDetailTitle: "Ton rapport",
      filterLabel: "Filtres",
      formatPdfComingSoon: "PDF",
      formatComingSoonBadge: "Bientôt",
      scopeLabel: "Filtre",
      scopeAll: "Tous les comptes",
      scopeAllInactive: "Tous les comptes + inactifs",
      scopeAccount: "Compte",
      scopeGroup: "Groupe",
      scopePropfirm: "Prop firm",
      scopeStrategy: "Stratégie",
      scopePickAccount: "Choisis un compte",
      scopePickGroup: "Choisis un groupe",
      scopePickPropfirm: "Choisis une prop firm",
      scopePickStrategy: "Choisis une stratégie",
      sectionsLabel: "Contenu inclus",
      sectionTrading: "Trading",
      sectionPropfirm: "Prop firm",
      sectionAnalytics: "Analytics",
      sectionsRequired: "Coche au moins une section",
      periodQuarterly: "Trimestriel",
      periodAnnual: "Annuel",
      deleteAriaLabel: "Supprimer le rapport",
      periodNavPrev: "Période précédente",
      periodNavNext: "Période suivante",
      deleteConfirm: "Supprimer ce rapport ?",

      detailTitle: "Rapport",
      detailGeneratedOn: "Généré le",
      detailShare: "Partager",
      detailSharing: "Génération de l'image...",
      detailBackToList: "Retour aux rapports",
      detailEmptySection: "Aucune donnée sur cette période",
      detailLegacyNotice: "Ce rapport a été généré avant la refonte : certaines sections ne sont pas disponibles.",

      summaryTitle: "Bilan",
      summaryNetPnl: "PnL net",
      summaryTrades: "Trades",
      summaryDaysTraded: "Jours tradés",
      summaryAvgPnlPerDay: "PnL moyen par jour",
      summaryOutOfDays: "sur {count} jours ouvrés",

      lifecycleTitle: "Comptes",
      lifecycleAdded: "Ajoutés",
      lifecycleBlown: "Perdus",
      lifecycleFunded: "Passés funded",
      lifecycleLowConfidence: "+ {count} probablement perdus (clôture sans drawdown constaté)",
      lifecycleNoEvents: "Aucun mouvement de compte sur cette période",
      lifecycleUnlinked: "challenge non rattaché",
      lifecycleOutcomesTitle: "Devenir des {count} comptes ajoutés",
      lifecycleOutcomeBlown: "Perdus",
      lifecycleOutcomeLikelyBlown: "Probablement perdus",
      lifecycleOutcomePassed: "Réussis",
      lifecycleOutcomeActive: "En cours",

      payoutsTitle: "Payouts",
      payoutsTotal: "Total",
      payoutsCount: "Nombre",
      payoutsBest: "Meilleur payout",
      payoutsNone: "Aucun payout sur cette période",
      payoutsNoPropFirm: "Sans prop firm",
      financeTitle: "Bilan prop firm",
      financeSpent: "Dépensé",
      financePayouts: "Reçu en payouts",
      financeNet: "Net",
      financeRoi: "ROI net",
      financeRoiNone: "Aucune dépense sur la période",

      advancementTitle: "Avancement des challenges",
      advancementTarget: "Objectif",
      advancementDays: "Jours tradés",
      advancementComplete: "Objectif atteint",
      advancementNone: "Aucun challenge en cours",
    },
    dateRangePicker: {
      allTime: "Tout",
      today: "Aujourd'hui",
      thisWeek: "Cette semaine",
      thisMonth: "Ce mois",
      last30Days: "30 derniers jours",
      lastMonth: "Mois dernier",
      thisQuarter: "Ce trimestre",
      ytd: "Depuis le 1er janv.",
      custom: "Personnalisé",
    },
    pages: {
      loadErrorTitle: "Impossible de charger cette page",
      loadErrorDescription: "La connexion au serveur a échoué. Ta page n'est pas perdue.",
      loadErrorRetry: "Réessayer",
      manage: "Mes pages",
      newPage: "Nouvelle page",
      pageName: "Nom de la page",
      pageIcon: "Icône",
      cancel: "Annuler",
      create: "Créer",
      noPages: "Aucune page personnalisée. Crée ta première page !",
      deleteConfirm: "Supprimer cette page ?",
      edit: "Modifier",
      emptyPage: "Cette page est vide.",
      addFirstWidget: "Ajouter un widget",
      topbarSection: "Topbar",
      topbarAdd: "+ Topbar",
      topbarFilters: "Filtres",
      topbarFiltersTitle: "Filtres",
      pinAsDefault: "Épingler comme défaut",
      pinAsDefaultConfirm: "Valeur par défaut sauvegardée",
      unpinDefault: "Filtre par défaut retiré",
      topbarAddPayout: "Ajouter un payout",
    },
    marketDataWidgets: {
      categoryLabel: 'News & Market Data',
      newsFeed: {
        title: 'News',
        noNews: 'Aucune news disponible',
        error: 'Impossible de charger les news',
        retry: 'Réessayer',
        showMore: 'Voir {count} news de plus',
        justNow: "À l'instant",
        sentimentPositive: 'Positif',
        sentimentNegative: 'Négatif',
        sentimentNeutral: 'Neutre',
      },
      interestRates: {
        title: 'Taux directeurs',
        lastUpdate: 'Mis à jour',
        nextMeeting: 'Prochaine réunion',
        consensusHold: 'Hold',
        consensusCut: 'Baisse',
        consensusHike: 'Hausse',
      },
      economicCalendar: {
        title: 'Calendrier économique',
        actual: 'Actuel',
        forecast: 'Prévision',
        previous: 'Précédent',
        noEvents: 'Aucun événement ce jour',
        filterHigh: 'High',
        filterMedium: 'Medium',
        filterLow: 'Low',
      },
      earningsCalendar: {
        title: 'Earnings',
        noEarnings: 'Aucun earnings ce jour',
        timingBmo: 'Avant ouverture',
        timingAmc: 'Après clôture',
        timingDmh: 'Pendant séance',
        guidanceRaised: 'Relevé',
        guidanceLowered: 'Abaissé',
        guidanceMaintained: 'Maintenu',
        colSymbol: 'Symbole',
        colCompany: 'Entreprise',
        colTiming: 'Timing',
        colEpsEstimate: 'EPS Est.',
        colEpsActual: 'EPS',
        colEpsSurprise: 'Surp. EPS',
        colRevenueEstimate: 'Rev. Est.',
        colRevenueActual: 'Revenue',
        colRevenueSurprise: 'Surp. Rev.',
        colPriceReaction: 'Réaction',
        colGuidance: 'Guidance',
        configureColumns: 'Colonnes',
      },
    },
    chart: {
      symbol: "Symbole",
      invalidSymbol: "Symbole invalide",
      unavailable: "Graphique momentanément indisponible",
      reload: "Recharger",
      noData: "Aucune donnée disponible",
      noTradesForSymbol: "Aucun trade pour ce symbole dans les filtres actifs",
      showTrades: "Afficher les trades",
      widgetTitle: "Graphique",
    },
    marketing: {
      journalPage: {
        heroTitle: "Journal de trading : suivez, analysez et améliorez chaque trade",
        heroSubtitle:
          "Le journal en français des prop traders futures. Importez vos trades, mesurez votre edge, passez au funded.",
        heroCta: "Créer mon journal gratuitement",
        statsWinRate: "Win rate",
        statsProfitFactor: "Profit factor",
        statsDrawdown: "Drawdown",
        pillarsTitle: "Un journal complet, pensé pour progresser",
        pillars: [
          { title: "Journalisation", body: "Setup, résultat, notes et screenshots pour chaque trade." },
          { title: "Analytics avancés", body: "Win rate, profit factor, R/R, drawdown et courbe d'équité automatiques." },
          { title: "Sessions & routines", body: "Préparez avant marché, débriefez à froid, reliez mental et résultats." },
          { title: "Objectifs", body: "Fixez et suivez vos objectifs de progression, période après période." },
          { title: "Prop Firm Manager", body: "Multi-comptes, drawdown par firme, alertes et payouts au même endroit." },
          { title: "Mode mentor", body: "Les coachs suivent le journal de leurs élèves selon les permissions." },
        ],
        importTitle: "Importez vos trades en quelques secondes",
        importSteps: [
          "Sync automatique Tradovate (plan Max, bêta)",
          "Copier-coller Tradovate",
          "Import CSV (Tradovate / TopStepX)",
          "Saisie manuelle",
          "Sync MetaTrader MT4/MT5 (bientôt)",
        ],
        whyTitle: "Pourquoi tenir un journal de trading ?",
        whyBody:
          "Pour identifier votre edge, corriger vos erreurs récurrentes et gérer votre psychologie. Un journal transforme des séances isolées en une trajectoire de progression mesurable.",
        toolsTitle: "Excel, papier ou application ?",
        toolsExcel: "Excel : calcule, mais saisie fastidieuse et formules qui cassent.",
        toolsPaper: "Papier : ne calcule rien et se perd.",
        toolsApp: "Edgely : import auto, calculs fiables, règles prop firm gérées.",
        pricingTitle: "Combien coûte un journal de trading ?",
        pricingFree: "Free : 0€, un compte, trades illimités, sans carte bancaire.",
        pricingPro: "Pro : 15€/mois, comptes illimités, Prop Firm Manager, analytics avancés.",
        pricingMax: "Max : 29€/mois, tout le Pro plus la sync automatique Tradovate (bêta), MetaTrader bientôt.",
        faqTitle: "Questions fréquentes",
        faq: [
          { q: "Comment faire un journal de trading ?", a: "Notez chaque trade dès sa clôture (instrument, sens, prix, résultat, setup, notes). Edgely importe vos trades et calcule vos statistiques automatiquement." },
          { q: "Quel journal de trading gratuit choisir ?", a: "Le plan gratuit d'Edgely journalise des trades illimités sur un compte, sans carte bancaire, avec win rate et profit factor automatiques." },
          { q: "Que noter dans un journal de trading ?", a: "L'instrument, le sens, les prix, le résultat, mais surtout le contexte : setup, état mental et erreurs. Ce sont ces notes qui révèlent votre edge." },
          { q: "Existe-t-il un journal de trading en français ?", a: "Oui, Edgely est entièrement en français, pensé pour les prop traders futures francophones." },
          { q: "Quelle différence entre Excel et une application ?", a: "Edgely importe vos trades automatiquement, calcule sans erreur et gère vos règles de drawdown, là où Excel demande une saisie manuelle et des formules fragiles." },
        ],
        ctaTitle: "Prêt à aller chercher votre premier payout ?",
        ctaButton: "Créer mon journal de trading gratuitement",
      },
      nav: {
        features: "Fonctionnalités",
        forWho: "Pour qui ?",
        pricing: "Tarifs",
        comparateur: "Comparateur",
        blog: "Blog",
        login: "Connexion",
        ctaStart: "Commencer gratuitement",
        megaFeatures: [
          { key: "journal", label: "Journal de Trading", desc: "Journalisez et analysez chaque trade" },
          { key: "propFirmManager", label: "Prop Firm Manager", desc: "Pilotez tous vos comptes prop firms" },
          { key: "analytics", label: "Analytics Avancées", desc: "Vos patterns, vos forces, vos fuites" },
          { key: "sessions", label: "Sessions de Trading", desc: "Revoyez et évaluez chaque session" },
          { key: "mentor", label: "Mode Mentor", desc: "Suivez vos élèves avec leurs vraies données" },
          { key: "dashboard", label: "Dashboard", desc: "Votre espace, vos règles" },
          { key: "integrations", label: "Intégrations", desc: "Sync auto et import CSV de tes trades" },
        ],
        megaSolutions: [
          { key: "debutant", label: "Prop Trader Débutant", desc: "Commence sans risque, progresse vite" },
          { key: "multiComptes", label: "Trader Multi-Comptes", desc: "Un dashboard, toutes tes prop firms" },
          { key: "coach", label: "Coach & Mentor", desc: "Accède aux dashboards de tes élèves" },
        ],
      },
      common: {
        ctaFinalTitle: "Prêt à passer au niveau supérieur ?",
        ctaFinalButton: "Commencer gratuitement",
        whyTitle: "Pourquoi les prop traders choisissent Edgely",
        relatedTitle: "Pour aller plus loin",
        criteria: [
          "Conçu spécifiquement pour les prop firms",
          "Prop Firm Manager intégré",
          "Plan gratuit sans CB",
          "Import CSV Tradovate / TopStepX",
          "Analytics avancées",
          "Mode Mentor",
          "Dashboard modulaire personnalisable",
          "Interface en français",
          "Support communautaire inclus",
          "Prix de départ",
        ],
        yes: "Oui",
        no: "Non",
      },
      features: {
        journal: {
          h1: "Le journal de trading conçu pour les prop traders.",
          subtitle:
            "Chaque trade documenté, chaque erreur tracée. Importez depuis Tradovate ou TopStepX, ajoutez vos notes, vos tags, vos screenshots - et construisez un historique que vous pourrez réellement exploiter.",
          bullets: [
            "Import broker : depuis Tradovate et TopStepX en quelques secondes",
            "Ajout manuel : disponible pour tous les brokers",
            "Contexte par trade : notes, tags et screenshots",
            "Erreurs taguées : visibles directement sur le calendrier",
            "Historique complet : filtrable par compte, stratégie, période",
            "Calculs automatiques : PNL et commissions calculés à l'import",
          ],
        },
        propFirmManager: {
          h1: "Un seul endroit pour piloter toutes tes prop firms.",
          subtitle:
            "Fini les tableaux Excel pour suivre tes challenges. Le Prop Firm Manager centralise tous tes comptes, tes payouts et tes dépenses pour que tu gardes le contrôle à tout moment.",
          bullets: [
            "Vue globale : total payouts, total dépenses, profit net, ROI global",
            "Calendrier : achats de challenges et charges mensuelles",
            "Récapitulatif mensuel : frais de challenge, activation, abonnements",
            "Suivi des payouts : date, montant, profit split et certificat",
            "Statut des comptes : actifs et inactifs (challenges cramés conservés)",
            "Compatible : FTMO, Apex, TopStep, Lucid, FundedNext, MyFundedFutures, TakeProfitTrader, Tradeify, Phidias, Funded Futures Family",
          ],
        },
        analytics: {
          h1: "Tes données de trading parlent. Écoute-les.",
          subtitle:
            "Arrête de deviner pourquoi tu perds. Edgely analyse chaque variable de ton trading et te montre exactement où se cachent tes patterns destructeurs - et tes vraies forces.",
          bullets: [
            "Métriques clés : PNL net, Win Rate, Profit Factor, ratio de Sharpe",
            "Timing : performances par jour de la semaine et par plage horaire",
            "Setups : analyse par durée de trade et par type de setup",
            "Courbes : distribution du PNL, drawdown, equity curve",
            "Psychologie & tags : corrélation état mental / performance",
            "Long terme : sizing et rolling Win Rate sur la durée",
            "Analyse par prop firm : compare tes performances entre comptes",
          ],
        },
        sessions: {
          h1: "Chaque session compte. Revois-la, améliore-la.",
          subtitle:
            "Une session non reviewée est une session perdue. Edgely te donne tous les outils pour analyser ce qui s'est passé, comprendre pourquoi, et ne plus répéter les mêmes erreurs.",
          bullets: [
            "Création auto : sessions générées à partir de tes trades importés",
            "Timer intégré : chronométre ta session en live",
            "Prise de notes : notes générales et notes par trade individuel",
            "État mental : évaluation de l'humeur et de l'état de la session",
            "Tags sur le calendrier : erreurs et stratégies visibles au mois",
            "Vidéo YouTube : enregistre ta session et attache-la",
            "Replay TradingView : revois tes trades directement dans Edgely",
          ],
        },
        mentor: {
          h1: "Coache tes élèves avec leurs vraies données.",
          subtitle:
            "Plus besoin que tes élèves te fassent des screenshots. Accède directement à leur dashboard Edgely et vois exactement ce qu'ils font - et ce qu'ils doivent corriger.",
          bullets: [
            "Dashboard élève : accès complet depuis ton compte",
            "Temps réel : PNL, sessions, statistiques détaillées",
            "Notes ciblées : ajoute des notes sur les sessions de l'élève",
            "Vidéos de session : visionne les replays enregistrés par l'élève",
            "Pour les coachs : idéal pour indépendants et écoles de prop trading",
            "Inclus : disponible à partir du Plan Pro, sans surcoût",
          ],
        },
        dashboard: {
          h1: "Ton dashboard, tes règles.",
          subtitle:
            "Edgely fonctionne comme Notion : tu crées tes pages, tu choisis tes widgets, tu organises ton espace exactement comme tu travailles. Pas l'inverse.",
          bullets: [
            "Pages multiples : une page par prop firm, par stratégie ou par période",
            "Widgets au choix : PNL, Profit Factor, Equity Curve, calendrier éco, news, résultats",
            "Layouts prédéfinis : Dashboard par défaut, Dashboard Pro, Market Data",
            "Filtres sauvegardables : par prop firm, stratégie, période, compte actif/inactif",
            "Personnalisation complète : déplace, redimensionne, supprime chaque widget",
            "Vues enregistrées : switche en un clic entre tes contextes",
          ],
        },
        integrations: {
          h1: "Intégrations",
          subtitle:
            "Edgely se connecte à tes plateformes pour alimenter ton journal, en synchronisation automatique ou par import CSV.",
          autoSyncTitle: "Synchronisation automatique",
          autoSyncCards: [
            {
              title: "NinjaTrader & Tradovate",
              badge: "Bêta",
              body: "Synchronisation directe de tes exécutions via une connexion sécurisée en lecture seule. En version bêta sur le plan Max. En attendant, l'import manuel est disponible dès aujourd'hui (voir ci-dessous).",
            },
            {
              title: "MetaAPI - MT4 & MT5",
              badge: "Bientôt",
              body: "Connecte un compte MetaTrader 4 ou 5, live ou prop, et synchronise tes exécutions automatiquement dans ton journal. Disponible prochainement sur le plan Max.",
            },
          ],
          csvTitle: "Imports CSV & copié collé",
          csvIntro: "Importe ton historique de trades depuis :",
          csvItems: ["Tradovate", "TopstepX"],
        },
      },
      solutions: {
        debutant: {
          h1: "Apprends tes habitudes. Évite potentiellement les erreurs coûteuses.",
          subtitle:
            "La plupart des traders débutants perdent leurs premiers comptes pour les mêmes raisons. Edgely te les montre avant que ça te coûte trop cher.",
          bullets: [
            "Plan gratuit sans carte bancaire pour commencer immédiatement",
            "Onboarding en 4 étapes : compte → prop firm → premier trade → analytics",
            "Analytics de base suffisantes pour identifier ses premiers patterns",
            "1 compte de trading, trades illimités, sessions illimitées",
            "Support communautaire Discord inclus",
            "Upgrade vers Pro quand tu es prêt, en un clic",
          ],
        },
        multiComptes: {
          h1: "Un tableau de bord. Toutes tes prop firms.",
          subtitle:
            "Quand tu trades sur 3, 5 ou 10 comptes simultanément, la complexité devient ton ennemi. Edgely la transforme en avantage compétitif.",
          bullets: [
            "Comptes illimités sur toutes les prop firms compatibles",
            "Prop Firm Manager : vue consolidée ROI, payouts, dépenses",
            "Filtres par prop firm pour analyser chaque compte séparément",
            "Comparaison de performances entre comptes et entre firmes",
            "Calendrier des charges et récapitulatif mensuel automatique",
            "Disponible à partir du Plan Pro à 15€/mois",
          ],
        },
        coach: {
          h1: "Coache avec les données réelles de tes élèves.",
          subtitle:
            "Tes élèves te disent ce qu'ils pensent avoir fait. Edgely te montre ce qu'ils ont réellement fait.",
          bullets: [
            "Mode Mentor inclus dans le Plan Pro",
            "Invite tes élèves et accède à leur dashboard complet",
            "Visualise leurs sessions, leur PNL, leurs statistiques détaillées",
            "Ajoute des notes directement sur leurs sessions de trading",
            "Regarde les vidéos qu'ils enregistrent pendant leurs sessions",
            "Idéal pour les coachs indépendants, mentors et écoles de trading",
          ],
        },
      },
      comparisons: {
        tradezella: {
          h1: "Edgely vs TradeZella : lequel choisir pour tes prop firms ?",
          subtitle:
            "TradeZella est le leader mondial du journal de trading. Edgely est conçu spécifiquement pour le marché francophone et les prop traders qui veulent un outil plus accessible.",
          why: [
            "Interface 100% française, pensée pour les prop traders FR",
            "Prix plus accessible : 15€/mois vs ~30$/mois",
            "Prop Firm Manager plus opérationnel pour le multi-comptes",
          ],
        },
        tradersync: {
          h1: "Edgely vs TraderSync : le comparatif honnête",
          subtitle:
            "TraderSync est un journal de trading généraliste. Edgely est construit pour les prop traders : Prop Firm Manager, import natif Tradovate/TopStepX, interface française.",
          why: [
            "Spécialisé prop trading là où TraderSync est généraliste",
            "Interface et support en français",
            "Prix plus accessible avec plan gratuit sans CB",
          ],
        },
        notion: {
          h1: "Pourquoi un journal Notion ne suffit plus pour tes prop firms",
          subtitle:
            "Notion est un outil formidable. Mais construire et maintenir un journal de trading dessus, c'est du temps volé à ton vrai travail : trader.",
          why: [
            "Analytics calculées automatiquement vs formules manuelles à maintenir",
            "Import CSV direct vs copier-coller case par case",
            "Prop Firm Manager intégré vs tableau bricolé",
          ],
        },
        excel: {
          h1: "Arrête de tenir ton journal de trading sur Excel",
          subtitle:
            "Excel fonctionne. Mais au bout de 200 trades sur 5 comptes différents, maintenir ses formules devient un travail à plein temps.",
          why: [
            "Zéro formule à créer ou maintenir",
            "Analytics visuelles instantanées dès l'import",
            "Prop Firm Manager impossible à reproduire sur Excel",
          ],
        },
        best: {
          h1: "Quel est le meilleur journal de trading en 2026 ?",
          subtitle:
            "On a comparé les 5 solutions les plus utilisées par les prop traders. Voici le verdict.",
          why: [
            "Débutant sans budget → Edgely Free",
            "Prop trader actif francophone → Edgely Pro",
            "Trader anglophone avec gros budget → TradeZella ; bricoleur autonome → Notion ou Excel",
          ],
        },
        alternatives: {
          h1: "Les meilleures alternatives à TradeZella en 2026",
          subtitle:
            "TradeZella domine le marché anglophone. Mais pour les prop traders francophones, il existe une alternative plus adaptée, plus accessible, et pensée pour votre marché.",
          why: [
            "Edgely en position 1 : prix accessible et plan gratuit sans CB",
            "Interface et support 100% français",
            "Spécialisation prop trading et Prop Firm Manager intégré",
          ],
        },
      },
      featureTabs: {
        eyebrow: "EDGELY",
        title: "Six outils. Un seul hub.",
        subtitle:
          "Tout ce qui fait la différence entre trader à l'aveugle et trader avec un vrai edge.",
        discoverPrefix: "Découvrir",
        tabs: [
          {
            key: "dashboard",
            tabLabel: "Dashboard",
            sectionLabel: "DASHBOARD",
            h1: "Ton dashboard, tes règles.",
            description:
              "Edgely fonctionne comme Notion : crée tes pages, choisis tes widgets, organise ton espace exactement comme tu travailles. PNL, calendrier éco, equity curve, news : tout en un seul endroit.",
            bullets: [
              "Dashboard modulaire avec widgets personnalisables",
              "Calendrier économique et news en temps réel",
              "Filtres par prop firm, stratégie et période",
              "Layouts prédéfinis : Dashboard Pro, Market Data",
            ],
            linkLabel: "Découvrir le Dashboard",
          },
          {
            key: "propFirmManager",
            tabLabel: "Prop Firm Manager",
            sectionLabel: "PROP FIRM MANAGER",
            h1: "Un seul endroit pour piloter toutes tes prop firms.",
            description:
              "Fini les tableaux Excel pour suivre tes challenges. Centralise tous tes comptes, tes payouts et tes dépenses pour garder le contrôle à tout moment.",
            bullets: [
              "Vue globale : payouts, dépenses, ROI, net profit",
              "Calendrier des achats et charges mensuelles",
              "Historique des payouts avec certificats",
              "Compatible Apex, TopStep, Lucid, FundedNext et +",
            ],
            linkLabel: "Découvrir le Prop Firm Manager",
          },
          {
            key: "analytics",
            tabLabel: "Analytics",
            sectionLabel: "ANALYTICS AVANCÉES",
            h1: "Tes données parlent. Écoute-les.",
            description:
              "Arrête de deviner pourquoi tu perds. Edgely analyse chaque variable de ton trading et te montre exactement où se cachent tes patterns destructeurs, et tes vraies forces.",
            bullets: [
              "Win Rate, Profit Factor, Sharpe Ratio, Espérance",
              "Biais cognitifs : FOMO, Revenge Trading, Overconfidence",
              "Performances par jour, horaire et durée de trade",
              "Equity curve et distribution du PNL",
            ],
            linkLabel: "Découvrir les Analytics",
          },
          {
            key: "sessions",
            tabLabel: "Sessions",
            sectionLabel: "SESSIONS DE TRADING",
            h1: "Chaque session compte. Revois-la, améliore-la.",
            description:
              "Une session non reviewée est une session perdue. Enregistre ta vidéo, note ton humeur, tague tes erreurs et analyse chaque trade directement sur le graphique TradingView.",
            bullets: [
              "Enregistrement vidéo YouTube attaché à la session",
              "Graphique TradingView intégré avec tes trades",
              "Évaluation humeur, performance et erreurs",
              "Trades détaillés et métriques P&L par compte",
            ],
            linkLabel: "Découvrir les Sessions",
          },
          {
            key: "marketData",
            tabLabel: "Market Data Pro",
            sectionLabel: "MARKET DATA PRO",
            h1: "Toutes les données de marché. En temps réel.",
            description:
              "News institutionnelles, calendrier économique, taux directeurs des banques centrales et graphiques TradingView : tout ce qu'il faut pour préparer ta session sans quitter Edgely.",
            bullets: [
              "Taux directeurs en temps réel (FED, BCE, BOE, BOJ...)",
              "Calendrier économique High/Medium/Low impact",
              "News institutionnelles FinancialJuice en direct",
              "Graphiques TradingView intégrés multi-timeframes",
            ],
            linkLabel: "Découvrir Market Data Pro",
          },
          {
            key: "mentor",
            tabLabel: "Mode Mentor",
            sectionLabel: "MODE MENTOR",
            h1: "Coache tes élèves avec leurs vraies données.",
            description:
              "Tes élèves te disent ce qu'ils pensent avoir fait. Edgely te montre ce qu'ils ont réellement fait. Accède à leur dashboard complet, leurs sessions, leur PNL, et laisse des notes directement sur leurs trades.",
            bullets: [
              "Accès complet au dashboard de chaque élève",
              "Vue Journal, Analytics, Sessions et Prop Firm Manager",
              "Notes de correction directement sur les sessions",
              "Mode lecture avec bannière \"Vue élève\" claire",
            ],
            linkLabel: "Découvrir le Mode Mentor",
          },
        ],
      },
      hero: {
        titleBeforeHighlight: "Arrête de perdre tes comptes ",
        titleHighlight: "prop firm",
        titleAfterHighlight: " avec les mêmes erreurs.",
        subtitle:
          "Edgely analyse chaque trade, révèle tes patterns destructeurs et t'indique exactement quoi corriger pour obtenir des résultats récurrents.",
        cta: "Commencer gratuitement",
        microcopy: "✦ Gratuit · Pas de CB · Upgrade quand tu veux",
        pillsLabel: "TOUT EN UN SEUL ENDROIT · 6 OUTILS",
        imageAlt:
          "Dashboard Edgely - journal de trading pour prop traders avec calendrier économique, PNL et sessions intégrés",
        pills: [
          { label: "Dashboard", to: "/fonctionnalites/dashboard-trading" },
          { label: "Prop Firm Manager", to: "/fonctionnalites/prop-firm-manager" },
          { label: "Analytics", to: "/fonctionnalites/analytics-trading" },
          { label: "Sessions", to: "/fonctionnalites/sessions-de-trading" },
          { label: "Market Data Pro", to: "/fonctionnalites/dashboard-trading" },
          { label: "Mode Mentor", to: "/fonctionnalites/mode-mentor" },
        ],
      },
      floatingDashboard: {
        ariaLabel: "Aperçu du dashboard Edgely : PnL net, profit factor, taux de réussite, calendrier, routines, objectifs et actualités",
        netPnlLabel: "NET P&L",
        winRateLabel: "TRADE WIN %",
        profitFactorLabel: "PROFIT FACTOR",
        avgWinLossLabel: "AVG WIN/LOSS TRADE",
        avgWin: "Avg Win",
        avgLoss: "Avg Loss",
        marketHoursLabel: "HORAIRES DES MARCHÉS",
        closed: "Fermé",
        calendarLabel: "CALENDRIER",
        weekLabel: "Sem.",
        ecoCalendarLabel: "CALENDRIER ÉCONOMIQUE",
        routinesLabel: "ROUTINES DU JOUR",
        routinePreOpen: "Pré-open routine",
        routineOpenUs: "Open US",
        daily: "Quotidien",
        goalsLabel: "OBJECTIFS EN COURS",
        goalName: "PnL Maker",
        newsLabel: "NEWS",
        tradesLabel: "trades",
        dashboardTitle: "Dashboard",
        liveBadge: "LIVE",
      },
      socialProof: {
        stats: [
          { value: "200+", label: "prop traders actifs" },
          { value: "100%", label: "pensé pour les prop firms" },
          { value: "10", label: "prop firms compatibles" },
          { value: "5★", label: "premiers retours utilisateurs" },
        ],
        compatibleLabel: "COMPATIBLE AVEC",
        partnerLabel: "MEMBRE OFFICIEL DE L'ÉCOSYSTÈME NINJATRADER",
        propFirms: [
          "FTMO",
          "Apex",
          "TopStep",
          "Lucid Trading",
          "FundedNext",
          "MyFundedFutures",
          "TakeProfitTrader",
          "Tradeify",
          "Phidias",
          "Funded Futures Family",
        ],
      },
    },
    affiliation: {
      kicker: "Programme d'affiliation",
      heroTitle: "Devenez ambassadeur Edgely",
      heroSubtitle:
        "Recommandez le journal de trading pensé pour les prop traders et touchez une commission récurrente sur chaque abonnement parrainé.",
      benefits: {
        recurring: {
          title: "Commission récurrente attractive",
          description:
            "Vous touchez un pourcentage de chaque paiement de vos filleuls, mois après mois, pendant toute votre durée de commissionnement.",
        },
        attribution: {
          title: "Attribution 60 jours",
          description:
            "Chaque visiteur venant de votre lien vous est attribué pendant 60 jours, même s'il s'inscrit plus tard.",
        },
        brandingKit: {
          title: "Kit branding fourni",
          description:
            "Logos, bannières et visuels prêts à l'emploi pour promouvoir Edgely sur vos réseaux, votre Discord ou votre chaîne.",
        },
        payments: {
          title: "Paiements fiables",
          description:
            "Suivez vos commissions en temps réel dans votre espace ambassadeur et demandez votre paiement dès 50 € confirmés.",
        },
      },
      howTitle: "Comment ça marche",
      steps: {
        apply: {
          title: "Candidatez",
          description:
            "Remplissez le formulaire de candidature en 2 minutes et parlez-nous de votre audience.",
        },
        access: {
          title: "Recevez vos accès",
          description:
            "Une fois validé, vous recevez votre lien d'affiliation unique et votre espace ambassadeur.",
        },
        earn: {
          title: "Touchez vos commissions",
          description:
            "Chaque abonnement parrainé génère une commission, versée sur demande dès 50 € confirmés.",
        },
      },
      cta: "Devenir ambassadeur",
      ctaNote: "Réponse sous 48 h ouvrées",
    },
    affiliate: {
      pageTitle: "Espace ambassadeur",
      pageSubtitle:
        "Suivez vos clics, vos parrainages et vos commissions Edgely.",
      kpis: {
        clicks: "Clics totaux",
        signups: "Inscriptions parrainées",
        conversions: "Conversions payantes",
        conversionRate: "Taux de conversion",
      },
      balances: {
        pending: "En attente",
        confirmed: "Confirmé",
        totalPaid: "Total payé",
      },
      commissions: {
        title: "Commissions",
        empty: "Aucune commission pour le moment, partagez votre lien !",
        colDate: "Date",
        colClient: "Client",
        colPayment: "Paiement",
        colRate: "Taux",
        colCommission: "Commission",
        colStatus: "Statut",
      },
      status: {
        pending: "En attente",
        confirmed: "Confirmée",
        cancelled: "Annulée",
        paid: "Payée",
        requested: "Demandé",
        rejected: "Rejeté",
      },
      payout: {
        requestButton: "Demander un paiement",
        requestHint:
          "Disponible dès {min} € de commissions confirmées et sans demande en cours.",
        detailsMissing:
          "Renseignez vos coordonnées de paiement dans les réglages pour demander un paiement.",
        requestSuccess: "Demande de paiement envoyée !",
        requestError: "Impossible d'envoyer la demande de paiement.",
        historyTitle: "Historique des paiements",
        historyEmpty: "Aucun paiement pour le moment.",
      },
      link: {
        title: "Mon lien d'affiliation",
        copy: "Copier",
        copied: "Copié !",
        terms:
          "Votre commission : {rate} % de chaque paiement pendant {months} mois par client parrainé. Attribution {days} jours (dernier clic gagne).",
      },
      settings: {
        title: "Réglages",
        method: "Méthode de paiement",
        methodIban: "Virement (IBAN)",
        methodPaypal: "PayPal",
        detailsIban: "IBAN",
        detailsPaypal: "Email PayPal",
        save: "Enregistrer",
        saved: "Coordonnées enregistrées.",
        saveError: "Impossible d'enregistrer les coordonnées.",
        changePassword: "Changer mon mot de passe",
      },
      branding: {
        title: "Kit branding",
        empty: "Le kit branding arrive bientôt.",
        download: "Télécharger",
        categoryLogo: "Logos",
        categoryBanner: "Bannières",
        categoryScreenshot: "Screenshots",
        categoryGuidelines: "Guidelines",
        rulesTitle: "Règles de la marque",
        rulesLogo:
          "Le logo est uniquement typographique, en Space Grotesk bold, avec le point teal :",
        ruleNoEarningsPromise:
          "Ne promettez jamais de gains ou de résultats de trading.",
        ruleNoLogoEdit:
          "Ne modifiez pas le logo : ni couleur, ni police, ni proportions.",
        ruleColors:
          "Couleurs de la marque : teal #00C9A7 sur fond navy #0F1729.",
      },
    },
    adminAffiliates: {
      title: "Affiliation",
      tabs: {
        affiliates: "Ambassadeurs",
        payouts: "Payouts",
        assets: "Assets branding",
      },
      newAffiliate: "Nouvel ambassadeur",
      create: "Créer",
      createHint:
        "Si un compte Edgely existe avec cet email, il sera rattaché. Sinon un compte est créé et les identifiants sont envoyés par email.",
      createdToast: "Ambassadeur créé, email d'accès envoyé.",
      savedToast: "Ambassadeur mis à jour.",
      cancel: "Annuler",
      save: "Enregistrer",
      cols: {
        email: "Email",
        refCode: "Code",
        rate: "Taux",
        duration: "Durée (mois)",
        clicks: "Clics",
        referred: "Parrainés",
        commissions: "Commissions",
        balanceDue: "Solde dû",
        actions: "Actions",
        active: "Actif",
        date: "Date",
        amount: "Montant",
        paymentDetails: "Coordonnées",
        status: "Statut",
      },
      form: {
        email: "Email",
        fullName: "Nom complet",
        companyName: "Société / pseudo",
        ratePercent: "Taux (%)",
        durationMonths: "Durée (mois)",
        refCode: "Code d'affiliation",
        refCodeOptional: "Code d'affiliation (optionnel)",
        refCodePlaceholder: "généré automatiquement si vide",
        notes: "Notes internes",
        adminNotes: "Note (visible en interne)",
        assetName: "Nom",
        assetCategory: "Catégorie",
        sortOrder: "Ordre",
        file: "Fichier",
      },
      emptyAffiliates: "Aucun ambassadeur pour le moment.",
      emptyPayouts: "Aucune demande de payout.",
      emptyAssets: "Aucun asset branding.",
      payoutStatus: {
        requested: "Demandé",
        paid: "Payé",
        rejected: "Rejeté",
      },
      markPaid: "Marquer payé",
      reject: "Rejeter",
      rejectTitle: "Rejeter la demande de payout",
      payoutPaidToast: "Payout marqué payé, email envoyé à l'ambassadeur.",
      payoutRejectedToast: "Demande de payout rejetée.",
      assetCategories: {
        logo: "Logo",
        banner: "Bannière",
        screenshot: "Screenshot",
        guidelines: "Guidelines",
      },
      upload: "Uploader",
      uploadError: "Échec de l'upload du fichier.",
      assetCreatedToast: "Asset ajouté.",
      assetDeletedToast: "Asset supprimé.",
    },
    adminB2b: {
      pageTitle: "B2B / VIP",
      partnersTitle: "Partenaires",
      newPartner: "Nouveau partenaire",
      partnerName: "Nom du partenaire",
      contactEmail: "Email de contact",
      createdAt: "Créé le",
      seats: "Sièges",
      seatsBillable: "Sièges facturables",
      statusActive: "Actif",
      statusPending: "En attente",
      statusShadowed: "Abonné par lui-même",
      statusRevoked: "Révoqué",
      apiActive: "API active",
      apiDisabled: "API coupée",
      rotateKey: "Rotation de clé",
      rotateKeyConfirm:
        "Générer une nouvelle clé ? L'ancienne cessera de fonctionner immédiatement.",
      disablePartner: "Couper",
      enablePartner: "Réactiver",
      disablePartnerConfirm:
        "Couper ce partenaire ? Son API sera refusée et ses coupures programmées seront gelées. Les membres existants gardent leur accès.",
      keyDialogTitle: "Clé API de",
      keyDialogWarning:
        "Cette clé ne sera plus jamais affichée. Copie-la maintenant et transmets-la au partenaire par un canal sûr.",
      copy: "Copier",
      copied: "Copié",
      close: "Fermer",
      grantsTitle: "Sièges",
      newGrant: "Nouveau siège",
      filterStatusAll: "Tous les statuts",
      filterEmailPlaceholder: "Filtrer par email",
      colEmail: "Email",
      colPartner: "Partenaire",
      colExternalRef: "Réf. Discord",
      colStatus: "Statut",
      colUser: "Compte Edgely",
      colActivatedAt: "Activé le",
      colEndsAt: "Fin programmée",
      colCreatedAt: "Créé le",
      noGrants: "Aucun siège",
      revoke: "Révoquer",
      revokeDialogTitle: "Révoquer ce siège",
      revokeNow: "Couper maintenant",
      revokeAtDate: "Couper à une date",
      revokePastDate: "La date de coupure doit être dans le futur",
      revokeScheduledToast: "Coupure programmée",
      revokedToast: "Siège révoqué",
      newGrantDialogTitle: "Nouveau siège",
      email: "Email",
      externalRef: "Réf. externe (optionnel)",
      create: "Créer",
      cancel: "Annuler",
      createdToast: "Siège créé",
      page: "Page",
      of: "sur",
      colActions: "Actions",
      previous: "Précédent",
      next: "Suivant",
      partnerUpdatedToast: "Partenaire mis à jour",
      filterPartnerAll: "Tous les partenaires",
    },
    b2bMember: {
      offeredBy: "Accès offert par {partner}",
      offeredBadge: "Offert",
      pricingBannerTitle: "Ton accès Max est offert par {partner}",
      pricingBannerBody:
        "Tu n'as rien à payer : toutes les fonctionnalités Max sont déjà incluses dans ton accès.",
      checkoutBlocked:
        "Ton accès Max est offert par un partenaire : il n'y a rien à acheter.",
      currentPlan: "Ton plan actuel",
    },
    adminDashboard: {
      usersByPlanTitle: "Utilisateurs par plan",
      usersByPlanSubtitle:
        "Les plans annuels sont regroupés avec leur équivalent mensuel.",
      activeUsersLabel: "actifs sur 7 j",
      activeUsersHint:
        "Utilisateurs ayant ouvert l'application au cours des 7 derniers jours.",
      activeUsersTotal: "actifs sur 7 jours, tous plans confondus",
      searchPlaceholder: "Rechercher un email ou un pseudo...",
      sortBy: "Trier par",
      sortNewest: "Inscription (récent)",
      sortOldest: "Inscription (ancien)",
      sortLastActive: "Dernière activité",
      sortEmail: "Email (A-Z)",
      emptyColumn: "Aucun utilisateur sur ce plan",
      noSearchResult: "Aucun résultat",
      annualBadge: "Annuel",
      adminBadge: "Admin",
      brokerBadge: "Broker connecté",
      graceBadge: "Paiement en échec",
      expiredBadge: "Grâce expirée",
      inactiveBadge: "Inactif",
      neverActive: "Jamais connecté",
      signedUpOn: "Inscrit le",
      lastActiveOn: "Vu le",
      mrrTitle: "Revenu récurrent",
      mrrTotal: "MRR total",
      mrrMax: "MRR Max",
      mrrPro: "MRR Pro",
      paymentIssues: "Paiements en échec",
      totalUsers: "Utilisateurs",
      mrrHint:
        "Calculé depuis les abonnements en base, plans annuels ramenés au mois.",
      cohortTitle: "Rétention par cohorte",
      cohortHint:
        "Part des inscrits de chaque mois encore actifs ces 30 derniers jours.",
      cohortMonth: "Mois",
      cohortSignups: "Inscrits",
      cohortRetained: "Encore actifs",
      cohortRate: "Rétention",
      cohortEmpty: "Aucune inscription sur la période",
      acquisitionTitle: "Acquisition",
      acquisitionHint: "Sources des inscriptions des 30 derniers jours.",
      signups7d: "Inscrits (7 j)",
      signups30d: "Inscrits (30 j)",
      sourceLabel: "Source",
      sourceDirect: "Direct",
      sourceEmpty: "Aucune inscription sur les 30 derniers jours",
      brokerHealthTitle: "Connexions broker à surveiller",
      brokerHealthHint:
        "Connexions en erreur ou nécessitant une reconnexion. Les connexions inactives ne sont pas listées.",
      brokerHealthEmpty: "Toutes les connexions broker sont saines",
      quotaTitle: "Quota API Tradovate",
      quotaHint:
        "Appels réellement émis sur la dernière heure. La limite qui joue est d'environ 5 000 appels/h par utilisateur Tradovate, toutes connexions confondues (mesuré le 14/10 : 6 028 appels/h depuis notre IP, pénalités chez un seul utilisateur). Le « 600/h par IP » annoncé oralement n'a jamais été appliqué.",
      quotaEmpty: "Aucun appel Tradovate sur la dernière heure",
      quotaPerUserLabel: "Par utilisateur (comptes, appels, part de sa limite horaire)",
      quotaWorstUser: "Utilisateur le plus consommateur",
      quotaIpTotal: "Total de l'IP : {calls} appels sur l'heure (information)",
      quotaNearLimit: "Proche de la limite",
      quotaOverLimit: "Limite dépassée",
      quotaHistoryTitle: "Historique du quota Tradovate",
      quotaHistoryHint:
        "Appels émis par heure. L'historique démarre à la mise en service de cette vue : les heures antérieures ne sont pas récupérables.",
      quotaHistoryEmpty:
        "Aucune donnée pour le moment. La courbe se remplit heure par heure.",
      quotaHistoryCallsLabel: "Appels",
      quotaHistoryRateLimitLabel: "Rate limits",
      quotaHistoryPeakLabel: "Pic",
      quotaHistoryRange24h: "24 h",
      quotaHistoryRange48h: "48 h",
      quotaHistoryRange7d: "7 j",
      quotaHistoryRange30d: "30 j",
      quotaHistoryQuotaLine: "Plafond",
      endpointBreakdownTitle: "Consommation REST par endpoint",
      endpointBreakdownHint:
        "Repartition reelle des appels Tradovate, mesuree et non estimee. Le poste dominant est celui qui decide de la tenue a l'echelle.",
      endpointBreakdownEmpty:
        "Aucun appel ventile sur cette periode. La ventilation ne couvre que les cycles de sync posterieurs a sa mise en service.",
      tradovateErrorsTitle: "Erreurs et penalites Tradovate",
      tradovateErrorsHint:
        "Quand et pourquoi le quota est tape : chaque 429/423 est horodate avec son endpoint, sa penalite p-time et sa phase. Sert aussi a verifier si les appels reussis comptent vraiment dans le quota.",
      tradovateErrorsEmpty:
        "Aucune penalite sur cette periode : aucun appel n'a recu de 429/423. C'est l'etat attendu.",
      tradovateErrorsRateLimits: "Penalites",
      tradovateErrorsCalls: "Appels emis",
      tradovateErrorsCaptcha: "p-captcha",
      tradovateErrorsCaptchaTag: "captcha",
      tradovateErrorsRetryFailures: "Retrys re-echoues",
      tradovateErrorsRetryHint:
        "Requetes rejouees apres le delai p-time et pourtant re-penalisees : la saturation persistait.",
      tradovateErrorsHitsCol: "Penalites",
      tradovateErrorsCallsCol: "Appels",
      tradovateErrorsRecentTitle: "Incidents recents",
      tradovateErrorsTimeCol: "Instant",
      tradovateErrorsStatusCol: "Statut",
      tradovateErrorsPenaltyCol: "p-time",
      tradovateErrorsPhaseCol: "Phase",
      tradovateErrorsHostCol: "Hote",
      tradovateErrorsUserCol: "Utilisateur",
      tradovateErrorsPhaseInitial: "1er essai",
      tradovateErrorsPhaseRetry: "retry echoue",
      accountLifecycleTitle: "Cycle de vie des comptes par le socket",
      accountLifecycleHint:
        "Le socket voit-il un compte nouveau, disparu, crame ou passe en funded AVANT que le REST ne le lise dans /account/list ? Mesure shadow : le REST reste seul decideur.",
      accountLifecycleEmpty: "Aucune decision de cycle de vie sur cette periode.",
      accountLifecycleRange14d: "14 j",
      accountLifecycleVerdictGreen: "Critere atteint : /account/list est remplacable par le socket sur cette fenetre.",
      accountLifecycleVerdictRed: "Critere non atteint. Ce qui manque :",
      accountLifecycleVerdictUnjudged: "Pas encore jugeable : au moins un type de decision n'a pas encore assez de cas avec socket vivant.",
      accountLifecycleReasonUnjudged: "aucune decision observee",
      accountLifecycleReasonNotSeen: "decision(s) non vue(s) par le socket",
      accountLifecycleReasonTooFew: "cas vu(s), il en faut 3",
      accountLifecycleReasonUnstable: "champs d'evidence differents, il en faut un seul",
      accountLifecycleReasonWsDeleted: "compte(s) que le socket croit supprime(s) alors que le REST les liste : zero tolere",
      accountLifecycleDecisionNew: "Nouveau compte",
      accountLifecycleDecisionGone: "Compte disparu",
      accountLifecycleDecisionBreach: "Breach de regle",
      accountLifecycleDecisionFunded: "Passage en funded",
      accountLifecycleCoverageTitle: "Couverture par decision",
      accountLifecycleColDecision: "Decision REST",
      accountLifecycleColWithSocket: "Avec socket",
      accountLifecycleColSeenBefore: "Vues avant",
      accountLifecycleColCoverage: "Couverture",
      accountLifecycleColMedianLead: "Avance mediane",
      accountLifecycleColEvidence: "Repartition (n)",
      accountLifecycleColNoSocket: "Sans socket",
      accountLifecycleNoSocketHint: "Decisions prises sans socket vivant : comptees a part, jamais dans le pourcentage.",
      accountLifecycleTransitionsTitle: "Transitions observees",
      accountLifecycleTransitionsHint: "Ce que les prop firms changent reellement sur un compte. C'est ici que se lit la signature d'un cramage ou d'un passage en funded.",
      accountLifecycleTransitionsEmpty: "Aucune transition sur la periode (les premieres vues ne comptent pas).",
      accountLifecycleColField: "Champ",
      accountLifecycleColFrom: "De",
      accountLifecycleColTo: "Vers",
      accountLifecycleColCount: "Comptes",
      accountLifecycleColLastSeen: "Derniere vue",
      accountLifecycleColExample: "Exemple (id Tradovate)",
      accountLifecycleFirstSight: "comptes vus pour la premiere fois",
      accountLifecycleParityTitle: "Parite /account/list vs socket",
      accountLifecycleParityRestOnly: "REST seulement",
      accountLifecycleParityWsOnly: "Socket seulement",
      accountLifecycleParityWsDeleted: "Supprime cote socket, liste cote REST",
      accountLifecycleParityWsDeletedLag: "Supprime cote socket AVANT le REST (avance)",
      accountLifecycleParityName: "Noms differents",
      accountLifecycleParityEmpty: "Aucun ecart de presence ni de nom.",
      accountLifecycleProbeTitle: "Entite accountRiskStatus",
      accountLifecycleProbeSeen: "poussee par Tradovate : candidate a la signature de cramage",
      accountLifecycleProbeNotSeen: "pas encore vue (ce n'est pas une preuve d'absence)",
      accountLifecycleReasonUnparsed: "ligne(s) vue(s) avant dont l'evidence est illisible : verdict bloque",
      accountLifecycleColCommonFields: "Champ commun",
      accountLifecycleTruncated: "Donnees partielles : plus de 5 000 evenements sur la fenetre, les plus anciens ne sont pas lus. Reduire la fenetre.",
      accountLifecycleLoading: "Chargement de la mesure...",
      accountLifecycleError: "Lecture de la mesure impossible : la carte ne peut pas rendre de verdict.",
      accountLifecycleParityRestLabel: "REST",
      accountLifecycleParityWsLabel: "WS",
      endpointBreakdownEndpointCol: "Endpoint",
      endpointBreakdownCallsCol: "Appels",
      endpointBreakdownShareCol: "Part",
      endpointBreakdownPerHourCol: "Par heure",
      endpointBreakdownUnattributed: "Appels non ventiles",
      endpointBreakdownUnattributedHint:
        "Appels comptes avant la mise en service de la ventilation. Ils restent dans le total, sans detail.",
      endpointBreakdownUsersTitle: "Par utilisateur",
      endpointBreakdownUserCol: "Utilisateur",
      endpointBreakdownAccountsCol: "Comptes actifs",
      endpointBreakdownRatioCol: "Appels / compte / h",
      endpointBreakdownRatioHint:
        "La seule grandeur comparable entre un utilisateur a 28 comptes et un autre a 3, et la seule qui se projette a l'echelle.",
      endpointBreakdownTransportCol: "Transport",
      endpointBreakdownTransportRest: "REST",
      endpointBreakdownTransportWs: "WebSocket",
      endpointBreakdownBetaBadge: "Socket",
      endpointBreakdownWitnessBadge: "REST",
      endpointBreakdownProjectionTitle: "Projection a l'echelle",
      endpointBreakdownProjectionHint:
        "Modele mesure sur la fenetre : cout fixe par connexion OAuth plus cout par compte, au nombre median de comptes par connexion. Compare au plafond de 600 appels par heure, pire hypothese tant que la mesure des penalites ne l'a pas tranche.",
      endpointBreakdownProjectionAccounts: "Comptes",
      endpointBreakdownProjectionConnections: "connexions",
      endpointBreakdownCostModel: "Cout mesure :",
      endpointBreakdownCostPerConnection: "appels / h par connexion +",
      endpointBreakdownCostPerAccount: "par compte",
      endpointBreakdownProjectionCalls: "Appels / h projetes",
      endpointBreakdownProjectionWithin: "Sous le plafond",
      endpointBreakdownProjectionOver: "Depasse le plafond",
      endpointBreakdownProjectionUnknown: "Pas assez de donnees",
      endpointBreakdownSocketTitle: "Sante WebSocket",
      endpointBreakdownSocketEvents: "Evenements recus",
      endpointBreakdownSocketReconnects: "Reconnexions",
      endpointBreakdownSocketSilent:
        "Aucun evenement recu alors que des utilisateurs sont sur le socket. Un socket muet ressemble a un succes total : verifier qu'il est bien connecte.",
      endpointBreakdownSocketNotStarted:
        "Transport WebSocket pas encore en service. Tout le monde est en REST.",
      endpointBreakdownMedianLabel: "Ratio median",
      shadowDetailRest: "REST",
      shadowDetailWs: "Temps reel",
      wsWrittenLabel: "Trades ecrits par le temps reel",
      wsWriteFailuresLabel: "Echecs d'ecriture",
      wsRepairedLabel: "Trous repares par le controle",
      wsWrittenActive:
        "Le flux temps reel ecrit reellement en base. Les trades ci-dessous en viennent.",
      wsWrittenNone:
        "Aucune ecriture par le temps reel sur la periode. Soit la bascule n'est pas active, soit aucun trade n'est arrive depuis.",
      compareTitle: "Comparaison trade par trade",
      compareHint:
        "Ce que chaque flux voit du meme trade. Les lignes qui concordent sont affichees aussi : verifier une concordance est ce qui permet d'affirmer qu'il n'y a pas de derive.",
      compareTrade: "Trade",
      compareMatch: "Concordant",
      compareMismatch: "Divergent",
      compareNotSeen: "Non vu en temps reel",
      compareFeesUnverified: "Frais non verifies",
      compareFeesUnverifiedHint:
        "Le flux ne transporte pas les frais : la commission affichee vient du REST des deux cotes, elle n'est donc pas reellement comparee.",
      compareEmpty:
        "Aucune comparaison disponible. Elle se remplit au prochain trade d'un betatesteur.",
      betaDiagTitle: "Diagnostic betatesteurs",
      betaDiagHint:
        "Etat par connexion sur les 24 dernieres heures de marche ouvert (week-ends et pauses exclus). Un socket muet ne consomme aucun quota et ressemble a un succes total : c'est ce que cet ecran sert a distinguer.",
      betaDiagEmpty: "Aucun utilisateur enrole dans la beta.",
      betaDiagConnection: "Connexion",
      betaDiagUser: "Utilisateur",
      betaDiagHealth: "Etat",
      betaDiagEvents: "Evenements",
      betaDiagReconnects: "Reconnexions",
      betaDiagDiffs: "Ecarts",
      betaDiagLeaseAge: "Bail (min)",
      betaDiagHealthy: "Sain",
      betaDiagSilent: "Muet",
      betaDiagStale: "Aucun evenement depuis 1 h de marche",
      betaDiagFlapping: "Instable",
      betaDiagCut: "Coupe, pas rouvert",
      betaDiagModeFallback: "Repli REST",
      betaDiagNeverConnected: "Jamais connecte",
      betaDiagFlagWithoutPlan: "Flag sans plan Max",
      betaDiagNotEnrolled: "Non enrole",
      betaDiagSubscribed: "Types souscrits",
      betaDiagSockets: "Sockets vivants",
      betaDiagSocketsHint:
        "Sockets vivants dans ce process, face a TRADOVATE_WS_MAX_SOCKETS : au-dela, une connexion reste en REST sans aucun signal. Distinct des 50 sockets que Tradovate autorise par utilisateur final. Coupe = le serveur a ferme le socket et rien ne le rouvre avant le prochain renouvellement de jeton.",
      betaDiagProblems: "A surveiller",
      betaDiagPropFirm: "Prop firm",
      betaDiagWriteMode: "Mode tenu",
      betaDiagCadence: "Cadence REST",
      betaDiagControl: "Controle",
      betaDiagLatency: "Latence socket",
      betaDiagModeWsOnly: "Socket seul",
      betaDiagModeRestOnly: "REST",
      betaDiagCadenceSpaced: "espacee",
      betaDiagCadenceNominal: "15 min",
      betaDiagControlNever: "jamais",
      betaDiagControlHint:
        "Mode tenu : ce que fait le prochain cycle, pas ce que le plan voudrait. Repli REST = socket refuse, le REST relit tout toutes les 15 min (motif au survol). Controle : heures depuis le dernier controle quotidien (rouge au-dela de 26 h en socket seul), puis trades que le socket avait manques et que le controle a repares sur 24 h.",
      betaDiagLatencyHint:
        "Latence : mediane en ms entre le fill chez Tradovate et l'ecriture par le socket, puis nombre de trades ecrits par le socket sur 24 h.",
      betaDiagWindow: "Fenetre depuis le {since}",
      betaDiagSocketShare: "Part socket",
      betaDiagFallback: "Repli",
      betaDiagShareHint:
        "Part socket : trades ecrits par le socket parmi tous les trades de la fenetre (socket, repli REST, controle). Repli : temps approximatif passe en repli REST (un cycle = 15 min), mesure depuis le 2026-10-18. Reconnexions et « Instable » : seules les coupures pendant les heures de marche comptent (celles de la cloture du vendredi sont ignorees).",
      reauthTitle: "Ré-autorisation du socket sur place (B2)",
      reauthHint: "Au renouvellement du jeton, le socket reçoit le nouveau jeton au lieu d'être fermé puis rouvert. Le test ne vaut que si la session est PROUVÉE vivante après l'expiration de l'ancien jeton : une requête de preuve passe par le socket 85 min après chaque ré-autorisation.",
      reauthDisabled: "B2 inactif sur ce serveur : la variable TRADOVATE_WS_REAUTH_USERS est absente. Tous les sockets sont recyclés comme avant.",
      reauthEmpty: "Aucune connexion Tradovate pour les utilisateurs de la liste.",
      reauthUsers: "Utilisateurs de la liste",
      reauthCountsHint: "Ré-autorisations et preuves : réussies / en échec sur la fenêtre. Un échec déclenche le recyclage habituel, sans perte de données.",
      reauthColUser: "Utilisateur",
      reauthColPropFirm: "Prop firm",
      reauthColVerdict: "Verdict",
      reauthColReauth: "Ré-autorisations",
      reauthColProof: "Preuves",
      reauthColLast: "Dernier événement",
      reauthVerdictProven: "Session prouvée",
      reauthVerdictAwaiting: "Preuve en attente",
      reauthVerdictFailing: "Échec, recyclé",
      reauthVerdictNoActivity: "Aucune tentative",
      backfillRepairTitle: "Backfill : comptes à rejouer",
      backfillRepairHint: "Jusqu'au 2026-10-19, le backfill ignorait tout trade perdant (format négatif non reconnu) : le journal ne gardait que les gains, et un compte importé voyait son solde initial recalé sur ce gain fictif. Rejouer un compte écrit ses perdants manquants et, pour un compte importé, remet le solde initial d'aplomb. Un compte rejoué sort de la liste.",
      backfillRepairEmpty: "Aucun compte à rejouer.",
      backfillRepairColAccount: "Compte",
      backfillRepairColUser: "Utilisateur",
      backfillRepairColTrades: "Trades backfillés",
      backfillRepairColInitial: "Solde initial",
      backfillRepairColBroker: "Solde broker",
      backfillRepairColKind: "Rejeu",
      backfillRepairKindNew: "Compte importé (solde initial corrigé)",
      backfillRepairKindGap: "Trou de synchro (journal complété)",
      backfillRepairInactive: "Inactif, non rejouable",
      backfillRepairReplay: "Rejouer",
      backfillRepairReplayAll: "Rejouer tous les comptes actifs",
      backfillRepairRequested: "Rejeu demandé",
      backfillRepairKindBoth: "Compte importé + trou de synchro",
      backfillRepairKindNone: "Rien à rejouer",
      backfillRepairLastRun: "Dernier passage",
      backfillRepairError: "Rejeu refusé : le backfill est peut-être coupé sur ce serveur (TRADOVATE_BACKFILL_ENABLED).",
      endpointBreakdownReplaceable: "WebSocket",
      endpointBreakdownReplaceableYes: "Remplacable",
      endpointBreakdownReplaceableNo: "Reste en REST",
      endpointBreakdownSavings: "Economie potentielle",
      endpointBreakdownAfterWs: "Apres bascule",
      betaTesterColumn: "Betatesteur",
      betaTesterEnable: "Enroler dans la beta",
      betaTesterDisable: "Retirer de la beta",
      betaTesterNoEffect:
        "Flag pose, mais sans effet tant que l'utilisateur n'est pas sur un plan Max.",
      brokerProvider: "Broker",
      brokerUser: "Utilisateur",
      brokerStatus: "Statut",
      brokerLastSync: "Dernière sync",
      brokerAccounts: "Comptes",
      brokerNeverSynced: "Jamais synchronisé",
      statusError: "Erreur",
      statusNeedsReauth: "Reconnexion requise",
      statusDelayed: "Sync en retard",
      statusDeprovisioned: "Déprovisionné",
      statusForeignToken: "Mauvais identifiants",
      detailTitle: "Fiche utilisateur",
      detailLoading: "Chargement...",
      detailPlan: "Plan",
      detailStatus: "Statut abonnement",
      detailJoined: "Inscrit le",
      detailLastActive: "Dernière activité",
      detailOnboarding: "Onboarding",
      detailOnboardingDone: "Terminé",
      detailOnboardingPending: "Non terminé",
      detailAcquisition: "Acquisition",
      detailActivity: "Activité",
      detailTrades: "Trades",
      detailSessions: "Sessions",
      detailNotes: "Notes",
      detailConnections: "Connexions broker",
      detailAccounts: "Comptes",
      detailNoAccounts: "Aucun compte",
      detailNextBilling: "Prochaine échéance",
      detailClose: "Fermer",
      detailNone: "Non renseigné",
    },
    playbook: {
      title: "Playbook",
      subtitle: "Déclare ta stratégie pour qu'Edgy puisse analyser ton trading.",
      stepOf: "Étape {current} sur {total}",
      next: "Suivant",
      back: "Retour",
      save: "Enregistrer",
      saving: "Enregistrement…",
      edit: "Modifier",
      versionLabel: "Version {version}",
      activeVersion: "Version active",
      createdOn: "Créée le {date}",
      emptyTitle: "Aucun playbook",
      emptyDescription:
        "Ton playbook décrit ta stratégie : marchés, sessions, setups et règles de risque. Il se remplit en 2-3 minutes et sert de base à toutes les analyses d'Edgy.",
      emptyCta: "Créer mon playbook",
      savedToast: "Playbook enregistré.",
      newVersionToast: "Nouvelle version du playbook créée.",
      saveError: "Impossible d'enregistrer le playbook.",
      newPlaybook: "Nouveau playbook",
      deleteAction: "Supprimer",
      deleteConfirm: "Supprimer ce playbook et toutes ses versions ? Tes analyses passées seront conservées.",
      deletedToast: "Playbook supprimé.",
      deleteError: "Impossible de supprimer le playbook.",
      nameLabel: "Nom du playbook",
      namePlaceholder: "Ex. Scalp NQ ouverture",
      nameRequired: "Le nom du playbook est obligatoire.",
      targetsLabel: "Comptes analysés",
      targetsDescription: "Edgy analysera les trades de ces comptes et groupes.",
      targetsAccounts: "Comptes",
      targetsGroups: "Groupes",
      targetsEmpty: "Aucun compte sélectionné. Edgy analysera alors tous tes trades.",
      referenceAccountLabel: "Compte de référence pour les règles prop firm",
      referenceAccountHelp: "Sert uniquement à pré-remplir drawdown et objectif de gain.",
      steps: {
        strategy: "Stratégie & marchés",
        setups: "Setups & risque",
        rules: "Règles",
      },
      strategyStep: {
        title: "Stratégie & marchés",
        description: "Sur quoi et quand tu trades.",
        strategyLabel: "Stratégie associée",
        strategyPlaceholder: "Aucune stratégie",
        strategyNameLabel: "Nom du playbook",
        strategyNamePlaceholder: "Ex : Breakout ouverture US",
        strategyNameHint: "Ce nom est figé dans cette version, même si tu renommes la stratégie plus tard.",
        marketsLabel: "Marchés tradés",
        marketsHint: "Sélectionne les instruments que tu trades avec cette stratégie.",
        marketsEmpty: "Aucun marché sélectionné",
        sessionsLabel: "Sessions de trading",
        sessionsHint: "Les horaires pendant lesquels tu prends des positions.",
        addSession: "Ajouter une session",
        sessionName: "Nom",
        sessionStart: "Début",
        sessionEnd: "Fin",
        sessionHours: "Heures",
        sessionMinutes: "Minutes",
        sessionTimezone: "Fuseau",
        removeSession: "Retirer cette session",
        presetAsia: "Asie",
        presetLondon: "Londres",
        presetNewYork: "New York",
      },
      setupsStep: {
        title: "Setups & risque",
        description: "Tes conditions d'entrée et de sortie, et ton risque par trade.",
        setupsLabel: "Setups",
        addSetup: "Ajouter un setup",
        setupName: "Nom du setup",
        setupNamePlaceholder: "Ex : Pullback sur VWAP",
        entryRules: "Règles d'entrée",
        entryRulesPlaceholder: "Ex : cassure du plus haut de la range d'ouverture avec volume au-dessus de la moyenne.",
        exitRules: "Règles de sortie",
        exitRulesPlaceholder: "Ex : TP au prochain niveau majeur, SL sous le dernier point bas.",
        removeSetup: "Retirer ce setup",
        noSetups: "Aucun setup pour l'instant. Ajoutes-en au moins un.",
        riskLabel: "Risque max par trade",
        riskTypePercent: "% du compte",
        riskTypeAmount: "Montant fixe",
        riskValueLabel: "Valeur",
        maxTradesPerDayLabel: "Trades max par jour",
        maxTradesPerDayPlaceholder: "Ex : 3",
        minRiskRewardLabel: "Ratio risque/rendement minimum",
        minRiskRewardPlaceholder: "Ex : 2",
      },
      rulesStep: {
        title: "Règles",
        description: "Les contraintes de ta prop firm et tes règles personnelles.",
        sourceAccountLabel: "Compte de référence",
        sourceAccountHint: "Les règles ci-dessous sont lues depuis ce compte.",
        sourceAccountNone: "Aucun compte",
        derivedTitle: "Règles détectées",
        derivedHint:
          "Ces valeurs proviennent de la prop firm de ton compte et restent à jour automatiquement. Modifie-les seulement si elles ne correspondent pas à ta réalité.",
        noRulesDetected: "Aucune règle prop firm détectée pour ce compte.",
        maxDailyDrawdown: "Perte journalière max",
        maxTotalDrawdown: "Drawdown total max",
        profitTarget: "Objectif de gain",
        overrideToggle: "Personnaliser ces règles",
        overrideHint: "Tes valeurs remplaceront celles détectées.",
        resetOverride: "Revenir aux valeurs détectées",
        unitPercent: "%",
        unitAmount: "montant",
        personalRulesLabel: "Règles personnelles",
        personalRulesPlaceholder:
          "Ex : pas de trade après deux pertes consécutives, pause de 15 min après chaque perte…",
        personalRulesHint: "Tes propres garde-fous, au-delà des règles de la prop firm.",
      },
      validation: {
        strategyNameRequired: "Donne un nom à ton playbook.",
        marketsRequired: "Sélectionne au moins un marché.",
        setupNameRequired: "Chaque setup doit avoir un nom.",
        setupRequired: "Ajoute au moins un setup.",
        riskValueRequired: "Indique un risque max par trade valide.",
      },
    },
    edgy: {
      buttonLabel: "Analyser mon trading",
      drawerTitle: "Edgy",
      drawerSubtitle: "Ton coach de trading",
      close: "Fermer",
      checking: "Vérification de tes données…",
      missingPrereqsTitle: "Encore une étape",
      missingPrereqsIntro: "Avant de lancer l'analyse, il me manque :",
      addStrategy: "Ajouter ma stratégie",
      addTrades: "Ajouter mes trades",
      noTradesOnStrategy:
        "Aucun de tes trades n'est associé à la stratégie de ce playbook. Associe-lui des trades depuis ton journal pour lancer une analyse.",
      selectStrategyTitle: "Quelle stratégie j'analyse ?",
      selectStrategySubtitle: "Choisis-en une, je démarre aussitôt.",
      tradesCount: "{count} trades",
      versionLabel: "v{version}",
      scanning: {
        reading: "Lecture de {trades} trades…",
        sessions: "Analyse des sessions et horaires…",
        playbook: "Comparaison avec ton playbook…",
        leaks: "Détection des fuites de capital…",
        writing: "Rédaction de ton Edge Report…",
      },
      reveal: {
        strengths: "Tes forces",
        leaks: "Tes fuites",
        playbookGaps: "Playbook vs réalité",
        weeklyActions: "Ton plan de la semaine",
        edgeScore: "Edge Score",
        declared: "Déclaré",
        actual: "Réel",
        impact: "Impact",
        estimatedCost: "Coût estimé",
        checkMetric: "À vérifier",
        copy: "Copier",
        copied: "Copié",
        copyAll: "Copier tout le report",
        lowSampleNotice: "Peu de trades sur la période : l'analyse s'affinera avec le temps.",
      },
      questions: {
        title: "Pose-moi une question",
        limitReached: "Tu as posé toutes tes questions sur ce bilan.",
        asking: "Edgy réfléchit…",
        error: "Je n'ai pas pu répondre. Réessaie dans un instant.",
      },
      credits: {
        remaining: "{remaining} analyses restantes ce mois-ci",
        discovery: "Analyse découverte offerte",
        none: "Plus d'analyse disponible ce mois-ci",
      },
      noCredits: {
        title: "Plus de crédits ce mois-ci",
        description: "Tes analyses se rechargent automatiquement.",
        refillOn: "Prochaine recharge le {date}",
        upgrade: "Passer à un plan supérieur",
      },
      betaLock: {
        badge: "Bêta",
        title: "Edgy est en bêta sur le plan Max",
        description:
          "Pendant la phase de bêta test, l'analyse par IA est réservée aux abonnés Max. Passe au plan Max pour en profiter dès maintenant.",
        upgrade: "Découvrir le plan Max",
        lockedTooltip: "Réservé au plan Max pendant la bêta",
      },
      errors: {
        title: "Désolé, ça a coincé",
        retry: "Réessayer",
        killSwitch: "Edgy est temporairement indisponible. Reviens un peu plus tard.",
        cooldown: "Tu viens de lancer une analyse. Prochaine possible à {time}.",
        generic: "Je n'ai pas réussi à terminer l'analyse. Ton crédit n'a pas été utilisé.",
        notConfigured: "Edgy n'est pas encore activé sur ce serveur.",
      },
      progress: {
        title: "Depuis la dernière fois",
        followed: "Tenu",
        partiallyFollowed: "Partiellement tenu",
        notFollowed: "Pas tenu",
        unverifiable: "Non mesurable",
      },
      history: {
        navLabel: "Mes analyses",
        pageTitle: "Mes analyses",
        pageSubtitle: "Retrouve chacun de tes bilans et ta progression.",
        empty: "Aucune analyse pour l'instant.",
        emptyCta: "Lance ta première analyse depuis le bouton « Analyser mon trading ».",
        scoreTrend: "Évolution de ton Edge Score",
        questionsCount: "{count} questions",
        tradesAnalysed: "{count} trades analysés",
        allStrategies: "Toutes les stratégies",
        filterStrategy: "Stratégie",
        back: "Retour aux analyses",
        loadMore: "Charger plus",
        conversation: "Ta discussion avec Edgy",
        analysedOn: "Analyse du {date}",
      },
    },
    notificationsCenter: {
      title: "Notifications",
      bellAriaLabel: "Notifications",
      unreadAriaLabel: "{count} notifications non lues",
      markAllRead: "Tout marquer comme lu",
      dismiss: "Marquer comme traitée",
      dismissAll: "Tout effacer",
      empty: "Aucune notification",
      reportReady: "Votre rapport {period} est disponible",
      planUpgraded: "Votre abonnement est passé à {plan}",
      planDowngraded: "Votre abonnement est passé à {plan}",
      paymentFailed: "Le paiement de votre abonnement a échoué",
      paymentFailedWithDays:
        "Paiement échoué : il vous reste {days} jours pour régulariser",
      planDowngradedPaymentFailed:
        "Votre abonnement {plan} a pris fin faute de paiement",
      accountChallengeCompleted:
        "Challenge validé : la prop firm a clôturé le compte {account}. Il passe en inactif, ses trades sont conservés.",
      accountClosedBroker:
        "Le compte {account} n'existe plus chez le broker. Il passe en inactif, ses trades sont conservés.",
      accountDrawdownBreach:
        "Règle de drawdown dépassée sur le compte {account}. Il passe en inactif, ses trades sont conservés.",
      subscriptionCancelQuestion:
        "{account} est clôturé : as-tu résilié son abonnement ? Sinon la prop firm prélèvera au renouvellement.",
      chargeUpcoming: "Prélèvement de {amount} pour {name} le {date}",
      subscriptionResetReactivate:
        "{account} a été renouvelé aujourd'hui et donc remis à zéro : le réactiver ?",
      payoutDetected:
        "Payout de {amount} détecté sur le compte {account}, ajouté au Prop Firm Manager. Si ce n'en est pas un, corrigez-le depuis le widget Suivi par compte.",
      propFirmAdjustmentDetected:
        "{amount} retirés du compte {account} : c'est le PnL de la séance du {session}, repris par la prop firm (infraction à une règle). Compté dans le solde, pas comme payout. Si c'était un payout, corrigez-le depuis le widget Suivi par compte.",
      walletTransferDetected:
        "Transfert de {gross} du compte {account} vers votre wallet, soit {amount} reçus. Ajouté à la carte Wallet du Prop Firm Manager.",
      walletTransferDetectedToConfirm:
        "Transfert de {gross} du compte {account} vers votre wallet, soit {amount} reçus (estimation). Confirmez le montant reçu dans la carte Wallet du Prop Firm Manager.",
      tradovateAccountsDiscoveredOne:
        "1 nouveau compte Tradovate détecté. Importez-le depuis vos comptes.",
      tradovateAccountsDiscovered:
        "{count} nouveaux comptes Tradovate détectés. Importez-les depuis vos comptes.",
      tradovateRealtimeInterrupted:
        "Connexion temps réel interrompue pour {propFirm}. Vos trades restent synchronisés toutes les 15 minutes.",
      tradovateNeedsReauth:
        "Reconnexion requise pour {propFirm} : la synchronisation de vos trades est en pause. Reconnectez depuis vos comptes.",
      mentorFallback: "Nouvelle activité dans votre espace mentor",
      periodMonthly: "mensuel",
      periodQuarterly: "trimestriel",
      periodAnnual: "annuel",
    },
    fundedLineage: {
      importLabel: "Suite du challenge",
      importNone: "Aucun (nouveau compte)",
      importHint: "Relie ce PA au challenge validé : le challenge est clos comme réussi et son suivi (PnL, dépenses, durée) se prolonge sur ce compte.",
      importSuggested: "suggéré",
      targetReached: "objectif atteint",
      inactiveTag: "désactivé",
      cardTitle: "Challenge d'origine",
      cardDescription: "Relie ce compte funded au challenge qui l'a produit, pour suivre le parcours complet (PnL, dépenses, durée de vie).",
      cardLinkedTo: "Suite du challenge {name}",
      cardLinkedHint: "Délier ne réactive pas le challenge.",
      cardSelectPlaceholder: "Choisir le challenge",
      cardNoCandidates: "Aucun challenge éligible : même prop firm, antérieur à ce compte, non cramé et pas déjà relié.",
      cardLinkButton: "Lier",
      cardUnlinkButton: "Délier",
      cardLinked: "Challenge rattaché",
      cardUnlinked: "Lien retiré",
      cardConfirmTitle: "Lier au challenge {name} ?",
      cardConfirmBody: "Le challenge sera désactivé comme réussi et son abonnement prop firm arrêté.",
      cancel: "Annuler",
      modalNone: "Aucun",
      modalLinkHint: "Le challenge choisi sera désactivé comme réussi et son abonnement prop firm arrêté. Le passage en funded apparaît dans le calendrier à la date d'arrivée de ce compte.",
      modalChangeHint: "Changer ou retirer le lien ne réactive pas l'ancien challenge.",
    },
    paymentGrace: {
      modalTitle: "Votre paiement n'a pas abouti",
      modalBody:
        "Nous n'avons pas pu prélever votre abonnement {plan}. Vous gardez l'accès complet pendant encore {days} jours, le temps de mettre à jour votre moyen de paiement.",
      modalBodyLastDay:
        "Nous n'avons pas pu prélever votre abonnement {plan}. Il vous reste aujourd'hui pour mettre à jour votre moyen de paiement.",
      modalConsequence:
        "Sans régularisation, votre compte repassera au plan gratuit le {date}.",
      updatePayment: "Mettre à jour mon paiement",
      later: "Plus tard",
      bannerText:
        "Paiement en échec. Il vous reste {days} jours avant le retour au plan gratuit.",
      bannerTextLastDay:
        "Paiement en échec. Dernier jour avant le retour au plan gratuit.",
      bannerAction: "Régulariser",
      expiredModalTitle: "Votre abonnement a pris fin",
      expiredModalBody:
        "Faute de paiement, votre compte est repassé au plan gratuit. Vos données sont intactes : réactivez votre abonnement pour retrouver toutes vos fonctionnalités.",
      expiredAction: "Réactiver mon abonnement",
      daysLeft: "{days} jours restants",
      oneDayLeft: "Dernier jour",
    },
    newNav: {
      more: "Plus",
      myPages: "Mes pages",
      addPage: "Ajouter une page",
      collapse: "Replier",
      expand: "Déplier",
      settings: "Réglages",
      lockedTooltip: "Disponible avec un plan supérieur",
      menu: "Menu",
      dashboard: {
        overview: "Vue d'ensemble",
      },
    },
  },
  en: {
    lite: liteEn,
    nav: {
      login: "Log in",
      dashboard: "Dashboard",
      journal: "Journal",
      analytics: "Analytics",
      routines: "Routines",
      goals: "Goals",
      notebook: "Notebook",
      disciplineManager: "Discipline",
      accounts: "Accounts",
      account: "Account",
      sessions: "Sessions",
      logout: "Logout",
      tools: "Tools",
      features: "Features",
      pricing: "Pricing",
      faq: "FAQ",
      propFirmManager: "Prop Firm Manager",
      reports: "Reports",
      blog: "Blog",
      mentor: "Mentor",
      affiliate: "Affiliate",
      addPage: "Add a page",
      addPagePlaceholder: "Page name...",
      upgradeMyPlan: "Upgrade plan",
    },
    themePicker: {
      label: "Theme",
      classicDark: "Classic Dark",
      classicLight: "Classic Light",
      deepJungle: "Deep Jungle",
      sunrise: "Sunrise",
      tokyoNight: "Tokyo Night",
      dark: "Dark",
      light: "Light",
      switchTo: "Switch theme",
      comingSoon: "Coming soon",
    },
    disciplineManager: {
      title: "Discipline Manager",
      sections: {
        routines: "Routines",
        goals: "Goals",
        notebook: "Notebook",
      },
    },
    mentor: {
      title: "Mentoring Program",
      dashboard: "Manage your students and invitations.",
      myStudents: "My students",
      myInvitations: "Invitations",
      noStudents: "No students yet. Invite a trader to join your space.",
      noInvitations: "No invitations sent.",
      upgradeToBecomeMentor: "Upgrade to Pro or Max to access the mentoring program.",
      revokeRelation: "Remove student",
      revokeConfirm: "Confirm removing this student? You will no longer have access to their space.",
      resendInvitation: "Resend",
      cancelInvitation: "Cancel",
      viewStudent: "View",
      status: {
        pending: "Pending",
        accepted: "Accepted",
        declined: "Declined",
        revoked: "Revoked",
        expired: "Expired",
      },
      invite: {
        title: "Invite a student",
        emailLabel: "Email address",
        emailPlaceholder: "trader@example.com",
        sendInvitation: "Invitation sent",
        inviteButton: "Invite a student",
        inviteEmailPlaceholder: "trader@example.com",
        inviteSubmit: "Send invitation",
        importList: "Import a list",
        importTitle: "CSV / Excel Import",
        importDescription: "Upload a .csv or .xlsx file containing emails (column named \"email\" or first column with @). Limited to 50 emails.",
        preview: "Preview",
        confirmSend: "Send {count} invitation(s)",
        willBeInvited: "Will be invited",
        alreadyInvited: "Already invited (skipped)",
        maxImport: "50 emails max",
      },
      notifications: {
        invitationReceived: "invited you as a student",
        invitationAccepted: "accepted your invitation",
        invitationDeclined: "declined your invitation",
        relationRevoked: "revoked the mentoring relationship",
        noteAdded: "left a note on your session",
        markAllRead: "Mark all as read",
        noNotifications: "No notifications",
      },
      errors: {
        invalidEmail: "Unsupported file format",
      },
      studentView: "Student view",
      readOnlyMode: "Read-only mode",
      backToStudents: "← My students",
      permissionsTitle: "Tutor access granted",
      canViewJournal: "Journal",
      canViewDashboard: "Dashboard",
      canViewAnalytics: "Analytics",
      canViewSessions: "Sessions",
      canViewPropFirmManager: "Prop Firm Manager",
      mentorNotes: "Mentor notes",
      addNote: "Add note",
      editNote: "Edit",
      deleteNote: "Delete",
      noNotes: "No notes yet.",
      notePlaceholder: "Leave a correction note...",
      saveNote: "Save",
      acceptInvitation: "Accept invitation",
      declineInvitation: "Decline",
      invitationExpired: "This invitation has expired or is no longer valid.",
      invitationNotFound: "Invitation not found.",
      invitationDeclinedConfirm: "Invitation declined.",
      loginToAccept: "Log in to accept this invitation",
      invitationFrom: "Invitation from",
      grantedPermissions: "Access granted:",
      mentorsSection: "My mentors",
      noMentors: "You have no active mentor.",
      revokeMentor: "Revoke",
      revokeMentorConfirm: "Revoke this mentor? They will lose access to your data.",
      activeSince: "Active since",
      pendingInvitations: "Pending invitations",
      globalNotes: "General notes",
      sessionNotes: "Session notes",
      tradeNotes: "Trade notes",
      noPermissionSection: "The student has not granted access to this section.",
    },
    comparator: {
      metaTitle: "Prop firm comparator 2026 | Edgely",
      metaDescription:
        "Compare the best futures prop firms: price, drawdown, platforms, commissions. Find the trading account that fits your style.",
      h1: "Futures prop firm comparator",
      introP1:
        "Compare at a glance the offers from the best futures prop firms: evaluation fees, drawdown type, supported platforms and round-trip commissions.",
      introP2:
        "Filter by account size, platform or challenge type, sort by price or rating, and compare up to 4 offers side by side to pick the firm that fits your style.",
      resultsCount: "{shown} / {total} results",
      filtersTitle: "Filters",
      searchLabel: "Search",
      searchPlaceholder: "Firm or plan name…",
      firmLabel: "Prop firm",
      showAll: "Show all",
      showLess: "Show less",
      sizeLabel: "Account size",
      accountTypeLabel: "Account type",
      drawdownTypeLabel: "Drawdown type",
      platformLabel: "Platform",
      dailyLossLabel: "Daily loss",
      ratingLabel: "Rating",
      maxPriceLabel: "Max price",
      resetFilters: "Reset all filters",
      filtersButton: "Filters",
      typeEval: "Evaluation",
      typeDirect: "Direct",
      ddEod: "EOD",
      ddEot: "EOT",
      ddTrailing: "Trailing",
      ddStatic: "Static",
      dailyAll: "All",
      dailyNone: "None",
      dailyDefined: "Defined",
      ratingAll: "All",
      ratingHigh: "High (8–10)",
      ratingMid: "Mid (4–7)",
      ratingLow: "Low (1–3)",
      colFirm: "Prop firm",
      colSize: "Size",
      colPrice: "Price",
      colBilling: "Billing",
      colActivation: "Activation",
      colPlatforms: "Platforms",
      colDrawdown: "Drawdown",
      colTarget: "Target",
      colRoundTrip: "Round trip",
      colRating: "Rating",
      colAction: "Action",
      sortBy: "Sort by",
      billingOneTime: "One-time",
      billingMonthly: "Monthly",
      free: "FREE",
      total: "Total",
      promoExpires: "Expires {date}",
      copyCode: "Copy code",
      codeCopied: "Code copied",
      startChallenge: "Start the challenge",
      officialSite: "Official site",
      compareCheckbox: "Compare",
      compareBar: "Compare ({n})",
      compareReset: "Reset",
      compareTitle: "Offer comparison",
      compareMax: "Maximum 4 offers",
      bestValue: "Best value",
      suggestFirmTitle: "A trusted firm to add?",
      suggestFirmCta: "Suggest a prop firm",
      edgelyCtaTitle:
        "Picked your prop firm? Journal your trades on it with Edgely.",
      edgelyCtaButton: "Create my free account",
      noResults: "No offer matches these filters.",
    },
    blog: {
      title: "Blog",
      readMore: "Read article",
      backToBlog: "← Back to blog",
      publishedOn: "Published on",
      by: "By",
      noPostsFound: "No articles found.",
      loadMore: "Load more",
      filterByCategory: "Filter by category",
      allCategories: "All categories",
      draft: "Draft",
      published: "Published",
      postNotFound: "Article not found.",
      subtitle: "Tips, strategies and resources for active traders.",
      tableOfContents: "Table of contents",
    },
    help: {
      navLabel: "Help Center",
      metaTitle: "Help Center | Edgely",
      heroTitle: "How can we help you?",
      heroSubtitle: "Quickly find answers to your questions about Edgely.",
      searchPlaceholder: "Search a question or keyword…",
      categoriesTitle: "Browse by category",
      questionsCount: "{count} question(s)",
      searchResultsTitle: "{count} result(s)",
      noResultsTitle: "No results for “{query}”",
      noResultsBody: "Try different keywords or contact our team.",
      backToCategories: "Clear search",
      inCategory: "in {category}",
      notFoundTitle: "Didn't find your answer?",
      notFoundBody: "Our team is here to help. Drop us a line and we'll get back to you quickly.",
      contactCta: "Contact support",
    },
    adminLayouts: {
      title: "Edgely Layouts",
      searchPlaceholder: "Search a layout...",
      scopeAll: "All scopes",
      noLayouts: "No layouts found.",
      owner: "Owner",
      widgets: "widgets",
      edgelyBadge: "Edgely",
      sharedBadge: "Shared",
      markEdgely: "Mark as Edgely",
      unmarkEdgely: "Remove Edgely",
      editMeta: "Edit metadata",
      displayName: "Display name",
      description: "Description",
      requiredPlan: "Required plan",
      allPlans: "All plans",
      saveMetaSuccess: "Metadata saved.",
      flagUpdated: "Edgely flag updated.",
      confirmUnmark: "Remove Edgely flag from this layout?",
    },
    adminBlog: {
      title: "Blog",
      newPost: "New article",
      manageCategories: "Manage categories",
      editPost: "Edit article",
      createPost: "Create article",
      titleField: "Title",
      slugField: "Slug",
      categoryField: "Category",
      noCategory: "No category",
      tagsField: "Tags",
      authorField: "Author",
      coverImage: "Cover image",
      seoSection: "SEO",
      metaTitle: "Meta title",
      metaDescription: "Meta description",
      seoBodyTitle: "SSR body (advanced)",
      seoBodyHelp:
        "Content served only to search-engine crawlers (hidden from visitors). Leave empty to use the generic body.",
      seoBodyH1: "SSR H1",
      seoBodySections: "H2 sections (one per line)",
      seoBodySectionsHelp: "Format: H2 title | Paragraph, one section per line.",
      seoBodyLinks: "Internal links (one per line)",
      seoBodyLinksHelp: "Format: /path | Label, one link per line.",
      excerptField: "Excerpt",
      saveDraft: "Save draft",
      publish: "Publish",
      unpublish: "Unpublish",
      savePublished: "Save",
      deletePost: "Delete",
      confirmDelete: "Delete this article?",
      statusAll: "All",
      statusDraft: "Drafts",
      statusPublished: "Published",
      categoryName: "Name",
      categorySlug: "Slug",
      addCategory: "Add category",
      deleteCategory: "Delete",
      confirmDeleteCategory: "Delete this category?",
      categoryHasPosts: "This category has posts. Reassign them before deleting.",
      postSaved: "Article saved.",
      postPublished: "Article published.",
      postUnpublished: "Article unpublished.",
      postDeleted: "Article deleted.",
      categorySaved: "Category saved.",
      categoryDeleted: "Category deleted.",
    },
    adminHelp: {
      categoriesTitle: "Help categories",
      questionsTitle: "Questions",
      newCategory: "New category",
      newQuestion: "New question",
      nameFr: "Name (FR)",
      nameEn: "Name (EN)",
      slug: "Slug",
      icon: "Icon (Lucide)",
      sortOrder: "Order",
      questionColumn: "Question",
      categoryColumn: "Category",
      statusColumn: "Status",
      published: "Published",
      draft: "Draft",
      edit: "Edit",
      delete: "Delete",
      save: "Save",
      cancel: "Cancel",
      questionFr: "Question (FR)",
      answerFr: "Answer (FR, markdown)",
      questionEn: "Question (EN)",
      answerEn: "Answer (EN, markdown)",
      keywords: "Keywords (synonyms, comma-separated)",
      category: "Category",
      preview: "Preview",
      publish: "Publish",
      unpublish: "Unpublish",
      deleteCategoryBlocked: "Cannot delete a category that still has questions.",
      confirmDelete: "Confirm deletion?",
      itemCount: "{count} questions",
      filterAllStatuses: "All statuses",
      filterAllCategories: "All categories",
      saved: "Saved.",
      deleted: "Deleted.",
      manageQuestions: "Manage questions",
      manageCategories: "Manage categories",
    },
    dashboard: {
      lockedWidget: { title: "Pro feature", description: "Upgrade to Pro to unlock this widget.", cta: "Upgrade to Pro" },
      title: "Dashboard",
      subtitle: "Overview of your performance and activities",
      totalPnl: "Total P&L",
      winRate: "Win Rate",
      profitFactor: "Profit Factor",
      totalTrades: "Total Trades",
      recentTrades: "Recent Trades",
      activeGoals: "Active Goals",
      activeRoutines: "Active Routines",
      viewAll: "View all",
      open: "Open",
      noRecentTrades: "No recent trades",
      noActiveGoals: "No active goals",
      tradesTotal: "trades total",
      tradesOpen: "open",
      addTrade: "Add Trade",
      selectPlan: "Choose your plan",
      selectPlanDescription: "Select the plan that best fits your trading needs.",
      upgradeTitle: "Upgrade to the next level",
      upgradeDescription: "Unlock all advanced features with the Pro plan",
      upgradeButton: "View plans",
      continueWithFree: "Continue with free plan",
      planChange: {
        upgradedTitle: "Plan upgraded",
        upgradedDescription: "Your new plan is active. We only charged the prorated difference.",
        alreadyOnPlan: "You're already on this plan.",
      },
      wins: "Wins",
      losses: "Losses",
      journalLabel: "Journal",
      goalsLabel: "Goals",
      routinesToCompleteToday: "routines to complete today",
      routineToCompleteToday: "routine to complete today",
      manageAccounts: "Manage your accounts",
      goalsWithDeadlineThisWeek: "goals with deadline this week",
      goalWithDeadlineThisWeek: "goal with deadline this week",
      topGoalProgress: "{progress}% toward « {title} »",
      topGoalDeadlineSoon: "⚡ {progress}% · {days}d left · « {title} »",
      routineReminderToday: "⏰ « {name} » to do today",
      routineReminderWeek: "⏰ « {name} » to do this week",
      routineReminderMonth: "⏰ « {name} » to do this month",
      calendar: {
        title: "Calendar",
        previousMonth: "Previous month",
        nextMonth: "Next month",
        trade: "Trade",
        routine: "Routine",
        goal: "Goal",
        completed: "Completed",
        notCompleted: "Not completed",
        more: "more",
        win: "Win",
        loss: "Loss",
        trades: "trades",
        noTrades: "No trades",
        profitDay: "Winning day",
        lossDay: "Losing day",
        routinesCompleted: "routines completed",
        goalDeadline: "Goal deadline",
        weeklyTotal: "Weekly total",
        week: "Wk",
        usMarketClosed: "US market closed",
        euMarketClosed: "EU market closed",
        marketClosed: "closed",
        frPublicHoliday: "Public holiday",
        accountCreated: "Account created",
        accountCrashed: "Account disabled",
        accountCreatedMany: "5+ accounts created",
        accountCrashedMany: "5+ accounts disabled",
        accountFunded: "Account transitioned to funded",
        accountFundedMany: "Accounts transitioned to funded",
        calendarPayout: "Payout",
        calendarPayoutMany: "Payouts",
        hideWeekend: "Hide weekend",
        showWeekend: "Show weekend",
        hideErrors: "Hide errors",
        showErrors: "Show errors",
        errorsOnDay: "Errors made on this day",
        calendarSettings: "Calendar settings",
        settingsWeekend: "Show weekends",
        settingsErrors: "Show session errors",
        settingsTradingDayStart: "Trading day start",
        settingsTradingDayStartDesc: "Trades taken after this hour are counted in the next day",
        violations: {
          dailyLossViolation: "Daily stop loss breached",
          dailyLossWarning: "Approaching daily stop loss",
          consistencyViolation: "Consistency rule violated",
          consistencyWarning: "Approaching consistency rule",
          consistencyViolationDetail: "Consistency rule violated: {share}% on {date} (limit {threshold}%)",
          consistencyWarningDetail: "Approaching consistency rule: {share}% on {date} (limit {threshold}%)",
          tooltipDailyLoss: "Daily loss: {loss} / {max} allowed",
          tooltipConsistency: "This day represents {pct}% of the target (max {max}%)",
        },
      },
      dayDetail: {
        title: "Day summary",
        noTrades: "No trades on this day",
        tradesCount: "trades",
        tradesSummaryOnly: "trades this day, summary by account",
        pnlByAccount: "P&L by account",
        pnlByGroup: "P&L by group",
        total: "Total",
        session: "Session",
        noSession: "No session this day",
        mood: "Mood",
        duration: "Duration",
        performance: "Performance",
        sessionNotes: "Notes",
        routines: "Routines",
        noRoutines: "No active routines",
        completed: "Completed",
        notCompleted: "Not completed",
        goalDeadlines: "Goals, deadline today",
        noGoalDeadlines: "No goal deadline this day",
        marketHolidays: "Market closures",
        marketClosed: "closed",
        symbol: "Symbol",
        direction: "Direction",
        pnl: "P&L",
        account: "Account",
        showAll: "Show all trades",
        hideAll: "Collapse",
        page: "Page",
        of: "of",
        accountEvents: "Accounts",
        noAccountEvents: "No account events",
        fees: "fees",
        deleteAllTrades: "Delete trades",
        deleteAllTradesTitle: "Delete all trades for this day",
        deleteAllTradesConfirm: "This will permanently delete the {count} trades on {date}. This action cannot be undone.",
        deleteAllTradesSuccess: "Trades deleted",
        winRate: "Win rate",
        profitFactor: "Profit factor",
        avgWin: "Avg win",
        avgLoss: "Avg loss",
        bestTrade: "Best trade",
        worstTrade: "Worst trade",
        accountsTraded: "Accounts",
        winsLosses: (wins: number, losses: number) => `${wins} wins · ${losses} losses`,
        intradayCurve: "Intraday curve",
      },
      marketHours: {
        title: "Market Hours",
        open: "Open",
        closed: "Closed",
        opensAt: "Opens at",
        closesAt: "Closes at",
      },
      cumulativePnl: "Cumulative P&L",
      chartModePnl: "Cumulative P&L",
      chartModeNetWorth: "Net Worth",
      accountsLabel: "Trading Accounts",
      noAccounts: "No account connected",
      addAccount: "Add an account",
      currentBalance: "Current balance",
      netPnlLabel: "Net P&L",
      profitFactorLabel: "Profit Factor",
      tradeWinPct: "Trade win %",
      avgWinLoss: "Avg win/loss trade",
      todayRoutines: "Today's routines",
      allRoutinesDone: "All routines are up to date!",
      noActiveRoutines: "No active routine",
      noActiveRoutinesDesc: "Create your first routine to structure your trading.",
      createFirstRoutine: "Create a routine",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      daysLeft: "days left",
      today: "Today",
      topGoals: "Goals in progress",
      noGoalInProgress: "No goal in progress",
      noGoalInProgressDesc: "Set a goal to track your progress.",
      createFirstGoal: "Create a goal",
      allGoalsCompleted: "All goals achieved! 🎯",
      allGoalsCompletedDesc: "Well done! Create new goals to keep improving.",
      goalProgress: "progress",
      noTradesYet: "No trades recorded",
      noTradesYetDesc: "Add your first trade to see your performance curve.",
      accountsTableName: "Account",
      accountsTableFirm: "Prop Firm",
      accountsTableStatus: "Status",
      accountsTableStrategy: "Strategy",
      accountsTableBalance: "Balance",
      accountsTablePnl: "PnL",
      accountsTableObjective: "Objective",
      accountsTableBuffer: "Buffer",
      avgWin: "Avg Win",
      avgLoss: "Avg Loss",
      bufferLeft: "% left",
      bufferUsed: "% used",
      statusFunded: "Funded",
      markRoutineComplete: "Mark as completed",
      period7d: "7D",
      period30d: "30D",
      period90d: "90D",
      customize: "Customize",
      done: "Done",
      addWidget: "Add widget",
      removeWidget: "Remove",
      resetLayout: "Reset",
      resetLayoutConfirm: "Reset the dashboard to its default configuration?",
      resetLayoutConfirmTitle: "Reset dashboard",
      allWidgetsVisible: "All widgets are already visible.",
      noWidgetsVisible: "No widgets visible. Click Customize to add some.",
      widgetSaved: "Configuration saved",
      widgetSaveError: "Save error",
      widgetSizeLabel: "Size",
      resizeWidgetRight: "Resize horizontally",
      resizeWidgetBottom: "Resize vertically",
      resizeWidgetCorner: "Resize",
      moveUp: "Move up",
      moveDown: "Move down",
      widgets: {
        netPnl: "Net P&L",
        profitFactor: "Profit Factor",
        winRate: "Win Rate",
        avgWinLoss: "Avg Win / Loss",
        calendar: "Trading Calendar",
        marketHours: "Market Hours",
        routines: "Today's Routines",
        goals: "Goals",
        session: "Session Launcher",
        equityChart: "Equity Curve",
        accountsTable: "Accounts",
        analyticsKpis: "KPIs Performance",
        equityCurve: "Equity Curve",
        pnlDistribution: "Distribution P&L",
        monthlyPnl: "Monthly P&L",
        symbolPerformance: "Symbol Performance",
        rollingWinRate: "Rolling Win Rate",
        heatmap: "Day/Hour Heatmap",
        behavioralBiases: "Behavioral Biases",
        sessionTimeSlots: "P&L by Time Slot",
        sessionMood: "Mood Analysis",
        sessionDuration: "P&L by Session Duration",
        sessionPnlChart: "P&L per Session",
        outsideSession: "Out-of-session trades",
        sessionInsight: "Key insight",
        sessionsList: "Sessions list",
        lastSession: "Last session",
        pipStats: "Pip Stats",
        pipDistribution: "Pip Distribution",
        pipBySymbol: "Pips by Symbol",
        longShort: "Long vs Short",
        dayOfWeek: "Day of Week Performance",
        pnlPerTrade: "P&L per Trade",
        netDailyPnl: "Net Daily P&L",
        riskAdjusted: "Risk-Adjusted Return",
        drawdownCurve: "Drawdown Curve",
        holdDuration: "Hold Duration",
        consistencyScore: "Consistency Score",
        streakAnalysis: "Streak Analysis",
        tagStats: "Tag Statistics",
        sizingAnalysis: "Sizing Analysis",
        propfirmKpis: "KPIs Prop Firm",
        propfirmCalendar: "Prop Firm Calendar",
        propfirmMonthRecap: "Month Recap",
        propfirmReporting: "Period Reporting",
        propfirmAccounts: "Account Tracking",
        propfirmAccountsTable: "Account Tracking Table",
        propfirmGlobalCharges: "Global Charges",
        propfirmUpcomingCharges: "Upcoming charges",
        propfirmPayoutsList: "Payouts List",
        propfirmFilters: "PFM Filters",
        propfirmWalletBalance: "TPT - Wallet Balance",
        propfirmWalletTransfers: "TPT - Transfers",
        propfirmWalletWithdrawals: "TPT - Withdrawals",
        propfirmChallengeOverview: "Account success rate",
        note: "Note",
        disciplineRoutines: "Routines",
        disciplineGoals: "Goals",
        notebookBrowser: "Notebook",
        mentorNotes: "Mentor notes",
        mentorNotesEmpty: "No notes from your mentor yet",
        topbarDateFilter: "Date filter",
        topbarAccountFilter: "Account filter",
        topbarStrategyFilter: "Strategy filter",
        topbarAddTrade: "Add trade",
        topbarAddPayout: "Add payout",
        topbarPfmFilter: "Prop Firm Filter",
        topbarPfmFilterAll: "All prop firms",
        topbarPhaseFilter: "Phase filter",
        topbarPhaseFilterAll: "All statuses",
        topbarPhaseFilterChallenge: "Challenge",
        topbarPhaseFilterPa: "Funded (PA)",
        topbarPhaseFilterLive: "Live",
        newsFeed: "News Feed",
        interestRates: "Interest Rates",
        economicCalendar: "Economic Calendar",
        earningsCalendar: "Earnings Calendar",
        chart: "Chart",
        tradesTable: "Trades List",
        topbarSymbolFilter: "Symbol filter",
        topbarSideFilter: "Side filter (Long/Short)",
        topbarStatusFilter: "Status filter",
        topbarPnlFilter: "P&L filter",
        topbarTagsFilter: "Tags filter",
      },
      widgetLock: {
        proFeatureLabel: "Pro Feature",
      },
      widgetsDesc: {
        netPnl: "Net profit or loss over the selected period",
        profitFactor: "Gain / loss ratio",
        winRate: "Percentage of winning trades",
        avgWinLoss: "Average win and loss per trade",
        calendar: "Monthly calendar with trades, routines and goals",
        marketHours: "Real-time status of main stock exchanges",
        routines: "Routines to complete today",
        goals: "Your most urgent goals",
        session: "Active trading session with timer",
        equityChart: "Cumulative P&L or net worth chart",
        accountsTable: "List of your trading accounts with P&L and progress",
        analyticsKpis: "Advanced metrics: Sharpe, Drawdown, Expectancy, Calmar, Recovery",
        equityCurve: "Cumulative equity curve with granularity selector",
        pnlDistribution: "Trade distribution by P&L range",
        monthlyPnl: "P&L aggregated by month",
        symbolPerformance: "Performance and statistics by symbol",
        rollingWinRate: "Rolling win rate over 20 trades",
        heatmap: "Performance visualization by day and hour",
        behavioralBiases: "Bias detection: FOMO, revenge, loss aversion",
        sessionTimeSlots: "P&L and stats by session time slot",
        sessionMood: "Mood analysis and its impact on performance",
        sessionDuration: "Average P&L grouped by session duration bucket",
        sessionPnlChart: "P&L of each individual session as a bar chart",
        outsideSession: "Performance of trades executed outside a trading session",
        sessionInsight: "Automatic insight derived from your session analysis",
        sessionsList: "List of your recent sessions with date, duration, trades and PnL",
        lastSession: "Condensed view of your last completed session",
        pipStats: "Overall pip statistics",
        pipDistribution: "Trade distribution by pip range",
        pipBySymbol: "Pip performance by symbol",
        longShort: "Long vs short performance comparison",
        dayOfWeek: "Average performance by day of the week",
        pnlPerTrade: "P&L of each trade in chronological order",
        netDailyPnl: "Net P&L aggregated by calendar day",
        riskAdjusted: "Calmar ratio and recovery factor",
        drawdownCurve: "Drawdown evolution from the equity peak",
        holdDuration: "Trade count, win rate and P&L by hold duration",
        consistencyScore: "Overall consistency score (0-100) and sub-scores",
        streakAnalysis: "Win/loss streaks and risk management",
        tagStats: "Performance statistics by tag",
        sizingAnalysis: "Position size evolution over time",
        propfirmKpis: "Global KPIs: payouts, expenses, ROI",
        propfirmCalendar: "Prop firm events and results calendar",
        propfirmMonthRecap: "Monthly prop firm recap",
        propfirmReporting: "Performance reporting by period",
        propfirmAccounts: "Detailed tracking by prop firm account",
        propfirmAccountsTable: "Payout tracking table by account",
        propfirmGlobalCharges: "Global and per-account charges management",
        propfirmUpcomingCharges: "Charges in the next 30 days, past charges to confirm and subscriptions to cancel",
        propfirmPayoutsList: "List of all received payouts",
        propfirmFilters: "Date range, account and strategy filters for Prop Firm widgets",
        propfirmWalletBalance: "Available balance and wallet history",
        propfirmWalletTransfers: "Table of transfers received in the wallet",
        propfirmWalletWithdrawals: "Table of withdrawals from the wallet",
        propfirmChallengeOverview: "Total accounts, accounts with payout and success rate",
        note: "Display a notebook note in read/write mode directly in the dashboard",
        mentorNotes: "General notes left by your mentor",
        mentorNotesEmpty: "",
        topbarDateFilter: "Date period filter displayed in the top bar",
        topbarAccountFilter: "Account filter displayed in the top bar",
        topbarStrategyFilter: "Strategy filter displayed in the top bar",
        topbarAddTrade: "Add trade button in the top bar",
        topbarAddPayout: "Add payout button in the top bar",
        topbarPfmFilter: "Prop Firm filter in the top bar",
        topbarPfmFilterAll: "All prop firms selector in the top bar",
        topbarPhaseFilter: "Phase filter in the top bar",
        topbarPhaseFilterAll: "All statuses selector in the top bar",
        topbarPhaseFilterChallenge: "Challenge",
        topbarPhaseFilterPa: "Funded (PA)",
        topbarPhaseFilterLive: "Live",
        newsFeed: "Real-time financial news feed",
        interestRates: "Key interest rates from major central banks",
        economicCalendar: "Upcoming macroeconomic events and data releases",
        earningsCalendar: "Upcoming corporate earnings announcements",
        chart: "Candlestick chart with trade entry/exit markers",
      },
      widgetCategories: {
        dashboard: "Dashboard",
        analytics: "Analytics",
        analyticsPerformance: "Analytics, Performance",
        analyticsSessions: "Analytics, Sessions",
        sessions: "Sessions",
        analyticsPips: "Analytics, Pips",
        propFirm: "Prop Firm",
        notes: "Notes",
        mentorProgram: "Mentor Program",
        topbar: "Toolbar",
        marketData: "News & Market Data",
        journal: "Journal",
        discipline: "Discipline, Routines, goals & notes",
      },
      // Rows + slots
      rowLabel: "Row {n}",
      addRow: "Add row",
      removeRow: "Remove row",
      removeRowConfirm: "Delete this row and all its widgets?",
      removeRowConfirmTitle: "Remove row",
      rowColumnsLabel: "Columns",
      rowColumnsCount: "{n} columns",
      changeRowColumns: "Change column count",
      changeRowColumnsWarning: "Reducing the column count will remove widgets that overflow. Continue?",
      addRowPosition: "Position",
      addRowPositionBottom: "Bottom",
      addRowPositionBefore: "Before row {n}",
      addRowPositionAfter: "After row {n}",
      widgetMinCols: "Min {n} col",
      widgetAlreadyPlaced: "Already placed",
      noCompatibleWidgets: "No widget compatible with this slot.",

      layouts: {
        menuTitle: "My layouts",
        activeBadge: "Active",
        defaultBadge: "Default",
        newLayout: "New layout",
        duplicate: "Duplicate active layout",
        rename: "Rename active layout",
        share: "Share…",
        importLink: "Import a shared link…",
        delete: "Delete active layout",
        upgradeForMultiple: "Available with Pro",
        cannotDeleteLast: "You must keep at least one layout",

        renameDialogTitle: "Rename layout",
        renameInputLabel: "Name",
        deleteDialogTitle: "Delete layout",
        deleteDialogConfirm: "Are you sure you want to delete \"{name}\"?",
        deleteDialogWarning: "This action is permanent and will delete all rows of this layout.",

        shareDialogTitle: "Share \"{name}\"",
        shareToggle: "Enable link sharing",
        shareWarning: "Anyone with this link will be able to import this layout into their own library.",
        copyLink: "Copy link",
        linkCopied: "Link copied!",

        importPromptTitle: "Import a layout",
        importPromptInputLabel: "Paste the shared link or code here",
        importedSuccess: "Layout imported into your library",

        sharePreviewBy: "Layout shared by @{username}",
        sharePreviewMeta: "{rows} rows · {widgets} widgets",
        importToLibrary: "Import to my library",
        previewLoginRequired: "You must be logged in to import this layout.",
        previewLoginCta: "Log in",
        previewLinkInactive: "This link is no longer active",
        previewLinkInactiveDesc: "The owner of this layout has disabled sharing or deleted the layout.",

        libraryTitle: "Layout Library",
        sourceMyLayouts: "My layouts",
        sourceEdgely: "Edgely",
        sourceCommunity: "Community",
        wrongScopeTooltip: "This layout is designed for {scope}",
        newLayoutPlaceholder: "Layout name...",
        previewEmpty: "Select a layout to see the preview",
        communityEmpty: "No shared layouts yet. Share yours from My Layouts!",
        edgelyEmpty: "No Edgely layouts available.",
        authorLabel: "by {username}",
        actionsRename: "Rename",
        actionsDuplicate: "Duplicate",
        actionsShare: "Share",
        actionsDelete: "Delete",
        importOnThisPage: "Import on this page",
        importNewPage: "Import to a new page",
        importSaveOnly: "Save to my layouts",
        importActivate: "Activate this layout",
        importConfirmTitle: "Import this layout",
        importConfirmOnThisPage: "Replace the current layout on this page",
        importConfirmNewPage: "Create a new Custom page with this layout",
        importConfirmSaveOnly: "Add to my layouts without activating",
        shareLibraryToggle: "Visible in the community library",
        shareDisplayNameLabel: "Public name",
        shareDescriptionLabel: "Description",
        shareDescriptionPlaceholder: "Explain what this layout shows and how it works...",
        shareSettingsSaved: "Share settings saved",
        newPageWithLayout: "Create a page with this layout",
        newPageBlank: "Start from a blank layout",
        scopeDashboard: "Dashboard",
        scopePfm: "Prop Firm Manager",
        scopeCustomPage: "Custom Pages",
        noName: "(unnamed)",
        requiresPro: "Requires Pro",
      },
    },
    common: {
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      delete: "Delete",
      create: "Create",
      update: "Update",
      loading: "Loading...",
      error: "Error",
      success: "Success",
      close: "Close",
      back: "Back",
      next: "Next",
      previous: "Previous",
      search: "Search",
      filter: "Filter",
      actions: "Actions",
      date: "Date",
      time: "Time",
      duration: "Duration",
      minutes: "minutes",
      validationError: "A validation error occurred",
      tradeLimitReached: "Trade Limit Reached",
      tradeLimitWarning: "You have reached your limit of 40 active trades. Creating this trade will automatically delete your oldest trade. Do you want to proceed?",
      proceed: "Proceed",
      soon: "Soon",
      beta: "Beta",
    },
    validation: {
      fieldRequired: "The field \"{field}\" is required",
      fieldInvalid: "The field \"{field}\" is invalid",
      stringTooShort: "The field \"{field}\" is required",
      stringTooLong: "The field \"{field}\" is too long",
      numberTooSmall: "The field \"{field}\" must be greater than {minimum}",
      numberTooBig: "The field \"{field}\" must be less than {maximum}",
      arrayTooSmall: "The field \"{field}\" must contain at least {minimum} item(s)",
      invalidType: "The field \"{field}\" has an invalid type",
      invalidEnum: "The field \"{field}\" has an invalid value",
      invalidDate: "The field \"{field}\" must be a valid date",
      invalidEmail: "The field \"{field}\" must be a valid email address",
      invalidString: "The field \"{field}\" is invalid",
      validationError: "A validation error occurred",
    },
    auth: {
      login: "Login",
      signup: "Sign up",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm password",
      forgotPassword: "Forgot password",
      resetPassword: "Reset password",
      requestPasswordReset: "Request password reset",
      passwordResetRequested: "If an account exists with this email, you will receive a reset link.",
      passwordResetSuccess: "Your password has been reset successfully.",
      passwordResetError: "An error occurred while resetting your password.",
      noAccount: "Don't have an account yet?",
      goToSignup: "Sign up",
      forgotPasswordQuestion: "Forgot your password?",
      resetIt: "Reset it",
      ifEverythingOk: "Already have an account?",
      goToLogin: "Sign in",
      emailPlaceholder: "your@email.com",
      passwordPlaceholder: "Your password",
      confirmPasswordPlaceholder: "Confirm your password",
      passwordsDoNotMatch: "Passwords do not match",
      passwordsMatch: "✓ Passwords match",
      passwordTooShort: "Password must be at least 8 characters",
      passwordMinLength: "8 characters minimum",
      signupError: "An error occurred during sign up",
      signingUp: "Signing up...",
      firstName: "First name",
      lastName: "Last name",
      firstNamePlaceholder: "John",
      lastNamePlaceholder: "Doe",
      firstNameRequired: "First name is required",
      lastNameRequired: "Last name is required",
      emailVerificationPending: "Check your inbox and click the confirmation link.",
      emailVerificationCheckSpam: "Can't find the email? Check your spam folder.",
      emailVerificationVerifying: "Verifying...",
      emailVerificationSuccess: "Your email has been verified. You can now log in.",
      emailVerificationError: "The verification link is invalid or has expired.",
      emailVerificationInvalidToken: "Invalid link. Check the email you received or your spam folder.",
      resendEmail: "Resend email",
      resendEmailLoading: "Sending...",
      resendEmailCooldown: "Resend ({seconds}s)",
      resendEmailSuccess: "Email sent! Check your inbox.",
      resendEmailError: "Failed to send email. Please try again.",
    },
    account: {
      title: "Account Information",
      email: "Email address",
      username: "Username",
      about: "About",
      plan: "Your Plan",
      freePlan: "Free Plan",
      upgradeToPro: "Upgrade to Pro",
      managePayment: "Manage Payment Details",
      notDefined: "Not defined",
      noDescription: "No description",
      profileUpdated: "Profile updated",
      updateError: "Unable to update",
      statusActive: "Active",
      statusCancelAtPeriodEnd: "Canceling",
      statusPastDue: "Past due",
      renewalOn: "Renews on",
      expiresOn: "Expires on",
      inDays: "in {days} day(s)",
      tomorrow: "tomorrow",
      today: "today",
      pastDueWarning: "Please update your payment details to avoid suspension.",
      statusDeleted: "Subscription expired",
      preferences: "Preferences",
      hideWeekend: "Hide weekends in calendar",
      showCalendarErrors: "Show session errors in calendar",
      hideInactiveAccounts: "Hide inactive accounts",
    },
    pricing: {
      title: "Pick your pricing",
      subtitle: "Select the plan that fits your trading needs.",
      perMonth: "/month",
      perYear: "/year",
      annual: "Annual",
      monthly: "Monthly",
      savePercent: "Save 20%",
      billedAnnually: "Billed €{amount}/year",
      manageSubscription: "Manage Subscription",
      cta: {
        free: "Get started",
        buy: "Buy plan",
        login: "Log in to buy plan",
      },
      checkout: {
        successTitle: "🥳 Payment Successful!",
        canceledTitle: "😢 Payment Canceled.",
        activating: "Activating your plan...",
        redirecting: "Redirecting to your account...",
        pendingTitle: "Payment not completed",
        pendingDescription:
          "We couldn't confirm your payment - the bank authentication may have failed. Your plan was not changed and you have not been charged. You can try again from your account.",
        backToAccount: "Back to my account",
      },
      beta: {
        banner: "🎉 Beta open, Free access during launch. Limited spots.",
        comingSoon: "Coming soon",
        comingSoonTooltip: "Paid plans are coming soon. You'll be notified!",
        modalTitle: "Paid plans coming soon",
        modalDescription: "Enjoy all features for free during the beta. You'll be notified as soon as the Pro plan is available.",
      },
      free: {
        price: "Free",
        description: "To log your first trades and discover your patterns",
        features: {
          trades: "Unlimited trades",
          accounts: "1 trading account",
          sessions: "Unlimited sessions",
          routines: "3 routines",
          goals: "3 goals",
          analytics: "Basic analytics",
          support: "Community support",
        },
      },
      pro: {
        description: "For the active prop trader with 1 to 3 accounts",
        badge: "Popular",
        priceAnnual: "€12",
        totalAnnual: "€144/year",
        savingsPercent: "Save 20%",
        features: {
          trades: "Unlimited trades",
          accounts: "Unlimited accounts (prop firms)",
          sessions: "Unlimited sessions",
          routines: "Unlimited routines",
          goals: "Unlimited goals",
          analytics: "Advanced analytics",
          support: "Priority support",
          propFirm: "Prop Firm Manager",
          customPages: "Modular dashboard & custom pages",
          mentorMode: "Mentor mode (student coaching)",
          futureFeatures: "All future features included",
        },
      },
      max: {
        name: "Max",
        description: "For the multi-firm prop trader who pulls cash out regularly",
        priceAnnual: "€23",
        totalAnnual: "€279/year",
        savingsPercent: "Save 20%",
        features: {
          allPro: "All Pro features",
          metaApi: "MetaTrader 4/5 sync (1 account)",
          tradovate: "Tradovate auto-sync (read-only)",
          support: "Priority support",
          futureFeatures: "All future features included",
        },
      },
    },
    metaApi: {
      sectionTitle: "MetaTrader Connection",
      comingSoonNote: "Automatic MetaTrader sync is coming soon. In the meantime, add your trades via CSV import or manual entry.",
      connect: "Connect MetaTrader",
      disconnect: "Disconnect",
      syncNow: "Sync now",
      deactivate: "Deactivate",
      reactivate: "Reactivate",
      retry: "Retry",
      retrying: "Retrying connection…",
      errorFriendly: "Broker connection failed. Try again; if the problem persists, check your credentials.",
      errorTransient: "The MetaTrader service didn't respond in time. Retrying automatically…",
      status: {
        pending: "Connecting…",
        active: "Connected",
        syncing: "Syncing…",
        idle: "Connected",
        error: "Sync error",
        paused: "Inactive",
        deprovisioned: "Disconnected",
        notConnected: "Not connected",
      },
      connectModal: {
        title: "Connect a MetaTrader account",
        description: "Enter your MetaTrader login credentials. They are encrypted and stored securely.",
        loginLabel: "Login (account number)",
        loginPlaceholder: "e.g. 12345678",
        passwordLabel: "Account password",
        serverLabel: "Broker server",
        serverPlaceholder: "e.g. ICMarkets-Demo02",
        platformLabel: "Platform",
        submitButton: "Connect",
        connecting: "Connecting…",
        cancel: "Cancel",
      },
      syncBadge: {
        lastSync: "Last sync",
        never: "Never",
        newTrades: "trades imported",
        initialSyncInProgress: "Importing trade history…",
        nextSync: "Next auto sync",
        suspendedNight: "Sync suspended · night",
        suspendedWeekend: "Sync suspended · weekend",
        pollQuota: "Manual polls",
        pollQuotaReset: "resets Monday",
        pollQuotaExhausted: "Quota reached · resets Monday",
        jobQueued: "Sync launched in background…",
      },
      errors: {
        limitReached: "Limit reached: 1 MetaTrader account maximum on Max plan.",
        connectionFailed: "Connection failed. Please check your MetaTrader credentials.",
        syncFailed: "Synchronization failed.",
        notPro: "MetaTrader sync is reserved for Max plan users.",
        syncInProgress: "A sync is already in progress.",
        quotaExhausted: "Weekly quota reached (5/5). Available again Monday.",
      },
      upsell: {
        title: "Automatic MetaTrader sync",
        description: "Upgrade to Max to connect 1 MT4/MT5 account and import your trades automatically.",
        cta: "Upgrade to Max",
      },
    },
    tradovate: {
      syncChoiceDescription: "Read-only connection: your Tradovate trades are imported and synced automatically.",
      maxPlanRequired: "Max plan only",
      wizardTitle: "Connect Tradovate",
      addAccount: "Add account",
      addAccountTitle: "Add a Tradovate account",
      sectionTitle: "Tradovate sync",
      sectionFirmCount: "{count} prop firm(s)",
      firmAccountCount: "{count} synced account(s)",
      addAccountForFirm: "Add a {firm} account",
      assignPropFirm: "Link a prop firm",
      assignPropFirmHint: "Identifies this connection and applies to accounts imported from now on. Already-imported accounts keep their current prop firm.",
      chooseTradovateTarget: "Add to an existing prop firm, or connect a new one",
      addAccountHere: "Add an account here",
      connectNewPropFirm: "Connect a new prop firm",
      pickPropFirm: "Choose the prop firm",
      pickPropFirmHint: "Sets the rules applied to all imported accounts.",
      otherPropFirm: "Other / none",
      environment: "Environment",
      envLive: "Live",
      envDemo: "Demo",
      continueToTradovate: "Continue to Tradovate",
      reviewTitle: "Tradovate accounts found",
      reviewSubtitle: "{count} account(s) ready to import.",
      tierLabel: "Size",
      tierPlaceholder: "Pick a size",
      statusLabel: "Status",
      costLabel: "Price paid (USD)",
      costPlaceholder: "e.g. 120",
      costHint: "New account: enter the price to track your costs.",
      lowConfidenceHint: "Confirm the detected size",
      knownAccountHint: "Already tracked: change its size and status from the Accounts page.",
      groupLabel: "Group",
      groupNone: "No group",
      groupNew: "New group",
      newGroupNameLabel: "New group name",
      groupNamePlaceholder: "Group name",
      strategyLabel: "Strategy (optional)",
      strategyNone: "No strategy",
      importButton: "Import & sync",
      importing: "Importing…",
      statusActive: "Synced",
      statusSyncing: "Syncing…",
      statusNeedsReauth: "Reconnection required",
      statusError: "Error",
      lastSync: "Last sync",
      syncNow: "Sync now",
      fullResync: "Re-import recent trades",
      reconnect: "Reconnect",
      moreActions: "More actions",
      disconnect: "Disconnect",
      disconnectConfirm: "Disconnect Tradovate? Auto-sync will stop. Your already imported trades are kept.",
      oauthErrorTitle: "Tradovate connection failed",
      oauthErrorDescription: "The connection to Tradovate didn't complete. Please try again.",
      oauthPlanRequiredDescription: "Tradovate sync is part of the Max plan. Reactivate your subscription to get it back.",
      connectionBannerTitle: "Tradovate connection",
      loadingAccountsTitle: "Fetching your Tradovate accounts…",
      loadingAccountsHint: "We're asking Tradovate to list your accounts and balances. This can take a few seconds.",
      loadAccountsErrorTitle: "Couldn't fetch your accounts",
      loadAccountsErrorHint: "Tradovate didn't respond correctly. Check your connection and try again.",
      loadAccountsRateLimitedTitle: "Tradovate is throttling our requests",
      loadAccountsRateLimitedHint: "We couldn't read your account balances, and without them the account size would be detected incorrectly. Nothing was imported. Please try again in a few hours; if it keeps happening, contact support.",
      loadAccountsBalanceErrorTitle: "Account balances unavailable",
      loadAccountsBalanceErrorHint: "One account's balance is unreadable or zero, and without it its size would be detected incorrectly. Nothing was imported. If that account is empty or closed, try again once it no longer shows up in Tradovate; otherwise contact support.",
      retry: "Retry",
      noAccountsTitle: "No accounts found",
      noAccountsHint: "Tradovate returned no accounts for this connection.",
      syncRunningTitle: "Sync in progress",
      syncRunningHint: "We're importing your history from Tradovate. Your totals fill in as we go and may still be incomplete.",
      syncThrottledTitle: "Partial sync",
      syncThrottledHint: "Tradovate throttled our requests on the last cycle, so some accounts may have been skipped. Syncing resumes automatically on the next cycle.",
      beamFlowLabel: "Sync flow",
      beamNoAccountsTitle: "No account synced yet",
      beamNoAccountsHint: "The connection is live, you just need to import an account.",
      beamAccountsMore: "+{count} accounts",
      environmentDemo: "Evaluation / PA",
      environmentLive: "Live",
      demoBannerTitle: "Demo data",
      demoBannerHint: "Simulated connections to preview the layout. No real data.",
      errorConnectionFailed: "Tradovate connection failed",
      errorLimitReached: "Connection limit reached",
      errorGeneric: "Something went wrong",
      syncErrorGeneric: "Sync failed. Try again or reconnect your account.",
      statusActiveNoAccounts: "Sync active, no active accounts",
      statusSyncedLive: "Live sync",
      statusSyncedStarting: "Synced, live sync starting",
      statusSyncedStartingHelp:
        "Connection successful. Live sync turns on once the first sync finishes, usually within 15 minutes. Your trades are already coming in, nothing to do.",
      statusSyncedDelayed: "Synced with a delay",
      statusSyncedDelayedHelp:
        "Your trades keep coming in, with a delay of up to 15 minutes. Reconnect the prop firm to restore live sync.",
      statusActiveNoAccountsHint:
        "The connection works, but no synced account is active. Import an account or reactivate one to see your trades.",
      lastSyncLive: "Trades received just now",
      statusDelayed: "Sync delayed, retrying automatically",
      statusIdle: "Sync paused",
      statusIdleHint: "No recent activity: the connection went to sleep. Reconnect to resume syncing.",
      statusForeignToken: "Wrong Tradovate credentials",
      statusForeignTokenHint:
        "None of this connection's accounts was found at the broker: the connection was probably authorized with another prop firm's credentials. Reconnect with this prop firm's credentials.",
      discoveredBannerTitle: "{count} new Tradovate account(s) detected",
      discoveredImport: "Import",
      discoveredDismiss: "Dismiss",
      foreignBannerTitle: "These accounts look like they belong to another prop firm",
      foreignBannerBody:
        "The detected accounts do not look like {propFirm} accounts. This happens when the Tradovate connection was authorised with another prop firm's credentials.",
      foreignBannerHint:
        "You can dismiss them, then start the connection again with the right credentials. Your accounts and their trade history are kept: reconnecting the correct prop firm links them back without losing anything.",
      foreignBadge: "Other prop firm?",
    },
    session: {
      title: "Session",
      start: "Start",
      pause: "Pause",
      resume: "Resume",
      stop: "Stop",
      remaining: "Remaining",
      paused: "Paused",
      started: "Session started",
      evaluation: "Session Evaluation",
      mood: "Mood",
      performance: "Performance",
      mentalState: "Mental State",
      opportunities: "Opportunities identified",
      openTrades: "Open trades",
      closedTrades: "Closed trades",
      totalPnl: "Total P&L",
      notes: "General notes",
      maxRunup: "Max Run-up ($)",
      maxDrawdown: "Max Drawdown ($)",
      maxRunupPlaceholder: "Ex: 250.00",
      maxDrawdownPlaceholder: "Ex: -150.00",
      selfEvaluation: "Self-evaluation and improvement points",
      endSession: "End session",
      skipEvaluation: "End without evaluation",
      sessionEnded: "Session ended",
      tip: "Tip",
      durationLabel: "Duration (minutes)",
      tipText: "You can find all your session evaluations in the Analytics page to track your progress and improve.",
      moodPlaceholder: "Select your mood",
      performancePlaceholder: "Select your performance",
      mentalStatePlaceholder: "E.g.: Focused, distracted, tired...",
      opportunitiesPlaceholder: "Describe the opportunities you spotted during this session...",
      notesPlaceholder: "General notes about the session...",
      lastSessionEmpty: "No completed session yet.",
      lastSessionOpen: "View detail",
      lastSessionVideo: "Video",
      lastSessionPnl: "Total PnL",
      lastSessionRunup: "Max runup",
      lastSessionDrawdown: "Max drawdown",
      selfEvaluationPlaceholder: "What did you do well? What could be improved? What did you learn?",
      moodExcellent: "Excellent",
      moodGood: "Good",
      moodNeutral: "Neutral",
      moodPoor: "Poor",
      moodBad: "Bad",
      perfExcellent: "Excellent",
      perfGood: "Good",
      perfAverage: "Average",
      perfPoor: "Poor",
      tradovateImport: "Import Tradovate trades",
      tradovatePastePlaceholder: "Paste the table copied from Tradovate here (Symbol, Qty, Buy Price, Buy Time...)",
      tradovateOrUploadCSV: "or import a CSV file",
      tradovateUploadCSV: "Choose a CSV file",
      tradovateParseSuccess: "trades detected, ready to import",
      tradovateParseErrorEmpty: "The file is empty or corrupted. Re-download the export from Tradovate.",
      tradovateParseErrorFormat: "Unrecognized format. Use the \"Performance.csv\" export from Tradovate.",
      tradovateParseErrorNoTrades: "No complete trade found in this file.",
      topstepxImport: "Import TopstepX trades",
      topstepxPastePlaceholder: "Paste trades copied from TopstepX here...",
      topstepxParseError: "Format not recognized. Make sure to copy trades from TopstepX.",
      topstepxUnmatchedFillsWarning: "Partial import: {count} order(s) could not be matched to a complete trade. Make sure your paste contains the full session.",
      tradovateImportConfirm: "Import to journal",
      tradovateImportDone: "trades imported successfully",
      tradovateImportFailed: "Error importing trades",
      noTimerLabel: "No timer",
      elapsed: "Elapsed",
      openSessionNotes: "Open session (notes)",
      startedNoTimerDescription: "Session started without a timer. Take notes in the session page.",
      sessionEndedDescription: "Your session has been saved. You can view it in the Sessions page.",
      evaluationDescription: "Share your thoughts on this session to improve",
      preMoodTitle: "How are you feeling?",
      preMoodDescription: "Record your mood before you start trading.",
      errors: "Errors",
      errorsPlaceholder: "No errors",
      errorCreateNew: "+ New error",
      errorNamePlaceholder: "Error name",
      errorDelete: "Remove from catalog",
      errorDeleteConfirm: "Remove this error from all sessions?",
    },
    sessions: {
      title: "Trading Sessions",
      subtitle: "Browse and filter your session history",
      filterMood: "Mood",
      filterPerformance: "Performance",
      filterDurationMin: "Min duration (min)",
      filterDurationMax: "Max duration (min)",
      filterReset: "Reset filters",
      filterDateStart: "From",
      filterDateEnd: "To",
      colDate: "Date",
      colMood: "Mood",
      colPerformance: "Performance",
      colRunup: "Max Run-up",
      colDrawdown: "Max Drawdown",
      colPnl: "Session P&L",
      colDuration: "Duration",
      colVideo: "Video",
      noSessions: "No sessions recorded yet. Start your first session from the dashboard.",
      noSessionsFiltered: "No sessions match the selected filters.",
      viewDetail: "View detail",
      viewAll: "View all sessions",
      allMoods: "All moods",
      allPerformances: "All performances",
      addSession: "New session",
      colName: "Name",
      editNameTitle: "Edit name",
      editNameSave: "Save",
      editNamePlaceholder: "Session name...",
      activeSession: "Ongoing session",
      inProgress: "In progress...",
      autoDetected: "Session automatically detected ({start} → {end})",
      autoDetectedMultiple: "{count} sessions automatically detected",
      autoDetectedLimitReached: "Session detected but monthly limit reached, Upgrade to Pro",
      autoDetectedViewSession: "View session",
      pendingMergeBanner: "An existing session can be extended ({start} → {end}, {count} trades)",
      pendingMergeAccept: "Accept",
      pendingMergeDismiss: "Dismiss",
      pendingMergeTitle: "Pending session merges",
      autoBadge: "Auto",
      accountsCount: "{count} accounts",
      pendingMergeSubtitle: "Recent trades overlap an existing session.",
      onboardingBanner: "Click on a session in the list below to view its detail and complete this step.",
      colErrors: "Errors",
      chartTitle: "Session chart",
      tradesAddedCount: "{count} trade(s) added",
    },
    sessionDetail: {
      back: "Back to sessions",
      title: "Session detail",
      timing: "Timing",
      evaluation: "Evaluation",
      metrics: "Metrics",
      opportunities: "Opportunities spotted",
      selfEval: "Self-evaluation",
      notes: "General notes",
      startedAt: "Start",
      endedAt: "End",
      duration: "Actual duration",
      planned: "Planned duration",
      mentalState: "Mental state",
      saved: "Session updated",
      saveError: "Error while saving",
      delete: "Delete session",
      deleteConfirm: "Confirm deletion of this session? This action is irreversible.",
      deleted: "Session deleted",
      pnl: "Session P&L",
      maxRunup: "Max Run-up",
      maxDrawdown: "Max Drawdown",
      noValue: "",
      videoTitle: "Session recording",
      videoInvite: "Add the YouTube URL of your recorded session",
      videoPlaceholder: "Paste your session's YouTube URL...",
      videoSave: "Save",
      videoCancel: "Cancel",
      videoEdit: "Edit video",
      videoDelete: "Remove video",
      videoInvalidUrl: "Invalid YouTube URL",
      videoSaved: "Video saved",
      videoDeleted: "Video removed",
      notFound: "Session not found",
      nameLabel: "Session name",
      namePlaceholder: "Name this session...",
      trades: "Session Trades",
      addTrade: "Add Trade",
      noTrades: "No trades recorded during this session.",
      shareButton: "Share",
      shareDialogTitle: "Share this session",
      shareDialogDescription: "Generate a unique link to let your coach view this session in read-only mode.",
      shareEnable: "Enable sharing",
      shareDisable: "Disable sharing",
      shareLinkLabel: "Share link",
      shareCopy: "Copy link",
      shareCopied: "Link copied!",
      shareEnabled: "Sharing enabled",
      shareDisabled: "Sharing disabled",
      shareError: "Error while sharing",
      errors: "Errors",
      pnlByAccount: "P&L by account",
      noAccount: "No account",
      sessionAccountsTitle: "Accounts active in this session",
      sessionContribution: "session",
    },
    journal: {
      title: "Trading Journal",
      subtitle: "Manage and track all your trades",
      createTrade: "New Trade",
      importTrades: "Import Trades",
      addTradeTitle: "Add trades",
      addTradeSubtitle: "Choose how you want to add your trades",
      methodManual: "Manual trade",
      methodManualDescription: "Enter a trade manually with all details",
      methodTradovate: "Paste from Tradovate",
      methodTradovateDescription: "Paste the trades table copied from your Tradovate account report",
      methodTopstepX: "Paste from Topstep X",
      methodTopstepXDescription: "Paste trades copied from your Topstep X account report",
      methodTopstepXNoAccount: "No Topstep account or group, create one first",
      methodCsvImport: "Import CSV",
      methodCsvImportDescription:
        "Import an edgely, Tradovate, TopstepX or Quantower CSV",
      topstepxImportTitle: "Paste Topstep X trades",
      topstepxImportSubtitle: "Copy the trades from your Topstep X report and paste them below",
      selectTopstepAccountOrGroup: "Topstep account or group",
      noTopstepAccountAvailable: "No Topstep account or group available",
      selectAccount: "Trading account",
      selectAccountPlaceholder: "Select an account (optional)",
      noAccount: "No account",
      tradovateImportTitle: "Paste Tradovate table",
      tradovateImportSubtitle: "Copy the table from your Tradovate account report and paste it below",
      commissionPerTrade: "Commission per trade ($)",
      commissionPerTradePlaceholder: "Ex: 2.09",
      commissionFromPropFirm: "from {name}",
      exportTrades: "Export",
      filters: "Filters",
      advancedFilters: "Advanced Filters",
      symbol: "Symbol",
      type: "Type",
      status: "Status",
      startDate: "Start date",
      endDate: "End date",
      allTypes: "All",
      long: "Long",
      short: "Short",
      allStatus: "All",
      open: "Open",
      closed: "Closed",
      date: "Date",
      exitDate: "Exit Date",
      entryPrice: "Entry Price",
      exitPrice: "Exit Price",
      quantity: "Quantity",
      pnl: "P&L",
      commission: "Commission",
      notes: "Notes",
      tags: "Tags",
      tagsPlaceholder: "Add a tag…",
      createTag: "Create",
      noTags: "No tags",
      tagCreateError: "Could not create tag",
      trades: "Trades",
      noTrades: "No trades found",
      noTradesDesc: "Add your first trade to start analyzing your performance",
      totalPnl: "Total P&L",
      newTrade: "New Trade",
      editTrade: "Edit Trade",
      deleteTrade: "Delete",
      confirmDelete: "Are you sure you want to delete this trade?",
      confirmBulkDelete: "Are you sure you want to delete {count} trade(s)?",
      bulkDeleteSuccess: "{count} trade(s) deleted",
      bulkDeleteError: "Error deleting trades",
      selectAll: "Select all",
      deselectAll: "Deselect all",
      selectedCount: "{count} selected",
      tradeCreated: "Trade created",
      tradeUpdated: "Trade updated",
      tradeDeleted: "Trade deleted",
      importSuccess: "Trades imported successfully",
      importSuccessDescription: "{count} trade(s) imported successfully.",
      importError: "Error importing trades",
      selectFile: "Select a CSV file",
      required: "This field is required",
      advancedFiltersTitle: "Advanced Filters",
      advancedFiltersDescription: "Use these filters to refine your trade search",
      account: "Account",
      allAccounts: "All accounts",
      strategy: "Strategy",
      allStrategies: "All strategies",
      clearStrategies: "Clear all",
      strategiesSelected: "{n} strategy(ies)",
      all: "All",
      noTagsAvailable: "No tags available",
      minPnl: "Min P&L ($)",
      maxPnl: "Max P&L ($)",
      customFields: "Custom Fields",
      applyFilters: "Apply Filters",
      reset: "Reset",
      equals: "Equals",
      greater: "Greater than",
      less: "Less than",
      between: "Between",
      contains: "Contains",
      startsWith: "Starts with",
      after: "After",
      before: "Before",
      yes: "Yes",
      no: "No",
      value: "Value",
      tagsSeparatedByCommas: "Tags separated by commas",
      setupRating: "Setup Rating",
      setupRatingHint: "Rate the quality and confidence of your setup (1 = poor, 5 = excellent)",
      cannotCreate: "Failed to create trade.",
      cannotUpdate: "Failed to update trade.",
      cannotDelete: "Failed to delete trade.",
      tradeLimitReachedTitle: "Trade Limit Reached",
      tradeLimitReachedDescription: "You have reached the limit of 500 managed trades. Creating a new trade will delete your oldest one.",
      tradeLimitReachedContinue: "Create and Delete Oldest",
      tradeLimitReachedUpgrade: "Upgrade Plan",
      swaps: "Swaps",
      noTradesExport: "No trades to export.",
      templateDownloaded: "Template downloaded",
      templateDownloadedDescription: "The CSV template was downloaded successfully.",
      downloadTemplate: "Download CSV template",
      cannotDownloadTemplate: "Failed to download template.",
      importErrors: "Import errors",
      noValidTrades: "No valid trades",
      noValidTradesDescription: "No valid trades found in the CSV file.",
      cannotImport: "Failed to import trades.",
      accountUpdatedTrade: "Account updated",
      accountUpdatedTradeDescription: "The trade account has been updated.",
      cannotUpdateAccount: "Failed to update account.",
      noAccountSelect: "No account",
      tradeUpdatedDescription: "The trade has been updated successfully.",
      tradeDeletedDescription: "The trade has been deleted successfully.",
      tradeCreatedDescription: "The trade has been added successfully.",
      columnsLabel: "Columns",
      hiddenBySpace: "Hidden (space)",
      customizeColumns: "Customize columns",
      customizeColumnsDescription: "Choose which columns to display and their order. Required columns cannot be hidden.",
      columnsSaved: "Configuration saved",
      columnsSavedDescription: "Column preferences have been updated.",
      cannotSaveColumns: "Failed to save configuration.",
      addCustomField: "Add custom field",
      editField: "Edit field",
      newCustomField: "New custom field",
      fieldTechnicalName: "Technical name (unique)",
      fieldNamePlaceholder: "e.g.: strategy_type",
      fieldNameHint: "Letters, numbers and underscores only",
      fieldDisplayLabel: "Label",
      fieldLabelPlaceholder: "e.g.: Strategy Type",
      fieldTypeLabel: "Type",
      fieldTypeText: "Text",
      fieldTypeNumber: "Number",
      fieldTypeDate: "Date",
      fieldTypeBoolean: "Boolean",
      fieldTypeTags: "Tags / Badges",
      fieldRequired: "Required field",
      fieldCreated: "Field created",
      fieldCreatedDescription: "The custom field has been created successfully.",
      fieldUpdated: "Field updated",
      fieldUpdatedDescription: "The custom field has been updated successfully.",
      fieldDeleted: "Field deleted",
      fieldDeletedDescription: "The custom field has been deleted successfully.",
      deleteFieldConfirm: "Are you sure you want to delete this field? All associated values will also be deleted.",
      cannotCreateField: "Failed to create field.",
      cannotUpdateField: "Failed to update field.",
      cannotDeleteField: "Failed to delete field.",
      save: "Save",
      create: "Create",
      importErrorsCount: "{count} error(s) detected. Click to see details.",
      selectAccountOrGroup: "Account or group",
      importNoAccountHint:
        "Imported trades will not be linked to any account: they will not show up in your balance or in per-account analytics.",
      selectAccountOrGroupPlaceholder: "No account",
      groupPrefix: "Group",
      importConfirm: "Import trades",
      importInProgress: "Importing…",
      importSelectedFile: "Selected file: {name}",
      importCommissionLabel: "Commission per contract ($)",
      importCommissionHint:
        "Applied to trades whose file does not detail fees (Tradovate). Leave at 0 if the CSV already includes them.",
      importGroupMultiplier: "x {count} accounts = {total} trades total",
      longsLabel: "longs",
      shortsLabel: "shorts",
      importingToGroup: "Trades imported to each account in the group",
      importAllRowsInvalid: "No valid rows to import (missing/incorrect price, quantity or date).",
      importSomeRowsSkipped: "{count} row(s) skipped because invalid (incorrect price, quantity or date).",
      importDuplicatesSkipped: "{count} trade(s) already in your journal, skipped.",
      importNothingNew: "No new trades: everything was already imported",
      manualTradeTitle: "Add a trade manually",
      manualTradeSubtitle: "Fill in the trade details",
      tradeAdded: "Trade added",
      tradeAddedDescription: "The trade was successfully added.",
      cannotAddTrade: "Cannot add trade",
      tradesListWidget: "Trades List",
      export: "Export",
      symbolPlaceholder: "AAPL, TSLA…",
      sideAll: "All",
      sideLong: "Long",
      sideShort: "Short",
      statusAll: "All",
      statusOpen: "Open",
      statusClosed: "Closed",
      pnlFilterLabel: "P&L",
      minPnlLabel: "Min P&L ($)",
      maxPnlLabel: "Max P&L ($)",
      tagsFilterLabel: "Tags",
    },
    tradingAccounts: {
      noRulesTitle: "No rules are being monitored on this account",
      noRulesBody: "This account is not linked to a prop firm, so Edgely cannot track its profit target, drawdown or daily loss, and no breach will be reported to you. Pick its prop firm to turn alerts on.",
      noRulesAction: "Pick the prop firm",
      ruleUpdateTitle: "This account's rules have changed",
      ruleUpdateBody: "Your prop firm updated its rules after this account was set up. Your alerts still use the previous rules until you accept.",
      ruleUpdateFrozenOn: "Rules frozen on {date}",
      ruleUpdateAccept: "Apply the new rules",
      ruleUpdateDecline: "Keep my rules",
      ruleUpdateStricter: "stricter",
      ruleUpdateLooser: "looser",
      ruleUpdateAccepted: "New rules applied",
      ruleUpdateDeclined: "Your original rules are kept",
      rulesFrozenBadge: "Rules frozen",
      title: "Trading Accounts",
      subtitle: "Manage your trading accounts and track their balances",
      newAccount: "New Account",
      createFirstAccount: "Create your first account",
      loading: "Loading...",
      noAccounts: "No accounts created yet.",
      allAccountsInactive: "No active account. Your inactive accounts are hidden by the filter.",
      currentBalance: "Current Balance",
      balanceStalePrefix: "updated",
      balanceStaleUnknown: "age of reading unknown",
      balanceStaleHelp:
        "The broker balance could not be refreshed recently. The amount shown is the last known value.",
      initialBalance: "Initial Balance",
      variation: "Variation",
      closedTrades: "closed trade(s)",
      synced: "Synced",
      manual: "Manual",
      createAccountTitle: "New Account",
      createAccountDescription: "Create a new trading account. Synced accounts will be available soon.",
      editAccountTitle: "Edit Account",
      editAccountDescription: "Edit account information. Account type cannot be modified.",
      accountName: "Account Name",
      accountNamePlaceholder: "Ex: Main Account",
      accountType: "Account Type",
      syncedComingSoon: "Synced (coming soon)",
      initialBalanceLabel: "Initial Balance",
      cancel: "Cancel",
      create: "Create",
      save: "Save",
      accountCreated: "Account Created",
      accountCreatedDescription: "The account has been created successfully.",
      accountUpdated: "Account Updated",
      accountUpdatedDescription: "The account has been updated successfully.",
      accountDeleted: "Account Deleted",
      accountDeletedDescription: "The account has been deleted successfully.",
      error: "Error",
      createError: "Unable to create account.",
      updateError: "Unable to update account.",
      deleteError: "Unable to delete account.",
      confirmDelete: "Are you sure you want to delete the account",
      deleteAccountConfirmTitle: "Delete account \"{name}\"?",
      deleteAccountConfirmDescription: "Associated trades will be kept but unlinked from this account.",
      deleteAccountConfirmWarning: "Deleting PERMANENTLY removes this account, all its trades and its entire history. This action cannot be undone.",
      deleteAccountConfirmHint: "If the account is blown or closed, deactivate it instead of deleting it: this keeps all your data and history.",
      deactivateInstead: "Deactivate instead",
      deactivateAccountConfirmTitle: "Deactivate account \"{name}\"?",
      deactivateAccountConfirmDescription: "The account will be marked as inactive and considered blown. You can reactivate it at any time and no data is lost.",
      deactivate: "Deactivate",
      delete: "Delete",
      accountNameRequired: "Account name is required",
      accountSizeRequired: "Account size is required",
      eur: "EUR - Euro",
      usd: "USD - US Dollar",
      gbp: "GBP - British Pound",
      chf: "CHF - Swiss Franc",
      jpy: "JPY - Japanese Yen",
      cad: "CAD - Canadian Dollar",
      aud: "AUD - Australian Dollar",
      nzd: "NZD - New Zealand Dollar",
      cny: "CNY - Chinese Yuan",
      btc: "BTC - Bitcoin",
      eth: "ETH - Ethereum",
      // Groups
      groups: "Groups",
      noGroups: "No groups created yet.",
      ungrouped: "Ungrouped",
      createGroup: "Create Group",
      editGroup: "Edit Group",
      deleteGroup: "Delete Group",
      groupName: "Group Name",
      groupNamePlaceholder: "Ex: Active prop firms",
      groupCreated: "Group Created",
      groupUpdated: "Group Updated",
      groupDeleted: "Group Deleted",
      groupDeleteConfirm: "Delete group? Accounts will not be deleted, they will move to \"Ungrouped\".",
      groupPropFirm: "Group Prop Firm",
      groupStrategy: "Group Strategy",
      groupStrategyHint: "Accounts in this group will automatically inherit this strategy",
      accountStrategy: "Account Strategy",
      accountStrategyHint: "Created accounts will automatically inherit this strategy",
      groupConsolidatedPnl: "Consolidated P&L",
      groupActiveAccounts: "active accounts",
      groupDeleteWarning: "Accounts in this group will not be deleted, they will move to \"Ungrouped\".",
      // Prop firms
      propFirm: "Prop Firm",
      propFirmApex: "Apex Trader Funding",
      propFirmFtmo: "FTMO",
      propFirmTopstep: "Topstep",
      propFirmTakeProfitTrader: "Take Profit Trader",
      propFirmLucid: "Lucid Trading",
      propFirmCustom: "Custom",
      noPropFirm: "No prop firm",
      inheritedFromGroup: "Inherited from group",
      // Account status
      statusChallenge: "Challenge",
      statusPa: "Funded Account (PA)",
      statusInactive: "Inactive",
      statusDemo: "Demo",
      statusLive: "Live",
      statusChallenge2: "Challenge Phase 2",
      activePhase: "Active phase",
      addPhase: "Add phase",
      editPhase: "Edit phase",
      deletePhase: "Delete phase",
      confirmDeletePhase: "Delete this phase?",
      phaseTypeLabel: "Phase type",
      phaseNameLabel: "Phase name",
      phaseTypeEvaluation: "Evaluation",
      phaseTypeEvaluation2: "Evaluation Phase 2",
      phaseFundedVariant: "Funded (variant)",
      phaseDrawdownType: "Drawdown type",
      phaseDrawdownTrailing: "Trailing",
      phaseDrawdownTotal: "Total loss",
      phaseDrawdownDaily: "Daily loss",
      phaseDrawdownNone: "None",
      accountStatus: "Account Status",
      personalAccount: "Personal Account",
      tradovateAccount: "Tradovate",
      tradovateAccountDescription: "Import from your Tradovate account",
      tradovateModeManual: "Manual import",
      tradovateModeSync: "Automatic sync",
      topstepxAccount: "Topstep X",
      topstepxAccountDescription: "Topstep account, import from Topstep X",
      metatraderAccount: "Synced MetaTrader",
      metatraderAccountDescription: "Auto-sync from MT4/MT5",
      mtCredentialsStep: "MT Login",
      mtAccountDetailsStep: "Details",
      mtLogin: "MetaTrader Login",
      mtLoginPlaceholder: "123456",
      mtPassword: "Password",
      mtServer: "Broker server",
      mtServerPlaceholder: "ICMarkets-Live01",
      mtPlatform: "Platform",
      mtAccountCreating: "Connecting...",
      mtAccountCreated: "MetaTrader account connected",
      mtAccountError: "Connection failed",
      mtConnectionRetryHint: "Check your credentials and try again. Go back to the previous step to edit them.",
      selectPropFirm: "Select prop firm",
      // Drawdown warnings
      drawdownWarning: "⚠️ Warning: drawdown at {percent}% of limit",
      drawdownViolation: "🚫 Drawdown limit exceeded",
      drawdownRemainingRoom: "{amount} remaining room",
      // Bulk creation
      addAccounts: "Add Accounts",
      addAccountsDescription: "Add multiple accounts at once. Select a prop firm, fill in the details, and assign a group.",
      selectPropFirmStep: "Prop Firm",
      selectAccountTypeStep: "Account Type",
      enterAccountsStep: "Accounts",
      assignGroupStep: "Group",
      selectAccountType: "Account Type",
      selectAccountTypeDescription: "Select the {name} account type you are adding. This applies to all accounts created.",
      selectTierBalance: "Account size",
      customBalance: "Custom amount",
      challengePriceMode: "Price entry mode",
      challengePriceModeTotal: "Total price",
      challengePriceModeUnit: "Price per account",
      challengePriceTotalLabel: "Total price paid ({count} accounts)",
      challengePriceUnitLabel: "Price per account",
      accountsCreated: "Accounts Created",
      accountsCreatedDescription: "{count} account(s) created successfully.",
      assignToGroup: "Assign to a group",
      selectGroupPlaceholder: "Choose a group...",
      propFirmRulesApplied: "The rules of {name} will be applied automatically based on each account's balance.",
      createNewGroup: "Create a new group",
      groupNameRequired: "Group name is required",
      existingGroup: "Existing group",
      noGroupOption: "No group",
      numberOfAccounts: "Number of accounts",
      addAnotherAccount: "Add account",
      removeAccount: "Remove",
      bulkCreateError: "Some accounts could not be created.",
      duplicateAccountName: "An account with this name already exists. Please choose a different name.",
      // Active toggle
      activateAccount: "Reactivate Account",
      deactivateAccount: "Deactivate Account",
      accountActivated: "Account Reactivated",
      accountDeactivated: "Account Deactivated",
      activeAccountsOnly: "Only active accounts are included in analytics.",
      inactiveAccountNote: "This account is disabled. Its trades are not included in global analytics.",
      // Plan limit
      accountLimitReached: "Limit Reached",
      accountLimitReachedDescription: "Free plan is limited to 1 account. Upgrade to Pro for unlimited accounts.",
      hiddenBanner: {
        title: "You have {{count}} hidden account(s). Your data is safe, subscribe to recover access.",
        cta: "View plans",
      },
      // Filter
      filterAllAccounts: "All accounts",
      filterAllAccountsWithInactive: "All accounts (+ inactive)",
      filterNoAccount: "No account",
      filterByAccount: "Filter by account",
      filterByGroup: "Filter by group",
      includeInactive: "Include inactive",
      hideInactiveAccounts: "Hide inactive accounts",
      showInactiveAccounts: "Show inactive accounts",
      // Misc
      proFeature: "Pro Feature",
      next: "Next",
      previous: "Previous",
      step: "Step",
      of: "of",
      // PropFirm DB-driven
      systemPropFirms: "Official prop firms",
      myCustomPropFirms: "My custom prop firms",
      createCustomPropFirm: "+ Create a custom prop firm",
      autoDetected: "Auto-detected",
      tierOverride: "Manual tier",
      selectTier: "Select a tier",
      // Progress / metrics
      challengeProgress: "Challenge progress",
      profitTarget: "Profit target",
      tradingDays: "Trading days",
      bufferDrawdown: "Drawdown buffer",
      bufferRemaining: "Buffer remaining",
      axisFloor: "Floor",
      axisFloorHint: "Max loss limit (MLL)",
      axisStart: "Start",
      axisTarget: "Target",
      axisPeak: "Peak",
      axisDrawdown: "Room",
      axisBalance: "Balance",
      axisRemaining: "Remaining",
      maxLossExceeded: "Max loss exceeded",
      daysTradedCount: "{count} trading days",
      tradesPnl: "Trades PnL",
      syncNeedsAttention: "Sync needs attention",
      rulesMeasured: "Progress",
      rulesDeclared: "Constraints",
      bufferConsumed: "consumed",
      payoutProgress: "Payout progress",
      payoutTarget: "Payout target",
      payoutTargetLabel: "Payout target ($)",
      payoutReached: "Payout target reached!",
      setPayoutTarget: "Set a target",
      // Detail sheet
      accountDetails: "Account details",
      rules: "Tier rules",
      maxDailyLoss: "Max daily loss",
      maxTotalLoss: "Max total loss",
      trailingDrawdown: "Trailing drawdown",
      consistencyRule: "Consistency rule",
      maxContracts: "Max contracts",
      minTradingDays: "Minimum days",
      payoutCooldown: "Payout cooldown",
      payoutThreshold: "Payout threshold",
      daysCountShort: "{count} d",
      consistencyRuleValue: "Max {percent}% / day",
      consistencyShort: "Consistency {percent}%",
      noRulesDefined: "No rules defined for this tier.",
      payoutsHistory: "Payout history",
      noPayouts: "No payouts recorded.",
      addPayout: "Add payout",
      totalPayoutsReceived: "Total received",
      fees: "Fees",
      totalCommissionTooltip: "Total commissions and swaps across all account trades",
      payoutAmount: "Amount",
      payoutGrossAmount: "Gross amount",
      payoutSplitPercent: "Profit split (%)",
      payoutNetAmount: "Net amount received",
      payoutGrossBelowNet: "The gross amount cannot be lower than the net amount received",
      payoutGrossHint: "Debited from the account, used for drawdown",
      payoutNetHint: "Received by you after the split",
      payoutEdit: "Edit payout",
      payoutUpdated: "Payout updated",
      payoutGrossShort: "Gross {amount}",
      payoutAmountsRequired: "Enter at least one amount",
      payoutDate: "Date",
      payoutNotes: "Notes",
      payoutNotesPlaceholder: "Optional notes...",
      payoutAdded: "Payout added",
      payoutAddedNoCert: "Payout added, but the certificate upload failed.",
      payoutCertificate: "Certificate (optional)",
      payoutDeleteConfirm: "Delete this payout? The attached certificate will also be deleted. This action cannot be undone.",
      payoutDeleted: "Payout deleted",
      peakBalance: "Peak balance",
      activeTier: "Active tier",
      changeTier: "Change tier",
      changeStatus: "Change status",
      // Challenge complete
      challengeComplete: "Challenge complete! 🎉",
      // Detail page
      backToAccounts: "Back to accounts",
      editAccount: "Edit",
      accountDetailTitle: "Account detail",
      groupDetailTitle: "Group detail",
      statsOverview: "Statistics",
      winRate: "Win Rate",
      wins: "Winners",
      losses: "Losers",
      avgWin: "Avg win",
      avgLoss: "Avg loss",
      profitFactor: "Profit Factor",
      maxDrawdown: "Max drawdown",
      totalPnl: "Total P&L",
      tradesCount: "Closed trades",
      equityCurve: "Equity curve",
      equityCurveMaxLossLimit: "Max loss limit",
      symbolStats: "Stats by symbol",
      symbol: "Symbol",
      pnl: "P&L",
      trades: "Trades",
      noSymbolStats: "No statistics available.",
      noEquityData: "No equity data available.",
      groupAccounts: "Group accounts",
      consolidatedStats: "Consolidated stats",
      activeAccounts: "Active accounts",
      totalAccounts: "Total accounts",
      totalInitialBalance: "Total initial balance",
      totalCurrentBalance: "Total current balance",
      totalPayoutsGroup: "Total payouts",
      accountsInGroup: "accounts in this group",
      noActiveAccounts: "No active accounts in this group.",
      viewAccountDetail: "View detail",
      challengeCostLabel: "Account cost",
      challengeCostPlaceholder: "e.g. 120",
      challengeCostHint: "Price actually paid for this account (challenge fee, subscription)",
      subscriptionPriceLabel: "Monthly subscription price",
      subscriptionPriceHint: "Counted every month in the Prop Firm Manager while the account stays in challenge.",
      directFundedHint: "Funded from purchase: no challenge phase.",
      purchasedAtLabel: "Purchase date",
      purchasedAtHint:
        "Actual date the account was purchased. Leave empty if bought today - useful to reconstruct past history.",
      closedAtLabel: "Closing date",
      closedAtNotice:
        "The account will be created as closed: excluded from global analytics and from your plan's account limit.",
      historicalAccountToggle: "Historical account (already bought or closed)",
      historicalAccountHint:
        "Fill in the real dates so expenses and payouts land in the right period in the Prop Firm Manager.",
      activationFeeLabel: "Activation fee",
      activationFeePlaceholder: "e.g. 89",
      activationFeeHint: "Amount paid when transitioning to funded (leave empty if no fee).",
      fundedAtLabel: "Funded transition date",
      fundedAtHint: "Actual date of the funded transition, used for the expense calendar.",
      markAsFunded: "Mark as Funded",
      // Challenge -> Funded transition
      fundedCelebrationTitle: "Target reached, congratulations!",
      fundedCelebrationSubtitle:
        "Well done, you hit your challenge's profit target. Great trading!",
      fundedCelebrationSyncNote:
        "Once the prop firm activates your Funded account, the sync will detect it automatically. You'll be able to enter your activation fee at that point.",
      fundedCelebrationCloseButton: "Thanks!",
      promoteToFunded: "Promote to Funded",
      promoteToFundedTitle: "Promote account to Funded",
      promoteToFundedDescription: "The account keeps its full trade history. The balance restarts from the funded starting balance on the date you set.",
      fundedDateLabel: "Funded transition date",
      fundedStartingBalanceLabel: "Funded starting balance",
      fundedStartingBalanceHint: "Defaults to the account's initial balance. Adjust it if the prop firm credits a different amount.",
      activationFeeOptionalLabel: "Activation fee (optional)",
      promotePreview: "{challenge} trade(s) will stay in the Challenge phase, {funded} will move to the Funded phase.",
      promoteConfirm: "Confirm promotion to Funded",
      promoteCancel: "Cancel",
      promoteSuccess: "Account promoted to Funded",
      promoteBalanceInvalid: "Enter a valid starting balance (greater than 0).",
      challengeRecapTitle: "Challenge passed",
      challengeRecapFinalBalance: "Balance reached",
      challengeRecapNetProfit: "Net profit",
      challengeRecapTradeCount: "Trades",
      challengeRecapWinRate: "Win rate",
      challengeRecapDuration: "Challenge duration",
      challengeRecapDurationDays: "{days} day(s)",
      phaseChallenge: "Challenge",
      phaseFunded: "Funded",
      phaseLive: "Live",
      phaseDemo: "Demo",
      phaseFilterAll: "All phases",
      phaseFilterLabel: "Phase",
      couponCode: "Coupon code",
      couponCodePlaceholder: "e.g. SAVE20",
      couponDiscount: "Discount (%)",
      couponDiscountPlaceholder: "e.g. 20",
      couponHint: "Enter the coupon used to calculate the actual price paid",
      charges: "Charges",
      addCharge: "Add charge",
      addGlobalCharge: "Add global charge",
      chargeName: "Name",
      chargeAmount: "Amount",
      chargeFrequency: "Frequency",
      chargeMonthly: "Monthly",
      chargeAnnual: "Annual",
      chargeOneTime: "One-time",
      chargeAdded: "Charge added",
      chargeDeleted: "Charge deleted",
      chargeAddError: "Error adding charge",
      chargeDeleteError: "Error deleting charge",
      chargeDelete: "Delete charge",
      chargeNamePlaceholder: "e.g. TradingView",
      chargeAmountPlaceholder: "e.g. 30",
      noCharges: "No charges",
      chargeStartDate: "Start date",
      chargeStartDatePlaceholder: "e.g. 2024-03-15",
      chargeBillingDay: "Billing day",
      chargeBillingDayPlaceholder: "e.g. 15",
      chargeEndDate: "End date",
      chargeEndDateHint: "Empty = still running",
      chargeSourceSubscription: "Prop firm subscription",
      chargeDeleteRecurringConfirm: "Deleting also erases every past charge. If you cancelled, use \"I cancelled\" in the \"Upcoming\" card instead. Click again to confirm.",
      chargeRemindersTitle: "Charge reminders",
      chargeRemindersLabel: "Email me 3 days before a charge",
      chargeRemindersHint: "The in-app notification stays on.",
      chargeEdit: "Edit charge",
      chargeEditError: "Error updating charge",
      chargeUpdated: "Charge updated",
      chargeSave: "Save",
      chargeCancel: "Cancel",
      chargeAddTitle: "Add a charge",
      chargeEditTitle: "Edit a charge",
      globalCharges: "Global charges",
      linkedCharges: "Linked charges",
      disabledAtLabel: "Disabled date",
      disabledAtHint: "Date when the account was disabled or crashed",
      consistencyRuleOverrideLabel: "Consistency rule (%)",
      consistencyRuleOverrideHint: "E.g. 40 → no single day can exceed 40% of total profit. Leave empty if already set in the tier.",
      maxDailyLossOverrideLabel: "Daily stop loss (%)",
      maxDailyLossOverrideHint: "E.g. 5 → max 5% of initial balance loss per day. Leave empty if already set in the tier.",
      violationDailyLoss: "Daily stop loss breached",
      warningDailyLoss: "Approaching daily stop loss",
      violationConsistency: "Consistency rule violated",
      warningConsistency: "Approaching consistency rule",
      violationConsistencyDetail: "Consistency rule violated: {share}% on {date}, limit {threshold}%",
      warningConsistencyDetail: "Approaching consistency rule: {share}% on {date}, limit {threshold}%",
      violationConsistencyBase: "Based on the {base} target",
      balanceGapJournalAhead: "Broker balance behind by {gap}",
      balanceGapJournalAheadHint: "Your trades are recorded, but the balance reported by the broker predates them. It updates on its own at the next reading.",
      balanceGapJournalIncomplete: "{gap} gap with the broker",
      balanceGapJournalIncompleteHint: "The broker reports more than your trades account for: trades predating the connection are missing from the journal.",
      balanceGapJournalLabel: "From your trades",
      balanceGapStale: "Last reading over 2 h ago",
      qualifyingDaysLabel: "Qualifying days",
      payoutEligible: "Eligible for payout",
      strategies: "Strategies",
      allStrategies: "All strategies",
      strategiesPlaceholder: "Select or create a strategy...",
      strategyNone: "No strategy",
      addStrategy: "Add a strategy",
      strategyName: "Strategy name",
      strategyNamePlaceholder: "e.g. Scalping, Swing, Mean Reversion...",
      strategyCreated: "Strategy created",
      strategyDeleted: "Strategy deleted",
      strategyCreateError: "Error creating strategy",
      strategyDeleteError: "Error deleting strategy",
      strategyExists: "A strategy with this name already exists",
      noStrategies: "No strategies defined",
      strategyHint: "Trades created on this account will inherit these strategies",
      resyncTradesStrategies: "Resync existing trades",
      resyncTradesStrategiesHint: "Applies the account's current strategies to all its existing trades (replaces their strategies)",
      resyncTradesStrategiesSuccess: "Trades resynced",
      resyncTradesStrategiesError: "Error while resyncing",
      tableView: "Table view",
      cardsView: "Cards view",
      colName: "Name",
      colStatus: "Status",
      colPropFirm: "Prop Firm",
      colGroup: "Group",
      colType: "Type",
      colSource: "Source",
      sourceTradovate: "Tradovate",
      sourceMetaTrader: "MetaTrader",
      sourceManual: "Manual",
      deactivatedBrokerGone: "Closed broker-side",
      deactivatedBrokerGoneHint: "This account no longer exists at Tradovate. Its trades are kept.",
      deactivatedChallengeCompleted: "Challenge passed",
      deactivatedChallengeCompletedHint: "You passed this challenge: your prop firm closed the evaluation account and issued a new one. Its trades are kept. Turn on inactive accounts to see its PnL in the calendar.",
      deactivatedDrawdown: "Drawdown breached",
      deactivatedDrawdownHint: "This account crossed its drawdown limit and was automatically deactivated. Its trades are kept.",
      payoutSuggestionLabel: "Payout detected",
      cashQuestionTitle: "Unexplained movement",
      cashQuestionBody:
        "This account's balance dropped with no matching trade. What happened?",
      cashQuestionAmountLabel: "Amount",
      cashQuestionAnswerWithdrawal: "A withdrawal",
      cashQuestionAnswerReset: "An account reset",
      cashQuestionAnswerFees: "Fees",
      cashQuestionAnswerUnknown: "I don't know",
      cashQuestionConfirm: "Confirm",
      cashQuestionDismiss: "Dismiss",
      cashQuestionCelebrate: "Withdrawal recorded. Nice work.",
      cashQuestionRecorded: "Answer recorded.",
      cashQuestionReasonUnavailable:
        "The broker's cash log could not be read.",
      payoutSuggestionCreate: "Create",
      payoutSuggestionDismiss: "Dismiss",
      accountResetSuggestionLabel: "Account reset detected",
      accountResetDetectedTitle: "Account reset likely detected",
      accountResetDetectedDescription:
        "The broker balance does not match the one Edgely computed, which happens when a prop firm resets an account. Check the date below and confirm: earlier trades will be archived (excluded from the balance) but will stay in your journal and analytics.",
      accountResetObservedBalance: "Broker balance",
      accountResetExpectedBalance: "Balance computed by Edgely",
      accountResetDiscrepancy: "Discrepancy",
      accountResetDateLabel: "Reset date",
      accountResetConfidenceHigh:
        "This date comes from the broker cash journal. Correct it if it does not match your reset.",
      accountResetConfidenceLow:
        "Warning: this date is only an estimate (the broker cash journal only goes back a few days). Check and correct it before confirming.",
      accountResetConfirm: "Confirm reset",
      accountResetConfirming: "Confirming...",
      accountResetDismiss: "Dismiss",
      accountResetInvalidDate: "Please enter a valid reset date.",
      accountResetError: "Confirming the reset failed. Please try again.",
      colStrategy: "Strategy",
      colBalance: "Balance",
      colPnl: "PnL",
      colProgress: "Progress",
      colTrades: "Trades",
      colActions: "Actions",
      blewUpModal: {
        title: "Account potentially blown",
        subtitleDailyLoss: "The maximum daily loss has been reached on **{accountName}**.",
        subtitleTotalLoss: "The maximum drawdown has been reached on **{accountName}**.",
        subtitleTrailingDrawdown: "The trailing drawdown has been triggered on **{accountName}**.",
        question: "Do you want to mark this account as inactive?",
        confirm: "Yes, disable account",
        dismiss: "No, continue",
      },
    },
    notebook: {
      title: "Notebook",
      subtitle: "Organize your notes, analyses, and lessons",
      newNote: "New Note",
      untitled: "Untitled",
      empty: "No notes",
      selectNote: "Select a note",
      createFirstNote: "Create your first note",
      loading: "Loading...",
      noNotes: "No notes found. Create your first note!",
      search: "Search",
      searchPlaceholder: "Search by title or content...",
      category: "Category",
      allCategories: "All categories",
      noCategory: "No category",
      categories: "Categories",
      manageCategories: "Manage Categories",
      createCategory: "Create Category",
      editCategory: "Edit Category",
      deleteCategory: "Delete Category",
      categoryName: "Category Name",
      categoryColor: "Color",
      categoryCreated: "Category created",
      categoryUpdated: "Category updated",
      categoryDeleted: "Category deleted",
      categoryError: "Error managing category",
      confirmDeleteCategory: "Are you sure you want to delete this category? Associated notes will not be deleted.",
      confirmDeleteNote: "Are you sure you want to delete this note?",
      noteCreated: "Note created",
      noteUpdated: "Note updated",
      noteDeleted: "Note deleted",
      noteError: "Error managing note",
      saving: "Saving...",
      saved: "Saved",
      saveError: "Error saving",
      editorPlaceholder: "Start writing your note...",
      toolbar: {
        bold: "Bold",
        italic: "Italic",
        underline: "Underline",
        strike: "Strike",
        heading1: "Heading 1",
        heading2: "Heading 2",
        heading3: "Heading 3",
        bulletList: "Bullet List",
        orderedList: "Ordered List",
        link: "Link",
        image: "Image",
        divider: "Divider",
      },
      uploadImage: "Upload Image",
      pasteImage: "Paste Image",
      imageUploaded: "Image uploaded successfully",
      imageUploadError: "Error uploading image",
      gridColumns1: "1 column",
      gridColumns2: "2 columns",
      gridColumns3: "3 columns",
      gridColumns4: "4 columns",
      updatedOn: "Updated on",
      readMore: "Read more",
      collapse: "Collapse",
      symbols: "Symbols (comma separated)",
      symbolsPlaceholder: "AAPL, TSLA, MSFT",
      tags: "Tags (comma separated)",
      tagsPlaceholder: "course, analysis, strategy",
      backToNotebook: "Back to notebook",
      editNote: "Edit Note",
      saveNote: "Save",
      cancel: "Cancel",
    },
    tradeDetail: {
      title: "Trade Details",
      backToJournal: "Back to journal",
      edit: "Edit",
      save: "Save",
      cancel: "Cancel",
      tradeInfo: "Trade Information",
      symbol: "Symbol",
      type: "Type",
      status: "Status",
      date: "Date",
      entryPrice: "Entry Price",
      exitPrice: "Exit Price",
      quantity: "Quantity",
      pnl: "P&L",
      commission: "Commission",
      notes: "Notes",
      notesSaved: "Notes saved",
      notesError: "Unable to save notes",
      templates: "Templates",
      noNotes: "No notes for this trade. Click \"Edit\" to add some.",
      clickToAdd: "Edit",
      previousContent: "Previous content",
      chartTitle: "Price Chart",
      chartError: "Failed to load chart data",
      noChartData: "No data available for this symbol",
      customFields: "Custom Fields",
      noValue: "",
      editCustomFields: "Edit fields",
      saveCustomFields: "Save",
    },
    analytics: {
      title: "Analytics",
      subtitle: "Analyze your trading performance",
      loading: "Loading...",
      timeFilter: "Time Period",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      last90Days: "Last 90 days",
      allTime: "All time",
      totalPnl: "Total P&L",
      winRate: "Win Rate",
      profitFactor: "Profit Factor",
      totalTrades: "Total Trades",
      averagePnl: "Average P&L",
      cumulativePnl: "Cumulative P&L",
      pnlDistribution: "P&L Distribution",
      monthlyPerformance: "Monthly Performance",
      pnlPerTrade: "P&L per Trade",
      sessionRecaps: "Session Recaps",
      noSessionRecaps: "No session recaps available. Start a session from the dashboard to begin tracking your performance.",
      startFromDashboard: "Start session from dashboard",
      date: "Date",
      duration: "Duration",
      mood: "Mood",
      performance: "Performance",
      opportunities: "Opportunities",
      openTrades: "Open Trades",
      closedTrades: "Closed Trades",
      totalPnlLabel: "Total P&L",
      selfEvaluation: "Self Evaluation",
      mentalState: "Mental State",
      notes: "Notes",
      excellent: "Excellent",
      good: "Good",
      neutral: "Neutral",
      poor: "Poor",
      bad: "Bad",
      average: "Average",
      symbol: "Symbol",
      noDataAvailable: "No data available",
      largestWinLoss: "Largest Win / Loss",
      numberOfTrades: "Number of trades",
      period: "Period",
      year: "Year",
      quarter: "Quarter",
      month: "Month",
      selectYear: "Select Year",
      selectPeriod: "Select Period",
      months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      quarters: ["Q1", "Q2", "Q3", "Q4"],
      perTradePerformance: "Per Trade Performance",
      netDailyPnlLabel: "Net Daily P&L",
      netDailyPnlTitle: "Net P&L per calendar day",
      holdDurationTradeCount: "Trade count by hold duration",
      longVsShort: "Long vs Short",
      winLossDistribution: "Win/Loss Distribution",
      dayOfWeekPerformance: "Day of Week Performance",
      longPerforming: "Longs performing better",
      shortPerforming: "Shorts performing better",
      equalPerformance: "Equal performance",
      longShortTotalLabel: "Total",
      longShortTotalTooltip: "Cumulative P&L across all trades in this direction",
      longShortAvgLabel: "Avg.",
      longShortAvgTooltip: "Average P&L per trade in this direction",
      wins: "Wins",
      losses: "Losses",
      breakeven: "B/E",
      best: "Best",
      worst: "Worst",
      equityCurve: "Equity Curve",
      maxDrawdown: "Max Drawdown",
      peakToTrough: "Peak to trough",
      sharpeRatio: "Sharpe Ratio",
      riskAdjustedReturn: "Risk-adjusted return",
      expectancy: "Expectancy",
      averagePerTrade: "Average per trade",
      topSymbols: "Top Symbols",
      symbolsLabel: "Symbols",
      insightsLabel: "Insights",
      sessionsLabel: "Sessions",
      sessionDurationLabel: "Avg P&L by session duration",
      distributionLabel: "Distribution",
      dayLabel: "Day",
      pnlLabel: "P&L",
      dateLabel: "Date",
      valueLabel: "Value",
      tradeLabel: "Trade",
      durationLabel: "Session duration",
      holdDurationLabel: "Hold duration",
      avgPnlLabel: "Avg P&L",
      moodLabel: "Mood",
      bucketLabel: "Time in session",
      sizeLabel: "Size",
      sessionsCountLabel: "Sessions",
      calendarLabel: "Calendar",
      marketsLabel: "Markets",
      sessionLabel: "Session",
      winsCount: "wins",
      lossesCount: "losses",
      consistencyScore: "Consistency Score",
      consistencyScoreMin: "Minimum 5 closed trades required",
      csWinRateStability: "Win rate stability",
      csRiskConsistency: "Risk consistency",
      csNoRevenge: "Discipline (no revenge)",
      csDrawdownMgmt: "Drawdown management",
      consistencyScoreInfo: "The score is calculated across 4 components (25 pts each):\n• Win rate stability: monthly variance of your win rate\n• Risk consistency: regularity of position sizes per trade\n• Discipline (no revenge): no trades within 30 min after a loss\n• Drawdown management: 25/25 if no loss streaks (sign of consistency), otherwise measures whether you reduce risk after 2+ consecutive losses",
      streakAnalysis: "Streak Analysis",
      currentStreak: "Current streak",
      streakWins: "wins",
      streakLosses: "losses",
      longestWinStreak: "Best streak",
      longestLossStreak: "Worst streak",
      trades: "trades",
      riskManagement: "Risk management in streaks",
      drawdownMgmt: "Risk reduction after losses",
      drawdownMgmtHint: "Reduce risk after 2+ consecutive losses",
      drawupMgmt: "Risk increase after wins",
      drawupMgmtHint: "Slightly increase risk after 3+ consecutive wins",
      streakInsufficient: "Not enough streaks of 2+ consecutive losses",
      streakInsufficientLoss: "Not enough streaks of 2+ consecutive losses to analyze yet",
      streakInsufficientWin: "Not enough streaks of 3+ consecutive wins to analyze yet",
      accountValue: "Account value",
      sessionDelete: "Delete session",
      sessionDeleteConfirm: "Delete this session?",
      sessionDeleted: "Session deleted",
      sessionDeleteError: "Error deleting session",
      sessionEdit: "Edit session",
      sessionSaved: "Session saved",
      sessionSaveError: "Error saving session",
      sessionExpandDetails: "View details",
      sessionMaxRunup: "Max Run-up",
      sessionMaxDrawdown: "Max Drawdown",
      sessionDate: "Date",
      sessionDurationMin: "min",
      sessionStartedAt: "Start",
      sessionEndedAt: "End",
      sessionPnl: "Session P&L",
      sessionAddManual: "Add a session",
      sessionAddManualTitle: "Add a session manually",
      sessionAddManualDescription: "Enter the details of a past session to enrich your history.",
      sessionAddManualSuccess: "Session added successfully",
      sessionAddManualError: "Error adding the session",
      sessionStartTime: "Start time",
      sessionEndTime: "End time",
      // Advanced analytics categories
      cat1Title: "Performance",
      cat1Desc: "Calmar Ratio, Recovery Factor, drawdown curve",
      cat2Title: "Risk & Statistics",
      cat2Desc: "Max drawdown, Sharpe, Expectancy, P&L distribution, top symbols",
      cat3Title: "Timing & Hours",
      cat3Desc: "Hour×Day heatmap, time slots, hold duration",
      cat4Title: "Psychology & Consistency",
      cat4Desc: "Consistency score, streak analysis",
      cat5Title: "Setups & Tags",
      cat5Desc: "Performance by tag, monthly P&L, rolling win rate",
      cat6Title: "Trading Sessions",
      cat6Desc: "Session recaps, mood, performance",
      sessionStatsTimeTitle: "PnL by time in session",
      sessionStatsTimeDesc: "Where in the session do your profits and losses concentrate?",
      sessionStatsWinRateTitle: "Win Rate by time in session",
      sessionStatsWinRateDesc: "At what point in the session are your trades most often winners?",
      sessionStatsMoodTitle: "Average PnL by mood",
      sessionStatsMoodDesc: "Your profitability depending on your declared mood at the start of the session.",
      sessionStatsDurationTitle: "Average PnL by session duration",
      sessionStatsDurationDesc: "Which session length is most profitable for you?",
      sessionInsightLabel: "Key insight",
      sessionInsightTimeBucket: "{pct}% of your profits are made in the {bucket} of the session.",
      sessionInsightMood: "Your best sessions are when you feel \"{mood}\" (avg {avg}).",
      sessionInsightDuration: "Sessions of {dur} are your most profitable on average ({avg}).",
      sessionNoData: "Not enough data, trade at least 3 sessions to see these stats.",
      sessionMoodExcellent: "Excellent",
      sessionMoodGood: "Good",
      sessionMoodNeutral: "Neutral",
      sessionMoodPoor: "Poor",
      sessionMoodBad: "Very poor",
      outsideSessionTitle: "Out-of-session trades",
      outsideSessionDesc: "Performance of trades executed outside a trading session.",
      outsideSessionTrades: "Trade count",
      outsideSessionWinRate: "Win Rate",
      outsideSessionPnl: "Total PnL",
      outsideSessionAvgPnl: "Avg PnL",
      outsideSessionProfitFactor: "Profit Factor",
      outsideSessionPctTrades: "% of total trades",
      outsideSessionPctPnl: "% of total PnL",
      outsideSessionNoSessions: "No sessions recorded, cannot distinguish out-of-session trades.",
      sessionsListNoSessions: "No sessions recorded.",
      sessionsListName: "Name",
      sessionsListDate: "Date",
      sessionsListDuration: "Duration",
      sessionsListTrades: "Trades",
      sessionsListPnl: "PnL",
      sessionPnlChartLabel: "P&L per session",
      calmarRatio: "Calmar Ratio",
      calmarRatioDesc: "Annualized return / Max Drawdown",
      recoveryFactor: "Recovery Factor",
      recoveryFactorDesc: "Total P&L / Max Drawdown, ability to recover",
      extendedStreak: "Extended streak analysis",
      drawdownCurveTitle: "Drawdown Curve",
      drawdownCurveDesc: "Cumulative drawdown evolution over time",
      heatmapTitle: "Hour × Day Heatmap",
      heatmapDesc: "Performance by hour and day of the week",
      heatmapPnl: "Avg P&L",
      heatmapWinRate: "Win Rate",
      heatmapMode: "Display mode",
      heatmapLegendBelow50: "WR < 50%",
      heatmapLegendAbove50: "WR ≥ 50%",
      timeSlotTitle: "P&L by Time Slot",
      timeSlotDesc: "Your best and worst trading windows",
      timeSlotPreMarket: "Pre-Market",
      timeSlotOpen: "Open (9h-11h)",
      timeSlotMidDay: "Mid-Day (11h-14h)",
      timeSlotAfternoon: "Afternoon (14h-16h)",
      timeSlotPowerHour: "Power Hour (16h+)",
      holdDurationTitle: "Trade hold duration",
      holdDurationDesc: "Correlation between trade duration and outcome",
      holdDurationX: "Duration (min)",
      holdDurationY: "P&L ($)",
      holdDurationWinRate: "Win Rate by hold duration",
      holdDurationAvgPnl: "Avg P&L by hold duration",
      sizingTitle: "Sizing Analysis",
      sizingDesc: "Position size evolution over time",
      tagStatsTitle: "Performance by Tag",
      tagStatsDesc: "Win rate, total and average P&L per setup/tag",
      noTags: "No tags on your trades",
      monthlyStatsTitle: "Monthly Table",
      monthlyStatsDesc: "Month-by-month comparison",
      rollingWinRateTitle: "Rolling Win Rate",
      rollingWinRateDesc: "Sliding win rate over the last N trades",
      rollingWindow: "Window",
      biasesTitle: "Cognitive Biases",
      biasesDesc: "Automatic detection of behavioral biases",
      fomoIndicator: "FOMO Indicator",
      fomoIndicatorDesc: "% of trades taken < 10min after a loss",
      lossAversionRatio: "Loss Aversion",
      lossAversionRatioDesc: "Avg loser duration / avg winner duration (>1 = holding losers too long)",
      overconfidenceScore: "Overconfidence",
      overconfidenceScoreDesc: "After 3+ wins in a row, did you size up next trade?",
      revengeTradingCount: "Revenge Trading",
      revengeTradingCountDesc: "Trades taken < 30min after a loss",
      noExitDateData: "Duration data requires exit date to be set on trades",
      proOnly: "Pro only",
      propFirmAnalytics: {
        categoryTitle: "Prop Firm Analytics",
        invested: "Invested",
        payoutsReceived: "Payouts received",
        netResult: "Net result",
        profitLabel: "Profit",
        profitFactor: "Profit factor",
        roiGlobal: "Global ROI",
        successRate: "Success rate",
        successRateDesc: "PA / closed",
        successRateByFirm: "Success rate by prop firm",
        bestAccountSize: "Best account size",
        timelineMetrics: "Timeline",
        avgChallengeDuration: "Avg challenge duration",
        avgPaDuration: "Avg PA duration",
        avgTotalDuration: "Avg total duration",
        fastestSuccess: "Fastest success",
        ratios: "Ratios",
        avgMultiplier: "Avg multiplier",
        avgPayoutPerSuccess: "Avg payout / success",
        avgCostPerSuccess: "Avg cost / success",
        bestFirm: "Best prop firms",
        colFirm: "Prop firm",
        colAccounts: "Accounts",
        colInvested: "Invested",
        colPayouts: "Payouts",
        colNet: "Net",
        colRoi: "ROI",
        colWinRate: "Challenge Success",
        colSuccess: "Success",
        totalRow: "Total",
        noData: "No prop firm accounts in current selection",
        noDataLink: "Go to Prop Firm Manager",
        days: "d",
        wins: "wins",
        inProgress: "in progress",
        paLabel: "PA",
        unknownFirm: "Unknown",
        multiplierPrefix: "×",
      },
      payoutsTitle: "Profitability",
      payoutsDesc: "Prop firm ROI, costs vs payouts, profitability per account",
      payoutsTotalReceived: "Total payouts",
      payoutsTotalCost: "Total expenses",
      payoutsRoi: "Net ROI",
      payoutsBest: "Best payout",
      payoutsNet: "Net result",
      payoutsNetSubtitle: "payouts − expenses",
      payoutsRoiSubtitle: "return on investment",
      payoutsOverTime: "Payouts & expenses",
      payoutsChartPayouts: "Cumulative payouts",
      payoutsChartCosts: "Cumulative expenses",
      payoutsChartNet: "Cumulative net",
      payoutsPerAccount: "Per-account tracking",
      payoutsAccountName: "Account",
      payoutsChallengeCost: "Challenge",
      payoutsActivationFee: "Activation",
      payoutsLinkedCharges: "Linked charges",
      payoutsReceived: "Payouts received",
      payoutsNetPnl: "Net PnL trades",
      payoutsNetResult: "Net result",
      payoutsEditAction: "Edit financial details",
      payoutsEditTitle: "Financial details of {account}",
      payoutsEditDescription: "Costs, payouts and charges for this account, without going back to the Accounts page.",
      payoutsEditPayouts: "Payouts",
      payoutsEditNoPayouts: "No payout recorded.",
      payoutsEditDetected: "Detected",
      payoutsEditDetectedHint: "Added automatically by the sync after a balance drop with no matching trade.",
      payoutsEditMarkAdjustment: "Not a payout: PnL taken back by the prop firm",
      payoutsEditAdjustments: "PnL taken back by the prop firm",
      payoutsEditAdjustmentsHint: "Profit removed from the account by the prop firm (for example a MAE rule violation). Counted in the balance, not in your payouts.",
      payoutsEditMarkPayout: "It was a payout",
      payoutsEditDeletePayout: "Delete this payout",
      payoutsEditDeleteConfirm: "Delete the {amount} payout?",
      payoutsEditCharges: "Linked charges",
      payoutsEditSave: "Save",
      payoutsEditClose: "Close",
      payoutsEditSaved: "Financial details updated",
      payoutsEditError: "Could not save your changes",
      payoutsEditInvalidAmount: "Invalid amount",
      payoutsDeleteExpenseAction: "Delete expense",
      payoutsDeleteExpenseTitle: "Delete this expense?",
      payoutsDeleteExpenseBody: "The amounts below will be removed from **{account}** and from the Prop Firm Manager.",
      payoutsDeleteExpenseTotal: "Total removed",
      payoutsDeleteExpenseKeepsAccount: "The account is kept, along with its trades, payouts and linked charges.",
      payoutsDeleteExpenseCancel: "Cancel",
      payoutsDeleteExpenseConfirm: "Delete expense",
      payoutsDeleteExpenseDone: "Expense deleted",
      payoutsGlobalCharges: "Global charges",
      payoutsGlobalChargesAnnual: "Annualized cost",
      payoutsHistory: "Payouts history",
      payoutsNoData: "No payouts recorded",
      payoutsPaginationPage: "Page",
      payoutsPaginationOf: "of",
      payoutsPaginationPrev: "Previous page",
      payoutsPaginationNext: "Next page",
      payoutsNoEligibleAccounts: "No funded accounts (PA) with payouts",
      payoutsAnnualized: "annualized",
      payoutsAccountsCount: "accounts",
      pips: {
        sectionTitle: "Pip Analysis",
        avgPips: "Avg pips / trade",
        totalPipsWonLost: "Pips won / lost",
        pipRatio: "Pip W/L ratio",
        bestWorstTrade: "Best / Worst trade",
        winRateByBucket: "Win rate by pip range",
        distribution: "Pip distribution",
        bySymbol: "Pips by symbol",
        avgPipsCol: "Avg pips",
        totalPipsCol: "Total pips",
        noData: "No trades with entry and exit prices available.",
        estimatedPipSize: "Estimated pip size",
        winRateSeriesLabel: "Win rate %",
        tradesSeriesLabel: "Trades",
        rangeHeader: "Range",
        avgPipsWin: "Avg win pips",
        avgPipsLoss: "Avg loss pips",
      },
      payoutsPerYear: "/yr",
      payoutsTotalCharges: "Charges",
      payoutsBreakdown: "Expense breakdown",
      equityViewGlobal: "Global",
      equityViewByGroup: "By group",
      equityViewByAccount: "By account",
      reportTitle: "Profitability report",
      reportGranMonth: "By month",
      reportGranQuarter: "By quarter",
      reportGranYear: "By year",
      reportColPeriod: "Period",
      reportColPayouts: "Payouts",
      reportColChallengeCost: "Challenges",
      reportColActivationFee: "Activation",
      reportColLinkedCharges: "Linked charges",
      reportColGlobalCharges: "Global charges",
      reportColTotalCost: "Total costs",
      reportColNet: "Net",
      reportTotalRow: "Total",
      reportNoData: "No data for the selected period",
      noData: "No data available",
      notEnoughSessionData: "Not enough session data",
      biasesfomoDesc: "Trades taken outside your usual window or after long breaks",
      biasesRevengeDesc: "Trades taken quickly after a loss",
      biasesLossAversionDesc: "Trades closed prematurely before reaching the target",
      biasesOverconfidenceDesc: "Overtrading after a winning streak",
      chartView: "Chart view",
      tableView: "Table view",
      sideLabel: "Side",
      winRateLabel: "Win rate",
      monthLabel: "Month",
      tagLabel: "Tag",
    },
    routines: {
      title: "Trading Routines",
      subtitle: "Create and track your daily routines",
      createRoutine: "Create Routine",
      editRoutine: "Edit Routine",
      deleteRoutine: "Delete",
      newRoutine: "New Routine",
      noRoutines: "No routines created. Create your first routine!",
      createFirst: "Create Routine",
      name: "Name",
      description: "Description",
      frequency: "Frequency",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      custom: "Custom",
      checklistItems: "Checklist Items",
      addItem: "Add Item",
      isActive: "Active Routine",
      cancel: "Cancel",
      save: "Save",
      routineCreated: "Routine created",
      routineUpdated: "Routine updated",
      routineDeleted: "Routine deleted",
      error: "Error",
      atLeastOneItem: "At least one checklist item is required.",
      cannotCreate: "Failed to create routine.",
      cannotUpdate: "Failed to update routine.",
      cannotDelete: "Failed to delete routine.",
      confirmDelete: "Are you sure you want to delete this routine?",
      completedToday: "(Completed today)",
      markCompleted: "Mark {count} item(s) as completed",
      items: "item(s)",
      lastCompletion: "Last completion",
      completionProgress: "Last completion progress",
      loading: "Loading...",
      routineCompleted: "Routine completed",
      heatmapTitle: "Global Routines Heatmap",
      heatmapSubtitle: "Overview of all your routine completions",
      month: "Month",
      quarter: "Quarter",
      monthNames: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      monthNamesShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      dayLetters: ["M", "T", "W", "T", "F", "S", "S"],
      excellent: "Excellent",
      good: "Good",
      partial: "Partial",
      notDue: "Not Due",
      missed: "Missed",
      limitReached: "Limit reached",
      limitReachedDescription: "Free plan is limited to 3 routines. Upgrade to Pro for unlimited routines.",
    },
    analysis: {
      title: "Fundamental Analysis",
      subtitle: "Document your fundamental analyses",
      newAnalysis: "New Analysis",
      editAnalysis: "Edit Analysis",
      createFirst: "Create Analysis",
      noAnalyses: "No analyses found. Create your first analysis!",
      loading: "Loading...",
      search: "Search",
      clear: "Clear",
      searchPlaceholder: "Search by title or content...",
      symbolPlaceholder: "Symbol (e.g., AAPL)",
      titleLabel: "Title",
      contentLabel: "Content (Markdown)",
      contentPlaceholder: "# Title\n\nYour analysis in markdown...",
      symbolsLabel: "Symbols (comma-separated)",
      symbolsPlaceholder: "AAPL, TSLA, MSFT",
      tagsLabel: "Tags (comma-separated)",
      tagsPlaceholder: "earnings, tech, growth",
      analysisCreated: "Analysis created",
      analysisUpdated: "Analysis updated",
      analysisDeleted: "Analysis deleted",
      cannotCreate: "Failed to create analysis.",
      cannotUpdate: "Failed to update analysis.",
      cannotDelete: "Failed to delete analysis.",
      confirmDelete: "Are you sure you want to delete this analysis?",
      editDescription: "Edit your fundamental analysis",
      createDescription: "Create a new fundamental analysis",
      updatedOn: "Updated on",
      readMore: "Read more",
      collapse: "Collapse",
    },
    templates: {
      noTemplate: "No template",
      manage: "Manage",
      createTemplate: "Create template",
      edit: "Edit",
      delete: "Delete",
      selectTemplate: "Select a template",
      default: "default",
      templateCreated: "Template created",
      templateUpdated: "Template updated",
      templateDeleted: "Template deleted",
      cannotCreate: "Failed to create template.",
      cannotUpdate: "Failed to update template.",
      cannotDelete: "Failed to delete template.",
      confirmDelete: "Are you sure you want to delete this template?",
      createDescription: "Create a custom template for your trade notes",
      editTemplate: "Edit Template",
      editDescription: "Edit your custom template",
      nameLabel: "Template name",
      namePlaceholder: "E.g., Basic template",
      contentLabel: "Content (Markdown)",
      contentPlaceholder: "# Title\n\nYour template in markdown...",
      setAsDefault: "Set as default template",
      systemDefaultNote: "This system default template cannot be deleted, but you can modify it.",
      create: "Create",
      loading: "Loading...",
    },
    markdownEditor: {
      placeholder: "Write your notes in Markdown...\n\nExamples:\n# Title\n**Bold** *Italic*\n- List\n![Image](url)\n\nTip: You can paste images directly from the clipboard!",
      helpText: "Tip: Use the toolbar above to format your text. Markdown supported: headings, bold, italic, lists, links, images.",
      titlePlaceholder: "Title",
      boldPlaceholder: "bold",
      italicPlaceholder: "italic",
      underlinePlaceholder: "underlined",
      listItemPlaceholder: "list item",
      heading1: "Heading 1",
      heading2: "Heading 2",
      heading3: "Heading 3",
      bold: "Bold",
      italic: "Italic",
      underline: "Underline",
      list: "List",
      link: "Link",
      image: "Image",
      edit: "Edit",
      preview: "Preview",
      noContent: "No content",
      imageUrlPrompt: "Image URL (you can also paste an image from the clipboard):",
      imageAltPrompt: "Alt text (optional):",
      linkUrlPrompt: "Link URL:",
      linkTextPrompt: "Link text:",
      pastedImage: "Pasted image",
    },
    goals: {
      title: "Trading Goals",
      subtitle: "Define and track your goals",
      createGoal: "Create Goal",
      editGoal: "Edit Goal",
      deleteGoal: "Delete",
      newGoal: "New Goal",
      noGoals: "No goals created. Create your first goal!",
      createFirst: "Create Goal",
      goalTitle: "Title",
      description: "Description",
      type: "Type",
      targetValue: "Target Value",
      deadline: "Deadline",
      pnl: "P&L",
      winRate: "Win Rate",
      tradesCount: "Trades Count",
      custom: "Custom",
      cancel: "Cancel",
      save: "Save",
      goalCreated: "Goal created",
      goalUpdated: "Goal updated",
      goalDeleted: "Goal deleted",
      error: "Error",
      cannotCreate: "Failed to create goal.",
      cannotUpdate: "Failed to update goal.",
      cannotDelete: "Failed to delete goal.",
      confirmDelete: "Are you sure you want to delete this goal?",
      progress: "Progress",
      completed: "Completed",
      notCompleted: "In Progress",
      daysRemaining: "days remaining",
      daysOverdue: "days overdue",
      loading: "Loading...",
      recurrence: "Recurrence",
      recurrenceNone: "None",
      recurrenceDaily: "Daily",
      recurrenceWeekly: "Weekly",
      recurrenceMonthly: "Monthly",
      badgeDaily: "Daily",
      badgeWeekly: "Weekly",
      badgeMonthly: "Monthly",
      resetAt: "Reset on",
      goalCreatedDescription: "Goal created successfully.",
      goalUpdatedDescription: "Goal updated successfully.",
      goalDeletedDescription: "Goal deleted successfully.",
      limitReached: "Limit reached",
      limitReachedDescription: "Free plan is limited to 3 goals. Upgrade to Pro for unlimited goals.",
      heatmapTitle: "History",
      heatmapAchieved: "achieved",
      heatmapOf: "/",
      heatmapDays: "days",
      heatmapWeeks: "weeks",
      heatmapMonths: "months",
      heatmapNoData: "No history available yet",
    },
    onboarding: {
      stepOf: "Step {current} of {total}",
      step1Title: "Create your first account",
      step1Description: "Without an account, you can't link your trades to a prop firm or personal account. It's the foundation of everything.",
      step1Cta: "Create my account",
      step1Celebration: "Account created! 🎉",
      step2Title: "Log your first trade",
      step2Description: "A journal without trades is a blank page. Start building your trading history.",
      step2Cta: "Add a trade",
      step2Celebration: "First trade logged! 🚀",
      step3Title: "Open a Session",
      step3Description: "Sessions let you time your trading days and analyze your performance. On the Sessions page, click on a row to open its detail, that's where the magic happens.",
      step3Cta: "Go to Sessions →",
      step3Celebration: "Sessions explored! ⏱️",
      step4Title: "Discover the Prop Firm Manager",
      step4Description: "Manage your prop firm accounts, track your payouts, and visualize your progress. Visit the page to complete this step.",
      step4Cta: "Open Prop Firm Manager",
      step4Celebration: "Prop Firm Manager discovered! 🏆",
      finalTitle: "You're ready to trade with a real edge. 🚀",
      finalCta: "Let's go!",
      pillExpand: "Resume onboarding",
    },
    landing: {
      announcement: {
        text: "🎯 Early Bird: Pro Annual at €144/year, save €36 vs monthly Pro!",
        cta: "See the offer →",
      },
      hero: {
        title: "Stop losing your prop firm accounts to the same mistakes.",
        subtitle: "Edgely analyzes every trade, reveals your destructive patterns, and tells you exactly what to fix to get consistent results.",
        ctaPricing: "View Pricing",
        ctaSignup: "Get Started Free",
        ctaGetStarted: "Get Started",
        ctaFeatures: "See features",
        badge: "Free · No credit card · Upgrade anytime",
      },
      features: {
        title: "From chaos to system.",
        description: "The tools that make the difference between trading blind and trading with a real edge.",
      },
      examples: {
        overline: "Edgely",
        title: "Everything in one place",
        subtitle: "Net PnL, Profit Factor, Win Rate, session calendar, a complete view to know exactly where you stand and never miss a payout from lack of visibility.",
        videoCta: "Watch the demo",
        videoAria: "Play the Edgely presentation video",
        integrationsTitle: "Prop firm integrations",
        integrationsSubtitle: "Connect your accounts from every prop firm.",
      },
      pricing: {
        title: "Pricing",
        subtitle: "Choose the plan that fits your needs.",
        popular: "Popular",
        seeAllPlans: "See all plans",
        needMoreInfo: "Need more information?",
        freeBadge: "Start here",
        trustCopy: "No credit card for free plan · Cancel Pro in 1 click · Secure Stripe payment",
        earlyAdopterBadge: "Early adopter · 25 spots",
        ctaFree: "Start for free →",
        ctaPro: "Start with Pro →",
        ctaMax: "Upgrade to Max →",
        disclaimer: "One trade avoided thanks to Edgely pays for 12 months of Pro.",
      },
      testimonials: {
        title: "Testimonials",
      },
      faq: {
        title: "FAQ",
        description: "Everything you need to know about our platform.",
      },
      footer: {
        product: "Product",
        company: "Company",
        legal: "Legal",
        resources: "Resources",
        documentation: "Documentation",
        blog: "Blog",
        about: "About",
        privacy: "Privacy",
        terms: "Terms of Service",
        disclosures: "Disclosures",
        affiliation: "Affiliate program",
        help: "Help",
        comparateur: "Prop firm comparator",
        copyright: "© {year} Edgely. All rights reserved.",
        allRightsReserved: "All rights reserved",
        community: "Community",
        followUs: "Follow us on X",
        followInstagram: "Follow us on Instagram",
        contactSupport: "Email support",
        joinDiscord: "Join the Discord",
      },
      featuresList: {
        personalizedJournal: {
          name: "Trading Journal",
          description: "Every trade documented, every mistake tracked. Import from CSV, add notes, tags, and screenshots and build a history you can actually learn from.",
        },
        advancedAnalytics: {
          name: "Advanced Trading Analytics",
          description: "Win Rate, Profit Factor, Sharpe Ratio, best time slots, patterns by setup... Stop guessing. Your data speaks, listen to it.",
        },
        disciplineSupport: {
          name: "Discipline & Routines",
          description: "Timed sessions, daily routines, measurable goals. Because consistency is what separates a good trader from a profitable one.",
        },
        propFirmManagement: {
          name: "Prop Firm Manager",
          description: "Manage your Apex, FTMO, Topstep and more challenges like a real business: statuses, payout cycles, account groups. A full overview so you never lose control.",
        },
        tradingSessions: {
          name: "Trading Sessions",
          description: "Start a timer, trade, then evaluate your session: mood, discipline, performance. Identify your best time slots and behavioral patterns over time.",
        },
        autoSync: {
          name: "Automatic MetaTrader Sync",
          description: "Connect MetaTrader 4 or 5 directly to Edgely. Your trades are imported automatically, no manual work needed. Available for Max members.",
        },
      },
      testimonialsList: {
        alexandre: {
          quote: "This journal helped me identify my trading patterns and improve my win rate by 15% in 3 months. Session tracking is a game-changer!",
        },
        sophie: {
          quote: "The analytics are incredible. I can finally see clearly where I need to improve. The integrated TradingView charts are perfect for analyzing my trades.",
        },
        marc: {
          quote: "The interface is intuitive and the routines help me stay disciplined. The heatmap really motivates me to complete my daily routines!",
        },
      },
      faqsList: {
        import: {
          question: "Can I import my existing trades and accounts?",
          answer: "Yes. The easiest way is automatic sync of your Tradovate trades (Max plan, in beta): your executions land in your journal on their own. Otherwise, Tradovate and TopstepX import in 10 seconds, CSV import, and manual entry are all available. MetaTrader sync is coming soon.",
        },
        metrics: {
          question: "What metrics are calculated?",
          answer: "We calculate win rate, profit factor, Sharpe ratio, max drawdown, expectancy and much more.",
        },
        security: {
          question: "Is my data secure?",
          answer: "Absolutely. All your data is encrypted and stored securely. We never share your information.",
        },
        export: {
          question: "Can I export my data?",
          answer: "Yes, you can export all your trades in CSV format at any time.",
        },
        sessions: {
          question: "How do trading sessions work?",
          answer: "edgely automatically detects your sessions as you add trades. Rate your mood, discipline and market conditions, add notes and even a YouTube video (replay, debrief). Each session is fully customizable and shareable.",
        },
        templates: {
          question: "Can I customize my note templates?",
          answer: "Yes, you can create, edit and delete your own templates. A default template is provided to help you get started.",
        },
        beginner: {
          question: "I'm new to trading. Is edgely right for me?",
          answer: "Absolutely. edgely is built for all levels. For beginners, keeping a trading journal from day one is one of the best habits you can build: you'll quickly understand your recurring mistakes, emotional biases, and which setups actually work for you. The free plan is more than enough to get started.",
        },
        propFirm: {
          question: "I trade with multiple prop firms. Can edgely handle that?",
          answer: "Yes, that's one of edgely's core strengths. You can create as many accounts as you need (Apex, FTMO, Topstep, etc.), organize them into groups, track their status (challenge, funded, inactive), and monitor your drawdowns and payouts in real time. One centralized view to run your prop firm business like a pro.",
        },
        freePlan: {
          question: "What does the free plan include?",
          answer: "The free plan includes 1 trading account, unlimited trades, unlimited sessions, 3 routines, 3 goals, and basic analytics. That's more than enough to get started and see whether edgely fits your trading style.",
        },
        multiAccount: {
          question: "Can I analyze my performance across multiple accounts at once?",
          answer: "Yes. You can create account groups and view aggregated performance across all of them. Perfect if you run multiple prop firm accounts or separate strategies by account.",
        },
        dataLoss: {
          question: "What happens if I delete a trade or an account?",
          answer: "Deletion is permanent. We recommend regularly exporting your data as CSV from the app. Your data is never deleted automatically and stays available as long as your account is active.",
        },
      },
      brokers: {
        title: "Supported Integrations & Brokers",
        description: "Import your trades from your favorite platforms. Automatic sync is coming soon.",
        colBroker: "Broker / Platform",
        colMarkets: "Markets",
        colManualImport: "Manual Import",
        colCsvUpload: "CSV & Copy-Paste",
        colAutoSync: "Auto Sync",
      },
      freePlan: {
        overline: "Integrations",
        title: "Every prop firm, one journal.",
        subtitle: "Apex, TopStep, FundedNext, MyFundedFutures and many more: bring all your prop firm accounts into Edgely and analyze your entire performance in one place.",
        badge: "Works with the major prop firms",
        trades: { value: "∞", label: "logged trades" },
        sessions: { value: "∞", label: "sessions" },
        routines: { value: "3", label: "active routines" },
        analytics: { value: "∞", label: "basic analytics" },
        accounts: { value: "1", label: "trading account" },
        cta: "Connect my accounts for free →",
        brokersImportTitle: "10-second import",
        brokersSyncTitle: "Automatic sync",
        comingSoon: "Coming soon",
      },
      howItWorks: {
        overline: "How it works",
        title: "Take your trading to the next level. In three steps.",
        subtitle: "Connect your accounts, analyze your patterns, fix what's costing you payouts.",
        step1: {
          badge: "Connect",
          title: "Every account. One place.",
          description: "Import your trades in 10 seconds.",
          statusConnected: "Connected",
          statusSyncing: "Syncing…",
        },
        step2: {
          badge: "Analyze",
          title: "Your patterns. Your biases. Your edge.",
          description: "Edgely automatically detects your winning setups, your losing hours, your cognitive biases. You finally see what you couldn't.",
          winRate: "Win Rate",
          profitFactor: "Profit Factor",
          biasRevenge: "Revenge trading detected",
          biasBestHour: "Best hour: 3:30 PM",
          biasAvg: "NQ scalp +$53 avg",
          biasOvertrading: "Overtrading on Fridays",
        },
        step3: {
          badge: "Perform",
          title: "Fix. Improve. Reach new heights.",
          description: "Apply Edgely's insights session after session. Your metrics become consistent. You take your trading to the next level. You reach a new level of results.",
          payoutLabel: "Payout approved, Apex 100K",
        },
      },
      ctaFinal: {
        title: "Your trading journal is waiting.",
        cta: "Start for free",
        badge: "No credit card · No commitment",
      },
    },
    propFirmManager: {
      title: "Prop Firm Manager",
      subtitle: "Prop firm account profitability",
      totalPayouts: "Total Payouts",
      totalExpenses: "Total Expenses",
      netProfit: "Net Profit",
      roiGlobal: "Global ROI",
      activeAccounts: "Active accounts",
      bestRoi: "Best ROI",
      noBestRoi: "",
      totalAccounts: "Total accounts",
      accountsWithPayout: "With payout(s)",
      successRate: "Success rate",
      calendarPayout: "Payout",
      calendarExpense: "Expense",
      calendarRecurring: "Recurring charge",
      calendarTooltipPayouts: "Payouts",
      calendarTooltipExpenses: "Expenses",
      calendarTooltipRecurring: "Recurring charges",
      calendarEventSingular: "event",
      calendarEventPlural: "events",
      calendarFunded: "Funded",
      calendarTooltipFunded: "Funded transitions",
      calendarReset: "Reset",
      calendarTooltipReset: "Account resets",
      noEventsThisMonth: "No events this month",
      eventsThisMonth: "Events this month",
      proRequired: "Pro feature",
      monthRecapTitle: "Month summary",
      monthRecapChallenges: "Challenges",
      monthRecapActivations: "Activations",
      monthRecapSubscriptions: "Subscriptions",
      monthRecapPayouts: "Payouts",
      monthRecapTotalExpenses: "Total expenses",
      monthRecapRoi: "Monthly ROI",
      monthRecapNoData: "No events this month",
      importHistory: "Import history (CSV)",
      importModalTitle: "Import account history",
      importModalDesc: "Import your inactive prop firm accounts and their payouts in a single operation. One row = one account.",
      downloadTemplate: "Download CSV template",
      importBtn: "Import",
      importSuccess: "Import complete",
      noValidRows: "No valid rows found in the file.",
      importDropZone: "Drop a CSV file here or click to select",
      importParseError: "Error",
      importParseErrorDesc: "Could not read the CSV file.",
      importWrongFormatTitle: "This file isn't a prop firm accounts export",
      importWrongFormatDesc:
        "This import expects your list of prop firm accounts (propfirm, nom_compte, tiers, taille_compte…), not trades. To import trades, use Journal → Import CSV. You can also download the template below.",
      importFailError: "Error",
      importFailErrorDesc: "Import failed.",
      importAccountsToCreate: "Accounts to create",
      importConflicts: "Conflicts detected",
      importConflictSkip: "Skip",
      importConflictUpdate: "Update",
      importConflictEventsWarning: "Linked payouts will be skipped if you choose Skip",
      importPayoutsSection: "Payouts",
      importSummaryLine: "{accounts} account(s) to create, {conflicts} conflict(s), {payouts} payout(s), {errors} error(s)",
      importSuccessAccounts: "{created} created, {updated} updated, {skipped} skipped, {payouts} payouts imported",
      importBlockedTitle: "Import blocked",
      importBlockedDesc: "Fix the following errors and re-upload the file.",
      importFixFile: "Fix the file",
      importSkippedRows: "Skipped rows",
      importErrorLine: "Row",
      importReferenceTitle: "Accepted values",
      importRefPropFirm: "Prop firm",
      importRefAccountType: "Account type",
      importReferenceSizes: "Sizes",
      importRefStrategies: "Existing strategies",
      importRefNoStrategy: "No strategies yet",
      addPayoutBtn: "Add payout",
      addPayoutModalTitle: "Add payouts",
      addPayoutLine: "Add a row",
      addPayoutSave: "Save ({n} payout(s))",
      addPayoutSuccess: "{n} payout(s) added",
      addPayoutSuccessWithCertFailures: "{n} payout(s) added. {f} certificate(s) failed to upload.",
      addPayoutAccount: "Account",
      addPayoutDate: "Date",
      addPayoutAmount: "Amount (USD)",
      addPayoutGrossAmount: "Gross amount",
      addPayoutSplitPercent: "Profit split (%)",
      addPayoutNetAmount: "Net amount received",
      addPayoutCertificate: "Certificate (optional)",
      addPayoutCertificatePick: "Choose a file",
      addPayoutNoActiveAccounts: "No active accounts available",
      recentPayoutsTitle: "Recent payouts",
      recentPayoutsEmpty: "No payouts yet.",
      recentPayoutsHeaderDate: "Date",
      recentPayoutsHeaderAccount: "Account",
      recentPayoutsHeaderAmount: "Amount",
      recentPayoutsHeaderActions: "Actions",
      wallet: {
        title: "TakeProfitTrader Wallet",
        availableBalance: "Available balance",
        initialBalance: "Initial balance",
        transfersReceived: "Transfers received",
        withdrawn: "Withdrawn",
        inFlight: "In flight",
        setInitialBalance: "Set initial balance",
        editInitialBalance: "Edit initial balance",
        initialBalanceBannerTitle: "Have existing TPT history?",
        initialBalanceBannerDesc: "Set an initial balance so your wallet total is right.",
        ignore: "Ignore",
        add: "Add",
        transferTab: "Transfer from account",
        withdrawalTab: "Withdrawal to external",
        withdrawalTabDesc: "Record a withdrawal from the wallet to your external account (bank, PayPal, Wise…).",
        transfersTableTitle: "TPT - Transfers (account → wallet)",
        withdrawalsTableTitle: "TPT - Withdrawals (wallet → external)",
        colDate: "Date",
        colAccount: "Account",
        colGross: "Gross",
        colSplit: "Split",
        colNet: "Net",
        colAmount: "Amount",
        colMethod: "Method",
        colStatus: "Status",
        colActions: "Actions",
        statusPending: "Pending",
        statusApproved: "Approved",
        statusRejected: "Rejected",
        statusCompleted: "Completed",
        detectedTag: "detected",
        detectedTitle: "Detected by the sync: check the share received, then set the transfer to Approved.",
        splitEditLabel: "Share received (%)",
        accountLabel: "Source account",
        dateLabel: "Date",
        grossAmountLabel: "Gross amount",
        splitPercentLabel: "Split (%)",
        netAmountLabel: "Net amount",
        amountLabel: "Amount",
        methodLabel: "Payment method",
        methodPlaceholder: "e.g. Wise, PayPal, ACH",
        notesLabel: "Notes",
        notesPlaceholder: "Optional",
        initialBalanceLabel: "Initial balance",
        initialBalanceDateLabel: "Reference date",
        saveInitialBalance: "Save",
        transferAdded: "Transfer added",
        withdrawalAdded: "Withdrawal added",
        transferDeleted: "Transfer deleted",
        withdrawalDeleted: "Withdrawal deleted",
        statusUpdated: "Status updated",
        noTransfers: "No transfers",
        noWithdrawals: "No withdrawals",
        deleteTransferConfirm: "Delete this transfer?",
        deleteWithdrawalConfirm: "Delete this withdrawal?",
        emptyTitle: "TakeProfitTrader wallet",
        emptyDesc: "This widget shows your TPT wallet (transfers and withdrawals). Add a TakeProfitTrader account to activate it.",
      },
    },
    attachments: {
      sectionTitle: "Documents",
      empty: "No document for this account yet.",
      uploadButton: "Add a document",
      uploadDialogTitle: "New document",
      uploadDialogDescription:
        "Account certificate, payout certificate, invoice… PDF, PNG or JPG. 5 MB max.",
      fieldName: "Name",
      fieldType: "Type",
      fieldFile: "File",
      fieldLinkedPayout: "Linked payout (optional)",
      fieldLinkedPayoutNone: "None",
      typeChallenge: "Account certificate",
      typePayout: "Payout certificate",
      typeInvoice: "Invoice",
      typeOther: "Other",
      view: "View",
      download: "Download",
      delete: "Delete",
      deleteConfirm: "Delete this document?",
      limitReached: "Limit reached (3/3). Upgrade to Pro for unlimited documents.",
      errorUpload: "Upload failed.",
      errorMimeRejected: "Unsupported format. PDF, PNG or JPG only.",
      errorTooLarge: "File too large (5 MB max).",
      errorPayoutTaken: "This payout already has a certificate.",
      uploading: "Uploading…",
      certificateBadge: "Certificate validated",
      addCertificate: "Add certificate",
    },
    reports: {
      shareButton: "Share",
      dialogTitle: "Generate a shareable report",
      dialogDescription: "Pick a period and a format. Your report will be ready to post on Instagram, X or WhatsApp.",
      period: "Period",
      periodDaily: "Daily",
      periodWeekly: "Weekly",
      periodMonthly: "Monthly",
      format: "Format",
      formatStory: "Story (vertical)",
      formatCarousel: "Carousel (up to 7 slides)",
      generate: "Generate",
      generating: "Generating…",
      copyLink: "Copy link",
      copied: "Link copied!",
      download: "Download",
      downloadAll: "Download all (ZIP)",
      saveToPhotos: "Save to camera roll",
      saving: "Saving…",
      share: "Share",
      openInstagram: "Publish on Instagram",
      openInstagramDesktopHint: "Download the image and publish it from your phone.",
      openTiktok: "Publish on TikTok",
      openX: "Publish on X",
      socialDesktopHint: "Image downloaded. Open the app on your phone to publish.",
      publicCta: "Start your trading journal on edgely.",
      publicCtaButton: "Get started, free",
      publicLoading: "Loading…",
      publicNotFound: "Report not found.",
      error: "Something went wrong. Try again.",
      pageTitle: "Reports",
      pageSubtitle: "Generate and share visual reports of your performance.",
      generateCta: "Generate a report",
      emptyState: "No report generated yet.",
      emptyStateCta: "Generate your first report",
      thumbnailAlt: "Report preview",
      viewReport: "View report",
      reportDetailTitle: "Your report",
      filterLabel: "Filters",
      formatPdfComingSoon: "PDF",
      formatComingSoonBadge: "Coming soon",
      scopeLabel: "Filter",
      scopeAll: "All accounts",
      scopeAllInactive: "All accounts + inactive",
      scopeAccount: "Account",
      scopeGroup: "Group",
      scopePropfirm: "Prop firm",
      scopeStrategy: "Strategy",
      scopePickAccount: "Pick an account",
      scopePickGroup: "Pick a group",
      scopePickPropfirm: "Pick a prop firm",
      scopePickStrategy: "Pick a strategy",
      sectionsLabel: "Included content",
      sectionTrading: "Trading",
      sectionPropfirm: "Prop firm",
      sectionAnalytics: "Analytics",
      sectionsRequired: "Pick at least one section",
      periodQuarterly: "Quarterly",
      periodAnnual: "Annual",
      deleteAriaLabel: "Delete report",
      periodNavPrev: "Previous period",
      periodNavNext: "Next period",
      deleteConfirm: "Delete this report?",

      detailTitle: "Report",
      detailGeneratedOn: "Generated on",
      detailShare: "Share",
      detailSharing: "Generating image...",
      detailBackToList: "Back to reports",
      detailEmptySection: "No data for this period",
      detailLegacyNotice: "This report was generated before the redesign, so some sections are unavailable.",

      summaryTitle: "Summary",
      summaryNetPnl: "Net PnL",
      summaryTrades: "Trades",
      summaryDaysTraded: "Days traded",
      summaryAvgPnlPerDay: "Average PnL per day",
      summaryOutOfDays: "of {count} trading days",

      lifecycleTitle: "Accounts",
      lifecycleAdded: "Added",
      lifecycleBlown: "Lost",
      lifecycleFunded: "Turned funded",
      lifecycleLowConfidence: "+ {count} likely lost (closed with no drawdown seen)",
      lifecycleNoEvents: "No account changes in this period",
      lifecycleUnlinked: "challenge not linked",
      lifecycleOutcomesTitle: "What became of the {count} added accounts",
      lifecycleOutcomeBlown: "Lost",
      lifecycleOutcomeLikelyBlown: "Likely lost",
      lifecycleOutcomePassed: "Passed",
      lifecycleOutcomeActive: "Ongoing",

      payoutsTitle: "Payouts",
      payoutsTotal: "Total",
      payoutsCount: "Count",
      payoutsBest: "Best payout",
      payoutsNone: "No payouts in this period",
      payoutsNoPropFirm: "No prop firm",
      financeTitle: "Prop firm balance",
      financeSpent: "Spent",
      financePayouts: "Received in payouts",
      financeNet: "Net",
      financeRoi: "Net ROI",
      financeRoiNone: "No spending this period",

      advancementTitle: "Challenge progress",
      advancementTarget: "Target",
      advancementDays: "Days traded",
      advancementComplete: "Target reached",
      advancementNone: "No challenge in progress",
    },
    dateRangePicker: {
      allTime: "All time",
      today: "Today",
      thisWeek: "This week",
      thisMonth: "This month",
      last30Days: "Last 30 days",
      lastMonth: "Last month",
      thisQuarter: "This quarter",
      ytd: "YTD (year to date)",
      custom: "Custom",
    },
    pages: {
      loadErrorTitle: "Couldn't load this page",
      loadErrorDescription: "The connection to the server failed. Your page is safe.",
      loadErrorRetry: "Retry",
      manage: "My pages",
      newPage: "New page",
      pageName: "Page name",
      pageIcon: "Icon",
      cancel: "Cancel",
      create: "Create",
      noPages: "No custom pages yet. Create your first page!",
      deleteConfirm: "Delete this page?",
      edit: "Edit",
      emptyPage: "This page is empty.",
      addFirstWidget: "Add a widget",
      topbarSection: "Topbar",
      topbarAdd: "+ Topbar",
      topbarFilters: "Filters",
      topbarFiltersTitle: "Filters",
      pinAsDefault: "Set as default",
      pinAsDefaultConfirm: "Default value saved",
      unpinDefault: "Default filter removed",
      topbarAddPayout: "Add payout",
    },
    marketDataWidgets: {
      categoryLabel: 'News & Market Data',
      newsFeed: {
        title: 'News',
        noNews: 'No news available',
        error: 'Unable to load news',
        retry: 'Retry',
        showMore: 'Show {count} more news',
        justNow: 'Just now',
        sentimentPositive: 'Positive',
        sentimentNegative: 'Negative',
        sentimentNeutral: 'Neutral',
      },
      interestRates: {
        title: 'Interest Rates',
        lastUpdate: 'Updated',
        nextMeeting: 'Next meeting',
        consensusHold: 'Hold',
        consensusCut: 'Cut',
        consensusHike: 'Hike',
      },
      economicCalendar: {
        title: 'Economic Calendar',
        actual: 'Actual',
        forecast: 'Forecast',
        previous: 'Previous',
        noEvents: 'No events today',
        filterHigh: 'High',
        filterMedium: 'Medium',
        filterLow: 'Low',
      },
      earningsCalendar: {
        title: 'Earnings',
        noEarnings: 'No earnings today',
        timingBmo: 'Before Open',
        timingAmc: 'After Close',
        timingDmh: 'During Hours',
        guidanceRaised: 'Raised',
        guidanceLowered: 'Lowered',
        guidanceMaintained: 'Maintained',
        colSymbol: 'Symbol',
        colCompany: 'Company',
        colTiming: 'Timing',
        colEpsEstimate: 'EPS Est.',
        colEpsActual: 'EPS',
        colEpsSurprise: 'EPS Surp.',
        colRevenueEstimate: 'Rev. Est.',
        colRevenueActual: 'Revenue',
        colRevenueSurprise: 'Rev. Surp.',
        colPriceReaction: 'Reaction',
        colGuidance: 'Guidance',
        configureColumns: 'Columns',
      },
    },
    chart: {
      symbol: "Symbol",
      invalidSymbol: "Invalid symbol",
      unavailable: "Chart temporarily unavailable",
      reload: "Reload",
      noData: "No data available",
      noTradesForSymbol: "No trades for this symbol in the current filters",
      showTrades: "Show trades",
      widgetTitle: "Chart",
    },
    marketing: {
      journalPage: {
        heroTitle: "Trading journal: track, analyze and improve every trade",
        heroSubtitle:
          "The French-first trading journal for futures prop traders. Import your trades, measure your edge, get funded.",
        heroCta: "Create my journal for free",
        statsWinRate: "Win rate",
        statsProfitFactor: "Profit factor",
        statsDrawdown: "Drawdown",
        pillarsTitle: "A complete journal, built for progress",
        pillars: [
          { title: "Journaling", body: "Setup, result, notes and screenshots for every trade." },
          { title: "Advanced analytics", body: "Win rate, profit factor, R/R, drawdown and equity curve, automatically." },
          { title: "Sessions & routines", body: "Prepare before the open, review afterwards, link mindset to results." },
          { title: "Goals", body: "Set and track your progression goals, period after period." },
          { title: "Prop Firm Manager", body: "Multi-account, per-firm drawdown, alerts and payouts in one place." },
          { title: "Mentor mode", body: "Coaches follow their students' journals based on permissions." },
        ],
        importTitle: "Import your trades in seconds",
        importSteps: [
          "Automatic Tradovate sync (Max plan, beta)",
          "Tradovate copy-paste",
          "CSV import (Tradovate / TopStepX)",
          "Manual entry",
          "MetaTrader MT4/MT5 sync (coming soon)",
        ],
        whyTitle: "Why keep a trading journal?",
        whyBody:
          "To identify your edge, fix recurring mistakes and manage your psychology. A journal turns isolated sessions into measurable progress.",
        toolsTitle: "Excel, paper or an app?",
        toolsExcel: "Excel: it computes, but entry is tedious and formulas break.",
        toolsPaper: "Paper: computes nothing and gets lost.",
        toolsApp: "Edgely: auto import, reliable metrics, prop firm rules handled.",
        pricingTitle: "How much does a trading journal cost?",
        pricingFree: "Free: 0€, one account, unlimited trades, no credit card.",
        pricingPro: "Pro: 15€/mo, unlimited accounts, Prop Firm Manager, advanced analytics.",
        pricingMax: "Max: 29€/mo, everything in Pro plus automatic Tradovate sync (beta), MetaTrader coming soon.",
        faqTitle: "Frequently asked questions",
        faq: [
          { q: "How do I keep a trading journal?", a: "Log every trade at close (instrument, side, prices, result, setup, notes). Edgely imports your trades and computes your stats automatically." },
          { q: "Which free trading journal should I choose?", a: "Edgely's free plan journals unlimited trades on one account, no credit card, with automatic win rate and profit factor." },
          { q: "What should I record in a trading journal?", a: "Instrument, side, prices, result, and above all the context: setup, mindset and mistakes. Those notes reveal your edge." },
          { q: "Is there a trading journal in French?", a: "Yes, Edgely is fully in French, built for French-speaking futures prop traders." },
          { q: "What's the difference between Excel and an app?", a: "Edgely imports trades automatically, computes without errors and handles drawdown rules, where Excel needs manual entry and fragile formulas." },
        ],
        ctaTitle: "Ready to chase your first payout?",
        ctaButton: "Create my trading journal for free",
      },
      nav: {
        features: "Features",
        forWho: "Who is it for?",
        pricing: "Pricing",
        comparateur: "Comparator",
        blog: "Blog",
        login: "Log in",
        ctaStart: "Start for free",
        megaFeatures: [
          { key: "journal", label: "Trading Journal", desc: "Log and analyze every trade" },
          { key: "propFirmManager", label: "Prop Firm Manager", desc: "Run all your prop firm accounts" },
          { key: "analytics", label: "Advanced Analytics", desc: "Your patterns, strengths and leaks" },
          { key: "sessions", label: "Trading Sessions", desc: "Review and rate every session" },
          { key: "mentor", label: "Mentor Mode", desc: "Coach students with their real data" },
          { key: "dashboard", label: "Dashboard", desc: "Your space, your rules" },
          { key: "integrations", label: "Integrations", desc: "Auto-sync and CSV import of your trades" },
        ],
        megaSolutions: [
          { key: "debutant", label: "Beginner Prop Trader", desc: "Start risk-free, progress fast" },
          { key: "multiComptes", label: "Multi-Account Trader", desc: "One dashboard, all your prop firms" },
          { key: "coach", label: "Coach & Mentor", desc: "Access your students' dashboards" },
        ],
      },
      common: {
        ctaFinalTitle: "Ready to level up?",
        ctaFinalButton: "Start for free",
        whyTitle: "Why prop traders choose Edgely",
        relatedTitle: "Go further",
        criteria: [
          "Built specifically for prop firms",
          "Built-in Prop Firm Manager",
          "Free plan, no credit card",
          "Tradovate / TopStepX CSV import",
          "Advanced analytics",
          "Mentor Mode",
          "Customizable modular dashboard",
          "French interface",
          "Community support included",
          "Starting price",
        ],
        yes: "Yes",
        no: "No",
      },
      features: {
        journal: {
          h1: "The trading journal built for prop traders.",
          subtitle:
            "Every trade documented, every mistake tracked. Import from Tradovate or TopStepX, add your notes, tags and screenshots - and build a history you can actually put to work.",
          bullets: [
            "Broker import: from Tradovate and TopStepX in seconds",
            "Manual entry: available for every broker",
            "Per-trade context: notes, tags and screenshots",
            "Tagged mistakes: shown directly on the calendar",
            "Full history: filterable by account, strategy and period",
            "Automatic calculations: PNL and commissions on import",
          ],
        },
        propFirmManager: {
          h1: "One place to run all your prop firms.",
          subtitle:
            "No more Excel spreadsheets to track your challenges. The Prop Firm Manager centralizes all your accounts, payouts and expenses so you stay in control at all times.",
          bullets: [
            "Global view: total payouts, total expenses, net profit, overall ROI",
            "Calendar: challenge purchases and monthly charges",
            "Monthly recap: challenge fees, activation, subscriptions",
            "Payout tracking: date, amount, profit split and certificate",
            "Account status: active and inactive (blown challenges kept)",
            "Compatible with: FTMO, Apex, TopStep, Lucid, FundedNext, MyFundedFutures, TakeProfitTrader, Tradeify, Phidias, Funded Futures Family",
          ],
        },
        analytics: {
          h1: "Your trading data is talking. Listen to it.",
          subtitle:
            "Stop guessing why you lose. Edgely analyzes every variable of your trading and shows you exactly where your destructive patterns hide - and your real strengths.",
          bullets: [
            "Key metrics: Net PNL, Win Rate, Profit Factor, Sharpe ratio",
            "Timing: performance by day of the week and by time slot",
            "Setups: analysis by trade duration and setup type",
            "Curves: PNL distribution, drawdown and equity curve",
            "Psychology & tags: correlation between mental state and performance",
            "Long term: sizing and rolling Win Rate over time",
            "By prop firm: compare your performance across accounts",
          ],
        },
        sessions: {
          h1: "Every session counts. Review it, improve it.",
          subtitle:
            "An unreviewed session is a wasted session. Edgely gives you every tool to analyze what happened, understand why, and stop repeating the same mistakes.",
          bullets: [
            "Auto-created: sessions generated from your imported trades",
            "Built-in timer: track your session live",
            "Note-taking: general notes and per-trade notes",
            "Mental state: rate the mood and state of the session",
            "Calendar tags: mistakes and strategies shown monthly",
            "YouTube video: record your session and attach it",
            "TradingView replay: review your trades inside Edgely",
          ],
        },
        mentor: {
          h1: "Coach your students with their real data.",
          subtitle:
            "No more relying on your students' screenshots. Access their Edgely dashboard directly and see exactly what they do - and what they need to fix.",
          bullets: [
            "Student dashboard: full access from your account",
            "Real-time: PNL, sessions, detailed statistics",
            "Targeted notes: add notes on the student's sessions",
            "Session videos: watch the replays recorded by the student",
            "For coaches: ideal for independents and prop trading schools",
            "Included: available from the Pro Plan, at no extra cost",
          ],
        },
        dashboard: {
          h1: "Your dashboard, your rules.",
          subtitle:
            "Edgely works like Notion: you create your pages, choose your widgets, and organize your space exactly the way you work. Not the other way around.",
          bullets: [
            "Multiple pages: one page per prop firm, strategy or period",
            "Widgets of your choice: PNL, Profit Factor, Equity Curve, economic calendar, news, earnings",
            "Preset layouts: Default Dashboard, Pro Dashboard, Market Data",
            "Saveable filters: by prop firm, strategy, period, active/inactive account",
            "Full customization: move, resize and remove every widget",
            "Saved views: switch between your contexts in one click",
          ],
        },
        integrations: {
          h1: "Integrations",
          subtitle:
            "Edgely connects to your platforms to feed your journal, through automatic sync or CSV import.",
          autoSyncTitle: "Automatic sync",
          autoSyncCards: [
            {
              title: "NinjaTrader & Tradovate",
              badge: "Beta",
              body: "Direct sync of your executions through a secure read-only connection. In beta on the Max plan. In the meantime, manual import is available today (see below).",
            },
            {
              title: "MetaAPI - MT4 & MT5",
              badge: "Soon",
              body: "Connect a MetaTrader 4 or 5 account, live or prop, and sync your executions automatically into your journal. Coming soon on the Max plan.",
            },
          ],
          csvTitle: "CSV imports & copy-paste",
          csvIntro: "Import your trade history from:",
          csvItems: ["Tradovate", "TopstepX"],
        },
      },
      solutions: {
        debutant: {
          h1: "Learn your habits. Potentially avoid costly mistakes.",
          subtitle:
            "Most beginner traders lose their first accounts for the same reasons. Edgely shows them to you before they cost you too much.",
          bullets: [
            "Free plan with no credit card to start immediately",
            "4-step onboarding: account → prop firm → first trade → analytics",
            "Basic analytics good enough to spot your first patterns",
            "1 trading account, unlimited trades, unlimited sessions",
            "Community Discord support included",
            "Upgrade to Pro when you're ready, in one click",
          ],
        },
        multiComptes: {
          h1: "One dashboard. All your prop firms.",
          subtitle:
            "When you trade on 3, 5 or 10 accounts at once, complexity becomes your enemy. Edgely turns it into a competitive advantage.",
          bullets: [
            "Unlimited accounts across every compatible prop firm",
            "Prop Firm Manager: consolidated view of ROI, payouts, expenses",
            "Filters by prop firm to analyze each account separately",
            "Performance comparison across accounts and across firms",
            "Charges calendar and automatic monthly recap",
            "Available from the Pro Plan at €15/month",
          ],
        },
        coach: {
          h1: "Coach with your students' real data.",
          subtitle:
            "Your students tell you what they think they did. Edgely shows you what they actually did.",
          bullets: [
            "Mentor Mode included in the Pro Plan",
            "Invite your students and access their full dashboard",
            "View their sessions, their PNL, their detailed statistics",
            "Add notes directly on their trading sessions",
            "Watch the videos they record during their sessions",
            "Ideal for independent coaches, mentors and trading schools",
          ],
        },
      },
      comparisons: {
        tradezella: {
          h1: "Edgely vs TradeZella: which one to choose for your prop firms?",
          subtitle:
            "TradeZella is the global leader in trading journals. Edgely is built specifically for the French-speaking market and prop traders who want a more accessible tool.",
          why: [
            "100% French interface, designed for French prop traders",
            "More accessible price: €15/month vs ~$30/month",
            "Prop Firm Manager more operational for multi-account setups",
          ],
        },
        tradersync: {
          h1: "Edgely vs TraderSync: the honest comparison",
          subtitle:
            "TraderSync is a general-purpose trading journal. Edgely is built for prop traders: Prop Firm Manager, native Tradovate/TopStepX import, French interface.",
          why: [
            "Prop trading specialist where TraderSync is general-purpose",
            "French interface and support",
            "More accessible price with a free plan and no credit card",
          ],
        },
        notion: {
          h1: "Why a Notion journal no longer cuts it for your prop firms",
          subtitle:
            "Notion is a great tool. But building and maintaining a trading journal on it steals time from your real job: trading.",
          why: [
            "Analytics calculated automatically vs manual formulas to maintain",
            "Direct CSV import vs copy-pasting cell by cell",
            "Built-in Prop Firm Manager vs a cobbled-together spreadsheet",
          ],
        },
        excel: {
          h1: "Stop keeping your trading journal in Excel",
          subtitle:
            "Excel works. But after 200 trades across 5 different accounts, maintaining your formulas becomes a full-time job.",
          why: [
            "Zero formulas to create or maintain",
            "Instant visual analytics right after import",
            "Prop Firm Manager impossible to reproduce in Excel",
          ],
        },
        best: {
          h1: "What is the best trading journal in 2026?",
          subtitle:
            "We compared the 5 solutions most used by prop traders. Here's the verdict.",
          why: [
            "Beginner with no budget → Edgely Free",
            "Active French-speaking prop trader → Edgely Pro",
            "English-speaking trader with a big budget → TradeZella; self-reliant tinkerer → Notion or Excel",
          ],
        },
        alternatives: {
          h1: "The best alternatives to TradeZella in 2026",
          subtitle:
            "TradeZella dominates the English-speaking market. But for French-speaking prop traders, there's a more suitable, more accessible alternative built for your market.",
          why: [
            "Edgely in first place: accessible price and free plan with no credit card",
            "100% French interface and support",
            "Prop trading specialization and built-in Prop Firm Manager",
          ],
        },
      },
      featureTabs: {
        eyebrow: "EDGELY",
        title: "Six tools. One hub.",
        subtitle:
          "Everything that separates trading blind from trading with a real edge.",
        discoverPrefix: "Discover",
        tabs: [
          {
            key: "dashboard",
            tabLabel: "Dashboard",
            sectionLabel: "DASHBOARD",
            h1: "Your dashboard, your rules.",
            description:
              "Edgely works like Notion: create your pages, pick your widgets, and organize your space exactly the way you work. PNL, economic calendar, equity curve, news: all in one place.",
            bullets: [
              "Modular dashboard with customizable widgets",
              "Real-time economic calendar and news",
              "Filters by prop firm, strategy and period",
              "Preset layouts: Dashboard Pro, Market Data",
            ],
            linkLabel: "Discover the Dashboard",
          },
          {
            key: "propFirmManager",
            tabLabel: "Prop Firm Manager",
            sectionLabel: "PROP FIRM MANAGER",
            h1: "One place to run all your prop firms.",
            description:
              "No more Excel spreadsheets to track your challenges. Centralize all your accounts, payouts and expenses to stay in control at all times.",
            bullets: [
              "Global view: payouts, expenses, ROI, net profit",
              "Calendar of purchases and monthly charges",
              "Payout history with certificates",
              "Compatible with Apex, TopStep, Lucid, FundedNext and more",
            ],
            linkLabel: "Discover the Prop Firm Manager",
          },
          {
            key: "analytics",
            tabLabel: "Analytics",
            sectionLabel: "ADVANCED ANALYTICS",
            h1: "Your data speaks. Listen to it.",
            description:
              "Stop guessing why you lose. Edgely analyzes every variable of your trading and shows you exactly where your destructive patterns hide, and your real strengths.",
            bullets: [
              "Win Rate, Profit Factor, Sharpe Ratio, Expectancy",
              "Cognitive biases: FOMO, Revenge Trading, Overconfidence",
              "Performance by day, time and trade duration",
              "Equity curve and PNL distribution",
            ],
            linkLabel: "Discover Analytics",
          },
          {
            key: "sessions",
            tabLabel: "Sessions",
            sectionLabel: "TRADING SESSIONS",
            h1: "Every session counts. Review it, improve it.",
            description:
              "A session you never review is a session wasted. Record your video, log your mood, tag your mistakes and analyze each trade directly on the TradingView chart.",
            bullets: [
              "YouTube video recording attached to the session",
              "Built-in TradingView chart with your trades",
              "Mood, performance and mistake evaluation",
              "Detailed trades and P&L metrics per account",
            ],
            linkLabel: "Discover Sessions",
          },
          {
            key: "marketData",
            tabLabel: "Market Data Pro",
            sectionLabel: "MARKET DATA PRO",
            h1: "All the market data. In real time.",
            description:
              "Institutional news, economic calendar, central bank policy rates and TradingView charts: everything you need to prepare your session without leaving Edgely.",
            bullets: [
              "Real-time policy rates (FED, ECB, BOE, BOJ...)",
              "Economic calendar with High/Medium/Low impact",
              "Live FinancialJuice institutional news",
              "Built-in multi-timeframe TradingView charts",
            ],
            linkLabel: "Discover Market Data Pro",
          },
          {
            key: "mentor",
            tabLabel: "Mentor Mode",
            sectionLabel: "MENTOR MODE",
            h1: "Coach your students with their real data.",
            description:
              "Your students tell you what they think they did. Edgely shows you what they actually did. Access their full dashboard, their sessions, their PNL, and leave notes directly on their trades.",
            bullets: [
              "Full access to each student's dashboard",
              "Journal, Analytics, Sessions and Prop Firm Manager views",
              "Correction notes directly on sessions",
              "Read-only mode with a clear \"Student view\" banner",
            ],
            linkLabel: "Discover Mentor Mode",
          },
        ],
      },
      hero: {
        titleBeforeHighlight: "Stop losing your ",
        titleHighlight: "prop firm",
        titleAfterHighlight: " accounts to the same mistakes.",
        subtitle:
          "Edgely analyzes every trade, reveals your destructive patterns and tells you exactly what to fix to get consistent results.",
        cta: "Start for free",
        microcopy: "✦ Free · No credit card · Upgrade anytime",
        pillsLabel: "ALL IN ONE PLACE · 6 TOOLS",
        imageAlt:
          "Edgely dashboard - trading journal for prop traders with built-in economic calendar, PNL and sessions",
        pills: [
          { label: "Dashboard", to: "/fonctionnalites/dashboard-trading" },
          { label: "Prop Firm Manager", to: "/fonctionnalites/prop-firm-manager" },
          { label: "Analytics", to: "/fonctionnalites/analytics-trading" },
          { label: "Sessions", to: "/fonctionnalites/sessions-de-trading" },
          { label: "Market Data Pro", to: "/fonctionnalites/dashboard-trading" },
          { label: "Mode Mentor", to: "/fonctionnalites/mode-mentor" },
        ],
      },
      floatingDashboard: {
        ariaLabel: "Edgely dashboard preview: net PnL, profit factor, win rate, calendar, routines, goals and news",
        netPnlLabel: "NET P&L",
        winRateLabel: "TRADE WIN %",
        profitFactorLabel: "PROFIT FACTOR",
        avgWinLossLabel: "AVG WIN/LOSS TRADE",
        avgWin: "Avg Win",
        avgLoss: "Avg Loss",
        marketHoursLabel: "MARKET HOURS",
        closed: "Closed",
        calendarLabel: "CALENDAR",
        weekLabel: "Wk",
        ecoCalendarLabel: "ECONOMIC CALENDAR",
        routinesLabel: "TODAY'S ROUTINES",
        routinePreOpen: "Pre-open routine",
        routineOpenUs: "Open US",
        daily: "Daily",
        goalsLabel: "ACTIVE GOALS",
        goalName: "PnL Maker",
        newsLabel: "NEWS",
        tradesLabel: "trades",
        dashboardTitle: "Dashboard",
        liveBadge: "LIVE",
      },
      socialProof: {
        stats: [
          { value: "200+", label: "active prop traders" },
          { value: "100%", label: "built for prop firms" },
          { value: "10", label: "compatible prop firms" },
          { value: "5★", label: "early user reviews" },
        ],
        compatibleLabel: "COMPATIBLE WITH",
        partnerLabel: "OFFICIAL NINJATRADER ECOSYSTEM MEMBER",
        propFirms: [
          "FTMO",
          "Apex",
          "TopStep",
          "Lucid Trading",
          "FundedNext",
          "MyFundedFutures",
          "TakeProfitTrader",
          "Tradeify",
          "Phidias",
          "Funded Futures Family",
        ],
      },
    },
    affiliation: {
      kicker: "Affiliate program",
      heroTitle: "Become an Edgely ambassador",
      heroSubtitle:
        "Recommend the trading journal built for prop traders and earn a recurring commission on every referred subscription.",
      benefits: {
        recurring: {
          title: "Attractive recurring commission",
          description:
            "You earn a percentage of every payment made by your referrals, month after month, for your whole commission period.",
        },
        attribution: {
          title: "60-day attribution",
          description:
            "Every visitor coming from your link is attributed to you for 60 days, even if they sign up later.",
        },
        brandingKit: {
          title: "Branding kit included",
          description:
            "Ready-to-use logos, banners and visuals to promote Edgely on your socials, Discord or channel.",
        },
        payments: {
          title: "Reliable payments",
          description:
            "Track your commissions in real time in your ambassador space and request a payout from €50 confirmed.",
        },
      },
      howTitle: "How it works",
      steps: {
        apply: {
          title: "Apply",
          description:
            "Fill in the application form in 2 minutes and tell us about your audience.",
        },
        access: {
          title: "Get your access",
          description:
            "Once approved, you receive your unique affiliate link and your ambassador space.",
        },
        earn: {
          title: "Earn commissions",
          description:
            "Every referred subscription generates a commission, paid on request from €50 confirmed.",
        },
      },
      cta: "Become an ambassador",
      ctaNote: "Reply within 48 business hours",
    },
    affiliate: {
      pageTitle: "Ambassador space",
      pageSubtitle: "Track your clicks, referrals and Edgely commissions.",
      kpis: {
        clicks: "Total clicks",
        signups: "Referred signups",
        conversions: "Paying conversions",
        conversionRate: "Conversion rate",
      },
      balances: {
        pending: "Pending",
        confirmed: "Confirmed",
        totalPaid: "Total paid",
      },
      commissions: {
        title: "Commissions",
        empty: "No commissions yet, share your link!",
        colDate: "Date",
        colClient: "Customer",
        colPayment: "Payment",
        colRate: "Rate",
        colCommission: "Commission",
        colStatus: "Status",
      },
      status: {
        pending: "Pending",
        confirmed: "Confirmed",
        cancelled: "Cancelled",
        paid: "Paid",
        requested: "Requested",
        rejected: "Rejected",
      },
      payout: {
        requestButton: "Request a payout",
        requestHint:
          "Available from €{min} of confirmed commissions and with no pending request.",
        detailsMissing:
          "Add your payment details in the settings to request a payout.",
        requestSuccess: "Payout request sent!",
        requestError: "Could not send the payout request.",
        historyTitle: "Payout history",
        historyEmpty: "No payouts yet.",
      },
      link: {
        title: "My affiliate link",
        copy: "Copy",
        copied: "Copied!",
        terms:
          "Your commission: {rate}% of every payment for {months} months per referred customer. {days}-day attribution (last click wins).",
      },
      settings: {
        title: "Settings",
        method: "Payment method",
        methodIban: "Bank transfer (IBAN)",
        methodPaypal: "PayPal",
        detailsIban: "IBAN",
        detailsPaypal: "PayPal email",
        save: "Save",
        saved: "Payment details saved.",
        saveError: "Could not save payment details.",
        changePassword: "Change my password",
      },
      branding: {
        title: "Branding kit",
        empty: "The branding kit is coming soon.",
        download: "Download",
        categoryLogo: "Logos",
        categoryBanner: "Banners",
        categoryScreenshot: "Screenshots",
        categoryGuidelines: "Guidelines",
        rulesTitle: "Brand rules",
        rulesLogo:
          "The logo is typographic only, in Space Grotesk bold, with the teal dot:",
        ruleNoEarningsPromise: "Never promise trading gains or results.",
        ruleNoLogoEdit:
          "Do not modify the logo: no color, font or proportion changes.",
        ruleColors: "Brand colors: teal #00C9A7 on navy #0F1729.",
      },
    },
    adminAffiliates: {
      title: "Affiliates",
      tabs: {
        affiliates: "Ambassadors",
        payouts: "Payouts",
        assets: "Branding assets",
      },
      newAffiliate: "New ambassador",
      create: "Create",
      createHint:
        "If an Edgely account exists with this email it will be linked. Otherwise an account is created and credentials are sent by email.",
      createdToast: "Ambassador created, access email sent.",
      savedToast: "Ambassador updated.",
      cancel: "Cancel",
      save: "Save",
      cols: {
        email: "Email",
        refCode: "Code",
        rate: "Rate",
        duration: "Duration (months)",
        clicks: "Clicks",
        referred: "Referred",
        commissions: "Commissions",
        balanceDue: "Balance due",
        actions: "Actions",
        active: "Active",
        date: "Date",
        amount: "Amount",
        paymentDetails: "Payment details",
        status: "Status",
      },
      form: {
        email: "Email",
        fullName: "Full name",
        companyName: "Company / alias",
        ratePercent: "Rate (%)",
        durationMonths: "Duration (months)",
        refCode: "Affiliate code",
        refCodeOptional: "Affiliate code (optional)",
        refCodePlaceholder: "auto-generated if empty",
        notes: "Internal notes",
        adminNotes: "Note (internal only)",
        assetName: "Name",
        assetCategory: "Category",
        sortOrder: "Order",
        file: "File",
      },
      emptyAffiliates: "No ambassadors yet.",
      emptyPayouts: "No payout requests.",
      emptyAssets: "No branding assets.",
      payoutStatus: {
        requested: "Requested",
        paid: "Paid",
        rejected: "Rejected",
      },
      markPaid: "Mark as paid",
      reject: "Reject",
      rejectTitle: "Reject the payout request",
      payoutPaidToast: "Payout marked as paid, email sent to the ambassador.",
      payoutRejectedToast: "Payout request rejected.",
      assetCategories: {
        logo: "Logo",
        banner: "Banner",
        screenshot: "Screenshot",
        guidelines: "Guidelines",
      },
      upload: "Upload",
      uploadError: "File upload failed.",
      assetCreatedToast: "Asset added.",
      assetDeletedToast: "Asset deleted.",
    },
    adminB2b: {
      pageTitle: "B2B / VIP",
      partnersTitle: "Partners",
      newPartner: "New partner",
      partnerName: "Partner name",
      contactEmail: "Contact email",
      createdAt: "Created on",
      seats: "Seats",
      seatsBillable: "Billable seats",
      statusActive: "Active",
      statusPending: "Pending",
      statusShadowed: "Self subscribed",
      statusRevoked: "Revoked",
      apiActive: "API active",
      apiDisabled: "API disabled",
      rotateKey: "Rotate key",
      rotateKeyConfirm:
        "Generate a new key? The old one will stop working immediately.",
      disablePartner: "Disable",
      enablePartner: "Re-enable",
      disablePartnerConfirm:
        "Disable this partner? Their API will be refused and their scheduled revocations will be frozen. Existing members keep their access.",
      keyDialogTitle: "API key for",
      keyDialogWarning:
        "This key will never be shown again. Copy it now and send it to the partner through a secure channel.",
      copy: "Copy",
      copied: "Copied",
      close: "Close",
      grantsTitle: "Seats",
      newGrant: "New seat",
      filterStatusAll: "All statuses",
      filterEmailPlaceholder: "Filter by email",
      colEmail: "Email",
      colPartner: "Partner",
      colExternalRef: "Discord ref",
      colStatus: "Status",
      colUser: "Edgely account",
      colActivatedAt: "Activated on",
      colEndsAt: "Scheduled end",
      colCreatedAt: "Created on",
      noGrants: "No seats",
      revoke: "Revoke",
      revokeDialogTitle: "Revoke this seat",
      revokeNow: "Revoke now",
      revokeAtDate: "Revoke on a date",
      revokePastDate: "The revocation date must be in the future",
      revokeScheduledToast: "Revocation scheduled",
      revokedToast: "Seat revoked",
      newGrantDialogTitle: "New seat",
      email: "Email",
      externalRef: "External ref (optional)",
      create: "Create",
      cancel: "Cancel",
      createdToast: "Seat created",
      page: "Page",
      of: "of",
      colActions: "Actions",
      previous: "Previous",
      next: "Next",
      partnerUpdatedToast: "Partner updated",
      filterPartnerAll: "All partners",
    },
    b2bMember: {
      offeredBy: "Access offered by {partner}",
      offeredBadge: "Offered",
      pricingBannerTitle: "Your Max access is offered by {partner}",
      pricingBannerBody:
        "You have nothing to pay: all Max features are already included in your access.",
      checkoutBlocked:
        "Your Max access is offered by a partner: there is nothing to buy.",
      currentPlan: "Your current plan",
    },
    adminDashboard: {
      usersByPlanTitle: "Users by plan",
      usersByPlanSubtitle: "Annual plans are grouped with their monthly tier.",
      activeUsersLabel: "active in 7d",
      activeUsersHint: "Users who opened the app within the last 7 days.",
      activeUsersTotal: "active in the last 7 days, all plans",
      searchPlaceholder: "Search an email or username...",
      sortBy: "Sort by",
      sortNewest: "Signup (newest)",
      sortOldest: "Signup (oldest)",
      sortLastActive: "Last active",
      sortEmail: "Email (A-Z)",
      emptyColumn: "No user on this plan",
      noSearchResult: "No result",
      annualBadge: "Annual",
      adminBadge: "Admin",
      brokerBadge: "Broker connected",
      graceBadge: "Payment failed",
      expiredBadge: "Grace expired",
      inactiveBadge: "Inactive",
      neverActive: "Never signed in",
      signedUpOn: "Joined",
      lastActiveOn: "Seen",
      mrrTitle: "Recurring revenue",
      mrrTotal: "Total MRR",
      mrrMax: "Max MRR",
      mrrPro: "Pro MRR",
      paymentIssues: "Failed payments",
      totalUsers: "Users",
      mrrHint: "Computed from live subscriptions, annual plans prorated monthly.",
      cohortTitle: "Retention by cohort",
      cohortHint:
        "Share of each month's signups still active in the last 30 days.",
      cohortMonth: "Month",
      cohortSignups: "Signups",
      cohortRetained: "Still active",
      cohortRate: "Retention",
      cohortEmpty: "No signup over the period",
      acquisitionTitle: "Acquisition",
      acquisitionHint: "Signup sources over the last 30 days.",
      signups7d: "Signups (7d)",
      signups30d: "Signups (30d)",
      sourceLabel: "Source",
      sourceDirect: "Direct",
      sourceEmpty: "No signup in the last 30 days",
      brokerHealthTitle: "Broker connections to watch",
      brokerHealthHint:
        "Connections in error or needing a reconnect. Idle connections are not listed.",
      brokerHealthEmpty: "All broker connections are healthy",
      quotaTitle: "Tradovate API quota",
      quotaHint:
        "Calls actually made over the last hour. The limit that applies is about 5,000 calls/h per Tradovate user, across all their connections (measured on 10/14: 6,028 calls/h from our IP, penalties for a single user only). The verbally announced \"600/h per IP\" was never enforced.",
      quotaEmpty: "No Tradovate call in the last hour",
      quotaPerUserLabel: "Per user (accounts, calls, share of their hourly limit)",
      quotaWorstUser: "Heaviest user",
      quotaIpTotal: "IP total: {calls} calls over the hour (for information)",
      quotaNearLimit: "Near limit",
      quotaOverLimit: "Limit exceeded",
      quotaHistoryTitle: "Tradovate quota history",
      quotaHistoryHint:
        "Calls issued per hour. History starts when this view went live: earlier hours cannot be recovered.",
      quotaHistoryEmpty: "No data yet. The curve fills in hour by hour.",
      quotaHistoryCallsLabel: "Calls",
      quotaHistoryRateLimitLabel: "Rate limits",
      quotaHistoryPeakLabel: "Peak",
      quotaHistoryRange24h: "24h",
      quotaHistoryRange48h: "48h",
      quotaHistoryRange7d: "7d",
      quotaHistoryRange30d: "30d",
      quotaHistoryQuotaLine: "Limit",
      endpointBreakdownTitle: "REST usage by endpoint",
      endpointBreakdownHint:
        "Actual breakdown of Tradovate calls, measured rather than estimated. The dominant endpoint is the one that decides whether this holds at scale.",
      endpointBreakdownEmpty:
        "No attributed calls over this period. The breakdown only covers sync cycles run after it went live.",
      tradovateErrorsTitle: "Tradovate errors and penalties",
      tradovateErrorsHint:
        "When and why the quota gets hit: every 429/423 is timestamped with its endpoint, p-time penalty and phase. Also the instrument to verify whether successful calls really count against the quota.",
      tradovateErrorsEmpty:
        "No penalty over this period: no call received a 429/423. This is the expected state.",
      tradovateErrorsRateLimits: "Penalties",
      tradovateErrorsCalls: "Calls made",
      tradovateErrorsCaptcha: "p-captcha",
      tradovateErrorsCaptchaTag: "captcha",
      tradovateErrorsRetryFailures: "Failed retries",
      tradovateErrorsRetryHint:
        "Requests replayed after the p-time delay and penalized again: the saturation persisted.",
      tradovateErrorsHitsCol: "Penalties",
      tradovateErrorsCallsCol: "Calls",
      tradovateErrorsRecentTitle: "Recent incidents",
      tradovateErrorsTimeCol: "Time",
      tradovateErrorsStatusCol: "Status",
      tradovateErrorsPenaltyCol: "p-time",
      tradovateErrorsPhaseCol: "Phase",
      tradovateErrorsHostCol: "Host",
      tradovateErrorsUserCol: "User",
      tradovateErrorsPhaseInitial: "first attempt",
      tradovateErrorsPhaseRetry: "failed retry",
      accountLifecycleTitle: "Account lifecycle through the socket",
      accountLifecycleHint:
        "Does the socket see a new, gone, breached or funded account BEFORE the REST reads it from /account/list? Shadow measure: REST remains the only decider.",
      accountLifecycleEmpty: "No lifecycle decision over this period.",
      accountLifecycleRange14d: "14d",
      accountLifecycleVerdictGreen: "Criterion met: /account/list can be replaced by the socket over this window.",
      accountLifecycleVerdictRed: "Criterion not met. What is missing:",
      accountLifecycleVerdictUnjudged: "Not yet judgeable: at least one decision type does not have enough cases with a live socket yet.",
      accountLifecycleReasonUnjudged: "no decision observed",
      accountLifecycleReasonNotSeen: "decision(s) not seen by the socket",
      accountLifecycleReasonTooFew: "case(s) seen, 3 required",
      accountLifecycleReasonUnstable: "different evidence fields, exactly one required",
      accountLifecycleReasonWsDeleted: "account(s) the socket believes deleted while REST still lists them: zero tolerated",
      accountLifecycleDecisionNew: "New account",
      accountLifecycleDecisionGone: "Account gone",
      accountLifecycleDecisionBreach: "Rule breach",
      accountLifecycleDecisionFunded: "Funded transition",
      accountLifecycleCoverageTitle: "Coverage by decision",
      accountLifecycleColDecision: "REST decision",
      accountLifecycleColWithSocket: "With socket",
      accountLifecycleColSeenBefore: "Seen before",
      accountLifecycleColCoverage: "Coverage",
      accountLifecycleColMedianLead: "Median lead",
      accountLifecycleColEvidence: "Distribution (n)",
      accountLifecycleColNoSocket: "No socket",
      accountLifecycleNoSocketHint: "Decisions taken without a live socket: counted apart, never in the percentage.",
      accountLifecycleTransitionsTitle: "Observed transitions",
      accountLifecycleTransitionsHint: "What prop firms actually change on an account. This is where the signature of a breach or a funded transition shows up.",
      accountLifecycleTransitionsEmpty: "No transition over the period (first sights do not count).",
      accountLifecycleColField: "Field",
      accountLifecycleColFrom: "From",
      accountLifecycleColTo: "To",
      accountLifecycleColCount: "Accounts",
      accountLifecycleColLastSeen: "Last seen",
      accountLifecycleColExample: "Example (Tradovate id)",
      accountLifecycleFirstSight: "accounts seen for the first time",
      accountLifecycleParityTitle: "/account/list vs socket parity",
      accountLifecycleParityRestOnly: "REST only",
      accountLifecycleParityWsOnly: "Socket only",
      accountLifecycleParityWsDeleted: "Deleted on socket, listed by REST",
      accountLifecycleParityWsDeletedLag: "Deleted on socket BEFORE REST (lead)",
      accountLifecycleParityName: "Different names",
      accountLifecycleParityEmpty: "No presence or name mismatch.",
      accountLifecycleProbeTitle: "accountRiskStatus entity",
      accountLifecycleProbeSeen: "pushed by Tradovate: a candidate for the breach signature",
      accountLifecycleProbeNotSeen: "not seen yet (not a proof of absence)",
      accountLifecycleReasonUnparsed: "seen-before row(s) with unreadable evidence: verdict blocked",
      accountLifecycleColCommonFields: "Common field",
      accountLifecycleTruncated: "Partial data: more than 5,000 events in the window, the oldest are not read. Narrow the window.",
      accountLifecycleLoading: "Loading the measure...",
      accountLifecycleError: "Could not read the measure: the card cannot render a verdict.",
      accountLifecycleParityRestLabel: "REST",
      accountLifecycleParityWsLabel: "WS",
      endpointBreakdownEndpointCol: "Endpoint",
      endpointBreakdownCallsCol: "Calls",
      endpointBreakdownShareCol: "Share",
      endpointBreakdownPerHourCol: "Per hour",
      endpointBreakdownUnattributed: "Unattributed calls",
      endpointBreakdownUnattributedHint:
        "Calls counted before the breakdown went live. They stay in the total, without detail.",
      endpointBreakdownUsersTitle: "By user",
      endpointBreakdownUserCol: "User",
      endpointBreakdownAccountsCol: "Active accounts",
      endpointBreakdownRatioCol: "Calls / account / h",
      endpointBreakdownRatioHint:
        "The only figure comparable between a user with 28 accounts and one with 3, and the only one that projects to scale.",
      endpointBreakdownTransportCol: "Transport",
      endpointBreakdownTransportRest: "REST",
      endpointBreakdownTransportWs: "WebSocket",
      endpointBreakdownBetaBadge: "Socket",
      endpointBreakdownWitnessBadge: "REST",
      endpointBreakdownProjectionTitle: "Projection at scale",
      endpointBreakdownProjectionHint:
        "Model measured over the window: fixed cost per OAuth connection plus cost per account, at the median number of accounts per connection. Compared against the 600 calls per hour limit, the worst case until the penalty measurement settles it.",
      endpointBreakdownProjectionAccounts: "Accounts",
      endpointBreakdownProjectionConnections: "connections",
      endpointBreakdownCostModel: "Measured cost:",
      endpointBreakdownCostPerConnection: "calls / h per connection +",
      endpointBreakdownCostPerAccount: "per account",
      endpointBreakdownProjectionCalls: "Projected calls / h",
      endpointBreakdownProjectionWithin: "Under the limit",
      endpointBreakdownProjectionOver: "Over the limit",
      endpointBreakdownProjectionUnknown: "Not enough data",
      endpointBreakdownSocketTitle: "WebSocket health",
      endpointBreakdownSocketEvents: "Events received",
      endpointBreakdownSocketReconnects: "Reconnections",
      endpointBreakdownSocketSilent:
        "No events received while users are on the socket. A silent socket looks exactly like a total success: check that it is actually connected.",
      endpointBreakdownSocketNotStarted:
        "WebSocket transport is not live yet. Everyone is on REST.",
      endpointBreakdownMedianLabel: "Median ratio",
      shadowDetailRest: "REST",
      shadowDetailWs: "Real time",
      wsWrittenLabel: "Trades written by real time",
      wsWriteFailuresLabel: "Write failures",
      wsRepairedLabel: "Gaps repaired by the control",
      wsWrittenActive:
        "The real-time flow is writing to the database. The trades below come from it.",
      wsWrittenNone:
        "No real-time writes over the period. Either the switch is off, or no trade has come in since.",
      compareTitle: "Trade-by-trade comparison",
      compareHint:
        "What each flow sees of the same trade. Matching rows are shown too: verifying a match is what lets you state there is no drift.",
      compareTrade: "Trade",
      compareMatch: "Match",
      compareMismatch: "Mismatch",
      compareNotSeen: "Not seen in real time",
      compareFeesUnverified: "Fees not verified",
      compareFeesUnverifiedHint:
        "The stream does not carry fees: the commission shown comes from REST on both sides, so it is not actually compared.",
      compareEmpty:
        "No comparison available yet. It fills in on the next beta tester trade.",
      betaDiagTitle: "Beta tester diagnostic",
      betaDiagHint:
        "Per-connection state over the last 24 hours of open market (weekends and breaks excluded). A silent socket uses no quota and looks exactly like a total success: telling those apart is what this screen is for.",
      betaDiagEmpty: "No user enrolled in the beta.",
      betaDiagConnection: "Connection",
      betaDiagUser: "User",
      betaDiagHealth: "State",
      betaDiagEvents: "Events",
      betaDiagReconnects: "Reconnections",
      betaDiagDiffs: "Discrepancies",
      betaDiagLeaseAge: "Lease (min)",
      betaDiagHealthy: "Healthy",
      betaDiagSilent: "Silent",
      betaDiagStale: "No event for 1 market hour",
      betaDiagFlapping: "Unstable",
      betaDiagCut: "Cut, not reopened",
      betaDiagModeFallback: "REST fallback",
      betaDiagNeverConnected: "Never connected",
      betaDiagFlagWithoutPlan: "Flag without Max plan",
      betaDiagNotEnrolled: "Not enrolled",
      betaDiagSubscribed: "Subscribed types",
      betaDiagSockets: "Live sockets",
      betaDiagSocketsHint:
        "Live sockets in this process, against TRADOVATE_WS_MAX_SOCKETS: beyond it, a connection stays on REST without any signal. Separate from the 50 sockets Tradovate allows per end user. Cut = the server closed the socket and nothing reopens it before the next token renewal.",
      betaDiagProblems: "Needs attention",
      betaDiagPropFirm: "Prop firm",
      betaDiagWriteMode: "Actual mode",
      betaDiagCadence: "REST cadence",
      betaDiagControl: "Control",
      betaDiagLatency: "Socket latency",
      betaDiagModeWsOnly: "Socket only",
      betaDiagModeRestOnly: "REST",
      betaDiagCadenceSpaced: "spaced",
      betaDiagCadenceNominal: "15 min",
      betaDiagControlNever: "never",
      betaDiagControlHint:
        "Actual mode: what the next cycle does, not what the plan intends. REST fallback = socket refused, REST rereads everything every 15 min (reason on hover). Control: hours since the last daily control (red beyond 26 h in socket mode), then trades the socket had missed and the control repaired over 24 h.",
      betaDiagLatencyHint:
        "Latency: median ms between the fill at Tradovate and the socket write, then the number of trades written by the socket over 24 h.",
      betaDiagWindow: "Window since {since}",
      betaDiagSocketShare: "Socket share",
      betaDiagFallback: "Fallback",
      betaDiagShareHint:
        "Socket share: trades written by the socket out of all trades in the window (socket, REST fallback, control). Fallback: approximate time spent in REST fallback (one cycle = 15 min), measured since 2026-10-18. Reconnects and \"Unstable\": only cuts during market hours count (the Friday-close cuts are ignored).",
      reauthTitle: "In-place socket re-authorization (B2)",
      reauthHint: "On token renewal, the socket receives the new token instead of being closed and reopened. The test only counts if the session is PROVEN alive after the old token expired: a proof request goes through the socket 85 min after each re-authorization.",
      reauthDisabled: "B2 is off on this server: TRADOVATE_WS_REAUTH_USERS is not set. Every socket is recycled as before.",
      reauthEmpty: "No Tradovate connection for the listed users.",
      reauthUsers: "Listed users",
      reauthCountsHint: "Re-authorizations and proofs: succeeded / failed over the window. A failure triggers the usual recycle, with no data loss.",
      reauthColUser: "User",
      reauthColPropFirm: "Prop firm",
      reauthColVerdict: "Verdict",
      reauthColReauth: "Re-authorizations",
      reauthColProof: "Proofs",
      reauthColLast: "Last event",
      reauthVerdictProven: "Session proven",
      reauthVerdictAwaiting: "Proof pending",
      reauthVerdictFailing: "Failed, recycled",
      reauthVerdictNoActivity: "No attempt",
      backfillRepairTitle: "Backfill: accounts to replay",
      backfillRepairHint: "Until 2026-10-19 the backfill skipped every losing trade (unrecognized negative format): the journal only kept wins, and an imported account had its starting balance shifted by that fictitious gain. Replaying an account writes its missing losers and, for an imported account, fixes the starting balance. A replayed account leaves the list.",
      backfillRepairEmpty: "No account to replay.",
      backfillRepairColAccount: "Account",
      backfillRepairColUser: "User",
      backfillRepairColTrades: "Backfilled trades",
      backfillRepairColInitial: "Starting balance",
      backfillRepairColBroker: "Broker balance",
      backfillRepairColKind: "Replay",
      backfillRepairKindNew: "Imported account (starting balance fixed)",
      backfillRepairKindGap: "Sync gap (journal completed)",
      backfillRepairInactive: "Inactive, cannot replay",
      backfillRepairReplay: "Replay",
      backfillRepairReplayAll: "Replay all active accounts",
      backfillRepairRequested: "Replay requested",
      backfillRepairKindBoth: "Imported account + sync gap",
      backfillRepairKindNone: "Nothing to replay",
      backfillRepairLastRun: "Last run",
      backfillRepairError: "Replay refused: the backfill may be off on this server (TRADOVATE_BACKFILL_ENABLED).",
      endpointBreakdownReplaceable: "WebSocket",
      endpointBreakdownReplaceableYes: "Replaceable",
      endpointBreakdownReplaceableNo: "Stays on REST",
      endpointBreakdownSavings: "Potential saving",
      endpointBreakdownAfterWs: "After switch",
      betaTesterColumn: "Beta tester",
      betaTesterEnable: "Enroll in beta",
      betaTesterDisable: "Remove from beta",
      betaTesterNoEffect:
        "Flag is set, but has no effect until the user is on a Max plan.",
      brokerProvider: "Broker",
      brokerUser: "User",
      brokerStatus: "Status",
      brokerLastSync: "Last sync",
      brokerAccounts: "Accounts",
      brokerNeverSynced: "Never synced",
      statusError: "Error",
      statusNeedsReauth: "Reconnect required",
      statusDelayed: "Sync delayed",
      statusDeprovisioned: "Deprovisioned",
      statusForeignToken: "Wrong credentials",
      detailTitle: "User details",
      detailLoading: "Loading...",
      detailPlan: "Plan",
      detailStatus: "Subscription status",
      detailJoined: "Joined",
      detailLastActive: "Last active",
      detailOnboarding: "Onboarding",
      detailOnboardingDone: "Completed",
      detailOnboardingPending: "Not completed",
      detailAcquisition: "Acquisition",
      detailActivity: "Activity",
      detailTrades: "Trades",
      detailSessions: "Sessions",
      detailNotes: "Notes",
      detailConnections: "Broker connections",
      detailAccounts: "Accounts",
      detailNoAccounts: "No account",
      detailNextBilling: "Next billing date",
      detailClose: "Close",
      detailNone: "Not set",
    },
    playbook: {
      title: "Playbook",
      subtitle: "Declare your strategy so Edgy can analyse your trading.",
      stepOf: "Step {current} of {total}",
      next: "Next",
      back: "Back",
      save: "Save",
      saving: "Saving…",
      edit: "Edit",
      versionLabel: "Version {version}",
      activeVersion: "Active version",
      createdOn: "Created on {date}",
      emptyTitle: "No playbook yet",
      emptyDescription:
        "Your playbook describes your strategy: markets, sessions, setups and risk rules. It takes 2-3 minutes to fill in and underpins every Edgy analysis.",
      emptyCta: "Create my playbook",
      savedToast: "Playbook saved.",
      newVersionToast: "New playbook version created.",
      saveError: "Could not save the playbook.",
      newPlaybook: "New playbook",
      deleteAction: "Delete",
      deleteConfirm: "Delete this playbook and all its versions? Your past analyses will be kept.",
      deletedToast: "Playbook deleted.",
      deleteError: "Could not delete the playbook.",
      nameLabel: "Playbook name",
      namePlaceholder: "e.g. NQ opening scalp",
      nameRequired: "The playbook name is required.",
      targetsLabel: "Analysed accounts",
      targetsDescription: "Edgy will analyse trades from these accounts and groups.",
      targetsAccounts: "Accounts",
      targetsGroups: "Groups",
      targetsEmpty: "No account selected. Edgy will analyse all your trades.",
      referenceAccountLabel: "Reference account for prop firm rules",
      referenceAccountHelp: "Only used to pre-fill drawdown and profit target.",
      steps: {
        strategy: "Strategy & markets",
        setups: "Setups & risk",
        rules: "Rules",
      },
      strategyStep: {
        title: "Strategy & markets",
        description: "What you trade, and when.",
        strategyLabel: "Linked strategy",
        strategyPlaceholder: "No strategy",
        strategyNameLabel: "Playbook name",
        strategyNamePlaceholder: "e.g. US open breakout",
        strategyNameHint: "This name is frozen in this version, even if you rename the strategy later.",
        marketsLabel: "Markets traded",
        marketsHint: "Pick the instruments you trade with this strategy.",
        marketsEmpty: "No market selected",
        sessionsLabel: "Trading sessions",
        sessionsHint: "The hours during which you take positions.",
        addSession: "Add a session",
        sessionName: "Name",
        sessionStart: "Start",
        sessionEnd: "End",
        sessionHours: "Hours",
        sessionMinutes: "Minutes",
        sessionTimezone: "Timezone",
        removeSession: "Remove this session",
        presetAsia: "Asia",
        presetLondon: "London",
        presetNewYork: "New York",
      },
      setupsStep: {
        title: "Setups & risk",
        description: "Your entry and exit conditions, and your risk per trade.",
        setupsLabel: "Setups",
        addSetup: "Add a setup",
        setupName: "Setup name",
        setupNamePlaceholder: "e.g. VWAP pullback",
        entryRules: "Entry rules",
        entryRulesPlaceholder: "e.g. break of the opening range high with above-average volume.",
        exitRules: "Exit rules",
        exitRulesPlaceholder: "e.g. TP at the next major level, SL below the last swing low.",
        removeSetup: "Remove this setup",
        noSetups: "No setup yet. Add at least one.",
        riskLabel: "Max risk per trade",
        riskTypePercent: "% of account",
        riskTypeAmount: "Fixed amount",
        riskValueLabel: "Value",
        maxTradesPerDayLabel: "Max trades per day",
        maxTradesPerDayPlaceholder: "e.g. 3",
        minRiskRewardLabel: "Minimum risk/reward ratio",
        minRiskRewardPlaceholder: "e.g. 2",
      },
      rulesStep: {
        title: "Rules",
        description: "Your prop firm constraints and your personal rules.",
        sourceAccountLabel: "Reference account",
        sourceAccountHint: "The rules below are read from this account.",
        sourceAccountNone: "No account",
        derivedTitle: "Detected rules",
        derivedHint:
          "These values come from your account's prop firm and stay up to date automatically. Only change them if they don't match your reality.",
        noRulesDetected: "No prop firm rule detected for this account.",
        maxDailyDrawdown: "Max daily loss",
        maxTotalDrawdown: "Max total drawdown",
        profitTarget: "Profit target",
        overrideToggle: "Customise these rules",
        overrideHint: "Your values will replace the detected ones.",
        resetOverride: "Back to detected values",
        unitPercent: "%",
        unitAmount: "amount",
        personalRulesLabel: "Personal rules",
        personalRulesPlaceholder:
          "e.g. no trade after two consecutive losses, 15-minute break after each loss…",
        personalRulesHint: "Your own guardrails, beyond the prop firm rules.",
      },
      validation: {
        strategyNameRequired: "Give your playbook a name.",
        marketsRequired: "Select at least one market.",
        setupNameRequired: "Every setup needs a name.",
        setupRequired: "Add at least one setup.",
        riskValueRequired: "Enter a valid max risk per trade.",
      },
    },
    edgy: {
      buttonLabel: "Analyse my trading",
      drawerTitle: "Edgy",
      drawerSubtitle: "Your trading coach",
      close: "Close",
      checking: "Checking your data…",
      missingPrereqsTitle: "One more step",
      missingPrereqsIntro: "Before I can analyse, I still need:",
      addStrategy: "Add my strategy",
      addTrades: "Add my trades",
      noTradesOnStrategy:
        "None of your trades is linked to this playbook's strategy. Link some from your journal to run an analysis.",
      selectStrategyTitle: "Which strategy should I analyse?",
      selectStrategySubtitle: "Pick one and I'll start right away.",
      tradesCount: "{count} trades",
      versionLabel: "v{version}",
      scanning: {
        reading: "Reading {trades} trades…",
        sessions: "Analysing sessions and hours…",
        playbook: "Comparing against your playbook…",
        leaks: "Detecting capital leaks…",
        writing: "Writing your Edge Report…",
      },
      reveal: {
        strengths: "Your strengths",
        leaks: "Your leaks",
        playbookGaps: "Playbook vs reality",
        weeklyActions: "Your plan for the week",
        edgeScore: "Edge Score",
        declared: "Declared",
        actual: "Actual",
        impact: "Impact",
        estimatedCost: "Estimated cost",
        checkMetric: "Track",
        copy: "Copy",
        copied: "Copied",
        copyAll: "Copy the whole report",
        lowSampleNotice: "Few trades in this period, the analysis will sharpen over time.",
      },
      questions: {
        title: "Ask me a question",
        limitReached: "You've asked every question on this report.",
        asking: "Edgy is thinking…",
        error: "I couldn't answer. Try again in a moment.",
      },
      credits: {
        remaining: "{remaining} analyses left this month",
        discovery: "Free discovery analysis",
        none: "No analysis left this month",
      },
      noCredits: {
        title: "Out of credits this month",
        description: "Your analyses refill automatically.",
        refillOn: "Next refill on {date}",
        upgrade: "Upgrade my plan",
      },
      betaLock: {
        badge: "Beta",
        title: "Edgy is in beta on the Max plan",
        description:
          "While Edgy is in beta testing, AI analysis is reserved for Max subscribers. Upgrade to Max to start using it today.",
        upgrade: "Explore the Max plan",
        lockedTooltip: "Max plan only during the beta",
      },
      errors: {
        title: "Sorry, that didn't work",
        retry: "Try again",
        killSwitch: "Edgy is temporarily unavailable. Check back a bit later.",
        cooldown: "You just ran an analysis. Next one available at {time}.",
        generic: "I couldn't finish the analysis. Your credit was not used.",
        notConfigured: "Edgy is not enabled on this server yet.",
      },
      progress: {
        title: "Since last time",
        followed: "Held",
        partiallyFollowed: "Partly held",
        notFollowed: "Not held",
        unverifiable: "Not measurable",
      },
      history: {
        navLabel: "My analyses",
        pageTitle: "My analyses",
        pageSubtitle: "Find every report you've had, and how you've progressed.",
        empty: "No analysis yet.",
        emptyCta: "Start your first analysis from the \"Analyse my trading\" button.",
        scoreTrend: "Your Edge Score over time",
        questionsCount: "{count} questions",
        tradesAnalysed: "{count} trades analysed",
        allStrategies: "All strategies",
        filterStrategy: "Strategy",
        back: "Back to analyses",
        loadMore: "Load more",
        conversation: "Your conversation with Edgy",
        analysedOn: "Analysis from {date}",
      },
    },
    notificationsCenter: {
      title: "Notifications",
      bellAriaLabel: "Notifications",
      unreadAriaLabel: "{count} unread notifications",
      markAllRead: "Mark all as read",
      dismiss: "Mark as done",
      dismissAll: "Clear all",
      empty: "No notifications",
      reportReady: "Your {period} report is ready",
      planUpgraded: "Your subscription moved to {plan}",
      planDowngraded: "Your subscription moved to {plan}",
      paymentFailed: "Your subscription payment failed",
      paymentFailedWithDays:
        "Payment failed: {days} days left to update your details",
      planDowngradedPaymentFailed:
        "Your {plan} subscription ended due to a failed payment",
      accountChallengeCompleted:
        "Challenge passed: your prop firm closed account {account}. It is now inactive, its trades are kept.",
      accountClosedBroker:
        "Account {account} no longer exists at your broker. It is now inactive, its trades are kept.",
      accountDrawdownBreach:
        "Drawdown rule breached on account {account}. It is now inactive, its trades are kept.",
      subscriptionCancelQuestion:
        "{account} is closed: did you cancel its subscription? If not, the prop firm will bill you on renewal.",
      chargeUpcoming: "{amount} charge for {name} on {date}",
      subscriptionResetReactivate: "{account} renewed today and was reset: reactivate it?",
      payoutDetected:
        "Payout of {amount} detected on account {account} and added to the Prop Firm Manager. If it is not a payout, fix it from the Per-account tracking widget.",
      propFirmAdjustmentDetected:
        "{amount} removed from account {account}: it matches the PnL of the {session} session, taken back by the prop firm (rule violation). Counted in the balance, not as a payout. If it was a payout, fix it from the Per-account tracking widget.",
      walletTransferDetected:
        "Transfer of {gross} from account {account} to your wallet, {amount} received. Added to the Wallet card of the Prop Firm Manager.",
      walletTransferDetectedToConfirm:
        "Transfer of {gross} from account {account} to your wallet, {amount} received (estimate). Confirm the amount received in the Wallet card of the Prop Firm Manager.",
      tradovateAccountsDiscoveredOne:
        "1 new Tradovate account detected. Import it from your accounts.",
      tradovateAccountsDiscovered:
        "{count} new Tradovate accounts detected. Import them from your accounts.",
      tradovateRealtimeInterrupted:
        "Real-time connection interrupted for {propFirm}. Your trades are still synced every 15 minutes.",
      tradovateNeedsReauth:
        "Reconnection required for {propFirm}: your trade sync is paused. Reconnect from your accounts.",
      mentorFallback: "New activity in your mentor space",
      periodMonthly: "monthly",
      periodQuarterly: "quarterly",
      periodAnnual: "annual",
    },
    fundedLineage: {
      importLabel: "Follows challenge",
      importNone: "None (new account)",
      importHint: "Links this PA to the passed challenge: the challenge is closed as passed and its tracking (PnL, costs, lifetime) carries on with this account.",
      importSuggested: "suggested",
      targetReached: "target reached",
      inactiveTag: "deactivated",
      cardTitle: "Original challenge",
      cardDescription: "Link this funded account to the challenge that produced it, to track the full journey (PnL, costs, lifetime).",
      cardLinkedTo: "Follows challenge {name}",
      cardLinkedHint: "Unlinking does not reactivate the challenge.",
      cardSelectPlaceholder: "Pick the challenge",
      cardNoCandidates: "No eligible challenge: same prop firm, older than this account, not breached and not already linked.",
      cardLinkButton: "Link",
      cardUnlinkButton: "Unlink",
      cardLinked: "Challenge linked",
      cardUnlinked: "Link removed",
      cardConfirmTitle: "Link to challenge {name}?",
      cardConfirmBody: "The challenge will be deactivated as passed and its prop firm subscription stopped.",
      cancel: "Cancel",
      modalNone: "None",
      modalLinkHint: "The selected challenge will be deactivated as passed and its prop firm subscription stopped. The funded transition shows in the calendar on the date this account arrived.",
      modalChangeHint: "Changing or removing the link does not reactivate the previous challenge.",
    },
    paymentGrace: {
      modalTitle: "Your payment didn't go through",
      modalBody:
        "We couldn't charge your {plan} subscription. You keep full access for another {days} days while you update your payment method.",
      modalBodyLastDay:
        "We couldn't charge your {plan} subscription. Today is your last day to update your payment method.",
      modalConsequence:
        "Without payment, your account returns to the free plan on {date}.",
      updatePayment: "Update my payment",
      later: "Later",
      bannerText:
        "Payment failed. {days} days left before returning to the free plan.",
      bannerTextLastDay:
        "Payment failed. Last day before returning to the free plan.",
      bannerAction: "Fix payment",
      expiredModalTitle: "Your subscription has ended",
      expiredModalBody:
        "Following a failed payment, your account returned to the free plan. Your data is intact: reactivate your subscription to get all your features back.",
      expiredAction: "Reactivate my subscription",
      daysLeft: "{days} days left",
      oneDayLeft: "Last day",
    },
    newNav: {
      more: "More",
      myPages: "My pages",
      addPage: "Add a page",
      collapse: "Collapse",
      expand: "Expand",
      settings: "Settings",
      lockedTooltip: "Available on a higher plan",
      menu: "Menu",
      dashboard: {
        overview: "Overview",
      },
    },
  },
};

